// traveliitm/frontend/src/App.js
// import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useMemo,
  useCallback
} from 'react';

import axios from 'axios';
import { io } from 'socket.io-client';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './App.css';
import * as turf from '@turf/turf';
import JourneyPlanner from './components/JourneyPlanner';
import JourneyMapLayer from './components/JourneyMapLayer';

// ---- Assets in src/assets ----
import userIcon from './assets/user-icon.png';
import stopIconImg from './assets/bus-stop.png';
import iitmLogo from './assets/iitm-logo.png';

// ---- Backend base URL ----
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---- Temporary maintenance mode ----
// Set this to false after server migration is completed.
const MAINTENANCE_MODE = true;

// ---- Static roads URL (public/roads.geojson) ----
const ROADS_URL = `${process.env.PUBLIC_URL || ''}/roads.geojson`;

// ---- Map zoom param ----
const DEFAULT_ZOOM = 18;

// Keep the public experience focused on IIT Madras instead of allowing users
// to pan across Tamil Nadu/world tiles. The small margin covers all campus roads.
const CAMPUS_MAP_BOUNDS = [
  [12.9780, 80.2170],
  [13.0115, 80.2485],
];

// ---- Smoothing / Snap params ----
const SMOOTH_ALPHA = 0.3;
const HEADING_ALPHA = 0.35;
const SNAP_MAX_DIST_M = 20;

// Staleness threshold for under-maintenance banner
const STALE_MS = 150_000; // ~2.5 minutes

// Initial center (fallback)
const center = { lat: 12.9915, lng: 80.2336 };

// ---------------- STOPS WITH IDS (for route math) ----------------
const busStops = [
  { name: 'Velachery Gate IIT Campus Stop', lat: 12.988763557585395, lng: 80.22367960515129, id: 1 },
  { name: 'NAC2 Stop Towards GC', lat: 12.990236, lng: 80.227548, id: 2 },
  { name: 'Nac2 Towards Velachery Stop', lat: 12.9898636, lng: 80.2271109, id: 3 },
  { name: 'CRC Towards GC Stop', lat: 12.99089, lng: 80.230274, id: 4 },
  { name: 'CRC Towards Velachery Stop', lat: 12.99079680, lng: 80.23016660, id: 5 },  
  { name: 'HSB Stop Towards Velachery Stop', lat: 12.99076780, lng: 80.23168950, id: 6},
  { name: 'HSB Stop Towards GC', lat: 12.991037, lng: 80.232065, id: 7},
  { name: 'KV Stop Towards Main Gate', lat: 12.991868573807471, lng: 80.23367454352295, id: 8 },
  { name: 'KV Stop Towards GC', lat: 12.991910, lng: 80.233785, id: 9 },
  { name: 'Postoffice Stop Towards Main Gate', lat: 12.99388680, lng: 80.23428760, id: 10 },
  { name: 'Postoffice Stop Towards GC', lat: 12.99393340, lng: 80.23459350, id: 11 },
  { name: 'E1 Stop Towards Main Gate', lat: 12.9960173, lng: 80.2359173, id: 12 },
  { name: 'E1 Stop Towards GC', lat: 12.996117, lng: 80.236184, id: 13},
  { name: 'Vana Vani Stop Towards Main Gate', lat: 12.998704, lng: 80.2391799, id: 14 },
  { name: 'Vana Vani Stop Towards GC', lat: 12.999084,   lng:  80.239380, id: 15 },
  { name: 'D1 Stop Towards Main Gate', lat:13.002546, lng: 80.240091, id: 16},
  { name: 'D1 Stop Towards GC', lat: 13.002679,  lng: 80.240219, id: 17 },
  { name: 'Main Gate', lat: 13.00612964850378, lng: 80.24191299117685, id: 18 },
  { name: 'GC Stop Towards Hostel', lat: 12.991342, lng: 80.233637, id: 19 },
  { name: 'Library Stop', lat: 12.9907517, lng: 80.2334554, id: 20 },
  { name: 'OAT Stop Towards Hostel', lat: 12.989257,  lng: 80.233031, id: 21},
  { name: 'Gymkhana Stop Towards GC', lat: 12.9866372, lng: 80.2332951, id: 22},
  { name: 'Gymkhana Stop Towards Hostel', lat: 12.986615, lng: 80.233366, id: 23 },
  { name: 'Narmada Stop Towards GC', lat: 12.9862759, lng: 80.2350107, id: 24 },
  { name: 'Narmada Stop Towards Hostel', lat: 12.986546, lng: 80.235301, id: 25 },
  { name: 'Jamuna and Ganga Stand', lat: 12.986566332825637, lng: 80.23855704439099, id: 26 },
  { name: 'RP Stand', lat: 12.990088, lng: 80.241799, id: 28},
  { name: 'ED Stand', lat: 12.989874633072303, lng: 80.22646042038593, id: 29},
];

// Route sequences by stop id
const Hostel_to_Main   = [26, 24, 22, 20, 6, 5, 3, 1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
const Main_to_Hostel   = [18, 17, 15, 13, 11, 9, 6, 5, 3, 1, 2, 4, 7, 19, 21, 23, 25, 26];
const Velachery_to_Main= [1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
const Velachery_to_Hostel=[1, 2, 4, 7, 19, 21, 23, 25, 26];
const Hotel_to_Velachery=[26, 24, 22, 20, 6, 5, 3, 1];
const Main_to_Velachery=[18, 17, 15, 13, 11, 9, 6, 5, 3, 1];
const RP_to_ED         = [28, 22, 21, 20, 6, 5, 3, 29];
const ED_to_RP         = [29, 2, 4, 7, 19, 21, 23, 28];

const ROUTES = [
  { code: "HOSTEL_MAIN", label: "Hostel → Main Gate", ids: Hostel_to_Main },
  { code: "MAIN_HOSTEL", label: "Main Gate → Hostel", ids: Main_to_Hostel },
  { code: "VG_MAIN",     label: "Velachery Gate → Main Gate", ids: Velachery_to_Main },
  { code: "VG_HOSTEL",   label: "Velachery Gate → Hostel", ids: Velachery_to_Hostel },
  { code: "MAIN_VG",     label: "Main Gate → Velachery Gate", ids: Main_to_Velachery },
  { code: "HOSTEL_VG",   label: "Hostel → Velachery Gate", ids: Hotel_to_Velachery },
  { code: "RP_ED",       label: "Research Park → ED", ids: RP_to_ED },
  { code: "ED_RP",       label: "ED → Research Park", ids: ED_to_RP },
];

// ---- Route aliases (frontend only) ----
// Backend only knows canonical codes: HOSTEL_MAIN, MAIN_HOSTEL, RP_ED, ED_RP
// But filters can still expose Velachery labels.
// Aliases map to canonical backend codes.
const ROUTE_ALIAS_TO_CANONICAL = {
  VG_MAIN: "HOSTEL_MAIN",
  HOSTEL_VG: "HOSTEL_MAIN",
  MAIN_VG: "MAIN_HOSTEL",
  VG_HOSTEL: "MAIN_HOSTEL",
};

function canonicalRoute(code) {
  return ROUTE_ALIAS_TO_CANONICAL[code] || code;
}
// Routes the admin can assign in backend (only canonical codes)
const ADMIN_ROUTES = [
  { code: "HOSTEL_MAIN", label: "Hostel → Main Gate" },
  { code: "MAIN_HOSTEL", label: "Main Gate → Hostel" },
  { code: "RP_ED", label: "Research Park → ED" },
  { code: "ED_RP", label: "ED → Research Park" },
];


// ---- Vehicles for driverRoute select ----
const VEHICLES = [
  "TN07DH1368","TN07DH1317","TN07DH1315","EV09","TN07DH1360","TN07DH1391","EV08","TN07DD5574",
  "TN07CX8698","TN07DD3376","TN07CX8200","TN07DD3360","EV-12","EV-14","EV-15","EV-10","EV-05",
  "EV-07","EV-06","EV-02","EV-03","EV-11","EV-01","EV-13","TN07CX8119"
];

// ---- Leaflet default icon fix ----
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

// ---- Utility helpers ----
const createCustomIcon = (iconUrl, size = [40, 40]) =>
  L.icon({
    iconUrl,
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1]],
    popupAnchor: [0, -size[1] / 2]
  });

const userIconInstance = createCustomIcon(userIcon, [40, 40]);

const toHeading = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? ((n % 360) + 360) % 360 : null;
};

// Exponential smoothing for latitude/longitude
function smoothCoord(prev, next, alpha = SMOOTH_ALPHA) {
  if (!prev) return next;
  return {
    lat: prev.lat + alpha * (next.lat - prev.lat),
    lng: prev.lng + alpha * (next.lng - prev.lng)
  };
}



// Circular smoothing for heading (degrees)
function smoothHeading(prevDeg, nextDeg, alpha = HEADING_ALPHA) {
  if (!Number.isFinite(prevDeg)) return nextDeg;
  if (!Number.isFinite(nextDeg)) return prevDeg;
  const p = (prevDeg * Math.PI) / 180;
  const n = (nextDeg * Math.PI) / 180;
  const x = (1 - alpha) * Math.cos(p) + alpha * Math.cos(n);
  const y = (1 - alpha) * Math.sin(p) + alpha * Math.sin(n);
  const ang = (Math.atan2(y, x) * 180) / Math.PI;
  return (((ang % 360) + 360) % 360);
}
function mBetween(a, b) { return L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng)); }

// ---- Cart classification (case-insensitive "EV") ----
function isCart(vehicleno) {
  const s = String(vehicleno || '').trim();
  return s.toUpperCase().startsWith('EV');
}

// Label: B for Bus, C for Cart
// function vehicleLabel(vehicleno) { return isCart(vehicleno) ? 'C' : 'B'; }
function vehicleLabel(vehicleno, routeCode) {
  if (!isCart(vehicleno)) return "B";

  const rc = canonicalRoute(routeCode || "");
  if (rc === "RP_ED" || rc === "ED_RP") return "CR"; // 👈 cart on RP↔ED
  return "C";
}


// ---- Block IDs starting with a digit ----
function startsWithDigit(vehicleno) { return /^[0-9]/.test(String(vehicleno || '')); }

// ---- Direction pointer “on the circle” DivIcon ----
// function createVehicleDivIcon(label, headingDeg) {
//   const circle = 20;
//   const border = 3;
//   const triH = 18;
//   const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
//   const totalH = triH + circle;

//   const html = `
//     <div style="position:relative;width:${circle}px;height:${totalH}px">
//       <div style="
//         position:absolute; left:0; right:0; bottom:0;
//         width:${circle}px; height:${circle}px;
//         border:${border}px solid #0D47A1; background:#ffffff; border-radius:50%;
//         display:flex; align-items:center; justify-content:center;
//         box-shadow:0 2px 8px rgba(0,0,0,0.25);
//         font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
//         letter-spacing:0.6px; user-select:none;">
//         <span style="font-size:16px; color:#0D47A1">${label}</span>
//       </div>
//       <div style="
//         position:absolute; left:50%; top:0;
//         transform: translateX(-250%) rotate(${rot}deg);
//         transform-origin: 50% ${triH * 0.7}px;
//         width:0; height:0;
//         border-left:${triH / 4}px solid transparent;
//         border-right:${triH / 4}px solid transparent;
//         border-bottom:${triH}px solid #0D47A1;
//         filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
//         z-index: 2; pointer-events: none;"></div>
//     </div>
//   `;

//   return L.divIcon({
//     className: 'veh-div-icon',
//     html,
//     iconSize: [circle, totalH],
//     iconAnchor: [circle / 2, totalH - circle / 2],
//     popupAnchor: [0, -(circle / 2 + triH / 2)]
//   });
// }

function createVehicleDivIcon(label, headingDeg, palette) {
  const circle = 20;
  const border = 3;
  const triH = 18;
  const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
  const totalH = triH + circle;

  // default bus palette
  const p = palette || {
    stroke: "#0D47A1",
    fill: "#ffffff",
    text: "#0D47A1",
    pointer: "#0D47A1",
  };

  const html = `
    <div style="position:relative;width:${circle}px;height:${totalH}px">
      <div style="
        position:absolute; left:0; right:0; bottom:0;
        width:${circle}px; height:${circle}px;
        border:${border}px solid ${p.stroke};
        background:${p.fill};
        border-radius:50%;
        display:flex; align-items:center; justify-content:center;
        box-shadow:0 2px 8px rgba(0,0,0,0.25);
        font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
        letter-spacing:0.6px; user-select:none;">
        <span style="font-size:16px; color:${p.text}">${label}</span>
      </div>
      <div style="
        position:absolute; left:50%; top:0;
        transform: translateX(-250%) rotate(${rot}deg);
        transform-origin: 50% ${triH * 0.7}px;
        width:0; height:0;
        border-left:${triH / 4}px solid transparent;
        border-right:${triH / 4}px solid transparent;
        border-bottom:${triH}px solid ${p.pointer};
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
        z-index: 2; pointer-events: none;"></div>
    </div>
  `;

  return L.divIcon({
    className: 'veh-div-icon',
    html,
    iconSize: [circle, totalH],
    iconAnchor: [circle / 2, totalH - circle / 2],
    popupAnchor: [0, -(circle / 2 + triH / 2)]
  });
}

const BUS_PALETTE = {
  stroke: "#0D47A1",
  fill: "#ffffff",
  text: "#0D47A1",
  pointer: "#0D47A1",
};

// const CART_PALETTE = {
//   stroke: "#0F766E",  // teal border
//   fill: "#ECFEFF",    // light teal fill
//   text: "#0F766E",
//   pointer: "#0F766E",
// };
const CART_PALETTE = {
  stroke: "#111827",   // near-black border
  fill:   "#FEF08A",   // bright yellow fill
  text:   "#111827",   // black text
  pointer:"#111827",   // black pointer
};





function matchesFilter(vehicleno, filter, assignments) {
  const cart = isCart(vehicleno);
  if (filter === "cart") return cart;
  if (filter === "bus") return !cart;

  if (filter?.startsWith("route:")) {
    const requested = canonicalRoute(filter.slice(6));
    const assigned = canonicalRoute(assignments[vehicleno]);
    return assigned === requested;
  }
  return true;
}

// ---- Filter helper ----
// function matchesFilter(vehicleno, filter, assignments) {
//   const cart = isCart(vehicleno);
//   if (filter === 'cart') return cart;
//   if (filter === 'bus')  return !cart;
//   if (filter?.startsWith('route:')) {
//     const code = filter.slice(6);
//     return assignments[vehicleno] === code;
//   }
//   return true;
// }

// ---------------- SCHEDULE DATA (kept intact) ----------------
const HOSTEL_TO_MAIN = [
  '06:20 AM','06:40 AM','07:00 AM','07:20 AM','07:40 AM','08:00 AM','08:20 AM','08:40 AM',
  '09:00 AM','09:20 AM','09:40 AM','10:00 AM','10:20 AM','10:40 AM','11:00 AM','11:20 AM',
  '11:40 AM','12:00 PM','12:20 PM','12:40 PM','01:00 PM','01:20 PM','01:40 PM','02:00 PM',
  '02:20 PM','02:40 PM','03:00 PM','03:20 PM','03:40 PM','04:00 PM','04:20 PM','04:40 PM',
  '05:00 PM','05:20 PM','05:40 PM','06:00 PM','06:20 PM','06:40 PM','07:00 PM','07:20 PM',
  '07:40 PM','08:00 PM','08:20 PM','08:40 PM','09:00 PM','09:20 PM'
];
const HOSTEL_NOTES = [
  { time: '09:30 PM', note: 'To Velachery Gate' },
  { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
];
const MAIN_TO_HOSTEL = [...HOSTEL_TO_MAIN];
const MAIN_NOTES = [
  { time: '09:40 PM', note: 'To Velachery Gate' },
  { time: '10:00 PM', note: 'To Velachery Gate' },
  { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
];
const VELACHERY_TO_MAIN = [
  '06:15 AM','06:35 AM','06:55 AM','07:15 AM','07:35 AM','07:55 AM','08:15 AM','08:35 AM','08:55 AM',
  '09:15 AM','09:35 AM','09:55 AM','10:15 AM','10:35 AM','10:55 AM','11:15 AM','11:35 AM',
  '12:15 PM','12:35 PM','12:55 PM','01:15 PM','01:35 PM','01:55 PM','02:15 PM','02:35 PM','02:55 PM',
  '03:15 PM','03:35 PM','03:55 PM','04:15 PM','04:35 PM','04:55 PM','05:15 PM','05:35 PM','05:55 PM',
  '06:15 PM','06:35 PM','06:55 PM','07:15 PM','07:35 PM','07:55 PM','08:15 PM','08:35 PM','08:55 PM',
  '09:15 PM','09:35 PM'
];
const VELACHERY_TO_HOSTEL = [
  '06:25 AM','06:45 AM','07:05 AM','07:25 AM','07:45 AM','08:05 AM','08:25 AM','08:45 AM','09:05 AM',
  '09:25 AM','09:45 AM','10:05 AM','10:25 AM','10:45 AM','11:05 AM','11:25 AM','11:45 AM',
  '12:05 PM','12:25 PM','12:45 PM','01:05 PM','01:25 PM','01:45 PM','02:05 PM','02:25 PM','02:45 PM',
  '03:05 PM','03:25 PM','03:45 PM','04:05 PM','04:25 PM','04:45 PM','05:05 PM','05:25 PM','05:45 PM',
  '06:05 PM','06:25 PM','06:45 PM','07:05 PM','07:25 PM','07:45 PM','08:05 PM','08:25 PM','08:45 PM',
  '09:05 PM','09:15 PM'
];
const VELACHERY_NOTES = [
  { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
];
const RP_TO_ED_TIMES = [
  '07:30 AM','08:05 AM','08:40 AM','09:00 AM','09:15 AM','09:35 AM','09:45 AM',
  '10:15 AM','10:25 AM','10:45 AM','11:20 AM','11:40 AM',
  '12:05 PM','12:45 PM','01:40 PM','02:20 PM','02:35 PM','03:00 PM','03:15 PM','03:45 PM',
  '04:15 PM','04:50 PM','05:05 PM','05:50 PM','06:25 PM','07:05 PM'
];
const ED_TO_RP_TIMES = [
  '07:50 AM','08:25 AM','09:00 AM','09:15 AM','09:30 AM','10:00 AM','10:05 AM','10:30 AM',
  '11:00 AM','11:15 AM','11:45 AM',
  '12:00 PM','12:25 PM','01:05 PM','02:00 PM','02:40 PM','03:00 PM','03:20 PM','03:30 PM',
  '04:05 PM','04:45 PM','05:10 PM','05:30 PM','06:10 PM','06:45 PM','07:20 PM'
];
const Cart1 = [['07:30','07:50'],['08:05','08:25'],['08:40','09:00'],['09:15','09:30'],['09:45','10:05'],['10:25','11:00'],['11:20','11:45'],['12:05','12:25'],['13:40','14:00'],['14:20','14:40'],['15:00','15:20'],['15:45','16:05'],['16:50','17:10']];
const Cart3 = [['09:00','09:15'],['09:35','10:00'],['10:15','10:30'],['10:45','11:15'],['11:40','12:00'],['12:45','13:05'],['14:35','15:00'],['15:15','15:30'],['16:15','16:45'],['17:05','17:30'],['17:50','18:10'],['18:25','18:45'],['19:05','19:20']];

// // ---- Feedback link ----
// const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

// ---- Feedback / temporary summit promo links ----
const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

const MINT_SUMMIT_REGISTRATION_URL =
  process.env.REACT_APP_MINT_SUMMIT_REGISTRATION_URL ||
  'https://docs.google.com/forms/d/e/1FAIpQLScB7Y6RtzcOwXRsoi4Czrr_vUnOcZm7joeeKSNKvcNm7RRjLw/viewform';

// Set this to false after the summit.
const SHOW_MINT_SUMMIT_PROMO = false;

const FOOTER_ACTION_URL = SHOW_MINT_SUMMIT_PROMO
  ? MINT_SUMMIT_REGISTRATION_URL
  : FEEDBACK_URL;

const FOOTER_ACTION_LABEL = SHOW_MINT_SUMMIT_PROMO
  ? 'Register for MInT Summit ✨'
  : 'Open feedback form ↗';

// ---------------- Tiny router helpers (for /driver, /driverRoute) ----------------
function usePath() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate = (p) => { if (p !== window.location.pathname) { window.history.pushState({}, "", p); setPath(p); } };
  return { path, navigate };
}
function getToken() { return localStorage.getItem("driverToken") || ""; }
function setToken(t) { localStorage.setItem("driverToken", t || ""); }
function getUsername() { return localStorage.getItem("driverUsername") || ""; }
function setUsername(u) { localStorage.setItem("driverUsername", u || ""); }

// --- Admin auth helpers (separate from driver) ---
function getAdminToken() {
  return localStorage.getItem("adminToken") || "";
}
function setAdminToken(t) {
  localStorage.setItem("adminToken", t || "");
}


// ---------------- Driver pages ----------------
// --- small responsive hook for narrow phones ---
function useIsNarrow(bp = 380) {
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= bp);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= bp);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bp]);
  return isNarrow;
}

function DriverLogin({ navigate }) {
  const [username, setU] = useState("");
  const [password, setP] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isNarrow = useIsNarrow(380);

  useEffect(() => {
    if (getToken()) navigate("/driverRoute");
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/driver/login`, { username, password });
      if (r.data?.success && r.data?.token) {
        setToken(r.data.token);
        setUsername(r.data.username);
        navigate("/driverRoute");
      } else setErr("Login failed");
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      style={{
        padding: isNarrow ? 12 : 20,
        margin: isNarrow ? "16px auto" : "32px auto",
        width: "min(100vw - 24px, 560px)",
      }}
    >
      {/* Header with Home button */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isNarrow ? 10 : 12,
          marginBottom: isNarrow ? 10 : 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
          <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Driver Login</h2>
        </div>

        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            marginLeft: "auto",
            padding: isNarrow ? "6px 10px" : "8px 12px",
            borderRadius: 10,
            border: "1px solid #D1D5DB",
            background: "#FFFFFF",
            fontWeight: 700,
            fontSize: isNarrow ? 13 : 14,
            cursor: "pointer",
          }}
          title="Go to Home"
        >
          Home
        </button>
      </div>

      {/* Card */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 12,
          padding: isNarrow ? 12 : 16,
        }}
      >
        <form onSubmit={onSubmit} style={{ display: "grid", gap: isNarrow ? 8 : 10 }}>
          <input
            value={username}
            onChange={(e) => setU(e.target.value)}
            placeholder="username"
            required
            style={{
              padding: isNarrow ? 10 : 12,
              borderRadius: 10,
              border: "1px solid #D1D5DB",
              fontWeight: 600,
              fontSize: isNarrow ? 14 : 16,
            }}
          />
          <input
            value={password}
            onChange={(e) => setP(e.target.value)}
            placeholder="password"
            type="password"
            required
            style={{
              padding: isNarrow ? 10 : 12,
              borderRadius: 10,
              border: "1px solid #D1D5DB",
              fontWeight: 600,
              fontSize: isNarrow ? 14 : 16,
            }}
          />
          <button
            disabled={busy}
            style={{
              padding: isNarrow ? "10px 12px" : "12px 14px",
              borderRadius: 10,
              border: "1px solid #0D47A1",
              background: "#0D47A1",
              color: "#fff",
              fontWeight: 800,
              cursor: "pointer",
              width: "100%",
              minHeight: 40,
              fontSize: "clamp(14px, 3.5vw, 16px)",
            }}
          >
            {busy ? "Signing in..." : "Login"}
          </button>
          {err && <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>}
        </form>
      </div>
    </div>
  );
}

function DriverRoute({ navigate }) {
  const [routeCode, setRouteCode] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [autoPilot, setAutoPilot] = useState(false); // 👈 NEW: auto-pilot flag
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isNarrow = useIsNarrow(380);

  useEffect(() => {
    if (!getToken()) navigate("/driver");
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const row = r.data?.row;
        if (row) {
          setVehicleNo(row.vehicleNo);
          setRouteCode(row.routeCode);
          setAutoPilot(!!row.autopilotEnabled); // 👈 load auto-pilot state from backend
          setSaved(true);
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await axios.post(
        `${BACKEND}/api/vehicle-route/assign`,
        {
          vehicleNo,
          routeCode,
          active: true,
          autopilotEnabled: autoPilot, // 👈 send auto-pilot choice to backend
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSaved(true);
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        padding: isNarrow ? 16 : 20,
        maxWidth: 640,
        margin: isNarrow ? "16px auto" : "32px auto",
        width: "min(100vw - 24px, 640px)",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
          <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
        </div>
        <div
          style={{
            marginLeft: 'auto',
            fontWeight: 700,
            opacity: 0.8,
            fontSize: "clamp(13px, 3.2vw, 16px)",
            maxWidth: '60%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={`@${getUsername()}`}
        >
          @{getUsername()}
        </div>
      </div>

      {/* Top-right actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: isNarrow ? '6px 10px' : '8px 12px',
              borderRadius: 10,
              border: '1px solid #D1D5DB',
              background: '#FFFFFF',
              fontWeight: 700,
              fontSize: isNarrow ? 13 : 14,
              cursor: 'pointer'
            }}
            title="Go to Home"
          >
            Home
          </button>
          <button
            type="button"
            onClick={() => setSaved(false)}
            disabled={!saved}
            style={{
              padding: isNarrow ? '6px 10px' : '8px 12px',
              borderRadius: 10,
              border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
              background: saved ? '#fff' : '#F9FAFB',
              color: saved ? '#111' : '#9CA3AF',
              fontWeight: 800,
              fontSize: isNarrow ? 13 : 14,
              cursor: saved ? 'pointer' : 'not-allowed'
            }}
            title="Edit selection"
          >
            Edit
          </button>
        </div>

        <button
          onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
          style={{
            padding: isNarrow ? '6px 10px' : 8,
            borderRadius: 10,
            border: '1px solid #D1D5DB',
            background: '#FFFFFF',
            fontSize: isNarrow ? 13 : 14,
            cursor: 'pointer'
          }}
          title="Logout"
        >
          Logout
        </button>
      </div>

      <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
        <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
        <select
          disabled={saved}
          value={routeCode}
          onChange={e=>setRouteCode(e.target.value)}
          required
          style={{
            padding: isNarrow ? 10 : 12,
            borderRadius: 10,
            border: '1px solid #D1D5DB',
            fontSize: isNarrow ? 14 : 16
          }}
        >
          <option value="">Select route…</option>
          {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>

        <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
        <select
          disabled={saved}
          value={vehicleNo}
          onChange={e=>setVehicleNo(e.target.value)}
          required
          style={{
            padding: isNarrow ? 10 : 12,
            borderRadius: 10,
            border: '1px solid #D1D5DB',
            fontSize: isNarrow ? 14 : 16
          }}
        >
          <option value="">Select vehicle…</option>
          {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        {/* 👇 NEW: Auto-pilot toggle */}
        <div
          style={{
            marginTop: isNarrow ? 6 : 8,
            padding: isNarrow ? 8 : 10,
            borderRadius: 10,
            border: '1px solid #E5E7EB',
            background: '#F9FAFB',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <input
            id="autoPilot"
            type="checkbox"
            checked={autoPilot}
            disabled={saved}
            onChange={(e) => setAutoPilot(e.target.checked)}
            style={{ marginTop: 3, cursor: saved ? 'default' : 'pointer' }}
          />
          <label
            htmlFor="autoPilot"
            style={{ cursor: saved ? 'default' : 'pointer' }}
          >
            <div style={{ fontWeight: 700, fontSize: isNarrow ? 14 : 15 }}>
              Auto-pilot (auto reverse at destination)
            </div>
            <div
              style={{
                fontSize: isNarrow ? 12 : 13,
                color: '#6B7280',
                marginTop: 2,
              }}
            >
              When enabled, once you reach the last stop of this route, your
              assignment will automatically switch to the return leg.
            </div>
          </label>
        </div>

        <button
          disabled={busy || saved}
          style={{
            padding: isNarrow ? '10px 12px' : '12px 14px',
            borderRadius: 10,
            border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
            background: busy || saved ? '#F3F4F6' : '#0D47A1',
            color: busy || saved ? '#6B7280' : '#fff',
            fontWeight: 800,
            cursor: busy || saved ? 'not-allowed' : 'pointer',
            minHeight: 44,
            fontSize: "clamp(14px, 3.5vw, 16px)"
          }}
          title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
        >
          {busy ? "Saving…" : saved ? "Saved" : "Save"}
        </button>

        {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
      </form>
    </div>
  );
}


// function DriverRoute({ navigate }) {
//   const [routeCode, setRouteCode] = useState("");
//   const [vehicleNo, setVehicleNo] = useState("");
//   const [saved, setSaved] = useState(false);
//   const [busy, setBusy] = useState(false);
//   const [err, setErr] = useState("");
//   const isNarrow = useIsNarrow(380);

//   useEffect(() => {
//     if (!getToken()) navigate("/driver");
//     (async () => {
//       try {
//         const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
//           headers: { Authorization: `Bearer ${getToken()}` },
//         });
//         const row = r.data?.row;
//         if (row) { setVehicleNo(row.vehicleNo); setRouteCode(row.routeCode); setSaved(true); }
//       } catch {}
//     })();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const onSave = async (e) => {
//     e.preventDefault();
//     setErr(""); setBusy(true);
//     try {
//       await axios.post(
//         `${BACKEND}/api/vehicle-route/assign`,
//         { vehicleNo, routeCode, active: true },
//         { headers: { Authorization: `Bearer ${getToken()}` } }
//       );
//       setSaved(true);
//     } catch (e1) {
//       setErr(e1?.response?.data?.message || "Save failed");
//     } finally {
//       setBusy(false);
//     }
//   };

//   return (
//     <div
//       style={{
//         padding: isNarrow ? 16 : 20,
//         maxWidth: 640,
//         margin: isNarrow ? "16px auto" : "32px auto",
//         width: "min(100vw - 24px, 640px)",
//       }}
//     >
//       <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
//           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
//           <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
//         </div>
//         <div
//           style={{
//             marginLeft: 'auto',
//             fontWeight: 700,
//             opacity: 0.8,
//             fontSize: "clamp(13px, 3.2vw, 16px)",
//             maxWidth: '60%',
//             overflow: 'hidden',
//             textOverflow: 'ellipsis',
//             whiteSpace: 'nowrap',
//           }}
//           title={`@${getUsername()}`}
//         >
//           @{getUsername()}
//         </div>
//       </div>

//       {/* Top-right actions */}
//       <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
//         <div style={{ display: 'flex', gap: 8 }}>
//           <button
//             onClick={() => navigate('/')}
//             style={{
//               padding: isNarrow ? '6px 10px' : '8px 12px',
//               borderRadius: 10,
//               border: '1px solid #D1D5DB',
//               background: '#FFFFFF',
//               fontWeight: 700,
//               fontSize: isNarrow ? 13 : 14,
//               cursor: 'pointer'
//             }}
//             title="Go to Home"
//           >
//             Home
//           </button>
//           <button
//             type="button"
//             onClick={() => setSaved(false)}
//             disabled={!saved}
//             style={{
//               padding: isNarrow ? '6px 10px' : '8px 12px',
//               borderRadius: 10,
//               border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
//               background: saved ? '#fff' : '#F9FAFB',
//               color: saved ? '#111' : '#9CA3AF',
//               fontWeight: 800,
//               fontSize: isNarrow ? 13 : 14,
//               cursor: saved ? 'pointer' : 'not-allowed'
//             }}
//             title="Edit selection"
//           >
//             Edit
//           </button>
//         </div>

//         <button
//           onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
//           style={{
//             padding: isNarrow ? '6px 10px' : 8,
//             borderRadius: 10,
//             border: '1px solid #D1D5DB',
//             background: '#FFFFFF',
//             fontSize: isNarrow ? 13 : 14,
//             cursor: 'pointer'
//           }}
//           title="Logout"
//         >
//           Logout
//         </button>
//       </div>

//       <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
//         <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
//         <select
//           disabled={saved}
//           value={routeCode}
//           onChange={e=>setRouteCode(e.target.value)}
//           required
//           style={{
//             padding: isNarrow ? 10 : 12,
//             borderRadius: 10,
//             border: '1px solid #D1D5DB',
//             fontSize: isNarrow ? 14 : 16
//           }}
//         >
//           <option value="">Select route…</option>
//           {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
//         </select>

//         <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
//         <select
//           disabled={saved}
//           value={vehicleNo}
//           onChange={e=>setVehicleNo(e.target.value)}
//           required
//           style={{
//             padding: isNarrow ? 10 : 12,
//             borderRadius: 10,
//             border: '1px solid #D1D5DB',
//             fontSize: isNarrow ? 14 : 16
//           }}
//         >
//           <option value="">Select vehicle…</option>
//           {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
//         </select>

//         <button
//           disabled={busy || saved}
//           style={{
//             padding: isNarrow ? '10px 12px' : '12px 14px',
//             borderRadius: 10,
//             border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
//             background: busy || saved ? '#F3F4F6' : '#0D47A1',
//             color: busy || saved ? '#6B7280' : '#fff',
//             fontWeight: 800,
//             cursor: busy || saved ? 'not-allowed' : 'pointer',
//             minHeight: 44,
//             fontSize: "clamp(14px, 3.5vw, 16px)"
//           }}
//           title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
//         >
//           {busy ? "Saving…" : saved ? "Saved" : "Save"}
//         </button>

//         {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
//       </form>
//     </div>
//   );
// }

/* ---------------------------- ADMIN PAGES ---------------------------- */

function AdminLogin({ navigate }) {
  const [username, setU] = useState("");
  const [password, setP] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isNarrow = useIsNarrow(380);

  useEffect(() => {
    if (getAdminToken()) navigate("/admin");
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/admin/login`, {
        username,
        password,
      });
      if (r.data?.success && r.data?.token) {
        setAdminToken(r.data.token);
        navigate("/admin");
      } else {
        setErr("Login failed");
      }
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        padding: isNarrow ? 12 : 20,
        margin: isNarrow ? "16px auto" : "32px auto",
        width: "min(100vw - 24px, 560px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isNarrow ? 10 : 12,
          marginBottom: isNarrow ? 10 : 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src={iitmLogo}
            alt="IITM"
            style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }}
          />
          <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Admin Login</h2>
        </div>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            marginLeft: "auto",
            padding: isNarrow ? "6px 10px" : "8px 12px",
            borderRadius: 10,
            border: "1px solid #D1D5DB",
            background: "#FFFFFF",
            fontWeight: 700,
            fontSize: isNarrow ? 13 : 14,
            cursor: "pointer",
          }}
          title="Go to Home"
        >
          Home
        </button>
      </div>
      <div
        style={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 12,
          padding: isNarrow ? 12 : 16,
        }}
      >
        <form
          onSubmit={onSubmit}
          style={{ display: "grid", gap: isNarrow ? 8 : 10 }}
        >
          <input
            value={username}
            onChange={(e) => setU(e.target.value)}
            placeholder="admin username"
            required
            style={{
              padding: isNarrow ? 10 : 12,
              borderRadius: 10,
              border: "1px solid #D1D5DB",
              fontWeight: 600,
              fontSize: isNarrow ? 14 : 16,
            }}
          />
          <input
            value={password}
            onChange={(e) => setP(e.target.value)}
            placeholder="admin password"
            type="password"
            required
            style={{
              padding: isNarrow ? 10 : 12,
              borderRadius: 10,
              border: "1px solid #D1D5DB",
              fontWeight: 600,
              fontSize: isNarrow ? 14 : 16,
            }}
          />
          <button
            disabled={busy}
            style={{
              padding: isNarrow ? "10px 12px" : "12px 14px",
              borderRadius: 10,
              border: "1px solid #0D47A1",
              background: "#0D47A1",
              color: "#fff",
              fontWeight: 800,
              cursor: "pointer",
              width: "100%",
              minHeight: 40,
              fontSize: "clamp(14px, 3.5vw, 16px)",
            }}
          >
            {busy ? "Signing in..." : "Login"}
          </button>
          {err && (
            <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>
          )}
        </form>
      </div>
    </div>
  );
}

function AdminDashboard({ navigate }) {
  const [tab, setTab] = useState("drivers"); // 'drivers' | 'routes' | 'live'
  const [drivers, setDrivers] = useState([]);
  const [assignments, setAssignments] = useState({}); // vehicleNo -> row
  const [localAssign, setLocalAssign] = useState({}); // vehicleNo -> { routeCode, driverId, autopilotEnabled }
  const [editing, setEditing] = useState({}); // vehicleNo -> true when row is in edit mode
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [err, setErr] = useState("");
  const [busySave, setBusySave] = useState(null); // vehicleno while saving
  const [liveBuses, setLiveBuses] = useState([]);
  const adminToken = getAdminToken();
  const isNarrow = useIsNarrow(480);

  useEffect(() => {
    if (!adminToken) navigate("/admin/login");
  }, [adminToken, navigate]);

  const authHeader = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${adminToken}` },
    }),
    [adminToken]
  );

  async function loadDrivers() {
    try {
      const r = await axios.get(`${BACKEND}/api/admin/drivers`, authHeader);
      setDrivers(r.data?.drivers || []);
    } catch (e) {
      console.warn("loadDrivers error", e?.message || e);
    }
  }

  async function loadAssignments() {
    try {
      const r = await axios.get(`${BACKEND}/api/admin/assignments`, authHeader);
      const rows = r.data?.rows || [];
      const map = {};
      for (const row of rows) {
        map[row.vehicleNo] = row;
      }
      setAssignments(map);
      // whenever we reload from backend, treat all as "saved" (not editing)
      setEditing({});
      setLocalAssign({});
    } catch (e) {
      console.warn("loadAssignments error", e?.message || e);
    }
  }

  useEffect(() => {
    if (!adminToken) return;
    loadDrivers();
    loadAssignments();
  }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // live tracking (simple polling)
  useEffect(() => {
    if (tab !== "live") return;
    let cancelled = false;

    async function loadLive() {
      try {
        const r = await axios.get(`${BACKEND}/api/vehicle-data`, {
          timeout: 8000,
        });
        if (!cancelled) {
          const arr = Array.isArray(r.data) ? r.data : [];
          setLiveBuses(arr);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn("admin live map error", e?.message || e);
        }
      }
    }
    loadLive();
    const id = setInterval(loadLive, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [tab]);

  const handleAddDriver = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const r = await axios.post(
        `${BACKEND}/api/admin/drivers`,
        { name: newName, phoneNumber: newPhone },
        authHeader
      );
      if (r.data?.success && r.data?.driver) {
        setDrivers((prev) => [...prev, r.data.driver]);
        setNewName("");
        setNewPhone("");
      } else {
        setErr("Failed to create driver");
      }
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Failed to create driver");
    }
  };

  const handleRemoveDriver = async (id) => {
    if (!window.confirm("Remove this driver?")) return;
    try {
      await axios.delete(`${BACKEND}/api/admin/drivers/${id}`, authHeader);
      setDrivers((prev) => prev.filter((d) => d._id !== id));
      // Note: assignments that still reference this driver will continue to exist until reassigned
    } catch (e1) {
      alert(e1?.response?.data?.message || "Failed to remove driver");
    }
  };

  function getBaseAssignForVehicle(veh) {
    const row = assignments[veh];
    if (!row) {
      return {
        vehicleNo: veh,
        routeCode: "",
        driverId: "",
        autopilotEnabled: false,
      };
    }
    return {
      vehicleNo: veh,
      routeCode: row.routeCode || "",
      driverId: row.driver?._id || "",
      autopilotEnabled: !!row.autopilotEnabled,
    };
  }

  function updateLocalAssign(veh, patch) {
    setLocalAssign((prev) => ({
      ...prev,
      [veh]: { ...getBaseAssignForVehicle(veh), ...(prev[veh] || {}), ...patch },
    }));
  }

  const handleSaveAssignment = async (veh) => {
  const base = getBaseAssignForVehicle(veh);
  const row = localAssign[veh] || base;
  const hasAssignment = !!assignments[veh]; // vehicle currently has an active route?

  // RESET error first
  setErr("");

  // ---- CASE 1: Admin cleared both fields and there *was* an assignment -> CLEAR MODE ----
  if (!row.routeCode && !row.driverId && hasAssignment) {
    setBusySave(veh);
    try {
      await axios.post(
        `${BACKEND}/api/admin/vehicle-assign`,
        {
          vehicleNo: row.vehicleNo,
          clear: true,              // 👈 tells backend: deactivate assignment
        },
        authHeader
      );
      await loadAssignments();      // refresh table
      setEditing((prev) => ({ ...prev, [veh]: false }));
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Failed to clear assignment");
    } finally {
      setBusySave(null);
    }
    return;
  }

  // ---- CASE 2: No assignment and still empty -> nothing to save ----
  if (!row.routeCode || !row.driverId) {
    setErr("Select both Route & Driver, or leave the row as-is.");
    return;
  }

  // ---- CASE 3: Normal ASSIGN / UPDATE ----
  setBusySave(veh);
  try {
    await axios.post(
      `${BACKEND}/api/admin/vehicle-assign`,
      {
        vehicleNo: row.vehicleNo,
        routeCode: row.routeCode,
        driverId: row.driverId,
        autopilotEnabled: !!row.autopilotEnabled,
      },
      authHeader
    );
    await loadAssignments();
    setEditing((prev) => ({ ...prev, [veh]: false }));
  } catch (e1) {
    setErr(e1?.response?.data?.message || "Failed to save assignment");
  } finally {
    setBusySave(null);
  }
};


  const onLogout = () => {
    setAdminToken("");
    navigate("/admin/login");
  };

  // simple driver lookup for live map
  const driverByVehicle = useMemo(() => {
    const out = {};
    Object.values(assignments).forEach((row) => {
      if (row.vehicleNo && row.driver) {
        out[row.vehicleNo] = row.driver;
      }
    });
    return out;
  }, [assignments]);

  return (
    <div className="admin-layout">
      {/* left sidebar */}
      <div
        className="sidebar"
        style={{
          width: isNarrow ? 220 : 260, // responsive width override
        }}
      >
        {/* top section: logo, back button, nav */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img
              src={iitmLogo}
              alt="IITM"
              style={{ width: 32, height: 32, borderRadius: 8 }}
            />
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Admin</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Moving Mountains</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            style={{
              marginTop: 8,
              textAlign: "left",
              padding: "6px 10px",
              borderRadius: 10,
              border: "1px solid #4b5563",
              background: "transparent",
              color: "#e5e7eb",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            ← Back to map
          </button>
          <div
            style={{
              marginTop: 16,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <button
              onClick={() => setTab("drivers")}
              style={{
                textAlign: "left",
                padding: "8px 10px",
                borderRadius: 10,
                border: "none",
                background: tab === "drivers" ? "#f9fafb" : "transparent",
                color: tab === "drivers" ? "#111827" : "#e5e7eb",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              + Add Driver
            </button>
            <button
              onClick={() => setTab("routes")}
              style={{
                textAlign: "left",
                padding: "8px 10px",
                borderRadius: 10,
                border: "none",
                background: tab === "routes" ? "#f9fafb" : "transparent",
                color: tab === "routes" ? "#111827" : "#e5e7eb",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Route Allocation
            </button>
            <button
              onClick={() => setTab("live")}
              style={{
                textAlign: "left",
                padding: "8px 10px",
                borderRadius: 10,
                border: "none",
                background: tab === "live" ? "#f9fafb" : "transparent",
                color: tab === "live" ? "#111827" : "#e5e7eb",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Live Tracking
            </button>
          </div>
        </div>

        {/* bottom logout – pinned to sidebar bottom */}
        <button
          onClick={onLogout}
          style={{
            marginTop: 12,
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid #6b7280",
            background: "transparent",
            color: "#e5e7eb",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          Logout
        </button>
      </div>

      {/* main content */}
      <div className="content">
        {tab === "drivers" && (
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <h2 style={{ marginTop: 0 }}>Drivers</h2>
            <form
              onSubmit={handleAddDriver}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 2fr auto",
                gap: 8,
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Driver name"
                required
                style={{
                  padding: 10,
                  borderRadius: 10,
                  border: "1px solid #D1D5DB",
                  fontSize: 14,
                }}
              />
              <input
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="Phone number (unique)"
                required
                style={{
                  padding: 10,
                  borderRadius: 10,
                  border: "1px solid #D1D5DB",
                  fontSize: 14,
                }}
              />
              <button
                type="submit"
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid #0D47A1",
                  background: "#0D47A1",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Add
              </button>
            </form>
            {err && (
              <div
                style={{
                  color: "#b00020",
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                {err}
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                gap: 12,
              }}
            >
              {drivers.map((d) => (
                <div
                  key={d._id}
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{d.name}</div>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    Phone: {d.phoneNumber || "—"}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.6 }}>
                    username: <code>{d.username}</code>
                  </div>
                  <button
                    onClick={() => handleRemoveDriver(d._id)}
                    style={{
                      marginTop: 8,
                      alignSelf: "flex-start",
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #f97373",
                      background: "#fef2f2",
                      color: "#b91c1c",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {drivers.length === 0 && (
                <div style={{ fontSize: 14, opacity: 0.7 }}>
                  No drivers yet. Add your first driver above.
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "routes" && (
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <h2 style={{ marginTop: 0 }}>Route Allocation</h2>
            {err && (
              <div
                style={{
                  color: "#b00020",
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                {err}
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fff",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f9fafb",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th style={{ padding: 8, textAlign: "left" }}>#</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Vehicle</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Route</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Driver</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Auto-pilot</th>
                    <th style={{ padding: 8, textAlign: "left" }} />
                  </tr>
                </thead>
                <tbody>
                  {VEHICLES.map((veh, idx) => {
                    const base = getBaseAssignForVehicle(veh);
                    const local = localAssign[veh] || base;
                    const hasAssignment =
                      !!base.routeCode && !!base.driverId; // existing saved row?
                    const isEditing = !!editing[veh];
                    const locked = hasAssignment && !isEditing;
                    const saving = busySave === veh;

                    return (
                      <tr
                        key={veh}
                        style={{ borderBottom: "1px solid #f3f4f6" }}
                      >
                        <td style={{ padding: 8 }}>{idx + 1}</td>
                        <td style={{ padding: 8, fontWeight: 600 }}>{veh}</td>
                        <td style={{ padding: 8 }}>
                          <select
                            value={local.routeCode}
                            onChange={(e) =>
                              updateLocalAssign(veh, {
                                routeCode: e.target.value,
                              })
                            }
                            disabled={locked || saving}
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: "1px solid #d1d5db",
                              fontSize: 13,
                              minWidth: 180,
                              backgroundColor: locked ? "#f9fafb" : "white",
                            }}
                          >
                            <option value="">Select route…</option>
                            {ADMIN_ROUTES.map((r) => (
                              <option key={r.code} value={r.code}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: 8 }}>
                          <select
                            value={local.driverId}
                            onChange={(e) =>
                              updateLocalAssign(veh, {
                                driverId: e.target.value,
                              })
                            }
                            disabled={locked || saving}
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: "1px solid #d1d5db",
                              fontSize: 13,
                              minWidth: 180,
                              backgroundColor: locked ? "#f9fafb" : "white",
                            }}
                          >
                            <option value="">Select driver…</option>
                            {drivers.map((d) => (
                              <option key={d._id} value={d._id}>
                                {d.name} ({d.phoneNumber || d.username})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: 8 }}>
                          <label
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 13,
                              opacity: locked ? 0.6 : 1,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!local.autopilotEnabled}
                              onChange={(e) =>
                                updateLocalAssign(veh, {
                                  autopilotEnabled: e.target.checked,
                                })
                              }
                              disabled={locked || saving}
                            />
                            On
                          </label>
                        </td>
                        <td style={{ padding: 8 }}>
                          <button
                            onClick={() => {
                              if (locked) {
                                // switch to edit mode
                                updateLocalAssign(veh, {}); // ensure local copy
                                setEditing((prev) => ({
                                  ...prev,
                                  [veh]: true,
                                }));
                              } else {
                                // save current data
                                handleSaveAssignment(veh);
                              }
                            }}
                            disabled={saving}
                            style={{
                              padding: "6px 12px",
                              borderRadius: 8,
                              border: "1px solid #0D47A1",
                              background: saving
                                ? "#e5e7eb"
                                : locked
                                ? "#0D47A1"
                                : "#0D47A1",
                              color: saving ? "#4b5563" : "#fff",
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: saving ? "not-allowed" : "pointer",
                            }}
                          >
                            {saving
                              ? "Saving…"
                              : locked
                              ? "Edit"
                              : "Save"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "live" && (
          <div style={{ height: "calc(100vh - 40px)" }}>
            <h2 style={{ marginTop: 0 }}>Live Tracking</h2>
            <div style={{ height: "calc(100% - 36px)" }}>
              <MapContainer
                center={center}
                zoom={DEFAULT_ZOOM}
                maxZoom={22}
                style={{ width: "100%", height: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxNativeZoom={19}
                  maxZoom={22}
                />
                {liveBuses.map((bus) => {
                  // const label = vehicleLabel(bus.vehicleno);
                  const rc = assignments[bus.vehicleno]?.routeCode;   // admin assignments map is row objects
                  const label = vehicleLabel(bus.vehicleno, rc);
                  const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
                  const drv = driverByVehicle[bus.vehicleno];
                  return (
                    <Marker
                      key={bus.vehicleno}
                      position={{
                        lat: bus.latitude,
                        lng: bus.longitude,
                      }}
                      // icon={createVehicleDivIcon(
                      //   label,
                      //   Number(bus.heading) || 0
                      // )}
                      icon={createVehicleDivIcon(label, Number(bus.heading) || 0, palette)}
                    >
                      <Popup>
                        <div className="info-window">
                          <h4>
                            {bus.vehicleno} ({label})
                          </h4>
                          <p>
                            <strong>Speed:</strong>{" "}
                            {Number.isFinite(bus.speed)
                              ? bus.speed.toFixed(1)
                              : "N/A"}{" "}
                            km/h
                          </p>
                          {drv && (
                            <>
                              <p>
                                <strong>Driver:</strong> {drv.name}</p>
                              <p>
                                <strong>Phone:</strong>{" "}
                                {drv.phoneNumber || "—"}
                              </p>
                            </>
                          )}
                          {!drv && (
                            <p style={{ fontSize: 13, opacity: 0.7 }}>
                              No driver assigned
                            </p>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RecenterMap({ center, disabled = false }) {
  const map = useMap();
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);

  useEffect(() => {
    if (disabled || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const target = L.latLng(lat, lng);
    // Preserve the user's current zoom. setView(DEFAULT_ZOOM) was fighting
    // JourneyMapLayer.fitBounds() and produced a visible zoom loop.
    if (map.getCenter().distanceTo(target) > 5) {
      map.panTo(target, { animate: true, duration: 0.45 });
    }
  }, [disabled, lat, lng, map]);

  return null;
}

function CampusMapPicker({ enabled, point, onPick }) {
  const map = useMapEvents({
    click(event) {
      if (!enabled) return;
      onPick?.({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });

  useEffect(() => {
    const container = map.getContainer();
    container.classList.toggle('is-picking-campus-place', Boolean(enabled));
    return () => container.classList.remove('is-picking-campus-place');
  }, [enabled, map]);

  if (!point) return null;
  const icon = L.divIcon({
    className: 'campus-picked-point-wrap',
    html: '<span class="campus-picked-point"><span></span></span>',
    iconSize: [34, 42],
    iconAnchor: [17, 39],
  });

  return (
    <Marker position={[point.lat, point.lng]} icon={icon} zIndexOffset={1800}>
      <Popup>Proposed public entrance</Popup>
    </Marker>
  );
}



/* ---------------------------- MAP PAGE ---------------------------- */
function MapPage() {
  const [buses, setBuses] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [socketStatus, setSocketStatus] = useState('disconnected');
  const [roads, setRoads] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [mapCenter, setMapCenter] = useState(center);
  const [journeyPlan, setJourneyPlan] = useState(null);
  const [campusMapPicking, setCampusMapPicking] = useState(false);
  const [campusPickedPoint, setCampusPickedPoint] = useState(null);
  const [locError, setLocError] = useState("");
  const [geoState, setGeoState] = useState("unknown"); // "granted" | "prompt" | "denied" | "unknown"
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(true);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // maintenance banner state
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceReason, setMaintenanceReason] = useState('');
  const lastUpdateRef = useRef(0);
    // ---- Backend ETA (JWT) state ----
  const [stopEtas, setStopEtas] = useState({});      // { [stopId]: { data, error } }

  const flatRoadLinesRef = useRef([]);
  const lastSmoothedRef = useRef(new Map());

  // ---- Schedule Modal state ----
  const [showSchedule, setShowSchedule] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // --- responsive helpers ---
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 480);
  const clamp = (min, pref, max) => `clamp(${min}, ${pref}, ${max})`;
  const pad = isNarrow ? 12 : 16;
  const gap = isNarrow ? 8  : 12;
  const chipFS = isNarrow ? 12 : 13;

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

    // ---- Poll all stop ETAs using one public read-only batch request ----
  useEffect(() => {
    if (!BACKEND) return undefined;
    let cancelled = false;

    async function fetchAllStopEtas() {
      try {
        const response = await axios.get(`${BACKEND}/api/eta/stops`, { timeout: 10000 });
        if (cancelled) return;
        const stops = response.data?.stops || {};
        const next = {};
        for (const stop of busStops) {
          const payload = stops[String(stop.id)] || stops[stop.id] || null;
          next[stop.id] = { data: payload, error: !payload };
        }
        setStopEtas(next);
      } catch (err) {
        if (!cancelled) {
          console.warn('ETA batch fetch failed:', err?.response?.status || '', err?.message || err);
        }
      }
    }

    fetchAllStopEtas();
    const id = window.setInterval(fetchAllStopEtas, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);


  // ---- stable socket + pause flag ----
  const socketRef = useRef(null);
  const ignoreUpdatesRef = useRef(false);

  // ---- assignments (vehicle -> routeCode) ----
  const [assignments, setAssignments] = useState({});
  async function refreshAssignments() {
    try {
      const r = await axios.get(`${BACKEND}/api/vehicle-route/active`);
      const map = {};
      for (const row of r.data?.rows || []) map[row.vehicleNo] = row.routeCode;
      setAssignments(map);
    } catch {}
  }
  useEffect(() => { refreshAssignments(); const t = setInterval(refreshAssignments, 60000); return () => clearInterval(t); }, []);

  // Load roads
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(ROADS_URL);
        if (!cancelled && r.ok) setRoads(await r.json());
      } catch (e) {
        if (!cancelled) console.warn('Failed to load roads:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Flatten roads to LineStrings
  useEffect(() => {
    if (!roads) return;
    const lines = [];
    turf.flattenEach(roads, (feat) => {
      if (feat.geometry?.type === 'LineString') lines.push(feat);
    });
    flatRoadLinesRef.current = lines;
  }, [roads]);

  // // Geolocation
  // useEffect(() => {
  //   if ('geolocation' in navigator) {
  //     navigator.geolocation.getCurrentPosition(
  //       (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
  //       (err) => console.warn('Geolocation error:', err?.message),
  //       { enableHighAccuracy: true, timeout: 5000 }
  //     );
  //   }
  // }, []);

  useEffect(() => {
  let perm;
  (async () => {
    try {
      if (!navigator.permissions?.query) return;
      perm = await navigator.permissions.query({ name: "geolocation" });
      setGeoState(perm.state);
      perm.onchange = () => setGeoState(perm.state);
    } catch {
      // ignore
    }
  })();
  return () => {
    if (perm) perm.onchange = null;
  };
}, []);


//   const requestUserLocation = useCallback(() => {
//   setLocError("");

//   if (!("geolocation" in navigator)) {
//     setLocError("Geolocation not supported on this device/browser.");
//     return;
//   }

//   navigator.geolocation.getCurrentPosition(
//     (pos) => {
//       const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
//       setUserLocation(loc);
//       setMapCenter(loc); // ✅ recenter map here
//       setLocError("");
//     },
//     (err) => {
//       console.warn("Geolocation error:", err?.message || err);
//       // If user didn't allow, browser will show prompt only if not blocked.
//       setLocError("Please allow location to center the map near you.");
//     },
//     { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
//   );
// }, []);

const requestUserLocation = useCallback(() => {
  setLocError("");

  if (!("geolocation" in navigator)) {
    setLocError("Geolocation not supported on this device/browser.");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setUserLocation(loc);
      setMapCenter(loc);
      setLocError("");
    },
    (err) => {
      // err.code === 1 => PERMISSION_DENIED
      if (err?.code === 1) {
        // If browser permission is truly "denied", it won't show popup again.
        setLocError(
          "Location is blocked in browser settings. Click the 🔒 icon near the URL → Site settings → Location → Allow (or Ask), then refresh."
        );
      } else {
        setLocError(err?.message || "Unable to fetch location.");
      }
    },
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
  );
}, []);


useEffect(() => {
  // ✅ On every page open (and after browser refresh), request location and recenter
  requestUserLocation();
}, [requestUserLocation]);


  // Initial fetch
  useEffect(() => {
    if (!BACKEND) {
      setMaintenance(true);
      setMaintenanceReason('Server URL not configured');
      return;
    }
    axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 })
      .then((res) => {
        const arr = Array.isArray(res.data) ? res.data : [];
        const byVeh = new Map();
        for (const d of arr) {
          const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
          byVeh.set(d.vehicleno, {
            vehicleno: d.vehicleno,
            latitude: d.latitude,
            longitude: d.longitude,
            speed: d.speed,
            heading
          });
        }
        const list = [...byVeh.values()];
        setBuses(processVehicles(list));
        lastUpdateRef.current = Date.now();
      })
      .catch((err) => {
        console.error('Initial fetch error:', err?.message || err);
        setMaintenance(true);
        setMaintenanceReason('Unable to fetch live data');
      });
  }, []);

  // ---- WebSocket packet handler ----
  const onPacket = (data) => {
    if (ignoreUpdatesRef.current) return;
    const incoming = {
      vehicleno: data.vehicleno,
      latitude: data.latitude,
      longitude: data.longitude,
      speed: data.speed,
      heading: Number.isFinite(data?.heading) ? toHeading(data.heading) : toHeading(data?.direction)
    };

    setBuses((prev) => {
      const nextByVeh = new Map(prev.map((b) => [b.vehicleno, b]));
      const processed = processVehicles([incoming], nextByVeh);
      const merged = new Map(prev.map((b) => [b.vehicleno, b]));
      for (const p of processed) merged.set(p.vehicleno, p);
      return [...merged.values()];
    });

    lastUpdateRef.current = Date.now();
  };

  // Live updates (Socket.IO)
  useEffect(() => {
    if (!BACKEND) return;

    setSocketStatus('connecting');
    const socket = io(BACKEND, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000,
      withCredentials: false,
      upgrade: true
    });
    socketRef.current = socket;

    socket.on('connect', () => setSocketStatus('connected'));
    socket.on('disconnect', () => setSocketStatus('disconnected'));
    socket.on('connect_error', (e) => {
      console.warn('Socket connect_error:', e?.message || e);
      setSocketStatus('disconnected');
    });

    socket.on('new-bus-data', onPacket);

    return () => {
      socket.off('new-bus-data', onPacket);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [roads]); // re-connect only if roads change

  // Staleness / maintenance detector
  useEffect(() => {
    const t = setInterval(() => {
      const sinceMs = Date.now() - lastUpdateRef.current;
      const stale = sinceMs > STALE_MS;
      const downAndEmpty = socketStatus === 'disconnected' && buses.length === 0;

      if (stale || downAndEmpty) {
        const mins = Math.floor(sinceMs / 60000);
        const secs = Math.floor((sinceMs % 60000) / 1000);
        setMaintenance(true);
        setMaintenanceReason(
          stale ? `No new data for ${mins}m ${secs}s` : 'Live connection unavailable'
        );
      } else {
        setMaintenance(false);
        setMaintenanceReason('');
      }
    }, 5000);
    return () => clearInterval(t);
  }, [socketStatus, buses.length]);

  // ---- Processing pipeline: smooth then snap ----
  function snapToRoad(coord, flatLines) {
    if (!flatLines || flatLines.length === 0) return null;
    const pt = turf.point([coord.lng, coord.lat]);

    let best = null;
    let bestDist = Infinity;

    for (const line of flatLines) {
      const np = turf.nearestPointOnLine(line, pt, { units: 'meters' });
      const dist = np?.properties?.dist;
      if (Number.isFinite(dist) && dist < bestDist) {
        bestDist = dist;
        const [lng, lat] = np.geometry.coordinates;
        best = { lat, lng, distM: dist };
      }
    }
    return best;
  }

  function processVehicles(list, prevMapOpt) {
    const flatLines = flatRoadLinesRef.current;
    const last = lastSmoothedRef.current;
    const result = [];

    for (const d of list) {
      const id = d.vehicleno;
      const raw = { lat: d.latitude, lng: d.longitude };

      const prev = last.get(id);
      const smoothedCoord = smoothCoord(prev?.coord, raw, SMOOTH_ALPHA);
      const smoothedHeading = smoothHeading(prev?.heading, d.heading, HEADING_ALPHA);

      const snapped = snapToRoad(smoothedCoord, flatLines);

      const use = snapped?.distM != null && snapped.distM <= SNAP_MAX_DIST_M
        ? { lat: snapped.lat, lng: snapped.lng }
        : smoothedCoord;

      last.set(id, { coord: use, heading: smoothedHeading });

      result.push({
        vehicleno: d.vehicleno,
        latitude: use.lat,
        longitude: use.lng,
        speed: d.speed,
        heading: smoothedHeading
      });
    }

    if (prevMapOpt) {
      for (const [id, b] of prevMapOpt) {
        if (list.find(x => x.vehicleno === id)) continue;
        const prev = last.get(id);
        if (!prev) {
          result.push(b);
          continue;
        }
        result.push({
          vehicleno: id,
          latitude: prev.coord.lat,
          longitude: prev.coord.lng,
          speed: b.speed,
          heading: prev.heading
        });
      }
    }

    return result;
  }

    // ---------------- Route geometry + ETA helpers ----------------
  const stopById = useMemo(() => Object.fromEntries(busStops.map(s => [s.id, s])), []);
  function routePolyline(ids) {
    return ids.map(id => ({ lat: stopById[id].lat, lng: stopById[id].lng }));
  }
  function cumDist(poly) {
    const out = [0];
    for (let i = 1; i < poly.length; i++) out.push(out[i - 1] + mBetween(poly[i - 1], poly[i]));
    return out;
  }
  const routeGeom = useMemo(() => {
    const g = {};
    for (const r of ROUTES) {
      const poly = routePolyline(r.ids);
      g[r.code] = { poly, cum: cumDist(poly), ids: r.ids };
    }
    return g;
  }, [stopById]);

  function projectOnRoute(pt, poly) {
    if (!poly || poly.length < 2) return null;
    let best = null;
    for (let i = 0; i < poly.length - 1; i++) {
      const seg = turf.lineString([[poly[i].lng, poly[i].lat], [poly[i + 1].lng, poly[i + 1].lat]]);
      const p = turf.nearestPointOnLine(seg, turf.point([pt.lng, pt.lat]), { units: "meters" });
      const d = p.properties?.dist ?? Infinity;
      if (!best || d < best.dist) {
        const [lng, lat] = p.geometry.coordinates;
        best = { idx: i, point: { lat, lng }, dist: d };
      }
    }
    return best;
  }

  function distanceAlong(poly, cum, proj, targetIndex) {
    const dHead = mBetween(proj.point, poly[proj.idx + 1]);
    const dTail = cum[targetIndex] - cum[proj.idx + 1];
    return dHead + Math.max(0, dTail);
  }

  // // ---- ETA constants: match backend ----
  // const AVG_SPEED_KMPH = 20;
  // const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;
  // const WAIT_RADIUS_M = 25;         // within 25m of first stop
  // const WAIT_SPEED_KMPH_MAX = 1.0;  // consider "waiting" if <= 1 km/h

  // // Constant-speed ETA (20 km/h), ignoring live speed
  // function etaFromMeters(distanceM) {
  //   const seconds = distanceM / AVG_SPEED_MPS;
  //   const minutes = Math.round(seconds / 60);
  //   return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
  // }

  // function nextStopIndex(routeIds, projIdx) {
  //   return Math.min(routeIds.length - 1, projIdx + 1);
  // }

  // // For a given bus, find nearest upcoming stop on its route + ETA
  // function nearestStopEtaForBusOnItsRoute(bus) {
  //   const routeCode = assignments[bus.vehicleno];
  //   if (!routeCode) return null;
  //   const geom = routeGeom[routeCode];
  //   if (!geom || geom.poly.length < 2) return null;

  //   const pt = { lat: bus.latitude, lng: bus.longitude };
  //   const proj = projectOnRoute(pt, geom.poly);
  //   if (!proj) return null;

  //   const firstStopId = geom.ids[0];
  //   const firstStop = stopById[firstStopId];
  //   let waitingAtFirstStop = false;
  //   if (firstStop) {
  //     const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
  //     const speed = Number(bus.speed);
  //     if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
  //       waitingAtFirstStop = true;
  //     }
  //   }

  //   const nextIdx = nextStopIndex(geom.ids, proj.idx);
  //   const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

  //   let etaMin = undefined;
  //   if (!waitingAtFirstStop) {
  //     etaMin = etaFromMeters(distanceM);
  //   }

  //   return {
  //     stopId: geom.ids[nextIdx],
  //     distanceM,
  //     etaMin,
  //     routeCode,
  //     waitingAtFirstStop,
  //   };
  // }

    // ---- ETA constants: match backend ----
  const AVG_SPEED_KMPH = 20;
  const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;

  // holding detection at ANY stop
  const HOLD_STOP_RADIUS_M = 30;
  const HOLD_SPEED_KMPH_MAX = 1.5;

  // Constant-speed ETA (20 km/h), ignoring live speed
  function etaFromMeters(distanceM) {
    const seconds = distanceM / AVG_SPEED_MPS;
    const minutes = Math.round(seconds / 60);
    return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
  }

  function nextStopIndex(routeIds, projIdx) {
    return Math.min(routeIds.length - 1, projIdx + 1);
  }

  function detectHoldingStopOnRoute(bus, geom) {
    const pt = { lat: bus.latitude, lng: bus.longitude };
    const speed = Number(bus.speed);

    let bestIdx = -1;
    let bestDist = Infinity;

    for (let i = 0; i < geom.ids.length; i++) {
      const s = stopById[geom.ids[i]];
      if (!s) continue;

      const dist = mBetween(pt, { lat: s.lat, lng: s.lng });
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    if (bestIdx < 0) {
      return {
        isHolding: false,
        stopId: null,
        stopIndex: -1,
        distToStop: Infinity
      };
    }

    const slowOrUnknown =
      !Number.isFinite(speed) || speed <= HOLD_SPEED_KMPH_MAX;

    const isHolding =
      bestDist <= HOLD_STOP_RADIUS_M && slowOrUnknown;

    return {
      isHolding,
      stopId: geom.ids[bestIdx],
      stopIndex: bestIdx,
      distToStop: bestDist
    };
  }

  // For a given bus, find nearest stop ETA on its route
  function nearestStopEtaForBusOnItsRoute(bus) {
    const routeCode = assignments[bus.vehicleno];
    if (!routeCode) return null;

    const geom = routeGeom[routeCode];
    if (!geom || geom.poly.length < 2) return null;

    const pt = { lat: bus.latitude, lng: bus.longitude };
    const proj = projectOnRoute(pt, geom.poly);
    if (!proj) return null;

    const holdInfo = detectHoldingStopOnRoute(bus, geom);

    // If bus is standing at a stop, show that stop as current stop with ETA 0
    if (holdInfo.isHolding) {
      return {
        stopId: holdInfo.stopId,
        distanceM: 0,
        etaMin: 0,
        routeCode,
        waitingAtStop: true
      };
    }

    const nextIdx = nextStopIndex(geom.ids, proj.idx);
    const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

    return {
      stopId: geom.ids[nextIdx],
      distanceM,
      etaMin: etaFromMeters(distanceM),
      routeCode,
      waitingAtStop: false
    };
  }

  // For a given stop, find the best incoming vehicle + ETA
  // function nextArrivalAtStop(stopId) {
  //   const candidates = [];

  //   for (const bus of buses) {
  //     if (startsWithDigit(bus.vehicleno)) continue;
  //     const rc = assignments[bus.vehicleno];
  //     if (!rc) continue;
  //     const geom = routeGeom[rc];
  //     if (!geom) continue;

  //     const indexInRoute = geom.ids.indexOf(stopId);
  //     if (indexInRoute < 0) continue;

  //     const pt = { lat: bus.latitude, lng: bus.longitude };
  //     const proj = projectOnRoute(pt, geom.poly);
  //     if (!proj) continue;

  //     const targetIndex = Math.max(indexInRoute, proj.idx + 1);
  //     if (targetIndex >= geom.ids.length) continue;

  //     const distanceM = distanceAlong(geom.poly, geom.cum, proj, targetIndex);

  //     // If bus is already past this stop, skip
  //     if (distanceM < 0) continue;

  //     const firstStopId = geom.ids[0];
  //     const firstStop = stopById[firstStopId];
  //     let waitingAtFirstStop = false;
  //     if (firstStop) {
  //       const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
  //       const speed = Number(bus.speed);
  //       if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
  //         waitingAtFirstStop = true;
  //       }
  //     }

  //     let etaMin = undefined;
  //     if (!waitingAtFirstStop) {
  //       etaMin = etaFromMeters(distanceM);
  //     }

  //     candidates.push({
  //       vehicle: bus.vehicleno,
  //       routeCode: rc,
  //       etaMin,
  //       waitingAtFirstStop,
  //       distanceM,
  //     });
  //   }

  //   if (!candidates.length) return null;

  //   // Prefer moving vehicles (etaMin != null), then fallback to closest waiting one
  //   const moving = candidates.filter(c => c.etaMin != null);
  //   if (moving.length) {
  //     return moving.reduce((a, b) => (a.etaMin <= b.etaMin ? a : b));
  //   }

  //   // All are waiting at first stop: pick closest along-route
  //   return candidates.reduce((a, b) => (a.distanceM <= b.distanceM ? a : b));
  // }


  // ---- Derived lists ----
  const visibleBuses = buses.filter(b => !startsWithDigit(b.vehicleno));
  const filteredBuses = visibleBuses.filter(b => matchesFilter(b.vehicleno, filterType, assignments));

  // -------------- SCHEDULE UI helpers --------------
  const TABS = [
    { key: 'hostel-main', title: 'Hostel → Main Gate', data: HOSTEL_TO_MAIN, notes: HOSTEL_NOTES, fmt: 'ampm' },
    { key: 'main-hostel', title: 'Main Gate → Hostel', data: MAIN_TO_HOSTEL, notes: MAIN_NOTES, fmt: 'ampm' },
    { key: 'vg-main',     title: 'Velachery Gate → Main Gate', data: VELACHERY_TO_MAIN, notes: VELACHERY_NOTES, fmt: 'ampm' },
    { key: 'vg-hostel',   title: 'Velachery Gate → Hostel', data: VELACHERY_TO_HOSTEL, notes: VELACHERY_NOTES, fmt: 'ampm' },
    { key: 'rp-ed',       title: 'Research Park → ED', data: RP_TO_ED_TIMES, notes: VELACHERY_NOTES, fmt: '24' },
    { key: 'ed-rp',       title: 'ED → Research Park', data: ED_TO_RP_TIMES, notes: VELACHERY_NOTES, fmt: '24' }
  ];

  function TimeChip({ t }) {
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          background: '#fff',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: clamp('12px','2.8vw','14px')
        }}
      >
        {t}
      </div>
    );
  }

  function CartTable({ rows, title }) {
    const th = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', fontWeight: 700 };
    const td = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #f2f2f2' };

    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontWeight: 700, margin: '8px 0' }}>{title}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Cart</th>
                <th style={th}>RP → ED</th>
                <th style={th}>ED → RP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([Cart, a, b], i) => (
                <tr key={`${Cart}-${i}`}>
                  <td style={td}>{Cart}</td>
                  <td style={td}>{a}</td>
                  <td style={td}>{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function ScheduleBody({ tab }) {
    if (tab.fmt === 'table-Cart') {
      const rows1 = Cart1.map(([a, b]) => ['Cart1', a, b]);
      const rows3 = Cart3.map(([a, b]) => ['Cart3', a, b]);
      return (
        <>
          <CartTable rows={rows1} title="Cart1" />
          <CartTable rows={rows3} title="Cart3" />
        </>
      );
    }
    if (tab.fmt === 'table-overall') {
      const all = [
        ...Cart1.map(([a, b]) => ['Cart1', a, b]),
        ...Cart3.map(([a, b]) => ['Cart3', a, b])
      ];
      return <CartTable rows={all} title="Overall" />;
    }

    const times = tab.data;
    return (
      <>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isNarrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8
          }}
        >
          {times.map((t) => <TimeChip key={t} t={t} />)}
        </div>

        {tab.notes && tab.notes.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {tab.notes.map((n) => (
              <div
                key={n.time}
                style={{
                  fontSize: 13,
                  background: '#fff8e1',
                  border: '1px solid #f0d48a',
                  padding: '6px 10px',
                  borderRadius: 10,
                  marginTop: 6
                }}
              >
                <strong>{n.time}</strong> – {n.note}
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  function ScheduleModal() {
    const tab = TABS[activeTab];
    const tabListRef = useRef(null);
    const tabScrollRef = useRef(0);

    useLayoutEffect(() => {
      const el = tabListRef.current;
      if (el && el.scrollLeft !== tabScrollRef.current) {
        el.scrollLeft = tabScrollRef.current;
      }
    });

    return (
      <div
        onClick={() => setShowSchedule(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: isNarrow ? '96vw' : 'min(1100px, 96vw)',
            maxHeight: '90dvh',
            background: '#ffffff',
            borderRadius: 16,
            boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: isNarrow ? '8px 10px' : '12px 16px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}
          >
            <img src={iitmLogo} alt="IIT Madras" style={{ width: isNarrow ? 26 : 32, height: isNarrow ? 26 : 32 }} />
            <div style={{ fontWeight: 800, fontSize: clamp('14px','2.8vw','18px') }}>
              IIT Madras – Campus Bus &amp; Cart Schedule
            </div>
            <button
              onClick={() => setShowSchedule(false)}
              style={{
                marginLeft: 'auto',
                border: 'none',
                background: '#f4f4f5',
                borderRadius: 999,
                width: 34,
                height: 34,
                cursor: 'pointer',
                fontSize: 16
              }}
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Tab bar */}
          <div
            ref={tabListRef}
            onScroll={(e) => { tabScrollRef.current = e.currentTarget.scrollLeft; }}
            style={{
              display: 'block',
              padding: '8px 12px',
              borderBottom: '1px solid #eee',
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              overscrollBehaviorX: 'contain',
              touchAction: 'pan-x'
            }}
          >
            <div style={{ display: 'inline-flex', gap: 8, minWidth: '100%', marginBottom: isNarrow ? '45px' : '18px'}}>
              {TABS.map((t, i) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(i)}
                  style={{
                    whiteSpace: 'nowrap',
                    padding: isNarrow ? '8px 10px' : '8px 12px',
                    borderRadius: 10,
                    border: i === activeTab ? '1px solid #0D47A1' : '1px solid #ddd',
                    background: i === activeTab ? '#eaf2ff' : '#ffffff',
                    fontWeight: 600,
                    cursor: 'pointer',
                    flex: '0 0 auto'
                  }}
                >
                  {t.title}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: isNarrow ? 8 : 12, overflow: 'auto' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{tab.title}</div>
            <ScheduleBody tab={tab} />
          </div>
        </div>
      </div>
    );
  }

  // Pause live updates when modal open; refresh once on close, then resume
  useEffect(() => {
    if (showSchedule) { ignoreUpdatesRef.current = true; return; }
    (async () => {
      try {
        if (!BACKEND) return;
        const res = await axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 });
        const arr = Array.isArray(res.data) ? res.data : [];
        const byVeh = new Map();
        for (const d of arr) {
          const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
          byVeh.set(d.vehicleno, {
            vehicleno: d.vehicleno,
            latitude: d.latitude,
            longitude: d.longitude,
            speed: d.speed,
            heading
          });
        }
        const list = [...byVeh.values()];
        setBuses(processVehicles(list));
        lastUpdateRef.current = Date.now();
      } catch (e) {
        console.warn('Refresh after modal close failed:', e?.message || e);
      } finally {
        ignoreUpdatesRef.current = false;
      }
    })();
  }, [showSchedule]);

  // Responsive stop icons (smaller on phones)
  const stopIconInstance = useMemo(
    () => createCustomIcon(stopIconImg, isNarrow ? [40, 40] : [50, 50]),
    [isNarrow]
  );

  useLayoutEffect(() => {
  const el = headerRef.current;
  if (!el) return;

  const updateHeaderHeight = () => {
    setHeaderHeight(el.getBoundingClientRect().height || 0);
  };

  updateHeaderHeight();

  let resizeObserver;

  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(el);
  }

  window.addEventListener("resize", updateHeaderHeight);

  return () => {
    if (resizeObserver) resizeObserver.disconnect();
    window.removeEventListener("resize", updateHeaderHeight);
  };
}, []);
  const handleJourneyLocationUpdate = useCallback((location) => {
    if (!location || !Number.isFinite(Number(location.lat)) || !Number.isFinite(Number(location.lng))) return;
    setUserLocation((previous) => {
      if (
        previous &&
        Math.abs(Number(previous.lat) - Number(location.lat)) < 0.000001 &&
        Math.abs(Number(previous.lng) - Number(location.lng)) < 0.000001
      ) {
        return previous;
      }
      return location;
    });
  }, []);

  const handleJourneyFocus = useCallback((location) => {
    const lat = Number(location?.lat);
    const lng = Number(location?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    setMapCenter((previous) => {
      if (
        previous &&
        Math.abs(Number(previous.lat) - lat) < 0.000001 &&
        Math.abs(Number(previous.lng) - lng) < 0.000001
      ) {
        return previous;
      }
      return { lat, lng };
    });
  }, []);

  // --------------------------- RENDER ---------------------------
  return (
    <div className="map-container">
      {/* Header */}
{/* Header */}
<div
ref={headerRef}
  style={{
    position: 'fixed',
    top: 'max(8px, env(safe-area-inset-top))',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10000,
    background: '#ffffff',
    border: '1px solid #e6e6e7',
    borderRadius: 16,
    padding: isHeaderCollapsed ? (isNarrow ? 8 : 10) : pad,
    boxShadow: '0 18px 40px rgba(0,0,0,0.25)',
    width: isHeaderCollapsed
      ? (isNarrow ? 'calc(100vw - 16px)' : 'min(420px, calc(100vw - 32px))')
      : (isNarrow ? 'calc(100vw - 16px)' : 'min(560px, calc(100vw - 32px))'),
    backdropFilter: 'blur(2px)',
    transition: 'width 180ms ease, padding 180ms ease'
  }}
>
  {/* Title row */}
  <div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? 8 : 12 }}>
    <img
      src={iitmLogo}
      alt="IIT Madras"
      style={{
        width: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
        height: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
        flex: '0 0 auto'
      }}
    />

    <h2
      style={{
        margin: 0,
        fontSize: isHeaderCollapsed
          ? clamp('15px', '3vw', '20px')
          : clamp('18px', '3.2vw', '28px'),
        lineHeight: 1.1,
        letterSpacing: 0.3,
        fontWeight: 800,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1
      }}
    >
      Moving Mountains
    </h2>

    {isHeaderCollapsed && (
      <div
        style={{
          fontSize: isNarrow ? 12 : 13,
          fontWeight: 800,
          color: '#111827',
          whiteSpace: 'nowrap'
        }}
        title="Visible vehicles / total vehicles"
      >
        {filteredBuses.length}/{buses.length}
      </div>
    )}

    {!isHeaderCollapsed && (
      <a
        href="/admin"
        style={{
          fontWeight: 700,
          whiteSpace: 'nowrap'
        }}
      >
        Admin ↗
      </a>
    )}

    <button
      type="button"
      onClick={() => setIsHeaderCollapsed((v) => !v)}
      aria-label={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
      title={isHeaderCollapsed ? 'Expand' : 'Collapse'}
      style={{
        border: '1px solid #D1D5DB',
        background: '#ffffff',
        color: '#111827',
        borderRadius: 10,
        padding: isNarrow ? '6px 8px' : '7px 10px',
        fontWeight: 800,
        fontSize: isNarrow ? 13 : 14,
        lineHeight: 1,
        cursor: 'pointer',
        minWidth: isNarrow ? 34 : 38,
        minHeight: isNarrow ? 32 : 36
      }}
    >
      {isHeaderCollapsed ? '▾' : '▴'}
    </button>
  </div>

  {!isHeaderCollapsed && (
    <>
      {/* Status row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 10,
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: chipFS,
            fontWeight: 700,
            background:
              socketStatus === 'connected'
                ? '#E9F9EE'
                : socketStatus === 'connecting'
                ? '#FFF7E1'
                : '#FFECEC',
            color:
              socketStatus === 'connected'
                ? '#0A7A2A'
                : socketStatus === 'connecting'
                ? '#7A5D00'
                : '#B00020',
            border:
              socketStatus === 'connected'
                ? '1px solid #C6EBD1'
                : socketStatus === 'connecting'
                ? '1px solid #F4E3B0'
                : '1px solid #FFD0D0',
            textTransform: 'lowercase'
          }}
          title={`Socket: ${socketStatus}`}
        >
          {socketStatus}
        </span>

        <div
          style={{
            fontSize: clamp('14px','2.6vw','18px'),
            fontWeight: 700,
            letterSpacing: 0.5,
            color: '#111827'
          }}
        >
          {filteredBuses.length} / {buses.length}
        </div>
      </div>

      {/* Controls row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: gap,
          marginTop: gap,
          flexWrap: 'wrap'
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            title="Filter vehicles"
            style={{
              width: '100%',
              padding: isNarrow ? '10px 34px 10px 12px' : '10px 40px 10px 14px',
              borderRadius: 10,
              border: '1px solid #D1D5DB',
              background: '#FFFFFF',
              fontWeight: 600,
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none'
            }}
          >
            <option value="all">All Vehicles</option>
            <option value="cart">Cart</option>
            <option value="bus">Bus</option>
            <optgroup label="Filter by Route">
              {ROUTES.map(r => (
                <option key={r.code} value={`route:${r.code}`}>{r.label}</option>
              ))}
            </optgroup>
          </select>

          <div
            style={{
              position: 'absolute',
              right: isNarrow ? 8 : 12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '7px solid #111',
              pointerEvents: 'none'
            }}
          />
        </div>

        <button
          onClick={() => {
            requestUserLocation();
            setTimeout(() => {
              window.location.reload();
            }, 150);
          }}
          title="Recenter + Reload"
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #D1D5DB',
            background: '#FFFFFF',
            fontWeight: 700,
            cursor: 'pointer',
            minHeight: isNarrow ? 36 : 40,
            fontSize: clamp('13px','2.8vw','14px')
          }}
        >
          Refresh
        </button>

        <button
          onClick={() => setShowSchedule(true)}
          title="Show schedule"
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: '1px solid #0D47A1',
            background: '#0D47A1',
            color: '#FFFFFF',
            fontWeight: 800,
            cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(13,71,161,0.25)',
            minHeight: isNarrow ? 36 : 40,
            fontSize: clamp('13px','2.8vw','14px')
          }}
        >
          Schedule
        </button>
      </div>

      {maintenance && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#fff8e1', border: '1px solid #f0d48a', fontWeight: 600 }}>
          Under maintenance — {maintenanceReason}
        </div>
      )}

      {locError && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#FFECEC', border: '1px solid #FFD0D0', fontWeight: 600 }}>
          {locError}

          <button
            type="button"
            onClick={requestUserLocation}
            style={{ marginLeft: 10, padding: '6px 10px', borderRadius: 10, border: '1px solid #D1D5DB', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
            title={geoState === "denied" ? "Enable in site settings first, then try again" : "Try again"}
          >
            {geoState === "denied" ? "Open site settings" : "Allow location"}
          </button>
        </div>
      )}
    </>
  )}
</div>

{/* {SHOW_MINT_SUMMIT_PROMO && (
  <a
    href={MINT_SUMMIT_REGISTRATION_URL}
    target="_blank"
    rel="noreferrer"
    className="mint-summit-floating-banner"
    style={{
      top: isHeaderCollapsed ? 76 : 310,
      width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
    }}
    title="Register for MInT Mobility Summit 2026"
  >
    <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
    <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
    <span className="mint-summit-cta">Register ↗</span>
  </a>
)} */}

{SHOW_MINT_SUMMIT_PROMO && (
  <a
    href={MINT_SUMMIT_REGISTRATION_URL}
    target="_blank"
    rel="noreferrer"
    className="mint-summit-floating-banner"
    style={{
      top: headerHeight
        ? `calc(max(8px, env(safe-area-inset-top)) + ${headerHeight}px + ${isNarrow ? 8 : 10}px)`
        : isHeaderCollapsed
        ? 76
        : 260,
      width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
    }}
    title="Register for MInT Mobility Summit 2026"
  >
    <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
    <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
    <span className="mint-summit-cta">Register ↗</span>
  </a>
)}

      <JourneyPlanner
        backend={BACKEND}
        userLocation={userLocation}
        requestUserLocation={requestUserLocation}
        onPlanChange={setJourneyPlan}
        onLocationUpdate={handleJourneyLocationUpdate}
        onFocus={handleJourneyFocus}
        campusMapPicking={campusMapPicking}
        pickedCampusPoint={campusPickedPoint}
        onBeginCampusMapPick={() => {
          setCampusPickedPoint(null);
          setCampusMapPicking(true);
        }}
        onEndCampusMapPick={() => setCampusMapPicking(false)}
        onClearCampusPickedPoint={() => setCampusPickedPoint(null)}
      />

      {campusMapPicking && (
        <div className="campus-map-pick-banner" role="status">
          <span className="campus-map-pick-target">⌖</span>
          <div><strong>Tap the exact public entrance</strong><small>Zoom in, then tap the point people should walk to.</small></div>
          <button type="button" onClick={() => setCampusMapPicking(false)}>Cancel</button>
        </div>
      )}

      {/* Schedule Modal */}
      {showSchedule && <ScheduleModal />}

      {/* Map uses dynamic viewport on mobile (100dvh) */}
      {/* <MapContainer center={center} zoom={DEFAULT_ZOOM} maxZoom={22} style={{ width: '100vw', height: '100dvh' }}> */}
      <MapContainer
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        minZoom={15}
        maxZoom={22}
        maxBounds={CAMPUS_MAP_BOUNDS}
        maxBoundsViscosity={1.0}
        worldCopyJump={false}
        style={{ width: '100vw', height: '100dvh' }}
      >
      <RecenterMap center={mapCenter} disabled={Boolean(journeyPlan)} />
      <CampusMapPicker
        enabled={campusMapPicking}
        point={campusPickedPoint}
        onPick={(point) => {
          setCampusPickedPoint(point);
          setCampusMapPicking(false);
        }}
      />
      <JourneyMapLayer plan={journeyPlan} />

        <TileLayer
          attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxNativeZoom={19}
          maxZoom={22}
          noWrap={true}
        />

        {userLocation && (
          <Marker position={{ lat: userLocation.lat, lng: userLocation.lng }} icon={userIconInstance}>
            <Popup>
              <div className="info-window">
                <h4>You are here</h4>
              </div>
            </Popup>
          </Marker>
        )}
        {/* here i have updated the backend eta */}
        {/* Bus Stops with backend ETA (polled every 5s) */}
        {busStops.map((stop) => {
          const etaState = stopEtas[stop.id];           // { data, error } or undefined
          const payload = etaState?.data;               // backend JSON
          const next = payload?.next || null;           // { vehicle, goingTowards, etaMinutes } | null

          return (
            <Marker
              key={stop.id}
              position={{ lat: stop.lat, lng: stop.lng }}
              icon={stopIconInstance}
            >
              <Popup>
                <div className="info-window">
                  <h4>{stop.name}</h4>

                  {/* 1) Still loading / first poll */}
                  {!etaState && <p>Loading ETA…</p>}

                  {/* 2) Request for this stop failed */}
                  {etaState && !payload && <p>ETA not available.</p>}

                  {/* 3) Backend says: no approaching assignment */}
                  {payload && payload.success && !next && (
                    <p>No assigned vehicle approaching.</p>
                  )}

                  {/* 4) We have a next arrival from backend */}
                  {payload && payload.success && next && (
                    <>
                      {/* You said you may not need vehicle number/type, 
                          so we only show direction + ETA. */}
                      <p>
                          Next Vehicle Details:
                        </p>
                      {next.vehicle && (
                        <p>
                          <strong>Vehicle Type:</strong> {next.vehicle}
                        </p>
                      )}
                      {next.goingTowards && (
                        <p>
                          <strong>Direction:</strong> {next.goingTowards}
                        </p>
                      )}
                      {/* <p>
                        <strong>ETA:</strong>{" "}
                        {next.etaMinutes != null
                          ? `${next.etaMinutes} min`
                          : "— (has not departed yet)"}
                      </p> */}
                      <p>
  <strong>ETA:</strong>{" "}
  {next.etaStatus === "AT_STOP"
    ? "0 min (at stop)"
    : next.etaMinutes != null
    ? `${next.etaMinutes} min`
    : "— (has not departed yet)"}
</p>
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}



        {/* Vehicles */}
        {filteredBuses.map((bus) => {
          // const label = vehicleLabel(bus.vehicleno);
          const rc = assignments[bus.vehicleno];
          const label = vehicleLabel(bus.vehicleno, rc);
          const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
          const info = nearestStopEtaForBusOnItsRoute(bus);
          // const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
          // const eta = info ? `${info.etaMin} min` : 'Unknown';
          const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
          const eta =
            info && info.etaMin != null
              ? `${info.etaMin} min`
              : info
              ? "—"
              : "Unknown";

          return (
            <Marker
              key={bus.vehicleno}
              position={{ lat: bus.latitude, lng: bus.longitude }}
              // icon={createVehicleDivIcon(label, bus.heading)}
              icon={createVehicleDivIcon(label, bus.heading, palette)}

            >
              <Popup>
                <div className="info-window">
                  <h4>{bus.vehicleno} ({label})</h4>
                  {/* <p><strong>Legend:</strong> B = Bus, C = Cart</p> */}
                  <p><strong>Legend:</strong> B = Bus, C = Cart, CR = Cart (RP↔ED)</p>
                  <p><strong>Speed:</strong> {Number.isFinite(bus.speed) ? bus.speed.toFixed(1) : 'N/A'} km/h</p>
                  {assignments[bus.vehicleno] && (
                    <p><strong>Route:</strong> {ROUTES.find(r=>r.code===assignments[bus.vehicleno])?.label || assignments[bus.vehicleno]}</p>
                  )}
                  {nearestName && <p><strong>Next Stop:</strong> {nearestName}</p>}
                  {/* <p><strong>ETA:</strong> {eta}</p> */}
                  {/* <p>
                    <strong>ETA:</strong>{" "}
                    {eta}
                    {info && info.waitingAtFirstStop ? " (has not departed yet)" : ""}
                  </p> */}
                  <p>
  <strong>ETA:</strong>{" "}
  {eta}
  {info && info.waitingAtStop ? " (waiting at stop)" : ""}
</p>
                  {Number.isFinite(bus.heading) && <p><strong>Heading:</strong> {Math.round(bus.heading)}°</p>}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Footer: Feedback link */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: -5,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 10000,
          pointerEvents: 'none',
          paddingBottom: 'max(6px, env(safe-area-inset-bottom))'
        }}
      >
        <div
          style={{
            width: '100vw',
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: isNarrow ? '6px 10px' : '6px 16px',
            borderRadius: 14,
            background:
              'linear-gradient(135deg, rgba(17,24,39,0.85), rgba(31,41,55,0.85))',
            color: '#F9FAFB',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow:
              '0 12px 30px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
            backdropFilter: 'saturate(160%) blur(10px)',
            WebkitBackdropFilter: 'saturate(160%) blur(10px)',
            fontFamily:
              '"SF Pro Text", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"',
            fontSize: clamp('12px','2.8vw','14px'),
            lineHeight: 1.4,
            fontWeight: 500,
            letterSpacing: '0.2px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: 9999,
                background:
                  'radial-gradient(circle at 30% 30%, #34D399, #059669)',
                boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
                flex: '0 0 auto',
              }}
            />
            <span
              style={{
                opacity: 0.95,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
               Amit Kumar | CE20B009 | MInT Lab
            </span>
          </div>

          {/* {FEEDBACK_URL ? (
            <a
              href={FEEDBACK_URL}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 12px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.18)',
                background:
                  'linear-gradient(180deg, rgba(128, 126, 126, 0.14), rgba(221, 212, 212, 0.06))',
                color: '#F9FAFB',
                textDecoration: 'none',
                fontWeight: 600,
                letterSpacing: '0.25px',
                transform: 'translateZ(0)',
                transition: 'transform 120ms ease, opacity 120ms ease',
                cursor: 'pointer',
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 14px rgba(0,0,0,0.25)',
              }}
              onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              Open feedback form ↗
            </a>
          ) : ( */}

          {FOOTER_ACTION_URL ? (
  <a
    href={FOOTER_ACTION_URL}
    target="_blank"
    rel="noreferrer"
    className={
      SHOW_MINT_SUMMIT_PROMO
        ? 'footer-action-button mint-summit-glitter-button'
        : 'footer-action-button'
    }
    title={FOOTER_ACTION_LABEL}
  >
    {FOOTER_ACTION_LABEL}
  </a>
) : (
            <span
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                border: '1px dashed rgba(255,255,255,0.25)',
                color: 'rgba(249,250,251,0.8)',
                fontWeight: 500,
              }}
            >
              {/* &lt;form-link&gt; */}
              &lt;form-link&gt;
            </span>
          )}
        </div>
      </div>

    </div>
  );
}

// function MaintenancePoster() {
//   return (
//     <div className="maintenance-page">
//       <div className="maintenance-card">
//         <div className="maintenance-logo-wrap">
//           <img src={iitmLogo} alt="IIT Madras" className="maintenance-logo" />
//         </div>

//         <div className="maintenance-badge">Server migration in progress</div>

//         <h1>We’ll be back very soon</h1>

//         <p className="maintenance-main-text">
//           Hey, I'm Amit Kumar. Thank you for using Moving Mountains. We value your time and effort.
//           Our server is currently being upgraded to provide a smoother, faster,
//           and more reliable experience.
//         </p>

//         <p className="maintenance-sub-text">
//           The website will be available again shortly once the migration is
//           completed. We appreciate your patience and support.
//         </p>

//         <div className="maintenance-loader">
//           <span></span>
//           <span></span>
//           <span></span>
//         </div>

//         <div className="maintenance-footer">
//           Thanks for using our service.
//           <br />
//           — Team Moving Mountains
//         </div>
//       </div>
//     </div>
//   );
// }

function MaintenancePoster() {
  return (
    <div className="maintenance-page">
      <div className="maintenance-card">
        <div className="maintenance-logo-wrap">
          <img src={iitmLogo} alt="IIT Madras" className="maintenance-logo" />
        </div>

        <div className="maintenance-badge">Temporary service issue</div>

        <h1>We’ll be back soon</h1>

        <p className="maintenance-main-text">
          Hey, I'm Amit Kumar. Thank you for using Moving Mountains. We’re
          currently experiencing a technical issue that may affect access to
          the website.
        </p>

        <p className="maintenance-sub-text">
          Our team is actively working to resolve the problem as soon as
          possible. We appreciate your patience and apologise for the
          inconvenience.
        </p>

        <div className="maintenance-loader">
          <span></span>
          <span></span>
          <span></span>
        </div>

        <div className="maintenance-footer">
          Thank you for your patience and support.
          <br />
          — Team Moving Mountains
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- APP (router) ---------------------------- */

export default function App() {
  const { path, navigate } = usePath();
  const token = getToken();
  const adminToken = getAdminToken();

  // Track page views on main map for Prometheus
  useEffect(() => {
    if (!BACKEND) return;
    if (path !== "/") return;
    axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
  }, [path]);

  // Forward /driver -> /driverRoute when logged in
  useEffect(() => {
    if (path === "/driver" && token) {
      navigate("/driverRoute");
    }
  }, [path, token, navigate]);

  // Admin redirects
  useEffect(() => {
    if (path === "/admin/login" && adminToken) {
      navigate("/admin");
    } else if (path === "/admin" && !adminToken) {
      navigate("/admin/login");
    }
  }, [path, adminToken, navigate]);

  if (MAINTENANCE_MODE) {
    return <MaintenancePoster />;
  }

  if (path === "/driver") {
    if (token) return null;
    return <DriverLogin navigate={navigate} />;
  }
  if (path === "/driverRoute") return <DriverRoute navigate={navigate} />;

  if (path === "/admin/login") {
    if (adminToken) return null;
    return <AdminLogin navigate={navigate} />;
  }
  if (path === "/admin") {
    return <AdminDashboard navigate={navigate} />;
  }

  return <MapPage />;
}

// export default function App() {
//   const { path, navigate } = usePath();
//   const token = getToken();

//   // Track page views on main map for Prometheus
//   useEffect(() => {
//     if (!BACKEND) return;
//     if (path !== "/") return;
//     axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
//   }, [path]);

//   // Forward /driver -> /driverRoute when logged in (no hooks called conditionally)
//   useEffect(() => {
//     if (path === '/driver' && token) {
//       navigate('/driverRoute');
//     }
//   }, [path, token, navigate]);

//   if (path === '/driver') {
//     if (token) return null;
//     return <DriverLogin navigate={navigate} />;
//   }
//   if (path === '/driverRoute') return <DriverRoute navigate={navigate} />;
//   return <MapPage />;
// }



// // traveliitm/frontend/src/App.js
// // import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
// import React, {
//   useEffect,
//   useState,
//   useRef,
//   useLayoutEffect,
//   useMemo,
//   useCallback
// } from 'react';

// import axios from 'axios';
// import { io } from 'socket.io-client';
// import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
// import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';
// import './App.css';
// import * as turf from '@turf/turf';
// import JourneyPlanner from './components/JourneyPlanner';
// import JourneyMapLayer from './components/JourneyMapLayer';

// // ---- Assets in src/assets ----
// import userIcon from './assets/user-icon.png';
// import stopIconImg from './assets/bus-stop.png';
// import iitmLogo from './assets/iitm-logo.png';

// // ---- Backend base URL ----
// const BACKEND = process.env.REACT_APP_BACKEND_URL;

// // ---- Temporary maintenance mode ----
// // Set this to false after server migration is completed.
// const MAINTENANCE_MODE = false;

// // ---- Static roads URL (public/roads.geojson) ----
// const ROADS_URL = `${process.env.PUBLIC_URL || ''}/roads.geojson`;

// // ---- Map zoom param ----
// const DEFAULT_ZOOM = 18;

// // Keep the public experience focused on IIT Madras instead of allowing users
// // to pan across Tamil Nadu/world tiles. The small margin covers all campus roads.
// const CAMPUS_MAP_BOUNDS = [
//   [12.9780, 80.2170],
//   [13.0115, 80.2485],
// ];

// // ---- Smoothing / Snap params ----
// const SMOOTH_ALPHA = 0.3;
// const HEADING_ALPHA = 0.35;
// const SNAP_MAX_DIST_M = 20;

// // Staleness threshold for under-maintenance banner
// const STALE_MS = 150_000; // ~2.5 minutes

// // Initial center (fallback)
// const center = { lat: 12.9915, lng: 80.2336 };

// // ---------------- STOPS WITH IDS (for route math) ----------------
// const busStops = [
//   { name: 'Velachery Gate IIT Campus Stop', lat: 12.988763557585395, lng: 80.22367960515129, id: 1 },
//   { name: 'NAC2 Stop Towards GC', lat: 12.990236, lng: 80.227548, id: 2 },
//   { name: 'Nac2 Towards Velachery Stop', lat: 12.9898636, lng: 80.2271109, id: 3 },
//   { name: 'CRC Towards GC Stop', lat: 12.99089, lng: 80.230274, id: 4 },
//   { name: 'CRC Towards Velachery Stop', lat: 12.99079680, lng: 80.23016660, id: 5 },  
//   { name: 'HSB Stop Towards Velachery Stop', lat: 12.99076780, lng: 80.23168950, id: 6},
//   { name: 'HSB Stop Towards GC', lat: 12.991037, lng: 80.232065, id: 7},
//   { name: 'KV Stop Towards Main Gate', lat: 12.991868573807471, lng: 80.23367454352295, id: 8 },
//   { name: 'KV Stop Towards GC', lat: 12.991910, lng: 80.233785, id: 9 },
//   { name: 'Postoffice Stop Towards Main Gate', lat: 12.99388680, lng: 80.23428760, id: 10 },
//   { name: 'Postoffice Stop Towards GC', lat: 12.99393340, lng: 80.23459350, id: 11 },
//   { name: 'E1 Stop Towards Main Gate', lat: 12.9960173, lng: 80.2359173, id: 12 },
//   { name: 'E1 Stop Towards GC', lat: 12.996117, lng: 80.236184, id: 13},
//   { name: 'Vana Vani Stop Towards Main Gate', lat: 12.998704, lng: 80.2391799, id: 14 },
//   { name: 'Vana Vani Stop Towards GC', lat: 12.999084,   lng:  80.239380, id: 15 },
//   { name: 'D1 Stop Towards Main Gate', lat:13.002546, lng: 80.240091, id: 16},
//   { name: 'D1 Stop Towards GC', lat: 13.002679,  lng: 80.240219, id: 17 },
//   { name: 'Main Gate', lat: 13.00612964850378, lng: 80.24191299117685, id: 18 },
//   { name: 'GC Stop Towards Hostel', lat: 12.991342, lng: 80.233637, id: 19 },
//   { name: 'Library Stop', lat: 12.9907517, lng: 80.2334554, id: 20 },
//   { name: 'OAT Stop Towards Hostel', lat: 12.989257,  lng: 80.233031, id: 21},
//   { name: 'Gymkhana Stop Towards GC', lat: 12.9866372, lng: 80.2332951, id: 22},
//   { name: 'Gymkhana Stop Towards Hostel', lat: 12.986615, lng: 80.233366, id: 23 },
//   { name: 'Narmada Stop Towards GC', lat: 12.9862759, lng: 80.2350107, id: 24 },
//   { name: 'Narmada Stop Towards Hostel', lat: 12.986546, lng: 80.235301, id: 25 },
//   { name: 'Jamuna and Ganga Stand', lat: 12.986566332825637, lng: 80.23855704439099, id: 26 },
//   { name: 'RP Stand', lat: 12.990088, lng: 80.241799, id: 28},
//   { name: 'ED Stand', lat: 12.989874633072303, lng: 80.22646042038593, id: 29},
// ];

// // Route sequences by stop id
// const Hostel_to_Main   = [26, 24, 22, 20, 6, 5, 3, 1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
// const Main_to_Hostel   = [18, 17, 15, 13, 11, 9, 6, 5, 3, 1, 2, 4, 7, 19, 21, 23, 25, 26];
// const Velachery_to_Main= [1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
// const Velachery_to_Hostel=[1, 2, 4, 7, 19, 21, 23, 25, 26];
// const Hotel_to_Velachery=[26, 24, 22, 20, 6, 5, 3, 1];
// const Main_to_Velachery=[18, 17, 15, 13, 11, 9, 6, 5, 3, 1];
// const RP_to_ED         = [28, 22, 21, 20, 6, 5, 3, 29];
// const ED_to_RP         = [29, 2, 4, 7, 19, 21, 23, 28];

// const ROUTES = [
//   { code: "HOSTEL_MAIN", label: "Hostel → Main Gate", ids: Hostel_to_Main },
//   { code: "MAIN_HOSTEL", label: "Main Gate → Hostel", ids: Main_to_Hostel },
//   { code: "VG_MAIN",     label: "Velachery Gate → Main Gate", ids: Velachery_to_Main },
//   { code: "VG_HOSTEL",   label: "Velachery Gate → Hostel", ids: Velachery_to_Hostel },
//   { code: "MAIN_VG",     label: "Main Gate → Velachery Gate", ids: Main_to_Velachery },
//   { code: "HOSTEL_VG",   label: "Hostel → Velachery Gate", ids: Hotel_to_Velachery },
//   { code: "RP_ED",       label: "Research Park → ED", ids: RP_to_ED },
//   { code: "ED_RP",       label: "ED → Research Park", ids: ED_to_RP },
// ];

// // ---- Route aliases (frontend only) ----
// // Backend only knows canonical codes: HOSTEL_MAIN, MAIN_HOSTEL, RP_ED, ED_RP
// // But filters can still expose Velachery labels.
// // Aliases map to canonical backend codes.
// const ROUTE_ALIAS_TO_CANONICAL = {
//   VG_MAIN: "HOSTEL_MAIN",
//   HOSTEL_VG: "HOSTEL_MAIN",
//   MAIN_VG: "MAIN_HOSTEL",
//   VG_HOSTEL: "MAIN_HOSTEL",
// };

// function canonicalRoute(code) {
//   return ROUTE_ALIAS_TO_CANONICAL[code] || code;
// }
// // Routes the admin can assign in backend (only canonical codes)
// const ADMIN_ROUTES = [
//   { code: "HOSTEL_MAIN", label: "Hostel → Main Gate" },
//   { code: "MAIN_HOSTEL", label: "Main Gate → Hostel" },
//   { code: "RP_ED", label: "Research Park → ED" },
//   { code: "ED_RP", label: "ED → Research Park" },
// ];


// // ---- Vehicles for driverRoute select ----
// const VEHICLES = [
//   "TN07DH1368","TN07DH1317","TN07DH1315","EV09","TN07DH1360","TN07DH1391","EV08","TN07DD5574",
//   "TN07CX8698","TN07DD3376","TN07CX8200","TN07DD3360","EV-12","EV-14","EV-15","EV-10","EV-05",
//   "EV-07","EV-06","EV-02","EV-03","EV-11","EV-01","EV-13","TN07CX8119"
// ];

// // ---- Leaflet default icon fix ----
// delete L.Icon.Default.prototype._getIconUrl;
// L.Icon.Default.mergeOptions({
//   iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
//   iconUrl: require('leaflet/dist/images/marker-icon.png'),
//   shadowUrl: require('leaflet/dist/images/marker-shadow.png')
// });

// // ---- Utility helpers ----
// const createCustomIcon = (iconUrl, size = [40, 40]) =>
//   L.icon({
//     iconUrl,
//     iconSize: size,
//     iconAnchor: [size[0] / 2, size[1]],
//     popupAnchor: [0, -size[1] / 2]
//   });

// const userIconInstance = createCustomIcon(userIcon, [40, 40]);

// const toHeading = (v) => {
//   const n = Number(v);
//   return Number.isFinite(n) ? ((n % 360) + 360) % 360 : null;
// };

// // Exponential smoothing for latitude/longitude
// function smoothCoord(prev, next, alpha = SMOOTH_ALPHA) {
//   if (!prev) return next;
//   return {
//     lat: prev.lat + alpha * (next.lat - prev.lat),
//     lng: prev.lng + alpha * (next.lng - prev.lng)
//   };
// }



// // Circular smoothing for heading (degrees)
// function smoothHeading(prevDeg, nextDeg, alpha = HEADING_ALPHA) {
//   if (!Number.isFinite(prevDeg)) return nextDeg;
//   if (!Number.isFinite(nextDeg)) return prevDeg;
//   const p = (prevDeg * Math.PI) / 180;
//   const n = (nextDeg * Math.PI) / 180;
//   const x = (1 - alpha) * Math.cos(p) + alpha * Math.cos(n);
//   const y = (1 - alpha) * Math.sin(p) + alpha * Math.sin(n);
//   const ang = (Math.atan2(y, x) * 180) / Math.PI;
//   return (((ang % 360) + 360) % 360);
// }
// function mBetween(a, b) { return L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng)); }

// // ---- Cart classification (case-insensitive "EV") ----
// function isCart(vehicleno) {
//   const s = String(vehicleno || '').trim();
//   return s.toUpperCase().startsWith('EV');
// }

// // Label: B for Bus, C for Cart
// // function vehicleLabel(vehicleno) { return isCart(vehicleno) ? 'C' : 'B'; }
// function vehicleLabel(vehicleno, routeCode) {
//   if (!isCart(vehicleno)) return "B";

//   const rc = canonicalRoute(routeCode || "");
//   if (rc === "RP_ED" || rc === "ED_RP") return "CR"; // 👈 cart on RP↔ED
//   return "C";
// }


// // ---- Block IDs starting with a digit ----
// function startsWithDigit(vehicleno) { return /^[0-9]/.test(String(vehicleno || '')); }

// // ---- Direction pointer “on the circle” DivIcon ----
// // function createVehicleDivIcon(label, headingDeg) {
// //   const circle = 20;
// //   const border = 3;
// //   const triH = 18;
// //   const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
// //   const totalH = triH + circle;

// //   const html = `
// //     <div style="position:relative;width:${circle}px;height:${totalH}px">
// //       <div style="
// //         position:absolute; left:0; right:0; bottom:0;
// //         width:${circle}px; height:${circle}px;
// //         border:${border}px solid #0D47A1; background:#ffffff; border-radius:50%;
// //         display:flex; align-items:center; justify-content:center;
// //         box-shadow:0 2px 8px rgba(0,0,0,0.25);
// //         font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
// //         letter-spacing:0.6px; user-select:none;">
// //         <span style="font-size:16px; color:#0D47A1">${label}</span>
// //       </div>
// //       <div style="
// //         position:absolute; left:50%; top:0;
// //         transform: translateX(-250%) rotate(${rot}deg);
// //         transform-origin: 50% ${triH * 0.7}px;
// //         width:0; height:0;
// //         border-left:${triH / 4}px solid transparent;
// //         border-right:${triH / 4}px solid transparent;
// //         border-bottom:${triH}px solid #0D47A1;
// //         filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
// //         z-index: 2; pointer-events: none;"></div>
// //     </div>
// //   `;

// //   return L.divIcon({
// //     className: 'veh-div-icon',
// //     html,
// //     iconSize: [circle, totalH],
// //     iconAnchor: [circle / 2, totalH - circle / 2],
// //     popupAnchor: [0, -(circle / 2 + triH / 2)]
// //   });
// // }

// function createVehicleDivIcon(label, headingDeg, palette) {
//   const circle = 20;
//   const border = 3;
//   const triH = 18;
//   const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
//   const totalH = triH + circle;

//   // default bus palette
//   const p = palette || {
//     stroke: "#0D47A1",
//     fill: "#ffffff",
//     text: "#0D47A1",
//     pointer: "#0D47A1",
//   };

//   const html = `
//     <div style="position:relative;width:${circle}px;height:${totalH}px">
//       <div style="
//         position:absolute; left:0; right:0; bottom:0;
//         width:${circle}px; height:${circle}px;
//         border:${border}px solid ${p.stroke};
//         background:${p.fill};
//         border-radius:50%;
//         display:flex; align-items:center; justify-content:center;
//         box-shadow:0 2px 8px rgba(0,0,0,0.25);
//         font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
//         letter-spacing:0.6px; user-select:none;">
//         <span style="font-size:16px; color:${p.text}">${label}</span>
//       </div>
//       <div style="
//         position:absolute; left:50%; top:0;
//         transform: translateX(-250%) rotate(${rot}deg);
//         transform-origin: 50% ${triH * 0.7}px;
//         width:0; height:0;
//         border-left:${triH / 4}px solid transparent;
//         border-right:${triH / 4}px solid transparent;
//         border-bottom:${triH}px solid ${p.pointer};
//         filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
//         z-index: 2; pointer-events: none;"></div>
//     </div>
//   `;

//   return L.divIcon({
//     className: 'veh-div-icon',
//     html,
//     iconSize: [circle, totalH],
//     iconAnchor: [circle / 2, totalH - circle / 2],
//     popupAnchor: [0, -(circle / 2 + triH / 2)]
//   });
// }

// const BUS_PALETTE = {
//   stroke: "#0D47A1",
//   fill: "#ffffff",
//   text: "#0D47A1",
//   pointer: "#0D47A1",
// };

// // const CART_PALETTE = {
// //   stroke: "#0F766E",  // teal border
// //   fill: "#ECFEFF",    // light teal fill
// //   text: "#0F766E",
// //   pointer: "#0F766E",
// // };
// const CART_PALETTE = {
//   stroke: "#111827",   // near-black border
//   fill:   "#FEF08A",   // bright yellow fill
//   text:   "#111827",   // black text
//   pointer:"#111827",   // black pointer
// };





// function matchesFilter(vehicleno, filter, assignments) {
//   const cart = isCart(vehicleno);
//   if (filter === "cart") return cart;
//   if (filter === "bus") return !cart;

//   if (filter?.startsWith("route:")) {
//     const requested = canonicalRoute(filter.slice(6));
//     const assigned = canonicalRoute(assignments[vehicleno]);
//     return assigned === requested;
//   }
//   return true;
// }

// // ---- Filter helper ----
// // function matchesFilter(vehicleno, filter, assignments) {
// //   const cart = isCart(vehicleno);
// //   if (filter === 'cart') return cart;
// //   if (filter === 'bus')  return !cart;
// //   if (filter?.startsWith('route:')) {
// //     const code = filter.slice(6);
// //     return assignments[vehicleno] === code;
// //   }
// //   return true;
// // }

// // ---------------- SCHEDULE DATA (kept intact) ----------------
// const HOSTEL_TO_MAIN = [
//   '06:20 AM','06:40 AM','07:00 AM','07:20 AM','07:40 AM','08:00 AM','08:20 AM','08:40 AM',
//   '09:00 AM','09:20 AM','09:40 AM','10:00 AM','10:20 AM','10:40 AM','11:00 AM','11:20 AM',
//   '11:40 AM','12:00 PM','12:20 PM','12:40 PM','01:00 PM','01:20 PM','01:40 PM','02:00 PM',
//   '02:20 PM','02:40 PM','03:00 PM','03:20 PM','03:40 PM','04:00 PM','04:20 PM','04:40 PM',
//   '05:00 PM','05:20 PM','05:40 PM','06:00 PM','06:20 PM','06:40 PM','07:00 PM','07:20 PM',
//   '07:40 PM','08:00 PM','08:20 PM','08:40 PM','09:00 PM','09:20 PM'
// ];
// const HOSTEL_NOTES = [
//   { time: '09:30 PM', note: 'To Velachery Gate' },
//   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// ];
// const MAIN_TO_HOSTEL = [...HOSTEL_TO_MAIN];
// const MAIN_NOTES = [
//   { time: '09:40 PM', note: 'To Velachery Gate' },
//   { time: '10:00 PM', note: 'To Velachery Gate' },
//   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// ];
// const VELACHERY_TO_MAIN = [
//   '06:15 AM','06:35 AM','06:55 AM','07:15 AM','07:35 AM','07:55 AM','08:15 AM','08:35 AM','08:55 AM',
//   '09:15 AM','09:35 AM','09:55 AM','10:15 AM','10:35 AM','10:55 AM','11:15 AM','11:35 AM',
//   '12:15 PM','12:35 PM','12:55 PM','01:15 PM','01:35 PM','01:55 PM','02:15 PM','02:35 PM','02:55 PM',
//   '03:15 PM','03:35 PM','03:55 PM','04:15 PM','04:35 PM','04:55 PM','05:15 PM','05:35 PM','05:55 PM',
//   '06:15 PM','06:35 PM','06:55 PM','07:15 PM','07:35 PM','07:55 PM','08:15 PM','08:35 PM','08:55 PM',
//   '09:15 PM','09:35 PM'
// ];
// const VELACHERY_TO_HOSTEL = [
//   '06:25 AM','06:45 AM','07:05 AM','07:25 AM','07:45 AM','08:05 AM','08:25 AM','08:45 AM','09:05 AM',
//   '09:25 AM','09:45 AM','10:05 AM','10:25 AM','10:45 AM','11:05 AM','11:25 AM','11:45 AM',
//   '12:05 PM','12:25 PM','12:45 PM','01:05 PM','01:25 PM','01:45 PM','02:05 PM','02:25 PM','02:45 PM',
//   '03:05 PM','03:25 PM','03:45 PM','04:05 PM','04:25 PM','04:45 PM','05:05 PM','05:25 PM','05:45 PM',
//   '06:05 PM','06:25 PM','06:45 PM','07:05 PM','07:25 PM','07:45 PM','08:05 PM','08:25 PM','08:45 PM',
//   '09:05 PM','09:15 PM'
// ];
// const VELACHERY_NOTES = [
//   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// ];
// const RP_TO_ED_TIMES = [
//   '07:30 AM','08:05 AM','08:40 AM','09:00 AM','09:15 AM','09:35 AM','09:45 AM',
//   '10:15 AM','10:25 AM','10:45 AM','11:20 AM','11:40 AM',
//   '12:05 PM','12:45 PM','01:40 PM','02:20 PM','02:35 PM','03:00 PM','03:15 PM','03:45 PM',
//   '04:15 PM','04:50 PM','05:05 PM','05:50 PM','06:25 PM','07:05 PM'
// ];
// const ED_TO_RP_TIMES = [
//   '07:50 AM','08:25 AM','09:00 AM','09:15 AM','09:30 AM','10:00 AM','10:05 AM','10:30 AM',
//   '11:00 AM','11:15 AM','11:45 AM',
//   '12:00 PM','12:25 PM','01:05 PM','02:00 PM','02:40 PM','03:00 PM','03:20 PM','03:30 PM',
//   '04:05 PM','04:45 PM','05:10 PM','05:30 PM','06:10 PM','06:45 PM','07:20 PM'
// ];
// const Cart1 = [['07:30','07:50'],['08:05','08:25'],['08:40','09:00'],['09:15','09:30'],['09:45','10:05'],['10:25','11:00'],['11:20','11:45'],['12:05','12:25'],['13:40','14:00'],['14:20','14:40'],['15:00','15:20'],['15:45','16:05'],['16:50','17:10']];
// const Cart3 = [['09:00','09:15'],['09:35','10:00'],['10:15','10:30'],['10:45','11:15'],['11:40','12:00'],['12:45','13:05'],['14:35','15:00'],['15:15','15:30'],['16:15','16:45'],['17:05','17:30'],['17:50','18:10'],['18:25','18:45'],['19:05','19:20']];

// // // ---- Feedback link ----
// // const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

// // ---- Feedback / temporary summit promo links ----
// const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

// const MINT_SUMMIT_REGISTRATION_URL =
//   process.env.REACT_APP_MINT_SUMMIT_REGISTRATION_URL ||
//   'https://docs.google.com/forms/d/e/1FAIpQLScB7Y6RtzcOwXRsoi4Czrr_vUnOcZm7joeeKSNKvcNm7RRjLw/viewform';

// // Set this to false after the summit.
// const SHOW_MINT_SUMMIT_PROMO = false;

// const FOOTER_ACTION_URL = SHOW_MINT_SUMMIT_PROMO
//   ? MINT_SUMMIT_REGISTRATION_URL
//   : FEEDBACK_URL;

// const FOOTER_ACTION_LABEL = SHOW_MINT_SUMMIT_PROMO
//   ? 'Register for MInT Summit ✨'
//   : 'Open feedback form ↗';

// // ---------------- Tiny router helpers (for /driver, /driverRoute) ----------------
// function usePath() {
//   const [path, setPath] = useState(window.location.pathname);
//   useEffect(() => {
//     const onPop = () => setPath(window.location.pathname);
//     window.addEventListener('popstate', onPop);
//     return () => window.removeEventListener('popstate', onPop);
//   }, []);
//   const navigate = (p) => { if (p !== window.location.pathname) { window.history.pushState({}, "", p); setPath(p); } };
//   return { path, navigate };
// }
// function getToken() { return localStorage.getItem("driverToken") || ""; }
// function setToken(t) { localStorage.setItem("driverToken", t || ""); }
// function getUsername() { return localStorage.getItem("driverUsername") || ""; }
// function setUsername(u) { localStorage.setItem("driverUsername", u || ""); }

// // --- Admin auth helpers (separate from driver) ---
// function getAdminToken() {
//   return localStorage.getItem("adminToken") || "";
// }
// function setAdminToken(t) {
//   localStorage.setItem("adminToken", t || "");
// }


// // ---------------- Driver pages ----------------
// // --- small responsive hook for narrow phones ---
// function useIsNarrow(bp = 380) {
//   const [isNarrow, setIsNarrow] = useState(window.innerWidth <= bp);
//   useEffect(() => {
//     const onResize = () => setIsNarrow(window.innerWidth <= bp);
//     window.addEventListener('resize', onResize);
//     return () => window.removeEventListener('resize', onResize);
//   }, [bp]);
//   return isNarrow;
// }

// function DriverLogin({ navigate }) {
//   const [username, setU] = useState("");
//   const [password, setP] = useState("");
//   const [err, setErr] = useState("");
//   const [busy, setBusy] = useState(false);
//   const isNarrow = useIsNarrow(380);

//   useEffect(() => {
//     if (getToken()) navigate("/driverRoute");
//   }, [navigate]);

//   const onSubmit = async (e) => {
//     e.preventDefault();
//     setErr(""); setBusy(true);
//     try {
//       const r = await axios.post(`${BACKEND}/api/driver/login`, { username, password });
//       if (r.data?.success && r.data?.token) {
//         setToken(r.data.token);
//         setUsername(r.data.username);
//         navigate("/driverRoute");
//       } else setErr("Login failed");
//     } catch (e1) {
//       setErr(e1?.response?.data?.message || "Login failed");
//     } finally { setBusy(false); }
//   };

//   return (
//     <div
//       style={{
//         padding: isNarrow ? 12 : 20,
//         margin: isNarrow ? "16px auto" : "32px auto",
//         width: "min(100vw - 24px, 560px)",
//       }}
//     >
//       {/* Header with Home button */}
//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           gap: isNarrow ? 10 : 12,
//           marginBottom: isNarrow ? 10 : 14,
//           flexWrap: "wrap",
//         }}
//       >
//         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
//           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
//           <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Driver Login</h2>
//         </div>

//         <button
//           type="button"
//           onClick={() => navigate("/")}
//           style={{
//             marginLeft: "auto",
//             padding: isNarrow ? "6px 10px" : "8px 12px",
//             borderRadius: 10,
//             border: "1px solid #D1D5DB",
//             background: "#FFFFFF",
//             fontWeight: 700,
//             fontSize: isNarrow ? 13 : 14,
//             cursor: "pointer",
//           }}
//           title="Go to Home"
//         >
//           Home
//         </button>
//       </div>

//       {/* Card */}
//       <div
//         style={{
//           background: "#fff",
//           border: "1px solid #E5E7EB",
//           borderRadius: 12,
//           padding: isNarrow ? 12 : 16,
//         }}
//       >
//         <form onSubmit={onSubmit} style={{ display: "grid", gap: isNarrow ? 8 : 10 }}>
//           <input
//             value={username}
//             onChange={(e) => setU(e.target.value)}
//             placeholder="username"
//             required
//             style={{
//               padding: isNarrow ? 10 : 12,
//               borderRadius: 10,
//               border: "1px solid #D1D5DB",
//               fontWeight: 600,
//               fontSize: isNarrow ? 14 : 16,
//             }}
//           />
//           <input
//             value={password}
//             onChange={(e) => setP(e.target.value)}
//             placeholder="password"
//             type="password"
//             required
//             style={{
//               padding: isNarrow ? 10 : 12,
//               borderRadius: 10,
//               border: "1px solid #D1D5DB",
//               fontWeight: 600,
//               fontSize: isNarrow ? 14 : 16,
//             }}
//           />
//           <button
//             disabled={busy}
//             style={{
//               padding: isNarrow ? "10px 12px" : "12px 14px",
//               borderRadius: 10,
//               border: "1px solid #0D47A1",
//               background: "#0D47A1",
//               color: "#fff",
//               fontWeight: 800,
//               cursor: "pointer",
//               width: "100%",
//               minHeight: 40,
//               fontSize: "clamp(14px, 3.5vw, 16px)",
//             }}
//           >
//             {busy ? "Signing in..." : "Login"}
//           </button>
//           {err && <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>}
//         </form>
//       </div>
//     </div>
//   );
// }

// function DriverRoute({ navigate }) {
//   const [routeCode, setRouteCode] = useState("");
//   const [vehicleNo, setVehicleNo] = useState("");
//   const [autoPilot, setAutoPilot] = useState(false); // 👈 NEW: auto-pilot flag
//   const [saved, setSaved] = useState(false);
//   const [busy, setBusy] = useState(false);
//   const [err, setErr] = useState("");
//   const isNarrow = useIsNarrow(380);

//   useEffect(() => {
//     if (!getToken()) navigate("/driver");
//     (async () => {
//       try {
//         const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
//           headers: { Authorization: `Bearer ${getToken()}` },
//         });
//         const row = r.data?.row;
//         if (row) {
//           setVehicleNo(row.vehicleNo);
//           setRouteCode(row.routeCode);
//           setAutoPilot(!!row.autopilotEnabled); // 👈 load auto-pilot state from backend
//           setSaved(true);
//         }
//       } catch {}
//     })();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const onSave = async (e) => {
//     e.preventDefault();
//     setErr(""); setBusy(true);
//     try {
//       await axios.post(
//         `${BACKEND}/api/vehicle-route/assign`,
//         {
//           vehicleNo,
//           routeCode,
//           active: true,
//           autopilotEnabled: autoPilot, // 👈 send auto-pilot choice to backend
//         },
//         { headers: { Authorization: `Bearer ${getToken()}` } }
//       );
//       setSaved(true);
//     } catch (e1) {
//       setErr(e1?.response?.data?.message || "Save failed");
//     } finally {
//       setBusy(false);
//     }
//   };

//   return (
//     <div
//       style={{
//         padding: isNarrow ? 16 : 20,
//         maxWidth: 640,
//         margin: isNarrow ? "16px auto" : "32px auto",
//         width: "min(100vw - 24px, 640px)",
//       }}
//     >
//       <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
//           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
//           <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
//         </div>
//         <div
//           style={{
//             marginLeft: 'auto',
//             fontWeight: 700,
//             opacity: 0.8,
//             fontSize: "clamp(13px, 3.2vw, 16px)",
//             maxWidth: '60%',
//             overflow: 'hidden',
//             textOverflow: 'ellipsis',
//             whiteSpace: 'nowrap',
//           }}
//           title={`@${getUsername()}`}
//         >
//           @{getUsername()}
//         </div>
//       </div>

//       {/* Top-right actions */}
//       <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
//         <div style={{ display: 'flex', gap: 8 }}>
//           <button
//             onClick={() => navigate('/')}
//             style={{
//               padding: isNarrow ? '6px 10px' : '8px 12px',
//               borderRadius: 10,
//               border: '1px solid #D1D5DB',
//               background: '#FFFFFF',
//               fontWeight: 700,
//               fontSize: isNarrow ? 13 : 14,
//               cursor: 'pointer'
//             }}
//             title="Go to Home"
//           >
//             Home
//           </button>
//           <button
//             type="button"
//             onClick={() => setSaved(false)}
//             disabled={!saved}
//             style={{
//               padding: isNarrow ? '6px 10px' : '8px 12px',
//               borderRadius: 10,
//               border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
//               background: saved ? '#fff' : '#F9FAFB',
//               color: saved ? '#111' : '#9CA3AF',
//               fontWeight: 800,
//               fontSize: isNarrow ? 13 : 14,
//               cursor: saved ? 'pointer' : 'not-allowed'
//             }}
//             title="Edit selection"
//           >
//             Edit
//           </button>
//         </div>

//         <button
//           onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
//           style={{
//             padding: isNarrow ? '6px 10px' : 8,
//             borderRadius: 10,
//             border: '1px solid #D1D5DB',
//             background: '#FFFFFF',
//             fontSize: isNarrow ? 13 : 14,
//             cursor: 'pointer'
//           }}
//           title="Logout"
//         >
//           Logout
//         </button>
//       </div>

//       <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
//         <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
//         <select
//           disabled={saved}
//           value={routeCode}
//           onChange={e=>setRouteCode(e.target.value)}
//           required
//           style={{
//             padding: isNarrow ? 10 : 12,
//             borderRadius: 10,
//             border: '1px solid #D1D5DB',
//             fontSize: isNarrow ? 14 : 16
//           }}
//         >
//           <option value="">Select route…</option>
//           {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
//         </select>

//         <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
//         <select
//           disabled={saved}
//           value={vehicleNo}
//           onChange={e=>setVehicleNo(e.target.value)}
//           required
//           style={{
//             padding: isNarrow ? 10 : 12,
//             borderRadius: 10,
//             border: '1px solid #D1D5DB',
//             fontSize: isNarrow ? 14 : 16
//           }}
//         >
//           <option value="">Select vehicle…</option>
//           {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
//         </select>

//         {/* 👇 NEW: Auto-pilot toggle */}
//         <div
//           style={{
//             marginTop: isNarrow ? 6 : 8,
//             padding: isNarrow ? 8 : 10,
//             borderRadius: 10,
//             border: '1px solid #E5E7EB',
//             background: '#F9FAFB',
//             display: 'flex',
//             alignItems: 'flex-start',
//             gap: 8,
//           }}
//         >
//           <input
//             id="autoPilot"
//             type="checkbox"
//             checked={autoPilot}
//             disabled={saved}
//             onChange={(e) => setAutoPilot(e.target.checked)}
//             style={{ marginTop: 3, cursor: saved ? 'default' : 'pointer' }}
//           />
//           <label
//             htmlFor="autoPilot"
//             style={{ cursor: saved ? 'default' : 'pointer' }}
//           >
//             <div style={{ fontWeight: 700, fontSize: isNarrow ? 14 : 15 }}>
//               Auto-pilot (auto reverse at destination)
//             </div>
//             <div
//               style={{
//                 fontSize: isNarrow ? 12 : 13,
//                 color: '#6B7280',
//                 marginTop: 2,
//               }}
//             >
//               When enabled, once you reach the last stop of this route, your
//               assignment will automatically switch to the return leg.
//             </div>
//           </label>
//         </div>

//         <button
//           disabled={busy || saved}
//           style={{
//             padding: isNarrow ? '10px 12px' : '12px 14px',
//             borderRadius: 10,
//             border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
//             background: busy || saved ? '#F3F4F6' : '#0D47A1',
//             color: busy || saved ? '#6B7280' : '#fff',
//             fontWeight: 800,
//             cursor: busy || saved ? 'not-allowed' : 'pointer',
//             minHeight: 44,
//             fontSize: "clamp(14px, 3.5vw, 16px)"
//           }}
//           title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
//         >
//           {busy ? "Saving…" : saved ? "Saved" : "Save"}
//         </button>

//         {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
//       </form>
//     </div>
//   );
// }


// // function DriverRoute({ navigate }) {
// //   const [routeCode, setRouteCode] = useState("");
// //   const [vehicleNo, setVehicleNo] = useState("");
// //   const [saved, setSaved] = useState(false);
// //   const [busy, setBusy] = useState(false);
// //   const [err, setErr] = useState("");
// //   const isNarrow = useIsNarrow(380);

// //   useEffect(() => {
// //     if (!getToken()) navigate("/driver");
// //     (async () => {
// //       try {
// //         const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
// //           headers: { Authorization: `Bearer ${getToken()}` },
// //         });
// //         const row = r.data?.row;
// //         if (row) { setVehicleNo(row.vehicleNo); setRouteCode(row.routeCode); setSaved(true); }
// //       } catch {}
// //     })();
// //     // eslint-disable-next-line react-hooks/exhaustive-deps
// //   }, []);

// //   const onSave = async (e) => {
// //     e.preventDefault();
// //     setErr(""); setBusy(true);
// //     try {
// //       await axios.post(
// //         `${BACKEND}/api/vehicle-route/assign`,
// //         { vehicleNo, routeCode, active: true },
// //         { headers: { Authorization: `Bearer ${getToken()}` } }
// //       );
// //       setSaved(true);
// //     } catch (e1) {
// //       setErr(e1?.response?.data?.message || "Save failed");
// //     } finally {
// //       setBusy(false);
// //     }
// //   };

// //   return (
// //     <div
// //       style={{
// //         padding: isNarrow ? 16 : 20,
// //         maxWidth: 640,
// //         margin: isNarrow ? "16px auto" : "32px auto",
// //         width: "min(100vw - 24px, 640px)",
// //       }}
// //     >
// //       <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
// //         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
// //           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
// //           <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
// //         </div>
// //         <div
// //           style={{
// //             marginLeft: 'auto',
// //             fontWeight: 700,
// //             opacity: 0.8,
// //             fontSize: "clamp(13px, 3.2vw, 16px)",
// //             maxWidth: '60%',
// //             overflow: 'hidden',
// //             textOverflow: 'ellipsis',
// //             whiteSpace: 'nowrap',
// //           }}
// //           title={`@${getUsername()}`}
// //         >
// //           @{getUsername()}
// //         </div>
// //       </div>

// //       {/* Top-right actions */}
// //       <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
// //         <div style={{ display: 'flex', gap: 8 }}>
// //           <button
// //             onClick={() => navigate('/')}
// //             style={{
// //               padding: isNarrow ? '6px 10px' : '8px 12px',
// //               borderRadius: 10,
// //               border: '1px solid #D1D5DB',
// //               background: '#FFFFFF',
// //               fontWeight: 700,
// //               fontSize: isNarrow ? 13 : 14,
// //               cursor: 'pointer'
// //             }}
// //             title="Go to Home"
// //           >
// //             Home
// //           </button>
// //           <button
// //             type="button"
// //             onClick={() => setSaved(false)}
// //             disabled={!saved}
// //             style={{
// //               padding: isNarrow ? '6px 10px' : '8px 12px',
// //               borderRadius: 10,
// //               border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
// //               background: saved ? '#fff' : '#F9FAFB',
// //               color: saved ? '#111' : '#9CA3AF',
// //               fontWeight: 800,
// //               fontSize: isNarrow ? 13 : 14,
// //               cursor: saved ? 'pointer' : 'not-allowed'
// //             }}
// //             title="Edit selection"
// //           >
// //             Edit
// //           </button>
// //         </div>

// //         <button
// //           onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
// //           style={{
// //             padding: isNarrow ? '6px 10px' : 8,
// //             borderRadius: 10,
// //             border: '1px solid #D1D5DB',
// //             background: '#FFFFFF',
// //             fontSize: isNarrow ? 13 : 14,
// //             cursor: 'pointer'
// //           }}
// //           title="Logout"
// //         >
// //           Logout
// //         </button>
// //       </div>

// //       <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
// //         <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
// //         <select
// //           disabled={saved}
// //           value={routeCode}
// //           onChange={e=>setRouteCode(e.target.value)}
// //           required
// //           style={{
// //             padding: isNarrow ? 10 : 12,
// //             borderRadius: 10,
// //             border: '1px solid #D1D5DB',
// //             fontSize: isNarrow ? 14 : 16
// //           }}
// //         >
// //           <option value="">Select route…</option>
// //           {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
// //         </select>

// //         <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
// //         <select
// //           disabled={saved}
// //           value={vehicleNo}
// //           onChange={e=>setVehicleNo(e.target.value)}
// //           required
// //           style={{
// //             padding: isNarrow ? 10 : 12,
// //             borderRadius: 10,
// //             border: '1px solid #D1D5DB',
// //             fontSize: isNarrow ? 14 : 16
// //           }}
// //         >
// //           <option value="">Select vehicle…</option>
// //           {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
// //         </select>

// //         <button
// //           disabled={busy || saved}
// //           style={{
// //             padding: isNarrow ? '10px 12px' : '12px 14px',
// //             borderRadius: 10,
// //             border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
// //             background: busy || saved ? '#F3F4F6' : '#0D47A1',
// //             color: busy || saved ? '#6B7280' : '#fff',
// //             fontWeight: 800,
// //             cursor: busy || saved ? 'not-allowed' : 'pointer',
// //             minHeight: 44,
// //             fontSize: "clamp(14px, 3.5vw, 16px)"
// //           }}
// //           title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
// //         >
// //           {busy ? "Saving…" : saved ? "Saved" : "Save"}
// //         </button>

// //         {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
// //       </form>
// //     </div>
// //   );
// // }

// /* ---------------------------- ADMIN PAGES ---------------------------- */

// function AdminLogin({ navigate }) {
//   const [username, setU] = useState("");
//   const [password, setP] = useState("");
//   const [err, setErr] = useState("");
//   const [busy, setBusy] = useState(false);
//   const isNarrow = useIsNarrow(380);

//   useEffect(() => {
//     if (getAdminToken()) navigate("/admin");
//   }, [navigate]);

//   const onSubmit = async (e) => {
//     e.preventDefault();
//     setErr("");
//     setBusy(true);
//     try {
//       const r = await axios.post(`${BACKEND}/api/admin/login`, {
//         username,
//         password,
//       });
//       if (r.data?.success && r.data?.token) {
//         setAdminToken(r.data.token);
//         navigate("/admin");
//       } else {
//         setErr("Login failed");
//       }
//     } catch (e1) {
//       setErr(e1?.response?.data?.message || "Login failed");
//     } finally {
//       setBusy(false);
//     }
//   };

//   return (
//     <div
//       style={{
//         padding: isNarrow ? 12 : 20,
//         margin: isNarrow ? "16px auto" : "32px auto",
//         width: "min(100vw - 24px, 560px)",
//       }}
//     >
//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           gap: isNarrow ? 10 : 12,
//           marginBottom: isNarrow ? 10 : 14,
//           flexWrap: "wrap",
//         }}
//       >
//         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
//           <img
//             src={iitmLogo}
//             alt="IITM"
//             style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }}
//           />
//           <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Admin Login</h2>
//         </div>
//         <button
//           type="button"
//           onClick={() => navigate("/")}
//           style={{
//             marginLeft: "auto",
//             padding: isNarrow ? "6px 10px" : "8px 12px",
//             borderRadius: 10,
//             border: "1px solid #D1D5DB",
//             background: "#FFFFFF",
//             fontWeight: 700,
//             fontSize: isNarrow ? 13 : 14,
//             cursor: "pointer",
//           }}
//           title="Go to Home"
//         >
//           Home
//         </button>
//       </div>
//       <div
//         style={{
//           background: "#fff",
//           border: "1px solid #E5E7EB",
//           borderRadius: 12,
//           padding: isNarrow ? 12 : 16,
//         }}
//       >
//         <form
//           onSubmit={onSubmit}
//           style={{ display: "grid", gap: isNarrow ? 8 : 10 }}
//         >
//           <input
//             value={username}
//             onChange={(e) => setU(e.target.value)}
//             placeholder="admin username"
//             required
//             style={{
//               padding: isNarrow ? 10 : 12,
//               borderRadius: 10,
//               border: "1px solid #D1D5DB",
//               fontWeight: 600,
//               fontSize: isNarrow ? 14 : 16,
//             }}
//           />
//           <input
//             value={password}
//             onChange={(e) => setP(e.target.value)}
//             placeholder="admin password"
//             type="password"
//             required
//             style={{
//               padding: isNarrow ? 10 : 12,
//               borderRadius: 10,
//               border: "1px solid #D1D5DB",
//               fontWeight: 600,
//               fontSize: isNarrow ? 14 : 16,
//             }}
//           />
//           <button
//             disabled={busy}
//             style={{
//               padding: isNarrow ? "10px 12px" : "12px 14px",
//               borderRadius: 10,
//               border: "1px solid #0D47A1",
//               background: "#0D47A1",
//               color: "#fff",
//               fontWeight: 800,
//               cursor: "pointer",
//               width: "100%",
//               minHeight: 40,
//               fontSize: "clamp(14px, 3.5vw, 16px)",
//             }}
//           >
//             {busy ? "Signing in..." : "Login"}
//           </button>
//           {err && (
//             <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>
//           )}
//         </form>
//       </div>
//     </div>
//   );
// }

// function AdminDashboard({ navigate }) {
//   const [tab, setTab] = useState("drivers"); // 'drivers' | 'routes' | 'live'
//   const [drivers, setDrivers] = useState([]);
//   const [assignments, setAssignments] = useState({}); // vehicleNo -> row
//   const [localAssign, setLocalAssign] = useState({}); // vehicleNo -> { routeCode, driverId, autopilotEnabled }
//   const [editing, setEditing] = useState({}); // vehicleNo -> true when row is in edit mode
//   const [newName, setNewName] = useState("");
//   const [newPhone, setNewPhone] = useState("");
//   const [err, setErr] = useState("");
//   const [busySave, setBusySave] = useState(null); // vehicleno while saving
//   const [liveBuses, setLiveBuses] = useState([]);
//   const adminToken = getAdminToken();
//   const isNarrow = useIsNarrow(480);

//   useEffect(() => {
//     if (!adminToken) navigate("/admin/login");
//   }, [adminToken, navigate]);

//   const authHeader = useMemo(
//     () => ({
//       headers: { Authorization: `Bearer ${adminToken}` },
//     }),
//     [adminToken]
//   );

//   async function loadDrivers() {
//     try {
//       const r = await axios.get(`${BACKEND}/api/admin/drivers`, authHeader);
//       setDrivers(r.data?.drivers || []);
//     } catch (e) {
//       console.warn("loadDrivers error", e?.message || e);
//     }
//   }

//   async function loadAssignments() {
//     try {
//       const r = await axios.get(`${BACKEND}/api/admin/assignments`, authHeader);
//       const rows = r.data?.rows || [];
//       const map = {};
//       for (const row of rows) {
//         map[row.vehicleNo] = row;
//       }
//       setAssignments(map);
//       // whenever we reload from backend, treat all as "saved" (not editing)
//       setEditing({});
//       setLocalAssign({});
//     } catch (e) {
//       console.warn("loadAssignments error", e?.message || e);
//     }
//   }

//   useEffect(() => {
//     if (!adminToken) return;
//     loadDrivers();
//     loadAssignments();
//   }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

//   // live tracking (simple polling)
//   useEffect(() => {
//     if (tab !== "live") return;
//     let cancelled = false;

//     async function loadLive() {
//       try {
//         const r = await axios.get(`${BACKEND}/api/vehicle-data`, {
//           timeout: 8000,
//         });
//         if (!cancelled) {
//           const arr = Array.isArray(r.data) ? r.data : [];
//           setLiveBuses(arr);
//         }
//       } catch (e) {
//         if (!cancelled) {
//           console.warn("admin live map error", e?.message || e);
//         }
//       }
//     }
//     loadLive();
//     const id = setInterval(loadLive, 5000);
//     return () => {
//       cancelled = true;
//       clearInterval(id);
//     };
//   }, [tab]);

//   const handleAddDriver = async (e) => {
//     e.preventDefault();
//     setErr("");
//     try {
//       const r = await axios.post(
//         `${BACKEND}/api/admin/drivers`,
//         { name: newName, phoneNumber: newPhone },
//         authHeader
//       );
//       if (r.data?.success && r.data?.driver) {
//         setDrivers((prev) => [...prev, r.data.driver]);
//         setNewName("");
//         setNewPhone("");
//       } else {
//         setErr("Failed to create driver");
//       }
//     } catch (e1) {
//       setErr(e1?.response?.data?.message || "Failed to create driver");
//     }
//   };

//   const handleRemoveDriver = async (id) => {
//     if (!window.confirm("Remove this driver?")) return;
//     try {
//       await axios.delete(`${BACKEND}/api/admin/drivers/${id}`, authHeader);
//       setDrivers((prev) => prev.filter((d) => d._id !== id));
//       // Note: assignments that still reference this driver will continue to exist until reassigned
//     } catch (e1) {
//       alert(e1?.response?.data?.message || "Failed to remove driver");
//     }
//   };

//   function getBaseAssignForVehicle(veh) {
//     const row = assignments[veh];
//     if (!row) {
//       return {
//         vehicleNo: veh,
//         routeCode: "",
//         driverId: "",
//         autopilotEnabled: false,
//       };
//     }
//     return {
//       vehicleNo: veh,
//       routeCode: row.routeCode || "",
//       driverId: row.driver?._id || "",
//       autopilotEnabled: !!row.autopilotEnabled,
//     };
//   }

//   function updateLocalAssign(veh, patch) {
//     setLocalAssign((prev) => ({
//       ...prev,
//       [veh]: { ...getBaseAssignForVehicle(veh), ...(prev[veh] || {}), ...patch },
//     }));
//   }

//   const handleSaveAssignment = async (veh) => {
//   const base = getBaseAssignForVehicle(veh);
//   const row = localAssign[veh] || base;
//   const hasAssignment = !!assignments[veh]; // vehicle currently has an active route?

//   // RESET error first
//   setErr("");

//   // ---- CASE 1: Admin cleared both fields and there *was* an assignment -> CLEAR MODE ----
//   if (!row.routeCode && !row.driverId && hasAssignment) {
//     setBusySave(veh);
//     try {
//       await axios.post(
//         `${BACKEND}/api/admin/vehicle-assign`,
//         {
//           vehicleNo: row.vehicleNo,
//           clear: true,              // 👈 tells backend: deactivate assignment
//         },
//         authHeader
//       );
//       await loadAssignments();      // refresh table
//       setEditing((prev) => ({ ...prev, [veh]: false }));
//     } catch (e1) {
//       setErr(e1?.response?.data?.message || "Failed to clear assignment");
//     } finally {
//       setBusySave(null);
//     }
//     return;
//   }

//   // ---- CASE 2: No assignment and still empty -> nothing to save ----
//   if (!row.routeCode || !row.driverId) {
//     setErr("Select both Route & Driver, or leave the row as-is.");
//     return;
//   }

//   // ---- CASE 3: Normal ASSIGN / UPDATE ----
//   setBusySave(veh);
//   try {
//     await axios.post(
//       `${BACKEND}/api/admin/vehicle-assign`,
//       {
//         vehicleNo: row.vehicleNo,
//         routeCode: row.routeCode,
//         driverId: row.driverId,
//         autopilotEnabled: !!row.autopilotEnabled,
//       },
//       authHeader
//     );
//     await loadAssignments();
//     setEditing((prev) => ({ ...prev, [veh]: false }));
//   } catch (e1) {
//     setErr(e1?.response?.data?.message || "Failed to save assignment");
//   } finally {
//     setBusySave(null);
//   }
// };


//   const onLogout = () => {
//     setAdminToken("");
//     navigate("/admin/login");
//   };

//   // simple driver lookup for live map
//   const driverByVehicle = useMemo(() => {
//     const out = {};
//     Object.values(assignments).forEach((row) => {
//       if (row.vehicleNo && row.driver) {
//         out[row.vehicleNo] = row.driver;
//       }
//     });
//     return out;
//   }, [assignments]);

//   return (
//     <div className="admin-layout">
//       {/* left sidebar */}
//       <div
//         className="sidebar"
//         style={{
//           width: isNarrow ? 220 : 260, // responsive width override
//         }}
//       >
//         {/* top section: logo, back button, nav */}
//         <div>
//           <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
//             <img
//               src={iitmLogo}
//               alt="IITM"
//               style={{ width: 32, height: 32, borderRadius: 8 }}
//             />
//             <div>
//               <div style={{ fontWeight: 800, fontSize: 16 }}>Admin</div>
//               <div style={{ fontSize: 12, opacity: 0.8 }}>Moving Mountains</div>
//             </div>
//           </div>
//           <button
//             onClick={() => navigate("/")}
//             style={{
//               marginTop: 8,
//               textAlign: "left",
//               padding: "6px 10px",
//               borderRadius: 10,
//               border: "1px solid #4b5563",
//               background: "transparent",
//               color: "#e5e7eb",
//               cursor: "pointer",
//               fontSize: 13,
//             }}
//           >
//             ← Back to map
//           </button>
//           <div
//             style={{
//               marginTop: 16,
//               display: "flex",
//               flexDirection: "column",
//               gap: 6,
//             }}
//           >
//             <button
//               onClick={() => setTab("drivers")}
//               style={{
//                 textAlign: "left",
//                 padding: "8px 10px",
//                 borderRadius: 10,
//                 border: "none",
//                 background: tab === "drivers" ? "#f9fafb" : "transparent",
//                 color: tab === "drivers" ? "#111827" : "#e5e7eb",
//                 fontWeight: 600,
//                 cursor: "pointer",
//               }}
//             >
//               + Add Driver
//             </button>
//             <button
//               onClick={() => setTab("routes")}
//               style={{
//                 textAlign: "left",
//                 padding: "8px 10px",
//                 borderRadius: 10,
//                 border: "none",
//                 background: tab === "routes" ? "#f9fafb" : "transparent",
//                 color: tab === "routes" ? "#111827" : "#e5e7eb",
//                 fontWeight: 600,
//                 cursor: "pointer",
//               }}
//             >
//               Route Allocation
//             </button>
//             <button
//               onClick={() => setTab("live")}
//               style={{
//                 textAlign: "left",
//                 padding: "8px 10px",
//                 borderRadius: 10,
//                 border: "none",
//                 background: tab === "live" ? "#f9fafb" : "transparent",
//                 color: tab === "live" ? "#111827" : "#e5e7eb",
//                 fontWeight: 600,
//                 cursor: "pointer",
//               }}
//             >
//               Live Tracking
//             </button>
//           </div>
//         </div>

//         {/* bottom logout – pinned to sidebar bottom */}
//         <button
//           onClick={onLogout}
//           style={{
//             marginTop: 12,
//             padding: "8px 10px",
//             borderRadius: 10,
//             border: "1px solid #6b7280",
//             background: "transparent",
//             color: "#e5e7eb",
//             cursor: "pointer",
//             fontSize: 13,
//           }}
//         >
//           Logout
//         </button>
//       </div>

//       {/* main content */}
//       <div className="content">
//         {tab === "drivers" && (
//           <div style={{ maxWidth: 800, margin: "0 auto" }}>
//             <h2 style={{ marginTop: 0 }}>Drivers</h2>
//             <form
//               onSubmit={handleAddDriver}
//               style={{
//                 display: "grid",
//                 gridTemplateColumns: "2fr 2fr auto",
//                 gap: 8,
//                 alignItems: "center",
//                 marginBottom: 16,
//               }}
//             >
//               <input
//                 value={newName}
//                 onChange={(e) => setNewName(e.target.value)}
//                 placeholder="Driver name"
//                 required
//                 style={{
//                   padding: 10,
//                   borderRadius: 10,
//                   border: "1px solid #D1D5DB",
//                   fontSize: 14,
//                 }}
//               />
//               <input
//                 value={newPhone}
//                 onChange={(e) => setNewPhone(e.target.value)}
//                 placeholder="Phone number (unique)"
//                 required
//                 style={{
//                   padding: 10,
//                   borderRadius: 10,
//                   border: "1px solid #D1D5DB",
//                   fontSize: 14,
//                 }}
//               />
//               <button
//                 type="submit"
//                 style={{
//                   padding: "10px 16px",
//                   borderRadius: 10,
//                   border: "1px solid #0D47A1",
//                   background: "#0D47A1",
//                   color: "#fff",
//                   fontWeight: 700,
//                   cursor: "pointer",
//                   fontSize: 14,
//                 }}
//               >
//                 Add
//               </button>
//             </form>
//             {err && (
//               <div
//                 style={{
//                   color: "#b00020",
//                   fontWeight: 600,
//                   marginBottom: 8,
//                 }}
//               >
//                 {err}
//               </div>
//             )}
//             <div
//               style={{
//                 display: "grid",
//                 gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
//                 gap: 12,
//               }}
//             >
//               {drivers.map((d) => (
//                 <div
//                   key={d._id}
//                   style={{
//                     background: "#fff",
//                     borderRadius: 12,
//                     border: "1px solid #e5e7eb",
//                     padding: 12,
//                     display: "flex",
//                     flexDirection: "column",
//                     gap: 4,
//                   }}
//                 >
//                   <div style={{ fontWeight: 700, fontSize: 15 }}>{d.name}</div>
//                   <div style={{ fontSize: 13, opacity: 0.8 }}>
//                     Phone: {d.phoneNumber || "—"}
//                   </div>
//                   <div style={{ fontSize: 12, opacity: 0.6 }}>
//                     username: <code>{d.username}</code>
//                   </div>
//                   <button
//                     onClick={() => handleRemoveDriver(d._id)}
//                     style={{
//                       marginTop: 8,
//                       alignSelf: "flex-start",
//                       padding: "6px 10px",
//                       borderRadius: 8,
//                       border: "1px solid #f97373",
//                       background: "#fef2f2",
//                       color: "#b91c1c",
//                       fontSize: 12,
//                       fontWeight: 600,
//                       cursor: "pointer",
//                     }}
//                   >
//                     Remove
//                   </button>
//                 </div>
//               ))}
//               {drivers.length === 0 && (
//                 <div style={{ fontSize: 14, opacity: 0.7 }}>
//                   No drivers yet. Add your first driver above.
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         {tab === "routes" && (
//           <div style={{ maxWidth: 1100, margin: "0 auto" }}>
//             <h2 style={{ marginTop: 0 }}>Route Allocation</h2>
//             {err && (
//               <div
//                 style={{
//                   color: "#b00020",
//                   fontWeight: 600,
//                   marginBottom: 8,
//                 }}
//               >
//                 {err}
//               </div>
//             )}
//             <div style={{ overflowX: "auto" }}>
//               <table
//                 style={{
//                   width: "100%",
//                   borderCollapse: "collapse",
//                   background: "#fff",
//                   borderRadius: 12,
//                   overflow: "hidden",
//                 }}
//               >
//                 <thead>
//                   <tr
//                     style={{
//                       background: "#f9fafb",
//                       borderBottom: "1px solid #e5e7eb",
//                     }}
//                   >
//                     <th style={{ padding: 8, textAlign: "left" }}>#</th>
//                     <th style={{ padding: 8, textAlign: "left" }}>Vehicle</th>
//                     <th style={{ padding: 8, textAlign: "left" }}>Route</th>
//                     <th style={{ padding: 8, textAlign: "left" }}>Driver</th>
//                     <th style={{ padding: 8, textAlign: "left" }}>Auto-pilot</th>
//                     <th style={{ padding: 8, textAlign: "left" }} />
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {VEHICLES.map((veh, idx) => {
//                     const base = getBaseAssignForVehicle(veh);
//                     const local = localAssign[veh] || base;
//                     const hasAssignment =
//                       !!base.routeCode && !!base.driverId; // existing saved row?
//                     const isEditing = !!editing[veh];
//                     const locked = hasAssignment && !isEditing;
//                     const saving = busySave === veh;

//                     return (
//                       <tr
//                         key={veh}
//                         style={{ borderBottom: "1px solid #f3f4f6" }}
//                       >
//                         <td style={{ padding: 8 }}>{idx + 1}</td>
//                         <td style={{ padding: 8, fontWeight: 600 }}>{veh}</td>
//                         <td style={{ padding: 8 }}>
//                           <select
//                             value={local.routeCode}
//                             onChange={(e) =>
//                               updateLocalAssign(veh, {
//                                 routeCode: e.target.value,
//                               })
//                             }
//                             disabled={locked || saving}
//                             style={{
//                               padding: 8,
//                               borderRadius: 8,
//                               border: "1px solid #d1d5db",
//                               fontSize: 13,
//                               minWidth: 180,
//                               backgroundColor: locked ? "#f9fafb" : "white",
//                             }}
//                           >
//                             <option value="">Select route…</option>
//                             {ADMIN_ROUTES.map((r) => (
//                               <option key={r.code} value={r.code}>
//                                 {r.label}
//                               </option>
//                             ))}
//                           </select>
//                         </td>
//                         <td style={{ padding: 8 }}>
//                           <select
//                             value={local.driverId}
//                             onChange={(e) =>
//                               updateLocalAssign(veh, {
//                                 driverId: e.target.value,
//                               })
//                             }
//                             disabled={locked || saving}
//                             style={{
//                               padding: 8,
//                               borderRadius: 8,
//                               border: "1px solid #d1d5db",
//                               fontSize: 13,
//                               minWidth: 180,
//                               backgroundColor: locked ? "#f9fafb" : "white",
//                             }}
//                           >
//                             <option value="">Select driver…</option>
//                             {drivers.map((d) => (
//                               <option key={d._id} value={d._id}>
//                                 {d.name} ({d.phoneNumber || d.username})
//                               </option>
//                             ))}
//                           </select>
//                         </td>
//                         <td style={{ padding: 8 }}>
//                           <label
//                             style={{
//                               display: "inline-flex",
//                               alignItems: "center",
//                               gap: 6,
//                               fontSize: 13,
//                               opacity: locked ? 0.6 : 1,
//                             }}
//                           >
//                             <input
//                               type="checkbox"
//                               checked={!!local.autopilotEnabled}
//                               onChange={(e) =>
//                                 updateLocalAssign(veh, {
//                                   autopilotEnabled: e.target.checked,
//                                 })
//                               }
//                               disabled={locked || saving}
//                             />
//                             On
//                           </label>
//                         </td>
//                         <td style={{ padding: 8 }}>
//                           <button
//                             onClick={() => {
//                               if (locked) {
//                                 // switch to edit mode
//                                 updateLocalAssign(veh, {}); // ensure local copy
//                                 setEditing((prev) => ({
//                                   ...prev,
//                                   [veh]: true,
//                                 }));
//                               } else {
//                                 // save current data
//                                 handleSaveAssignment(veh);
//                               }
//                             }}
//                             disabled={saving}
//                             style={{
//                               padding: "6px 12px",
//                               borderRadius: 8,
//                               border: "1px solid #0D47A1",
//                               background: saving
//                                 ? "#e5e7eb"
//                                 : locked
//                                 ? "#0D47A1"
//                                 : "#0D47A1",
//                               color: saving ? "#4b5563" : "#fff",
//                               fontSize: 13,
//                               fontWeight: 700,
//                               cursor: saving ? "not-allowed" : "pointer",
//                             }}
//                           >
//                             {saving
//                               ? "Saving…"
//                               : locked
//                               ? "Edit"
//                               : "Save"}
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         )}

//         {tab === "live" && (
//           <div style={{ height: "calc(100vh - 40px)" }}>
//             <h2 style={{ marginTop: 0 }}>Live Tracking</h2>
//             <div style={{ height: "calc(100% - 36px)" }}>
//               <MapContainer
//                 center={center}
//                 zoom={DEFAULT_ZOOM}
//                 maxZoom={22}
//                 style={{ width: "100%", height: "100%" }}
//               >
//                 <TileLayer
//                   attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
//                   url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//                   maxNativeZoom={19}
//                   maxZoom={22}
//                 />
//                 {liveBuses.map((bus) => {
//                   // const label = vehicleLabel(bus.vehicleno);
//                   const rc = assignments[bus.vehicleno]?.routeCode;   // admin assignments map is row objects
//                   const label = vehicleLabel(bus.vehicleno, rc);
//                   const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
//                   const drv = driverByVehicle[bus.vehicleno];
//                   return (
//                     <Marker
//                       key={bus.vehicleno}
//                       position={{
//                         lat: bus.latitude,
//                         lng: bus.longitude,
//                       }}
//                       // icon={createVehicleDivIcon(
//                       //   label,
//                       //   Number(bus.heading) || 0
//                       // )}
//                       icon={createVehicleDivIcon(label, Number(bus.heading) || 0, palette)}
//                     >
//                       <Popup>
//                         <div className="info-window">
//                           <h4>
//                             {bus.vehicleno} ({label})
//                           </h4>
//                           <p>
//                             <strong>Speed:</strong>{" "}
//                             {Number.isFinite(bus.speed)
//                               ? bus.speed.toFixed(1)
//                               : "N/A"}{" "}
//                             km/h
//                           </p>
//                           {drv && (
//                             <>
//                               <p>
//                                 <strong>Driver:</strong> {drv.name}</p>
//                               <p>
//                                 <strong>Phone:</strong>{" "}
//                                 {drv.phoneNumber || "—"}
//                               </p>
//                             </>
//                           )}
//                           {!drv && (
//                             <p style={{ fontSize: 13, opacity: 0.7 }}>
//                               No driver assigned
//                             </p>
//                           )}
//                         </div>
//                       </Popup>
//                     </Marker>
//                   );
//                 })}
//               </MapContainer>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// function RecenterMap({ center, zoom }) {
//   const map = useMap();

//   useEffect(() => {
//     if (!center) return;
//     map.setView([center.lat, center.lng], zoom ?? map.getZoom(), { animate: true });
//   }, [center, zoom, map]);

//   return null;
// }

// function CampusMapPicker({ enabled, point, onPick }) {
//   const map = useMapEvents({
//     click(event) {
//       if (!enabled) return;
//       onPick?.({ lat: event.latlng.lat, lng: event.latlng.lng });
//     },
//   });

//   useEffect(() => {
//     const container = map.getContainer();
//     container.classList.toggle('is-picking-campus-place', Boolean(enabled));
//     return () => container.classList.remove('is-picking-campus-place');
//   }, [enabled, map]);

//   if (!point) return null;
//   const icon = L.divIcon({
//     className: 'campus-picked-point-wrap',
//     html: '<span class="campus-picked-point"><span></span></span>',
//     iconSize: [34, 42],
//     iconAnchor: [17, 39],
//   });

//   return (
//     <Marker position={[point.lat, point.lng]} icon={icon} zIndexOffset={1800}>
//       <Popup>Proposed public entrance</Popup>
//     </Marker>
//   );
// }



// /* ---------------------------- MAP PAGE ---------------------------- */
// function MapPage() {
//   const [buses, setBuses] = useState([]);
//   const [userLocation, setUserLocation] = useState(null);
//   const [socketStatus, setSocketStatus] = useState('disconnected');
//   const [roads, setRoads] = useState(null);
//   const [filterType, setFilterType] = useState('all');
//   const [mapCenter, setMapCenter] = useState(center);
//   const [journeyPlan, setJourneyPlan] = useState(null);
//   const [campusMapPicking, setCampusMapPicking] = useState(false);
//   const [campusPickedPoint, setCampusPickedPoint] = useState(null);
//   const [locError, setLocError] = useState("");
//   const [geoState, setGeoState] = useState("unknown"); // "granted" | "prompt" | "denied" | "unknown"
//   const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(true);
//   const headerRef = useRef(null);
//   const [headerHeight, setHeaderHeight] = useState(0);

//   // maintenance banner state
//   const [maintenance, setMaintenance] = useState(false);
//   const [maintenanceReason, setMaintenanceReason] = useState('');
//   const lastUpdateRef = useRef(0);
//     // ---- Backend ETA (JWT) state ----
//   const [stopEtas, setStopEtas] = useState({});      // { [stopId]: { data, error } }

//   const flatRoadLinesRef = useRef([]);
//   const lastSmoothedRef = useRef(new Map());

//   // ---- Schedule Modal state ----
//   const [showSchedule, setShowSchedule] = useState(false);
//   const [activeTab, setActiveTab] = useState(0);

//   // --- responsive helpers ---
//   const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 480);
//   const clamp = (min, pref, max) => `clamp(${min}, ${pref}, ${max})`;
//   const pad = isNarrow ? 12 : 16;
//   const gap = isNarrow ? 8  : 12;
//   const chipFS = isNarrow ? 12 : 13;

//   useEffect(() => {
//     const onResize = () => setIsNarrow(window.innerWidth <= 480);
//     window.addEventListener('resize', onResize);
//     return () => window.removeEventListener('resize', onResize);
//   }, []);

//     // ---- Poll all stop ETAs using one public read-only batch request ----
//   useEffect(() => {
//     if (!BACKEND) return undefined;
//     let cancelled = false;

//     async function fetchAllStopEtas() {
//       try {
//         const response = await axios.get(`${BACKEND}/api/eta/stops`, { timeout: 10000 });
//         if (cancelled) return;
//         const stops = response.data?.stops || {};
//         const next = {};
//         for (const stop of busStops) {
//           const payload = stops[String(stop.id)] || stops[stop.id] || null;
//           next[stop.id] = { data: payload, error: !payload };
//         }
//         setStopEtas(next);
//       } catch (err) {
//         if (!cancelled) {
//           console.warn('ETA batch fetch failed:', err?.response?.status || '', err?.message || err);
//         }
//       }
//     }

//     fetchAllStopEtas();
//     const id = window.setInterval(fetchAllStopEtas, 5000);
//     return () => {
//       cancelled = true;
//       window.clearInterval(id);
//     };
//   }, []);


//   // ---- stable socket + pause flag ----
//   const socketRef = useRef(null);
//   const ignoreUpdatesRef = useRef(false);

//   // ---- assignments (vehicle -> routeCode) ----
//   const [assignments, setAssignments] = useState({});
//   async function refreshAssignments() {
//     try {
//       const r = await axios.get(`${BACKEND}/api/vehicle-route/active`);
//       const map = {};
//       for (const row of r.data?.rows || []) map[row.vehicleNo] = row.routeCode;
//       setAssignments(map);
//     } catch {}
//   }
//   useEffect(() => { refreshAssignments(); const t = setInterval(refreshAssignments, 60000); return () => clearInterval(t); }, []);

//   // Load roads
//   useEffect(() => {
//     let cancelled = false;
//     (async () => {
//       try {
//         const r = await fetch(ROADS_URL);
//         if (!cancelled && r.ok) setRoads(await r.json());
//       } catch (e) {
//         if (!cancelled) console.warn('Failed to load roads:', e?.message || e);
//       }
//     })();
//     return () => { cancelled = true; };
//   }, []);

//   // Flatten roads to LineStrings
//   useEffect(() => {
//     if (!roads) return;
//     const lines = [];
//     turf.flattenEach(roads, (feat) => {
//       if (feat.geometry?.type === 'LineString') lines.push(feat);
//     });
//     flatRoadLinesRef.current = lines;
//   }, [roads]);

//   // // Geolocation
//   // useEffect(() => {
//   //   if ('geolocation' in navigator) {
//   //     navigator.geolocation.getCurrentPosition(
//   //       (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
//   //       (err) => console.warn('Geolocation error:', err?.message),
//   //       { enableHighAccuracy: true, timeout: 5000 }
//   //     );
//   //   }
//   // }, []);

//   useEffect(() => {
//   let perm;
//   (async () => {
//     try {
//       if (!navigator.permissions?.query) return;
//       perm = await navigator.permissions.query({ name: "geolocation" });
//       setGeoState(perm.state);
//       perm.onchange = () => setGeoState(perm.state);
//     } catch {
//       // ignore
//     }
//   })();
//   return () => {
//     if (perm) perm.onchange = null;
//   };
// }, []);


// //   const requestUserLocation = useCallback(() => {
// //   setLocError("");

// //   if (!("geolocation" in navigator)) {
// //     setLocError("Geolocation not supported on this device/browser.");
// //     return;
// //   }

// //   navigator.geolocation.getCurrentPosition(
// //     (pos) => {
// //       const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
// //       setUserLocation(loc);
// //       setMapCenter(loc); // ✅ recenter map here
// //       setLocError("");
// //     },
// //     (err) => {
// //       console.warn("Geolocation error:", err?.message || err);
// //       // If user didn't allow, browser will show prompt only if not blocked.
// //       setLocError("Please allow location to center the map near you.");
// //     },
// //     { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
// //   );
// // }, []);

// const requestUserLocation = useCallback(() => {
//   setLocError("");

//   if (!("geolocation" in navigator)) {
//     setLocError("Geolocation not supported on this device/browser.");
//     return;
//   }

//   navigator.geolocation.getCurrentPosition(
//     (pos) => {
//       const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
//       setUserLocation(loc);
//       setMapCenter(loc);
//       setLocError("");
//     },
//     (err) => {
//       // err.code === 1 => PERMISSION_DENIED
//       if (err?.code === 1) {
//         // If browser permission is truly "denied", it won't show popup again.
//         setLocError(
//           "Location is blocked in browser settings. Click the 🔒 icon near the URL → Site settings → Location → Allow (or Ask), then refresh."
//         );
//       } else {
//         setLocError(err?.message || "Unable to fetch location.");
//       }
//     },
//     { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
//   );
// }, []);


// useEffect(() => {
//   // ✅ On every page open (and after browser refresh), request location and recenter
//   requestUserLocation();
// }, [requestUserLocation]);


//   // Initial fetch
//   useEffect(() => {
//     if (!BACKEND) {
//       setMaintenance(true);
//       setMaintenanceReason('Server URL not configured');
//       return;
//     }
//     axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 })
//       .then((res) => {
//         const arr = Array.isArray(res.data) ? res.data : [];
//         const byVeh = new Map();
//         for (const d of arr) {
//           const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
//           byVeh.set(d.vehicleno, {
//             vehicleno: d.vehicleno,
//             latitude: d.latitude,
//             longitude: d.longitude,
//             speed: d.speed,
//             heading
//           });
//         }
//         const list = [...byVeh.values()];
//         setBuses(processVehicles(list));
//         lastUpdateRef.current = Date.now();
//       })
//       .catch((err) => {
//         console.error('Initial fetch error:', err?.message || err);
//         setMaintenance(true);
//         setMaintenanceReason('Unable to fetch live data');
//       });
//   }, []);

//   // ---- WebSocket packet handler ----
//   const onPacket = (data) => {
//     if (ignoreUpdatesRef.current) return;
//     const incoming = {
//       vehicleno: data.vehicleno,
//       latitude: data.latitude,
//       longitude: data.longitude,
//       speed: data.speed,
//       heading: Number.isFinite(data?.heading) ? toHeading(data.heading) : toHeading(data?.direction)
//     };

//     setBuses((prev) => {
//       const nextByVeh = new Map(prev.map((b) => [b.vehicleno, b]));
//       const processed = processVehicles([incoming], nextByVeh);
//       const merged = new Map(prev.map((b) => [b.vehicleno, b]));
//       for (const p of processed) merged.set(p.vehicleno, p);
//       return [...merged.values()];
//     });

//     lastUpdateRef.current = Date.now();
//   };

//   // Live updates (Socket.IO)
//   useEffect(() => {
//     if (!BACKEND) return;

//     setSocketStatus('connecting');
//     const socket = io(BACKEND, {
//       transports: ['polling', 'websocket'],
//       reconnection: true,
//       reconnectionAttempts: Infinity,
//       reconnectionDelay: 1000,
//       timeout: 10000,
//       withCredentials: false,
//       upgrade: true
//     });
//     socketRef.current = socket;

//     socket.on('connect', () => setSocketStatus('connected'));
//     socket.on('disconnect', () => setSocketStatus('disconnected'));
//     socket.on('connect_error', (e) => {
//       console.warn('Socket connect_error:', e?.message || e);
//       setSocketStatus('disconnected');
//     });

//     socket.on('new-bus-data', onPacket);

//     return () => {
//       socket.off('new-bus-data', onPacket);
//       socket.disconnect();
//       socketRef.current = null;
//     };
//   }, [roads]); // re-connect only if roads change

//   // Staleness / maintenance detector
//   useEffect(() => {
//     const t = setInterval(() => {
//       const sinceMs = Date.now() - lastUpdateRef.current;
//       const stale = sinceMs > STALE_MS;
//       const downAndEmpty = socketStatus === 'disconnected' && buses.length === 0;

//       if (stale || downAndEmpty) {
//         const mins = Math.floor(sinceMs / 60000);
//         const secs = Math.floor((sinceMs % 60000) / 1000);
//         setMaintenance(true);
//         setMaintenanceReason(
//           stale ? `No new data for ${mins}m ${secs}s` : 'Live connection unavailable'
//         );
//       } else {
//         setMaintenance(false);
//         setMaintenanceReason('');
//       }
//     }, 5000);
//     return () => clearInterval(t);
//   }, [socketStatus, buses.length]);

//   // ---- Processing pipeline: smooth then snap ----
//   function snapToRoad(coord, flatLines) {
//     if (!flatLines || flatLines.length === 0) return null;
//     const pt = turf.point([coord.lng, coord.lat]);

//     let best = null;
//     let bestDist = Infinity;

//     for (const line of flatLines) {
//       const np = turf.nearestPointOnLine(line, pt, { units: 'meters' });
//       const dist = np?.properties?.dist;
//       if (Number.isFinite(dist) && dist < bestDist) {
//         bestDist = dist;
//         const [lng, lat] = np.geometry.coordinates;
//         best = { lat, lng, distM: dist };
//       }
//     }
//     return best;
//   }

//   function processVehicles(list, prevMapOpt) {
//     const flatLines = flatRoadLinesRef.current;
//     const last = lastSmoothedRef.current;
//     const result = [];

//     for (const d of list) {
//       const id = d.vehicleno;
//       const raw = { lat: d.latitude, lng: d.longitude };

//       const prev = last.get(id);
//       const smoothedCoord = smoothCoord(prev?.coord, raw, SMOOTH_ALPHA);
//       const smoothedHeading = smoothHeading(prev?.heading, d.heading, HEADING_ALPHA);

//       const snapped = snapToRoad(smoothedCoord, flatLines);

//       const use = snapped?.distM != null && snapped.distM <= SNAP_MAX_DIST_M
//         ? { lat: snapped.lat, lng: snapped.lng }
//         : smoothedCoord;

//       last.set(id, { coord: use, heading: smoothedHeading });

//       result.push({
//         vehicleno: d.vehicleno,
//         latitude: use.lat,
//         longitude: use.lng,
//         speed: d.speed,
//         heading: smoothedHeading
//       });
//     }

//     if (prevMapOpt) {
//       for (const [id, b] of prevMapOpt) {
//         if (list.find(x => x.vehicleno === id)) continue;
//         const prev = last.get(id);
//         if (!prev) {
//           result.push(b);
//           continue;
//         }
//         result.push({
//           vehicleno: id,
//           latitude: prev.coord.lat,
//           longitude: prev.coord.lng,
//           speed: b.speed,
//           heading: prev.heading
//         });
//       }
//     }

//     return result;
//   }

//     // ---------------- Route geometry + ETA helpers ----------------
//   const stopById = useMemo(() => Object.fromEntries(busStops.map(s => [s.id, s])), []);
//   function routePolyline(ids) {
//     return ids.map(id => ({ lat: stopById[id].lat, lng: stopById[id].lng }));
//   }
//   function cumDist(poly) {
//     const out = [0];
//     for (let i = 1; i < poly.length; i++) out.push(out[i - 1] + mBetween(poly[i - 1], poly[i]));
//     return out;
//   }
//   const routeGeom = useMemo(() => {
//     const g = {};
//     for (const r of ROUTES) {
//       const poly = routePolyline(r.ids);
//       g[r.code] = { poly, cum: cumDist(poly), ids: r.ids };
//     }
//     return g;
//   }, [stopById]);

//   function projectOnRoute(pt, poly) {
//     if (!poly || poly.length < 2) return null;
//     let best = null;
//     for (let i = 0; i < poly.length - 1; i++) {
//       const seg = turf.lineString([[poly[i].lng, poly[i].lat], [poly[i + 1].lng, poly[i + 1].lat]]);
//       const p = turf.nearestPointOnLine(seg, turf.point([pt.lng, pt.lat]), { units: "meters" });
//       const d = p.properties?.dist ?? Infinity;
//       if (!best || d < best.dist) {
//         const [lng, lat] = p.geometry.coordinates;
//         best = { idx: i, point: { lat, lng }, dist: d };
//       }
//     }
//     return best;
//   }

//   function distanceAlong(poly, cum, proj, targetIndex) {
//     const dHead = mBetween(proj.point, poly[proj.idx + 1]);
//     const dTail = cum[targetIndex] - cum[proj.idx + 1];
//     return dHead + Math.max(0, dTail);
//   }

//   // // ---- ETA constants: match backend ----
//   // const AVG_SPEED_KMPH = 20;
//   // const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;
//   // const WAIT_RADIUS_M = 25;         // within 25m of first stop
//   // const WAIT_SPEED_KMPH_MAX = 1.0;  // consider "waiting" if <= 1 km/h

//   // // Constant-speed ETA (20 km/h), ignoring live speed
//   // function etaFromMeters(distanceM) {
//   //   const seconds = distanceM / AVG_SPEED_MPS;
//   //   const minutes = Math.round(seconds / 60);
//   //   return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
//   // }

//   // function nextStopIndex(routeIds, projIdx) {
//   //   return Math.min(routeIds.length - 1, projIdx + 1);
//   // }

//   // // For a given bus, find nearest upcoming stop on its route + ETA
//   // function nearestStopEtaForBusOnItsRoute(bus) {
//   //   const routeCode = assignments[bus.vehicleno];
//   //   if (!routeCode) return null;
//   //   const geom = routeGeom[routeCode];
//   //   if (!geom || geom.poly.length < 2) return null;

//   //   const pt = { lat: bus.latitude, lng: bus.longitude };
//   //   const proj = projectOnRoute(pt, geom.poly);
//   //   if (!proj) return null;

//   //   const firstStopId = geom.ids[0];
//   //   const firstStop = stopById[firstStopId];
//   //   let waitingAtFirstStop = false;
//   //   if (firstStop) {
//   //     const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
//   //     const speed = Number(bus.speed);
//   //     if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
//   //       waitingAtFirstStop = true;
//   //     }
//   //   }

//   //   const nextIdx = nextStopIndex(geom.ids, proj.idx);
//   //   const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

//   //   let etaMin = undefined;
//   //   if (!waitingAtFirstStop) {
//   //     etaMin = etaFromMeters(distanceM);
//   //   }

//   //   return {
//   //     stopId: geom.ids[nextIdx],
//   //     distanceM,
//   //     etaMin,
//   //     routeCode,
//   //     waitingAtFirstStop,
//   //   };
//   // }

//     // ---- ETA constants: match backend ----
//   const AVG_SPEED_KMPH = 20;
//   const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;

//   // holding detection at ANY stop
//   const HOLD_STOP_RADIUS_M = 30;
//   const HOLD_SPEED_KMPH_MAX = 1.5;

//   // Constant-speed ETA (20 km/h), ignoring live speed
//   function etaFromMeters(distanceM) {
//     const seconds = distanceM / AVG_SPEED_MPS;
//     const minutes = Math.round(seconds / 60);
//     return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
//   }

//   function nextStopIndex(routeIds, projIdx) {
//     return Math.min(routeIds.length - 1, projIdx + 1);
//   }

//   function detectHoldingStopOnRoute(bus, geom) {
//     const pt = { lat: bus.latitude, lng: bus.longitude };
//     const speed = Number(bus.speed);

//     let bestIdx = -1;
//     let bestDist = Infinity;

//     for (let i = 0; i < geom.ids.length; i++) {
//       const s = stopById[geom.ids[i]];
//       if (!s) continue;

//       const dist = mBetween(pt, { lat: s.lat, lng: s.lng });
//       if (dist < bestDist) {
//         bestDist = dist;
//         bestIdx = i;
//       }
//     }

//     if (bestIdx < 0) {
//       return {
//         isHolding: false,
//         stopId: null,
//         stopIndex: -1,
//         distToStop: Infinity
//       };
//     }

//     const slowOrUnknown =
//       !Number.isFinite(speed) || speed <= HOLD_SPEED_KMPH_MAX;

//     const isHolding =
//       bestDist <= HOLD_STOP_RADIUS_M && slowOrUnknown;

//     return {
//       isHolding,
//       stopId: geom.ids[bestIdx],
//       stopIndex: bestIdx,
//       distToStop: bestDist
//     };
//   }

//   // For a given bus, find nearest stop ETA on its route
//   function nearestStopEtaForBusOnItsRoute(bus) {
//     const routeCode = assignments[bus.vehicleno];
//     if (!routeCode) return null;

//     const geom = routeGeom[routeCode];
//     if (!geom || geom.poly.length < 2) return null;

//     const pt = { lat: bus.latitude, lng: bus.longitude };
//     const proj = projectOnRoute(pt, geom.poly);
//     if (!proj) return null;

//     const holdInfo = detectHoldingStopOnRoute(bus, geom);

//     // If bus is standing at a stop, show that stop as current stop with ETA 0
//     if (holdInfo.isHolding) {
//       return {
//         stopId: holdInfo.stopId,
//         distanceM: 0,
//         etaMin: 0,
//         routeCode,
//         waitingAtStop: true
//       };
//     }

//     const nextIdx = nextStopIndex(geom.ids, proj.idx);
//     const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

//     return {
//       stopId: geom.ids[nextIdx],
//       distanceM,
//       etaMin: etaFromMeters(distanceM),
//       routeCode,
//       waitingAtStop: false
//     };
//   }

//   // For a given stop, find the best incoming vehicle + ETA
//   // function nextArrivalAtStop(stopId) {
//   //   const candidates = [];

//   //   for (const bus of buses) {
//   //     if (startsWithDigit(bus.vehicleno)) continue;
//   //     const rc = assignments[bus.vehicleno];
//   //     if (!rc) continue;
//   //     const geom = routeGeom[rc];
//   //     if (!geom) continue;

//   //     const indexInRoute = geom.ids.indexOf(stopId);
//   //     if (indexInRoute < 0) continue;

//   //     const pt = { lat: bus.latitude, lng: bus.longitude };
//   //     const proj = projectOnRoute(pt, geom.poly);
//   //     if (!proj) continue;

//   //     const targetIndex = Math.max(indexInRoute, proj.idx + 1);
//   //     if (targetIndex >= geom.ids.length) continue;

//   //     const distanceM = distanceAlong(geom.poly, geom.cum, proj, targetIndex);

//   //     // If bus is already past this stop, skip
//   //     if (distanceM < 0) continue;

//   //     const firstStopId = geom.ids[0];
//   //     const firstStop = stopById[firstStopId];
//   //     let waitingAtFirstStop = false;
//   //     if (firstStop) {
//   //       const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
//   //       const speed = Number(bus.speed);
//   //       if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
//   //         waitingAtFirstStop = true;
//   //       }
//   //     }

//   //     let etaMin = undefined;
//   //     if (!waitingAtFirstStop) {
//   //       etaMin = etaFromMeters(distanceM);
//   //     }

//   //     candidates.push({
//   //       vehicle: bus.vehicleno,
//   //       routeCode: rc,
//   //       etaMin,
//   //       waitingAtFirstStop,
//   //       distanceM,
//   //     });
//   //   }

//   //   if (!candidates.length) return null;

//   //   // Prefer moving vehicles (etaMin != null), then fallback to closest waiting one
//   //   const moving = candidates.filter(c => c.etaMin != null);
//   //   if (moving.length) {
//   //     return moving.reduce((a, b) => (a.etaMin <= b.etaMin ? a : b));
//   //   }

//   //   // All are waiting at first stop: pick closest along-route
//   //   return candidates.reduce((a, b) => (a.distanceM <= b.distanceM ? a : b));
//   // }


//   // ---- Derived lists ----
//   const visibleBuses = buses.filter(b => !startsWithDigit(b.vehicleno));
//   const filteredBuses = visibleBuses.filter(b => matchesFilter(b.vehicleno, filterType, assignments));

//   // -------------- SCHEDULE UI helpers --------------
//   const TABS = [
//     { key: 'hostel-main', title: 'Hostel → Main Gate', data: HOSTEL_TO_MAIN, notes: HOSTEL_NOTES, fmt: 'ampm' },
//     { key: 'main-hostel', title: 'Main Gate → Hostel', data: MAIN_TO_HOSTEL, notes: MAIN_NOTES, fmt: 'ampm' },
//     { key: 'vg-main',     title: 'Velachery Gate → Main Gate', data: VELACHERY_TO_MAIN, notes: VELACHERY_NOTES, fmt: 'ampm' },
//     { key: 'vg-hostel',   title: 'Velachery Gate → Hostel', data: VELACHERY_TO_HOSTEL, notes: VELACHERY_NOTES, fmt: 'ampm' },
//     { key: 'rp-ed',       title: 'Research Park → ED', data: RP_TO_ED_TIMES, notes: VELACHERY_NOTES, fmt: '24' },
//     { key: 'ed-rp',       title: 'ED → Research Park', data: ED_TO_RP_TIMES, notes: VELACHERY_NOTES, fmt: '24' }
//   ];

//   function TimeChip({ t }) {
//     return (
//       <div
//         style={{
//           padding: '10px 12px',
//           borderRadius: 12,
//           border: '1px solid #e2e8f0',
//           background: '#fff',
//           textAlign: 'center',
//           fontWeight: 700,
//           fontSize: clamp('12px','2.8vw','14px')
//         }}
//       >
//         {t}
//       </div>
//     );
//   }

//   function CartTable({ rows, title }) {
//     const th = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', fontWeight: 700 };
//     const td = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #f2f2f2' };

//     return (
//       <div style={{ marginTop: 8 }}>
//         <div style={{ fontWeight: 700, margin: '8px 0' }}>{title}</div>
//         <div style={{ overflowX: 'auto' }}>
//           <table style={{ width: '100%', borderCollapse: 'collapse' }}>
//             <thead>
//               <tr>
//                 <th style={th}>Cart</th>
//                 <th style={th}>RP → ED</th>
//                 <th style={th}>ED → RP</th>
//               </tr>
//             </thead>
//             <tbody>
//               {rows.map(([Cart, a, b], i) => (
//                 <tr key={`${Cart}-${i}`}>
//                   <td style={td}>{Cart}</td>
//                   <td style={td}>{a}</td>
//                   <td style={td}>{b}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     );
//   }

//   function ScheduleBody({ tab }) {
//     if (tab.fmt === 'table-Cart') {
//       const rows1 = Cart1.map(([a, b]) => ['Cart1', a, b]);
//       const rows3 = Cart3.map(([a, b]) => ['Cart3', a, b]);
//       return (
//         <>
//           <CartTable rows={rows1} title="Cart1" />
//           <CartTable rows={rows3} title="Cart3" />
//         </>
//       );
//     }
//     if (tab.fmt === 'table-overall') {
//       const all = [
//         ...Cart1.map(([a, b]) => ['Cart1', a, b]),
//         ...Cart3.map(([a, b]) => ['Cart3', a, b])
//       ];
//       return <CartTable rows={all} title="Overall" />;
//     }

//     const times = tab.data;
//     return (
//       <>
//         <div
//           style={{
//             display: 'grid',
//             gridTemplateColumns: isNarrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(140px, 1fr))',
//             gap: 8
//           }}
//         >
//           {times.map((t) => <TimeChip key={t} t={t} />)}
//         </div>

//         {tab.notes && tab.notes.length > 0 && (
//           <div style={{ marginTop: 12 }}>
//             {tab.notes.map((n) => (
//               <div
//                 key={n.time}
//                 style={{
//                   fontSize: 13,
//                   background: '#fff8e1',
//                   border: '1px solid #f0d48a',
//                   padding: '6px 10px',
//                   borderRadius: 10,
//                   marginTop: 6
//                 }}
//               >
//                 <strong>{n.time}</strong> – {n.note}
//               </div>
//             ))}
//           </div>
//         )}
//       </>
//     );
//   }

//   function ScheduleModal() {
//     const tab = TABS[activeTab];
//     const tabListRef = useRef(null);
//     const tabScrollRef = useRef(0);

//     useLayoutEffect(() => {
//       const el = tabListRef.current;
//       if (el && el.scrollLeft !== tabScrollRef.current) {
//         el.scrollLeft = tabScrollRef.current;
//       }
//     });

//     return (
//       <div
//         onClick={() => setShowSchedule(false)}
//         style={{
//           position: 'fixed',
//           inset: 0,
//           background: 'rgba(0,0,0,0.45)',
//           zIndex: 99999,
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'center',
//           padding: 16
//         }}
//       >
//         <div
//           onClick={(e) => e.stopPropagation()}
//           style={{
//             position: 'relative',
//             width: isNarrow ? '96vw' : 'min(1100px, 96vw)',
//             maxHeight: '90dvh',
//             background: '#ffffff',
//             borderRadius: 16,
//             boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
//             display: 'flex',
//             flexDirection: 'column',
//             overflow: 'hidden'
//           }}
//         >
//           {/* Header */}
//           <div
//             style={{
//               padding: isNarrow ? '8px 10px' : '12px 16px',
//               borderBottom: '1px solid #eee',
//               display: 'flex',
//               alignItems: 'center',
//               gap: 12
//             }}
//           >
//             <img src={iitmLogo} alt="IIT Madras" style={{ width: isNarrow ? 26 : 32, height: isNarrow ? 26 : 32 }} />
//             <div style={{ fontWeight: 800, fontSize: clamp('14px','2.8vw','18px') }}>
//               IIT Madras – Campus Bus &amp; Cart Schedule
//             </div>
//             <button
//               onClick={() => setShowSchedule(false)}
//               style={{
//                 marginLeft: 'auto',
//                 border: 'none',
//                 background: '#f4f4f5',
//                 borderRadius: 999,
//                 width: 34,
//                 height: 34,
//                 cursor: 'pointer',
//                 fontSize: 16
//               }}
//               title="Close"
//             >
//               ✕
//             </button>
//           </div>

//           {/* Tab bar */}
//           <div
//             ref={tabListRef}
//             onScroll={(e) => { tabScrollRef.current = e.currentTarget.scrollLeft; }}
//             style={{
//               display: 'block',
//               padding: '8px 12px',
//               borderBottom: '1px solid #eee',
//               overflowX: 'auto',
//               overflowY: 'hidden',
//               whiteSpace: 'nowrap',
//               WebkitOverflowScrolling: 'touch',
//               scrollbarWidth: 'thin',
//               overscrollBehaviorX: 'contain',
//               touchAction: 'pan-x'
//             }}
//           >
//             <div style={{ display: 'inline-flex', gap: 8, minWidth: '100%', marginBottom: isNarrow ? '45px' : '18px'}}>
//               {TABS.map((t, i) => (
//                 <button
//                   key={t.key}
//                   onClick={() => setActiveTab(i)}
//                   style={{
//                     whiteSpace: 'nowrap',
//                     padding: isNarrow ? '8px 10px' : '8px 12px',
//                     borderRadius: 10,
//                     border: i === activeTab ? '1px solid #0D47A1' : '1px solid #ddd',
//                     background: i === activeTab ? '#eaf2ff' : '#ffffff',
//                     fontWeight: 600,
//                     cursor: 'pointer',
//                     flex: '0 0 auto'
//                   }}
//                 >
//                   {t.title}
//                 </button>
//               ))}
//             </div>
//           </div>

//           {/* Body */}
//           <div style={{ padding: isNarrow ? 8 : 12, overflow: 'auto' }}>
//             <div style={{ fontWeight: 700, marginBottom: 8 }}>{tab.title}</div>
//             <ScheduleBody tab={tab} />
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Pause live updates when modal open; refresh once on close, then resume
//   useEffect(() => {
//     if (showSchedule) { ignoreUpdatesRef.current = true; return; }
//     (async () => {
//       try {
//         if (!BACKEND) return;
//         const res = await axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 });
//         const arr = Array.isArray(res.data) ? res.data : [];
//         const byVeh = new Map();
//         for (const d of arr) {
//           const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
//           byVeh.set(d.vehicleno, {
//             vehicleno: d.vehicleno,
//             latitude: d.latitude,
//             longitude: d.longitude,
//             speed: d.speed,
//             heading
//           });
//         }
//         const list = [...byVeh.values()];
//         setBuses(processVehicles(list));
//         lastUpdateRef.current = Date.now();
//       } catch (e) {
//         console.warn('Refresh after modal close failed:', e?.message || e);
//       } finally {
//         ignoreUpdatesRef.current = false;
//       }
//     })();
//   }, [showSchedule]);

//   // Responsive stop icons (smaller on phones)
//   const stopIconInstance = useMemo(
//     () => createCustomIcon(stopIconImg, isNarrow ? [40, 40] : [50, 50]),
//     [isNarrow]
//   );

//   useLayoutEffect(() => {
//   const el = headerRef.current;
//   if (!el) return;

//   const updateHeaderHeight = () => {
//     setHeaderHeight(el.getBoundingClientRect().height || 0);
//   };

//   updateHeaderHeight();

//   let resizeObserver;

//   if (typeof ResizeObserver !== "undefined") {
//     resizeObserver = new ResizeObserver(updateHeaderHeight);
//     resizeObserver.observe(el);
//   }

//   window.addEventListener("resize", updateHeaderHeight);

//   return () => {
//     if (resizeObserver) resizeObserver.disconnect();
//     window.removeEventListener("resize", updateHeaderHeight);
//   };
// }, []);
//   // --------------------------- RENDER ---------------------------
//   return (
//     <div className="map-container">
//       {/* Header */}
// {/* Header */}
// <div
// ref={headerRef}
//   style={{
//     position: 'fixed',
//     top: 'max(8px, env(safe-area-inset-top))',
//     left: '50%',
//     transform: 'translateX(-50%)',
//     zIndex: 10000,
//     background: '#ffffff',
//     border: '1px solid #e6e6e7',
//     borderRadius: 16,
//     padding: isHeaderCollapsed ? (isNarrow ? 8 : 10) : pad,
//     boxShadow: '0 18px 40px rgba(0,0,0,0.25)',
//     width: isHeaderCollapsed
//       ? (isNarrow ? 'calc(100vw - 16px)' : 'min(420px, calc(100vw - 32px))')
//       : (isNarrow ? 'calc(100vw - 16px)' : 'min(560px, calc(100vw - 32px))'),
//     backdropFilter: 'blur(2px)',
//     transition: 'width 180ms ease, padding 180ms ease'
//   }}
// >
//   {/* Title row */}
//   <div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? 8 : 12 }}>
//     <img
//       src={iitmLogo}
//       alt="IIT Madras"
//       style={{
//         width: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
//         height: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
//         flex: '0 0 auto'
//       }}
//     />

//     <h2
//       style={{
//         margin: 0,
//         fontSize: isHeaderCollapsed
//           ? clamp('15px', '3vw', '20px')
//           : clamp('18px', '3.2vw', '28px'),
//         lineHeight: 1.1,
//         letterSpacing: 0.3,
//         fontWeight: 800,
//         whiteSpace: 'nowrap',
//         overflow: 'hidden',
//         textOverflow: 'ellipsis',
//         flex: 1
//       }}
//     >
//       Moving Mountains
//     </h2>

//     {isHeaderCollapsed && (
//       <div
//         style={{
//           fontSize: isNarrow ? 12 : 13,
//           fontWeight: 800,
//           color: '#111827',
//           whiteSpace: 'nowrap'
//         }}
//         title="Visible vehicles / total vehicles"
//       >
//         {filteredBuses.length}/{buses.length}
//       </div>
//     )}

//     {!isHeaderCollapsed && (
//       <a
//         href="/admin"
//         style={{
//           fontWeight: 700,
//           whiteSpace: 'nowrap'
//         }}
//       >
//         Admin ↗
//       </a>
//     )}

//     <button
//       type="button"
//       onClick={() => setIsHeaderCollapsed((v) => !v)}
//       aria-label={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
//       title={isHeaderCollapsed ? 'Expand' : 'Collapse'}
//       style={{
//         border: '1px solid #D1D5DB',
//         background: '#ffffff',
//         color: '#111827',
//         borderRadius: 10,
//         padding: isNarrow ? '6px 8px' : '7px 10px',
//         fontWeight: 800,
//         fontSize: isNarrow ? 13 : 14,
//         lineHeight: 1,
//         cursor: 'pointer',
//         minWidth: isNarrow ? 34 : 38,
//         minHeight: isNarrow ? 32 : 36
//       }}
//     >
//       {isHeaderCollapsed ? '▾' : '▴'}
//     </button>
//   </div>

//   {!isHeaderCollapsed && (
//     <>
//       {/* Status row */}
//       <div
//         style={{
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'space-between',
//           marginTop: 10,
//           gap: 12,
//           flexWrap: 'wrap'
//         }}
//       >
//         <span
//           style={{
//             display: 'inline-block',
//             padding: '4px 10px',
//             borderRadius: 999,
//             fontSize: chipFS,
//             fontWeight: 700,
//             background:
//               socketStatus === 'connected'
//                 ? '#E9F9EE'
//                 : socketStatus === 'connecting'
//                 ? '#FFF7E1'
//                 : '#FFECEC',
//             color:
//               socketStatus === 'connected'
//                 ? '#0A7A2A'
//                 : socketStatus === 'connecting'
//                 ? '#7A5D00'
//                 : '#B00020',
//             border:
//               socketStatus === 'connected'
//                 ? '1px solid #C6EBD1'
//                 : socketStatus === 'connecting'
//                 ? '1px solid #F4E3B0'
//                 : '1px solid #FFD0D0',
//             textTransform: 'lowercase'
//           }}
//           title={`Socket: ${socketStatus}`}
//         >
//           {socketStatus}
//         </span>

//         <div
//           style={{
//             fontSize: clamp('14px','2.6vw','18px'),
//             fontWeight: 700,
//             letterSpacing: 0.5,
//             color: '#111827'
//           }}
//         >
//           {filteredBuses.length} / {buses.length}
//         </div>
//       </div>

//       {/* Controls row */}
//       <div
//         style={{
//           display: 'flex',
//           alignItems: 'center',
//           gap: gap,
//           marginTop: gap,
//           flexWrap: 'wrap'
//         }}
//       >
//         <div style={{ position: 'relative', flex: '1 1 200px' }}>
//           <select
//             value={filterType}
//             onChange={(e) => setFilterType(e.target.value)}
//             title="Filter vehicles"
//             style={{
//               width: '100%',
//               padding: isNarrow ? '10px 34px 10px 12px' : '10px 40px 10px 14px',
//               borderRadius: 10,
//               border: '1px solid #D1D5DB',
//               background: '#FFFFFF',
//               fontWeight: 600,
//               cursor: 'pointer',
//               appearance: 'none',
//               WebkitAppearance: 'none',
//               MozAppearance: 'none'
//             }}
//           >
//             <option value="all">All Vehicles</option>
//             <option value="cart">Cart</option>
//             <option value="bus">Bus</option>
//             <optgroup label="Filter by Route">
//               {ROUTES.map(r => (
//                 <option key={r.code} value={`route:${r.code}`}>{r.label}</option>
//               ))}
//             </optgroup>
//           </select>

//           <div
//             style={{
//               position: 'absolute',
//               right: isNarrow ? 8 : 12,
//               top: '50%',
//               transform: 'translateY(-50%)',
//               width: 0,
//               height: 0,
//               borderLeft: '6px solid transparent',
//               borderRight: '6px solid transparent',
//               borderTop: '7px solid #111',
//               pointerEvents: 'none'
//             }}
//           />
//         </div>

//         <button
//           onClick={() => {
//             requestUserLocation();
//             setTimeout(() => {
//               window.location.reload();
//             }, 150);
//           }}
//           title="Recenter + Reload"
//           style={{
//             padding: '10px 14px',
//             borderRadius: 10,
//             border: '1px solid #D1D5DB',
//             background: '#FFFFFF',
//             fontWeight: 700,
//             cursor: 'pointer',
//             minHeight: isNarrow ? 36 : 40,
//             fontSize: clamp('13px','2.8vw','14px')
//           }}
//         >
//           Refresh
//         </button>

//         <button
//           onClick={() => setShowSchedule(true)}
//           title="Show schedule"
//           style={{
//             padding: '10px 16px',
//             borderRadius: 10,
//             border: '1px solid #0D47A1',
//             background: '#0D47A1',
//             color: '#FFFFFF',
//             fontWeight: 800,
//             cursor: 'pointer',
//             boxShadow: '0 6px 16px rgba(13,71,161,0.25)',
//             minHeight: isNarrow ? 36 : 40,
//             fontSize: clamp('13px','2.8vw','14px')
//           }}
//         >
//           Schedule
//         </button>
//       </div>

//       {maintenance && (
//         <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#fff8e1', border: '1px solid #f0d48a', fontWeight: 600 }}>
//           Under maintenance — {maintenanceReason}
//         </div>
//       )}

//       {locError && (
//         <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#FFECEC', border: '1px solid #FFD0D0', fontWeight: 600 }}>
//           {locError}

//           <button
//             type="button"
//             onClick={requestUserLocation}
//             style={{ marginLeft: 10, padding: '6px 10px', borderRadius: 10, border: '1px solid #D1D5DB', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
//             title={geoState === "denied" ? "Enable in site settings first, then try again" : "Try again"}
//           >
//             {geoState === "denied" ? "Open site settings" : "Allow location"}
//           </button>
//         </div>
//       )}
//     </>
//   )}
// </div>

// {/* {SHOW_MINT_SUMMIT_PROMO && (
//   <a
//     href={MINT_SUMMIT_REGISTRATION_URL}
//     target="_blank"
//     rel="noreferrer"
//     className="mint-summit-floating-banner"
//     style={{
//       top: isHeaderCollapsed ? 76 : 310,
//       width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
//     }}
//     title="Register for MInT Mobility Summit 2026"
//   >
//     <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
//     <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
//     <span className="mint-summit-cta">Register ↗</span>
//   </a>
// )} */}

// {SHOW_MINT_SUMMIT_PROMO && (
//   <a
//     href={MINT_SUMMIT_REGISTRATION_URL}
//     target="_blank"
//     rel="noreferrer"
//     className="mint-summit-floating-banner"
//     style={{
//       top: headerHeight
//         ? `calc(max(8px, env(safe-area-inset-top)) + ${headerHeight}px + ${isNarrow ? 8 : 10}px)`
//         : isHeaderCollapsed
//         ? 76
//         : 260,
//       width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
//     }}
//     title="Register for MInT Mobility Summit 2026"
//   >
//     <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
//     <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
//     <span className="mint-summit-cta">Register ↗</span>
//   </a>
// )}

//       <JourneyPlanner
//         backend={BACKEND}
//         userLocation={userLocation}
//         requestUserLocation={requestUserLocation}
//         onPlanChange={setJourneyPlan}
//         onLocationUpdate={(location) => {
//           setUserLocation(location);
//         }}
//         onFocus={(location) => {
//           if (location?.lat && location?.lng) setMapCenter({ lat: location.lat, lng: location.lng });
//         }}
//         campusMapPicking={campusMapPicking}
//         pickedCampusPoint={campusPickedPoint}
//         onBeginCampusMapPick={() => {
//           setCampusPickedPoint(null);
//           setCampusMapPicking(true);
//         }}
//         onEndCampusMapPick={() => setCampusMapPicking(false)}
//         onClearCampusPickedPoint={() => setCampusPickedPoint(null)}
//       />

//       {campusMapPicking && (
//         <div className="campus-map-pick-banner" role="status">
//           <span className="campus-map-pick-target">⌖</span>
//           <div><strong>Tap the exact public entrance</strong><small>Zoom in, then tap the point people should walk to.</small></div>
//           <button type="button" onClick={() => setCampusMapPicking(false)}>Cancel</button>
//         </div>
//       )}

//       {/* Schedule Modal */}
//       {showSchedule && <ScheduleModal />}

//       {/* Map uses dynamic viewport on mobile (100dvh) */}
//       {/* <MapContainer center={center} zoom={DEFAULT_ZOOM} maxZoom={22} style={{ width: '100vw', height: '100dvh' }}> */}
//       <MapContainer
//         center={mapCenter}
//         zoom={DEFAULT_ZOOM}
//         minZoom={15}
//         maxZoom={22}
//         maxBounds={CAMPUS_MAP_BOUNDS}
//         maxBoundsViscosity={1.0}
//         worldCopyJump={false}
//         style={{ width: '100vw', height: '100dvh' }}
//       >
//       <RecenterMap center={mapCenter} zoom={DEFAULT_ZOOM} />
//       <CampusMapPicker
//         enabled={campusMapPicking}
//         point={campusPickedPoint}
//         onPick={(point) => {
//           setCampusPickedPoint(point);
//           setCampusMapPicking(false);
//         }}
//       />
//       <JourneyMapLayer plan={journeyPlan} />

//         <TileLayer
//           attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
//           url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//           maxNativeZoom={19}
//           maxZoom={22}
//           noWrap={true}
//         />

//         {userLocation && (
//           <Marker position={{ lat: userLocation.lat, lng: userLocation.lng }} icon={userIconInstance}>
//             <Popup>
//               <div className="info-window">
//                 <h4>You are here</h4>
//               </div>
//             </Popup>
//           </Marker>
//         )}
//         {/* here i have updated the backend eta */}
//         {/* Bus Stops with backend ETA (polled every 5s) */}
//         {busStops.map((stop) => {
//           const etaState = stopEtas[stop.id];           // { data, error } or undefined
//           const payload = etaState?.data;               // backend JSON
//           const next = payload?.next || null;           // { vehicle, goingTowards, etaMinutes } | null

//           return (
//             <Marker
//               key={stop.id}
//               position={{ lat: stop.lat, lng: stop.lng }}
//               icon={stopIconInstance}
//             >
//               <Popup>
//                 <div className="info-window">
//                   <h4>{stop.name}</h4>

//                   {/* 1) Still loading / first poll */}
//                   {!etaState && <p>Loading ETA…</p>}

//                   {/* 2) Request for this stop failed */}
//                   {etaState && !payload && <p>ETA not available.</p>}

//                   {/* 3) Backend says: no approaching assignment */}
//                   {payload && payload.success && !next && (
//                     <p>No assigned vehicle approaching.</p>
//                   )}

//                   {/* 4) We have a next arrival from backend */}
//                   {payload && payload.success && next && (
//                     <>
//                       {/* You said you may not need vehicle number/type, 
//                           so we only show direction + ETA. */}
//                       <p>
//                           Next Vehicle Details:
//                         </p>
//                       {next.vehicle && (
//                         <p>
//                           <strong>Vehicle Type:</strong> {next.vehicle}
//                         </p>
//                       )}
//                       {next.goingTowards && (
//                         <p>
//                           <strong>Direction:</strong> {next.goingTowards}
//                         </p>
//                       )}
//                       {/* <p>
//                         <strong>ETA:</strong>{" "}
//                         {next.etaMinutes != null
//                           ? `${next.etaMinutes} min`
//                           : "— (has not departed yet)"}
//                       </p> */}
//                       <p>
//   <strong>ETA:</strong>{" "}
//   {next.etaStatus === "AT_STOP"
//     ? "0 min (at stop)"
//     : next.etaMinutes != null
//     ? `${next.etaMinutes} min`
//     : "— (has not departed yet)"}
// </p>
//                     </>
//                   )}
//                 </div>
//               </Popup>
//             </Marker>
//           );
//         })}



//         {/* Vehicles */}
//         {filteredBuses.map((bus) => {
//           // const label = vehicleLabel(bus.vehicleno);
//           const rc = assignments[bus.vehicleno];
//           const label = vehicleLabel(bus.vehicleno, rc);
//           const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
//           const info = nearestStopEtaForBusOnItsRoute(bus);
//           // const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
//           // const eta = info ? `${info.etaMin} min` : 'Unknown';
//           const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
//           const eta =
//             info && info.etaMin != null
//               ? `${info.etaMin} min`
//               : info
//               ? "—"
//               : "Unknown";

//           return (
//             <Marker
//               key={bus.vehicleno}
//               position={{ lat: bus.latitude, lng: bus.longitude }}
//               // icon={createVehicleDivIcon(label, bus.heading)}
//               icon={createVehicleDivIcon(label, bus.heading, palette)}

//             >
//               <Popup>
//                 <div className="info-window">
//                   <h4>{bus.vehicleno} ({label})</h4>
//                   {/* <p><strong>Legend:</strong> B = Bus, C = Cart</p> */}
//                   <p><strong>Legend:</strong> B = Bus, C = Cart, CR = Cart (RP↔ED)</p>
//                   <p><strong>Speed:</strong> {Number.isFinite(bus.speed) ? bus.speed.toFixed(1) : 'N/A'} km/h</p>
//                   {assignments[bus.vehicleno] && (
//                     <p><strong>Route:</strong> {ROUTES.find(r=>r.code===assignments[bus.vehicleno])?.label || assignments[bus.vehicleno]}</p>
//                   )}
//                   {nearestName && <p><strong>Next Stop:</strong> {nearestName}</p>}
//                   {/* <p><strong>ETA:</strong> {eta}</p> */}
//                   {/* <p>
//                     <strong>ETA:</strong>{" "}
//                     {eta}
//                     {info && info.waitingAtFirstStop ? " (has not departed yet)" : ""}
//                   </p> */}
//                   <p>
//   <strong>ETA:</strong>{" "}
//   {eta}
//   {info && info.waitingAtStop ? " (waiting at stop)" : ""}
// </p>
//                   {Number.isFinite(bus.heading) && <p><strong>Heading:</strong> {Math.round(bus.heading)}°</p>}
//                 </div>
//               </Popup>
//             </Marker>
//           );
//         })}
//       </MapContainer>

//       {/* Footer: Feedback link */}
//       <div
//         style={{
//           position: 'fixed',
//           left: 0,
//           right: 0,
//           bottom: -5,
//           display: 'flex',
//           justifyContent: 'center',
//           zIndex: 10000,
//           pointerEvents: 'none',
//           paddingBottom: 'max(6px, env(safe-area-inset-bottom))'
//         }}
//       >
//         <div
//           style={{
//             width: '100vw',
//             pointerEvents: 'auto',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'space-between',
//             gap: 12,
//             padding: isNarrow ? '6px 10px' : '6px 16px',
//             borderRadius: 14,
//             background:
//               'linear-gradient(135deg, rgba(17,24,39,0.85), rgba(31,41,55,0.85))',
//             color: '#F9FAFB',
//             border: '1px solid rgba(255,255,255,0.14)',
//             boxShadow:
//               '0 12px 30px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
//             backdropFilter: 'saturate(160%) blur(10px)',
//             WebkitBackdropFilter: 'saturate(160%) blur(10px)',
//             fontFamily:
//               '"SF Pro Text", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"',
//             fontSize: clamp('12px','2.8vw','14px'),
//             lineHeight: 1.4,
//             fontWeight: 500,
//             letterSpacing: '0.2px',
//           }}
//         >
//           <div
//             style={{
//               display: 'flex',
//               alignItems: 'center',
//               gap: 10,
//               flex: 1,
//               minWidth: 0,
//             }}
//           >
//             <div
//               style={{
//                 width: 8,
//                 height: 8,
//                 borderRadius: 9999,
//                 background:
//                   'radial-gradient(circle at 30% 30%, #34D399, #059669)',
//                 boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
//                 flex: '0 0 auto',
//               }}
//             />
//             <span
//               style={{
//                 opacity: 0.95,
//                 whiteSpace: 'nowrap',
//                 overflow: 'hidden',
//                 textOverflow: 'ellipsis',
//               }}
//             >
//                Amit Kumar | CE20B009 | MInT Lab
//             </span>
//           </div>

//           {/* {FEEDBACK_URL ? (
//             <a
//               href={FEEDBACK_URL}
//               target="_blank"
//               rel="noreferrer"
//               style={{
//                 display: 'inline-flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 padding: '8px 12px',
//                 borderRadius: 10,
//                 border: '1px solid rgba(255,255,255,0.18)',
//                 background:
//                   'linear-gradient(180deg, rgba(128, 126, 126, 0.14), rgba(221, 212, 212, 0.06))',
//                 color: '#F9FAFB',
//                 textDecoration: 'none',
//                 fontWeight: 600,
//                 letterSpacing: '0.25px',
//                 transform: 'translateZ(0)',
//                 transition: 'transform 120ms ease, opacity 120ms ease',
//                 cursor: 'pointer',
//                 boxShadow:
//                   'inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 14px rgba(0,0,0,0.25)',
//               }}
//               onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
//               onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
//             >
//               Open feedback form ↗
//             </a>
//           ) : ( */}

//           {FOOTER_ACTION_URL ? (
//   <a
//     href={FOOTER_ACTION_URL}
//     target="_blank"
//     rel="noreferrer"
//     className={
//       SHOW_MINT_SUMMIT_PROMO
//         ? 'footer-action-button mint-summit-glitter-button'
//         : 'footer-action-button'
//     }
//     title={FOOTER_ACTION_LABEL}
//   >
//     {FOOTER_ACTION_LABEL}
//   </a>
// ) : (
//             <span
//               style={{
//                 padding: '8px 12px',
//                 borderRadius: 10,
//                 border: '1px dashed rgba(255,255,255,0.25)',
//                 color: 'rgba(249,250,251,0.8)',
//                 fontWeight: 500,
//               }}
//             >
//               {/* &lt;form-link&gt; */}
//               &lt;form-link&gt;
//             </span>
//           )}
//         </div>
//       </div>

//     </div>
//   );
// }

// function MaintenancePoster() {
//   return (
//     <div className="maintenance-page">
//       <div className="maintenance-card">
//         <div className="maintenance-logo-wrap">
//           <img src={iitmLogo} alt="IIT Madras" className="maintenance-logo" />
//         </div>

//         <div className="maintenance-badge">Server migration in progress</div>

//         <h1>We’ll be back very soon</h1>

//         <p className="maintenance-main-text">
//           Hey, I'm Amit Kumar. Thank you for using Moving Mountains. We value your time and effort.
//           Our server is currently being upgraded to provide a smoother, faster,
//           and more reliable experience.
//         </p>

//         <p className="maintenance-sub-text">
//           The website will be available again shortly once the migration is
//           completed. We appreciate your patience and support.
//         </p>

//         <div className="maintenance-loader">
//           <span></span>
//           <span></span>
//           <span></span>
//         </div>

//         <div className="maintenance-footer">
//           Thanks for using our service.
//           <br />
//           — Team Moving Mountains
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ---------------------------- APP (router) ---------------------------- */

// export default function App() {
//   const { path, navigate } = usePath();
//   const token = getToken();
//   const adminToken = getAdminToken();

//   // Track page views on main map for Prometheus
//   useEffect(() => {
//     if (!BACKEND) return;
//     if (path !== "/") return;
//     axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
//   }, [path]);

//   // Forward /driver -> /driverRoute when logged in
//   useEffect(() => {
//     if (path === "/driver" && token) {
//       navigate("/driverRoute");
//     }
//   }, [path, token, navigate]);

//   // Admin redirects
//   useEffect(() => {
//     if (path === "/admin/login" && adminToken) {
//       navigate("/admin");
//     } else if (path === "/admin" && !adminToken) {
//       navigate("/admin/login");
//     }
//   }, [path, adminToken, navigate]);

//   if (MAINTENANCE_MODE) {
//     return <MaintenancePoster />;
//   }

//   if (path === "/driver") {
//     if (token) return null;
//     return <DriverLogin navigate={navigate} />;
//   }
//   if (path === "/driverRoute") return <DriverRoute navigate={navigate} />;

//   if (path === "/admin/login") {
//     if (adminToken) return null;
//     return <AdminLogin navigate={navigate} />;
//   }
//   if (path === "/admin") {
//     return <AdminDashboard navigate={navigate} />;
//   }

//   return <MapPage />;
// }

// // export default function App() {
// //   const { path, navigate } = usePath();
// //   const token = getToken();

// //   // Track page views on main map for Prometheus
// //   useEffect(() => {
// //     if (!BACKEND) return;
// //     if (path !== "/") return;
// //     axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
// //   }, [path]);

// //   // Forward /driver -> /driverRoute when logged in (no hooks called conditionally)
// //   useEffect(() => {
// //     if (path === '/driver' && token) {
// //       navigate('/driverRoute');
// //     }
// //   }, [path, token, navigate]);

// //   if (path === '/driver') {
// //     if (token) return null;
// //     return <DriverLogin navigate={navigate} />;
// //   }
// //   if (path === '/driverRoute') return <DriverRoute navigate={navigate} />;
// //   return <MapPage />;
// // }


// // // traveliitm/frontend/src/App.js
// // // import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
// // import React, {
// //   useEffect,
// //   useState,
// //   useRef,
// //   useLayoutEffect,
// //   useMemo,
// //   useCallback
// // } from 'react';

// // import axios from 'axios';
// // import { io } from 'socket.io-client';
// // import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
// // import L from 'leaflet';
// // import 'leaflet/dist/leaflet.css';
// // import './App.css';
// // import * as turf from '@turf/turf';
// // import JourneyPlanner from './components/JourneyPlanner';
// // import JourneyMapLayer from './components/JourneyMapLayer';

// // // ---- Assets in src/assets ----
// // import userIcon from './assets/user-icon.png';
// // import stopIconImg from './assets/bus-stop.png';
// // import iitmLogo from './assets/iitm-logo.png';

// // // ---- Backend base URL ----
// // const BACKEND = process.env.REACT_APP_BACKEND_URL;
// // const API_KEY = process.env.REACT_APP_API_KEY;

// // // ---- Temporary maintenance mode ----
// // // Set this to false after server migration is completed.
// // const MAINTENANCE_MODE = false;

// // // ---- Static roads URL (public/roads.geojson) ----
// // const ROADS_URL = `${process.env.PUBLIC_URL || ''}/roads.geojson`;

// // // ---- Map zoom param ----
// // const DEFAULT_ZOOM = 18;

// // // Keep the public experience focused on IIT Madras instead of allowing users
// // // to pan across Tamil Nadu/world tiles. The small margin covers all campus roads.
// // const CAMPUS_MAP_BOUNDS = [
// //   [12.9780, 80.2170],
// //   [13.0115, 80.2485],
// // ];

// // // ---- Smoothing / Snap params ----
// // const SMOOTH_ALPHA = 0.3;
// // const HEADING_ALPHA = 0.35;
// // const SNAP_MAX_DIST_M = 20;

// // // Staleness threshold for under-maintenance banner
// // const STALE_MS = 150_000; // ~2.5 minutes

// // // Initial center (fallback)
// // const center = { lat: 12.9915, lng: 80.2336 };

// // // ---------------- STOPS WITH IDS (for route math) ----------------
// // const busStops = [
// //   { name: 'Velachery Gate IIT Campus Stop', lat: 12.988763557585395, lng: 80.22367960515129, id: 1 },
// //   { name: 'NAC2 Stop Towards GC', lat: 12.990236, lng: 80.227548, id: 2 },
// //   { name: 'Nac2 Towards Velachery Stop', lat: 12.9898636, lng: 80.2271109, id: 3 },
// //   { name: 'CRC Towards GC Stop', lat: 12.99089, lng: 80.230274, id: 4 },
// //   { name: 'CRC Towards Velachery Stop', lat: 12.99079680, lng: 80.23016660, id: 5 },  
// //   { name: 'HSB Stop Towards Velachery Stop', lat: 12.99076780, lng: 80.23168950, id: 6},
// //   { name: 'HSB Stop Towards GC', lat: 12.991037, lng: 80.232065, id: 7},
// //   { name: 'KV Stop Towards Main Gate', lat: 12.991868573807471, lng: 80.23367454352295, id: 8 },
// //   { name: 'KV Stop Towards GC', lat: 12.991910, lng: 80.233785, id: 9 },
// //   { name: 'Postoffice Stop Towards Main Gate', lat: 12.99388680, lng: 80.23428760, id: 10 },
// //   { name: 'Postoffice Stop Towards GC', lat: 12.99393340, lng: 80.23459350, id: 11 },
// //   { name: 'E1 Stop Towards Main Gate', lat: 12.9960173, lng: 80.2359173, id: 12 },
// //   { name: 'E1 Stop Towards GC', lat: 12.996117, lng: 80.236184, id: 13},
// //   { name: 'Vana Vani Stop Towards Main Gate', lat: 12.998704, lng: 80.2391799, id: 14 },
// //   { name: 'Vana Vani Stop Towards GC', lat: 12.999084,   lng:  80.239380, id: 15 },
// //   { name: 'D1 Stop Towards Main Gate', lat:13.002546, lng: 80.240091, id: 16},
// //   { name: 'D1 Stop Towards GC', lat: 13.002679,  lng: 80.240219, id: 17 },
// //   { name: 'Main Gate', lat: 13.00612964850378, lng: 80.24191299117685, id: 18 },
// //   { name: 'GC Stop Towards Hostel', lat: 12.991342, lng: 80.233637, id: 19 },
// //   { name: 'Library Stop', lat: 12.9907517, lng: 80.2334554, id: 20 },
// //   { name: 'OAT Stop Towards Hostel', lat: 12.989257,  lng: 80.233031, id: 21},
// //   { name: 'Gymkhana Stop Towards GC', lat: 12.9866372, lng: 80.2332951, id: 22},
// //   { name: 'Gymkhana Stop Towards Hostel', lat: 12.986615, lng: 80.233366, id: 23 },
// //   { name: 'Narmada Stop Towards GC', lat: 12.9862759, lng: 80.2350107, id: 24 },
// //   { name: 'Narmada Stop Towards Hostel', lat: 12.986546, lng: 80.235301, id: 25 },
// //   { name: 'Jamuna and Ganga Stand', lat: 12.986566332825637, lng: 80.23855704439099, id: 26 },
// //   { name: 'RP Stand', lat: 12.990088, lng: 80.241799, id: 28},
// //   { name: 'ED Stand', lat: 12.989874633072303, lng: 80.22646042038593, id: 29},
// // ];

// // // Route sequences by stop id
// // const Hostel_to_Main   = [26, 24, 22, 20, 6, 5, 3, 1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
// // const Main_to_Hostel   = [18, 17, 15, 13, 11, 9, 6, 5, 3, 1, 2, 4, 7, 19, 21, 23, 25, 26];
// // const Velachery_to_Main= [1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
// // const Velachery_to_Hostel=[1, 2, 4, 7, 19, 21, 23, 25, 26];
// // const Hotel_to_Velachery=[26, 24, 22, 20, 6, 5, 3, 1];
// // const Main_to_Velachery=[18, 17, 15, 13, 11, 9, 6, 5, 3, 1];
// // const RP_to_ED         = [28, 22, 21, 20, 6, 5, 3, 29];
// // const ED_to_RP         = [29, 2, 4, 7, 19, 21, 23, 28];

// // const ROUTES = [
// //   { code: "HOSTEL_MAIN", label: "Hostel → Main Gate", ids: Hostel_to_Main },
// //   { code: "MAIN_HOSTEL", label: "Main Gate → Hostel", ids: Main_to_Hostel },
// //   { code: "VG_MAIN",     label: "Velachery Gate → Main Gate", ids: Velachery_to_Main },
// //   { code: "VG_HOSTEL",   label: "Velachery Gate → Hostel", ids: Velachery_to_Hostel },
// //   { code: "MAIN_VG",     label: "Main Gate → Velachery Gate", ids: Main_to_Velachery },
// //   { code: "HOSTEL_VG",   label: "Hostel → Velachery Gate", ids: Hotel_to_Velachery },
// //   { code: "RP_ED",       label: "Research Park → ED", ids: RP_to_ED },
// //   { code: "ED_RP",       label: "ED → Research Park", ids: ED_to_RP },
// // ];

// // // ---- Route aliases (frontend only) ----
// // // Backend only knows canonical codes: HOSTEL_MAIN, MAIN_HOSTEL, RP_ED, ED_RP
// // // But filters can still expose Velachery labels.
// // // Aliases map to canonical backend codes.
// // const ROUTE_ALIAS_TO_CANONICAL = {
// //   VG_MAIN: "HOSTEL_MAIN",
// //   HOSTEL_VG: "HOSTEL_MAIN",
// //   MAIN_VG: "MAIN_HOSTEL",
// //   VG_HOSTEL: "MAIN_HOSTEL",
// // };

// // function canonicalRoute(code) {
// //   return ROUTE_ALIAS_TO_CANONICAL[code] || code;
// // }
// // // Routes the admin can assign in backend (only canonical codes)
// // const ADMIN_ROUTES = [
// //   { code: "HOSTEL_MAIN", label: "Hostel → Main Gate" },
// //   { code: "MAIN_HOSTEL", label: "Main Gate → Hostel" },
// //   { code: "RP_ED", label: "Research Park → ED" },
// //   { code: "ED_RP", label: "ED → Research Park" },
// // ];


// // // ---- Vehicles for driverRoute select ----
// // const VEHICLES = [
// //   "TN07DH1368","TN07DH1317","TN07DH1315","EV09","TN07DH1360","TN07DH1391","EV08","TN07DD5574",
// //   "TN07CX8698","TN07DD3376","TN07CX8200","TN07DD3360","EV-12","EV-14","EV-15","EV-10","EV-05",
// //   "EV-07","EV-06","EV-02","EV-03","EV-11","EV-01","EV-13","TN07CX8119"
// // ];

// // // ---- Leaflet default icon fix ----
// // delete L.Icon.Default.prototype._getIconUrl;
// // L.Icon.Default.mergeOptions({
// //   iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
// //   iconUrl: require('leaflet/dist/images/marker-icon.png'),
// //   shadowUrl: require('leaflet/dist/images/marker-shadow.png')
// // });

// // // ---- Utility helpers ----
// // const createCustomIcon = (iconUrl, size = [40, 40]) =>
// //   L.icon({
// //     iconUrl,
// //     iconSize: size,
// //     iconAnchor: [size[0] / 2, size[1]],
// //     popupAnchor: [0, -size[1] / 2]
// //   });

// // const userIconInstance = createCustomIcon(userIcon, [40, 40]);

// // const toHeading = (v) => {
// //   const n = Number(v);
// //   return Number.isFinite(n) ? ((n % 360) + 360) % 360 : null;
// // };

// // // Exponential smoothing for latitude/longitude
// // function smoothCoord(prev, next, alpha = SMOOTH_ALPHA) {
// //   if (!prev) return next;
// //   return {
// //     lat: prev.lat + alpha * (next.lat - prev.lat),
// //     lng: prev.lng + alpha * (next.lng - prev.lng)
// //   };
// // }



// // // Circular smoothing for heading (degrees)
// // function smoothHeading(prevDeg, nextDeg, alpha = HEADING_ALPHA) {
// //   if (!Number.isFinite(prevDeg)) return nextDeg;
// //   if (!Number.isFinite(nextDeg)) return prevDeg;
// //   const p = (prevDeg * Math.PI) / 180;
// //   const n = (nextDeg * Math.PI) / 180;
// //   const x = (1 - alpha) * Math.cos(p) + alpha * Math.cos(n);
// //   const y = (1 - alpha) * Math.sin(p) + alpha * Math.sin(n);
// //   const ang = (Math.atan2(y, x) * 180) / Math.PI;
// //   return (((ang % 360) + 360) % 360);
// // }
// // function mBetween(a, b) { return L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng)); }

// // // ---- Cart classification (case-insensitive "EV") ----
// // function isCart(vehicleno) {
// //   const s = String(vehicleno || '').trim();
// //   return s.toUpperCase().startsWith('EV');
// // }

// // // Label: B for Bus, C for Cart
// // // function vehicleLabel(vehicleno) { return isCart(vehicleno) ? 'C' : 'B'; }
// // function vehicleLabel(vehicleno, routeCode) {
// //   if (!isCart(vehicleno)) return "B";

// //   const rc = canonicalRoute(routeCode || "");
// //   if (rc === "RP_ED" || rc === "ED_RP") return "CR"; // 👈 cart on RP↔ED
// //   return "C";
// // }


// // // ---- Block IDs starting with a digit ----
// // function startsWithDigit(vehicleno) { return /^[0-9]/.test(String(vehicleno || '')); }

// // // ---- Direction pointer “on the circle” DivIcon ----
// // // function createVehicleDivIcon(label, headingDeg) {
// // //   const circle = 20;
// // //   const border = 3;
// // //   const triH = 18;
// // //   const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
// // //   const totalH = triH + circle;

// // //   const html = `
// // //     <div style="position:relative;width:${circle}px;height:${totalH}px">
// // //       <div style="
// // //         position:absolute; left:0; right:0; bottom:0;
// // //         width:${circle}px; height:${circle}px;
// // //         border:${border}px solid #0D47A1; background:#ffffff; border-radius:50%;
// // //         display:flex; align-items:center; justify-content:center;
// // //         box-shadow:0 2px 8px rgba(0,0,0,0.25);
// // //         font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
// // //         letter-spacing:0.6px; user-select:none;">
// // //         <span style="font-size:16px; color:#0D47A1">${label}</span>
// // //       </div>
// // //       <div style="
// // //         position:absolute; left:50%; top:0;
// // //         transform: translateX(-250%) rotate(${rot}deg);
// // //         transform-origin: 50% ${triH * 0.7}px;
// // //         width:0; height:0;
// // //         border-left:${triH / 4}px solid transparent;
// // //         border-right:${triH / 4}px solid transparent;
// // //         border-bottom:${triH}px solid #0D47A1;
// // //         filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
// // //         z-index: 2; pointer-events: none;"></div>
// // //     </div>
// // //   `;

// // //   return L.divIcon({
// // //     className: 'veh-div-icon',
// // //     html,
// // //     iconSize: [circle, totalH],
// // //     iconAnchor: [circle / 2, totalH - circle / 2],
// // //     popupAnchor: [0, -(circle / 2 + triH / 2)]
// // //   });
// // // }

// // function createVehicleDivIcon(label, headingDeg, palette) {
// //   const circle = 20;
// //   const border = 3;
// //   const triH = 18;
// //   const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
// //   const totalH = triH + circle;

// //   // default bus palette
// //   const p = palette || {
// //     stroke: "#0D47A1",
// //     fill: "#ffffff",
// //     text: "#0D47A1",
// //     pointer: "#0D47A1",
// //   };

// //   const html = `
// //     <div style="position:relative;width:${circle}px;height:${totalH}px">
// //       <div style="
// //         position:absolute; left:0; right:0; bottom:0;
// //         width:${circle}px; height:${circle}px;
// //         border:${border}px solid ${p.stroke};
// //         background:${p.fill};
// //         border-radius:50%;
// //         display:flex; align-items:center; justify-content:center;
// //         box-shadow:0 2px 8px rgba(0,0,0,0.25);
// //         font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
// //         letter-spacing:0.6px; user-select:none;">
// //         <span style="font-size:16px; color:${p.text}">${label}</span>
// //       </div>
// //       <div style="
// //         position:absolute; left:50%; top:0;
// //         transform: translateX(-250%) rotate(${rot}deg);
// //         transform-origin: 50% ${triH * 0.7}px;
// //         width:0; height:0;
// //         border-left:${triH / 4}px solid transparent;
// //         border-right:${triH / 4}px solid transparent;
// //         border-bottom:${triH}px solid ${p.pointer};
// //         filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
// //         z-index: 2; pointer-events: none;"></div>
// //     </div>
// //   `;

// //   return L.divIcon({
// //     className: 'veh-div-icon',
// //     html,
// //     iconSize: [circle, totalH],
// //     iconAnchor: [circle / 2, totalH - circle / 2],
// //     popupAnchor: [0, -(circle / 2 + triH / 2)]
// //   });
// // }

// // const BUS_PALETTE = {
// //   stroke: "#0D47A1",
// //   fill: "#ffffff",
// //   text: "#0D47A1",
// //   pointer: "#0D47A1",
// // };

// // // const CART_PALETTE = {
// // //   stroke: "#0F766E",  // teal border
// // //   fill: "#ECFEFF",    // light teal fill
// // //   text: "#0F766E",
// // //   pointer: "#0F766E",
// // // };
// // const CART_PALETTE = {
// //   stroke: "#111827",   // near-black border
// //   fill:   "#FEF08A",   // bright yellow fill
// //   text:   "#111827",   // black text
// //   pointer:"#111827",   // black pointer
// // };





// // function matchesFilter(vehicleno, filter, assignments) {
// //   const cart = isCart(vehicleno);
// //   if (filter === "cart") return cart;
// //   if (filter === "bus") return !cart;

// //   if (filter?.startsWith("route:")) {
// //     const requested = canonicalRoute(filter.slice(6));
// //     const assigned = canonicalRoute(assignments[vehicleno]);
// //     return assigned === requested;
// //   }
// //   return true;
// // }

// // // ---- Filter helper ----
// // // function matchesFilter(vehicleno, filter, assignments) {
// // //   const cart = isCart(vehicleno);
// // //   if (filter === 'cart') return cart;
// // //   if (filter === 'bus')  return !cart;
// // //   if (filter?.startsWith('route:')) {
// // //     const code = filter.slice(6);
// // //     return assignments[vehicleno] === code;
// // //   }
// // //   return true;
// // // }

// // // ---------------- SCHEDULE DATA (kept intact) ----------------
// // const HOSTEL_TO_MAIN = [
// //   '06:20 AM','06:40 AM','07:00 AM','07:20 AM','07:40 AM','08:00 AM','08:20 AM','08:40 AM',
// //   '09:00 AM','09:20 AM','09:40 AM','10:00 AM','10:20 AM','10:40 AM','11:00 AM','11:20 AM',
// //   '11:40 AM','12:00 PM','12:20 PM','12:40 PM','01:00 PM','01:20 PM','01:40 PM','02:00 PM',
// //   '02:20 PM','02:40 PM','03:00 PM','03:20 PM','03:40 PM','04:00 PM','04:20 PM','04:40 PM',
// //   '05:00 PM','05:20 PM','05:40 PM','06:00 PM','06:20 PM','06:40 PM','07:00 PM','07:20 PM',
// //   '07:40 PM','08:00 PM','08:20 PM','08:40 PM','09:00 PM','09:20 PM'
// // ];
// // const HOSTEL_NOTES = [
// //   { time: '09:30 PM', note: 'To Velachery Gate' },
// //   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// // ];
// // const MAIN_TO_HOSTEL = [...HOSTEL_TO_MAIN];
// // const MAIN_NOTES = [
// //   { time: '09:40 PM', note: 'To Velachery Gate' },
// //   { time: '10:00 PM', note: 'To Velachery Gate' },
// //   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// // ];
// // const VELACHERY_TO_MAIN = [
// //   '06:15 AM','06:35 AM','06:55 AM','07:15 AM','07:35 AM','07:55 AM','08:15 AM','08:35 AM','08:55 AM',
// //   '09:15 AM','09:35 AM','09:55 AM','10:15 AM','10:35 AM','10:55 AM','11:15 AM','11:35 AM',
// //   '12:15 PM','12:35 PM','12:55 PM','01:15 PM','01:35 PM','01:55 PM','02:15 PM','02:35 PM','02:55 PM',
// //   '03:15 PM','03:35 PM','03:55 PM','04:15 PM','04:35 PM','04:55 PM','05:15 PM','05:35 PM','05:55 PM',
// //   '06:15 PM','06:35 PM','06:55 PM','07:15 PM','07:35 PM','07:55 PM','08:15 PM','08:35 PM','08:55 PM',
// //   '09:15 PM','09:35 PM'
// // ];
// // const VELACHERY_TO_HOSTEL = [
// //   '06:25 AM','06:45 AM','07:05 AM','07:25 AM','07:45 AM','08:05 AM','08:25 AM','08:45 AM','09:05 AM',
// //   '09:25 AM','09:45 AM','10:05 AM','10:25 AM','10:45 AM','11:05 AM','11:25 AM','11:45 AM',
// //   '12:05 PM','12:25 PM','12:45 PM','01:05 PM','01:25 PM','01:45 PM','02:05 PM','02:25 PM','02:45 PM',
// //   '03:05 PM','03:25 PM','03:45 PM','04:05 PM','04:25 PM','04:45 PM','05:05 PM','05:25 PM','05:45 PM',
// //   '06:05 PM','06:25 PM','06:45 PM','07:05 PM','07:25 PM','07:45 PM','08:05 PM','08:25 PM','08:45 PM',
// //   '09:05 PM','09:15 PM'
// // ];
// // const VELACHERY_NOTES = [
// //   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// // ];
// // const RP_TO_ED_TIMES = [
// //   '07:30 AM','08:05 AM','08:40 AM','09:00 AM','09:15 AM','09:35 AM','09:45 AM',
// //   '10:15 AM','10:25 AM','10:45 AM','11:20 AM','11:40 AM',
// //   '12:05 PM','12:45 PM','01:40 PM','02:20 PM','02:35 PM','03:00 PM','03:15 PM','03:45 PM',
// //   '04:15 PM','04:50 PM','05:05 PM','05:50 PM','06:25 PM','07:05 PM'
// // ];
// // const ED_TO_RP_TIMES = [
// //   '07:50 AM','08:25 AM','09:00 AM','09:15 AM','09:30 AM','10:00 AM','10:05 AM','10:30 AM',
// //   '11:00 AM','11:15 AM','11:45 AM',
// //   '12:00 PM','12:25 PM','01:05 PM','02:00 PM','02:40 PM','03:00 PM','03:20 PM','03:30 PM',
// //   '04:05 PM','04:45 PM','05:10 PM','05:30 PM','06:10 PM','06:45 PM','07:20 PM'
// // ];
// // const Cart1 = [['07:30','07:50'],['08:05','08:25'],['08:40','09:00'],['09:15','09:30'],['09:45','10:05'],['10:25','11:00'],['11:20','11:45'],['12:05','12:25'],['13:40','14:00'],['14:20','14:40'],['15:00','15:20'],['15:45','16:05'],['16:50','17:10']];
// // const Cart3 = [['09:00','09:15'],['09:35','10:00'],['10:15','10:30'],['10:45','11:15'],['11:40','12:00'],['12:45','13:05'],['14:35','15:00'],['15:15','15:30'],['16:15','16:45'],['17:05','17:30'],['17:50','18:10'],['18:25','18:45'],['19:05','19:20']];

// // // // ---- Feedback link ----
// // // const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

// // // ---- Feedback / temporary summit promo links ----
// // const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

// // const MINT_SUMMIT_REGISTRATION_URL =
// //   process.env.REACT_APP_MINT_SUMMIT_REGISTRATION_URL ||
// //   'https://docs.google.com/forms/d/e/1FAIpQLScB7Y6RtzcOwXRsoi4Czrr_vUnOcZm7joeeKSNKvcNm7RRjLw/viewform';

// // // Set this to false after the summit.
// // const SHOW_MINT_SUMMIT_PROMO = false;

// // const FOOTER_ACTION_URL = SHOW_MINT_SUMMIT_PROMO
// //   ? MINT_SUMMIT_REGISTRATION_URL
// //   : FEEDBACK_URL;

// // const FOOTER_ACTION_LABEL = SHOW_MINT_SUMMIT_PROMO
// //   ? 'Register for MInT Summit ✨'
// //   : 'Open feedback form ↗';

// // // ---------------- Tiny router helpers (for /driver, /driverRoute) ----------------
// // function usePath() {
// //   const [path, setPath] = useState(window.location.pathname);
// //   useEffect(() => {
// //     const onPop = () => setPath(window.location.pathname);
// //     window.addEventListener('popstate', onPop);
// //     return () => window.removeEventListener('popstate', onPop);
// //   }, []);
// //   const navigate = (p) => { if (p !== window.location.pathname) { window.history.pushState({}, "", p); setPath(p); } };
// //   return { path, navigate };
// // }
// // function getToken() { return localStorage.getItem("driverToken") || ""; }
// // function setToken(t) { localStorage.setItem("driverToken", t || ""); }
// // function getUsername() { return localStorage.getItem("driverUsername") || ""; }
// // function setUsername(u) { localStorage.setItem("driverUsername", u || ""); }

// // // --- Admin auth helpers (separate from driver) ---
// // function getAdminToken() {
// //   return localStorage.getItem("adminToken") || "";
// // }
// // function setAdminToken(t) {
// //   localStorage.setItem("adminToken", t || "");
// // }


// // // ---------------- Driver pages ----------------
// // // --- small responsive hook for narrow phones ---
// // function useIsNarrow(bp = 380) {
// //   const [isNarrow, setIsNarrow] = useState(window.innerWidth <= bp);
// //   useEffect(() => {
// //     const onResize = () => setIsNarrow(window.innerWidth <= bp);
// //     window.addEventListener('resize', onResize);
// //     return () => window.removeEventListener('resize', onResize);
// //   }, [bp]);
// //   return isNarrow;
// // }

// // function DriverLogin({ navigate }) {
// //   const [username, setU] = useState("");
// //   const [password, setP] = useState("");
// //   const [err, setErr] = useState("");
// //   const [busy, setBusy] = useState(false);
// //   const isNarrow = useIsNarrow(380);

// //   useEffect(() => {
// //     if (getToken()) navigate("/driverRoute");
// //   }, [navigate]);

// //   const onSubmit = async (e) => {
// //     e.preventDefault();
// //     setErr(""); setBusy(true);
// //     try {
// //       const r = await axios.post(`${BACKEND}/api/driver/login`, { username, password });
// //       if (r.data?.success && r.data?.token) {
// //         setToken(r.data.token);
// //         setUsername(r.data.username);
// //         navigate("/driverRoute");
// //       } else setErr("Login failed");
// //     } catch (e1) {
// //       setErr(e1?.response?.data?.message || "Login failed");
// //     } finally { setBusy(false); }
// //   };

// //   return (
// //     <div
// //       style={{
// //         padding: isNarrow ? 12 : 20,
// //         margin: isNarrow ? "16px auto" : "32px auto",
// //         width: "min(100vw - 24px, 560px)",
// //       }}
// //     >
// //       {/* Header with Home button */}
// //       <div
// //         style={{
// //           display: "flex",
// //           alignItems: "center",
// //           gap: isNarrow ? 10 : 12,
// //           marginBottom: isNarrow ? 10 : 14,
// //           flexWrap: "wrap",
// //         }}
// //       >
// //         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
// //           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
// //           <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Driver Login</h2>
// //         </div>

// //         <button
// //           type="button"
// //           onClick={() => navigate("/")}
// //           style={{
// //             marginLeft: "auto",
// //             padding: isNarrow ? "6px 10px" : "8px 12px",
// //             borderRadius: 10,
// //             border: "1px solid #D1D5DB",
// //             background: "#FFFFFF",
// //             fontWeight: 700,
// //             fontSize: isNarrow ? 13 : 14,
// //             cursor: "pointer",
// //           }}
// //           title="Go to Home"
// //         >
// //           Home
// //         </button>
// //       </div>

// //       {/* Card */}
// //       <div
// //         style={{
// //           background: "#fff",
// //           border: "1px solid #E5E7EB",
// //           borderRadius: 12,
// //           padding: isNarrow ? 12 : 16,
// //         }}
// //       >
// //         <form onSubmit={onSubmit} style={{ display: "grid", gap: isNarrow ? 8 : 10 }}>
// //           <input
// //             value={username}
// //             onChange={(e) => setU(e.target.value)}
// //             placeholder="username"
// //             required
// //             style={{
// //               padding: isNarrow ? 10 : 12,
// //               borderRadius: 10,
// //               border: "1px solid #D1D5DB",
// //               fontWeight: 600,
// //               fontSize: isNarrow ? 14 : 16,
// //             }}
// //           />
// //           <input
// //             value={password}
// //             onChange={(e) => setP(e.target.value)}
// //             placeholder="password"
// //             type="password"
// //             required
// //             style={{
// //               padding: isNarrow ? 10 : 12,
// //               borderRadius: 10,
// //               border: "1px solid #D1D5DB",
// //               fontWeight: 600,
// //               fontSize: isNarrow ? 14 : 16,
// //             }}
// //           />
// //           <button
// //             disabled={busy}
// //             style={{
// //               padding: isNarrow ? "10px 12px" : "12px 14px",
// //               borderRadius: 10,
// //               border: "1px solid #0D47A1",
// //               background: "#0D47A1",
// //               color: "#fff",
// //               fontWeight: 800,
// //               cursor: "pointer",
// //               width: "100%",
// //               minHeight: 40,
// //               fontSize: "clamp(14px, 3.5vw, 16px)",
// //             }}
// //           >
// //             {busy ? "Signing in..." : "Login"}
// //           </button>
// //           {err && <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>}
// //         </form>
// //       </div>
// //     </div>
// //   );
// // }

// // function DriverRoute({ navigate }) {
// //   const [routeCode, setRouteCode] = useState("");
// //   const [vehicleNo, setVehicleNo] = useState("");
// //   const [autoPilot, setAutoPilot] = useState(false); // 👈 NEW: auto-pilot flag
// //   const [saved, setSaved] = useState(false);
// //   const [busy, setBusy] = useState(false);
// //   const [err, setErr] = useState("");
// //   const isNarrow = useIsNarrow(380);

// //   useEffect(() => {
// //     if (!getToken()) navigate("/driver");
// //     (async () => {
// //       try {
// //         const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
// //           headers: { Authorization: `Bearer ${getToken()}` },
// //         });
// //         const row = r.data?.row;
// //         if (row) {
// //           setVehicleNo(row.vehicleNo);
// //           setRouteCode(row.routeCode);
// //           setAutoPilot(!!row.autopilotEnabled); // 👈 load auto-pilot state from backend
// //           setSaved(true);
// //         }
// //       } catch {}
// //     })();
// //     // eslint-disable-next-line react-hooks/exhaustive-deps
// //   }, []);

// //   const onSave = async (e) => {
// //     e.preventDefault();
// //     setErr(""); setBusy(true);
// //     try {
// //       await axios.post(
// //         `${BACKEND}/api/vehicle-route/assign`,
// //         {
// //           vehicleNo,
// //           routeCode,
// //           active: true,
// //           autopilotEnabled: autoPilot, // 👈 send auto-pilot choice to backend
// //         },
// //         { headers: { Authorization: `Bearer ${getToken()}` } }
// //       );
// //       setSaved(true);
// //     } catch (e1) {
// //       setErr(e1?.response?.data?.message || "Save failed");
// //     } finally {
// //       setBusy(false);
// //     }
// //   };

// //   return (
// //     <div
// //       style={{
// //         padding: isNarrow ? 16 : 20,
// //         maxWidth: 640,
// //         margin: isNarrow ? "16px auto" : "32px auto",
// //         width: "min(100vw - 24px, 640px)",
// //       }}
// //     >
// //       <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
// //         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
// //           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
// //           <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
// //         </div>
// //         <div
// //           style={{
// //             marginLeft: 'auto',
// //             fontWeight: 700,
// //             opacity: 0.8,
// //             fontSize: "clamp(13px, 3.2vw, 16px)",
// //             maxWidth: '60%',
// //             overflow: 'hidden',
// //             textOverflow: 'ellipsis',
// //             whiteSpace: 'nowrap',
// //           }}
// //           title={`@${getUsername()}`}
// //         >
// //           @{getUsername()}
// //         </div>
// //       </div>

// //       {/* Top-right actions */}
// //       <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
// //         <div style={{ display: 'flex', gap: 8 }}>
// //           <button
// //             onClick={() => navigate('/')}
// //             style={{
// //               padding: isNarrow ? '6px 10px' : '8px 12px',
// //               borderRadius: 10,
// //               border: '1px solid #D1D5DB',
// //               background: '#FFFFFF',
// //               fontWeight: 700,
// //               fontSize: isNarrow ? 13 : 14,
// //               cursor: 'pointer'
// //             }}
// //             title="Go to Home"
// //           >
// //             Home
// //           </button>
// //           <button
// //             type="button"
// //             onClick={() => setSaved(false)}
// //             disabled={!saved}
// //             style={{
// //               padding: isNarrow ? '6px 10px' : '8px 12px',
// //               borderRadius: 10,
// //               border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
// //               background: saved ? '#fff' : '#F9FAFB',
// //               color: saved ? '#111' : '#9CA3AF',
// //               fontWeight: 800,
// //               fontSize: isNarrow ? 13 : 14,
// //               cursor: saved ? 'pointer' : 'not-allowed'
// //             }}
// //             title="Edit selection"
// //           >
// //             Edit
// //           </button>
// //         </div>

// //         <button
// //           onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
// //           style={{
// //             padding: isNarrow ? '6px 10px' : 8,
// //             borderRadius: 10,
// //             border: '1px solid #D1D5DB',
// //             background: '#FFFFFF',
// //             fontSize: isNarrow ? 13 : 14,
// //             cursor: 'pointer'
// //           }}
// //           title="Logout"
// //         >
// //           Logout
// //         </button>
// //       </div>

// //       <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
// //         <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
// //         <select
// //           disabled={saved}
// //           value={routeCode}
// //           onChange={e=>setRouteCode(e.target.value)}
// //           required
// //           style={{
// //             padding: isNarrow ? 10 : 12,
// //             borderRadius: 10,
// //             border: '1px solid #D1D5DB',
// //             fontSize: isNarrow ? 14 : 16
// //           }}
// //         >
// //           <option value="">Select route…</option>
// //           {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
// //         </select>

// //         <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
// //         <select
// //           disabled={saved}
// //           value={vehicleNo}
// //           onChange={e=>setVehicleNo(e.target.value)}
// //           required
// //           style={{
// //             padding: isNarrow ? 10 : 12,
// //             borderRadius: 10,
// //             border: '1px solid #D1D5DB',
// //             fontSize: isNarrow ? 14 : 16
// //           }}
// //         >
// //           <option value="">Select vehicle…</option>
// //           {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
// //         </select>

// //         {/* 👇 NEW: Auto-pilot toggle */}
// //         <div
// //           style={{
// //             marginTop: isNarrow ? 6 : 8,
// //             padding: isNarrow ? 8 : 10,
// //             borderRadius: 10,
// //             border: '1px solid #E5E7EB',
// //             background: '#F9FAFB',
// //             display: 'flex',
// //             alignItems: 'flex-start',
// //             gap: 8,
// //           }}
// //         >
// //           <input
// //             id="autoPilot"
// //             type="checkbox"
// //             checked={autoPilot}
// //             disabled={saved}
// //             onChange={(e) => setAutoPilot(e.target.checked)}
// //             style={{ marginTop: 3, cursor: saved ? 'default' : 'pointer' }}
// //           />
// //           <label
// //             htmlFor="autoPilot"
// //             style={{ cursor: saved ? 'default' : 'pointer' }}
// //           >
// //             <div style={{ fontWeight: 700, fontSize: isNarrow ? 14 : 15 }}>
// //               Auto-pilot (auto reverse at destination)
// //             </div>
// //             <div
// //               style={{
// //                 fontSize: isNarrow ? 12 : 13,
// //                 color: '#6B7280',
// //                 marginTop: 2,
// //               }}
// //             >
// //               When enabled, once you reach the last stop of this route, your
// //               assignment will automatically switch to the return leg.
// //             </div>
// //           </label>
// //         </div>

// //         <button
// //           disabled={busy || saved}
// //           style={{
// //             padding: isNarrow ? '10px 12px' : '12px 14px',
// //             borderRadius: 10,
// //             border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
// //             background: busy || saved ? '#F3F4F6' : '#0D47A1',
// //             color: busy || saved ? '#6B7280' : '#fff',
// //             fontWeight: 800,
// //             cursor: busy || saved ? 'not-allowed' : 'pointer',
// //             minHeight: 44,
// //             fontSize: "clamp(14px, 3.5vw, 16px)"
// //           }}
// //           title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
// //         >
// //           {busy ? "Saving…" : saved ? "Saved" : "Save"}
// //         </button>

// //         {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
// //       </form>
// //     </div>
// //   );
// // }


// // // function DriverRoute({ navigate }) {
// // //   const [routeCode, setRouteCode] = useState("");
// // //   const [vehicleNo, setVehicleNo] = useState("");
// // //   const [saved, setSaved] = useState(false);
// // //   const [busy, setBusy] = useState(false);
// // //   const [err, setErr] = useState("");
// // //   const isNarrow = useIsNarrow(380);

// // //   useEffect(() => {
// // //     if (!getToken()) navigate("/driver");
// // //     (async () => {
// // //       try {
// // //         const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
// // //           headers: { Authorization: `Bearer ${getToken()}` },
// // //         });
// // //         const row = r.data?.row;
// // //         if (row) { setVehicleNo(row.vehicleNo); setRouteCode(row.routeCode); setSaved(true); }
// // //       } catch {}
// // //     })();
// // //     // eslint-disable-next-line react-hooks/exhaustive-deps
// // //   }, []);

// // //   const onSave = async (e) => {
// // //     e.preventDefault();
// // //     setErr(""); setBusy(true);
// // //     try {
// // //       await axios.post(
// // //         `${BACKEND}/api/vehicle-route/assign`,
// // //         { vehicleNo, routeCode, active: true },
// // //         { headers: { Authorization: `Bearer ${getToken()}` } }
// // //       );
// // //       setSaved(true);
// // //     } catch (e1) {
// // //       setErr(e1?.response?.data?.message || "Save failed");
// // //     } finally {
// // //       setBusy(false);
// // //     }
// // //   };

// // //   return (
// // //     <div
// // //       style={{
// // //         padding: isNarrow ? 16 : 20,
// // //         maxWidth: 640,
// // //         margin: isNarrow ? "16px auto" : "32px auto",
// // //         width: "min(100vw - 24px, 640px)",
// // //       }}
// // //     >
// // //       <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
// // //         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
// // //           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
// // //           <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
// // //         </div>
// // //         <div
// // //           style={{
// // //             marginLeft: 'auto',
// // //             fontWeight: 700,
// // //             opacity: 0.8,
// // //             fontSize: "clamp(13px, 3.2vw, 16px)",
// // //             maxWidth: '60%',
// // //             overflow: 'hidden',
// // //             textOverflow: 'ellipsis',
// // //             whiteSpace: 'nowrap',
// // //           }}
// // //           title={`@${getUsername()}`}
// // //         >
// // //           @{getUsername()}
// // //         </div>
// // //       </div>

// // //       {/* Top-right actions */}
// // //       <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
// // //         <div style={{ display: 'flex', gap: 8 }}>
// // //           <button
// // //             onClick={() => navigate('/')}
// // //             style={{
// // //               padding: isNarrow ? '6px 10px' : '8px 12px',
// // //               borderRadius: 10,
// // //               border: '1px solid #D1D5DB',
// // //               background: '#FFFFFF',
// // //               fontWeight: 700,
// // //               fontSize: isNarrow ? 13 : 14,
// // //               cursor: 'pointer'
// // //             }}
// // //             title="Go to Home"
// // //           >
// // //             Home
// // //           </button>
// // //           <button
// // //             type="button"
// // //             onClick={() => setSaved(false)}
// // //             disabled={!saved}
// // //             style={{
// // //               padding: isNarrow ? '6px 10px' : '8px 12px',
// // //               borderRadius: 10,
// // //               border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
// // //               background: saved ? '#fff' : '#F9FAFB',
// // //               color: saved ? '#111' : '#9CA3AF',
// // //               fontWeight: 800,
// // //               fontSize: isNarrow ? 13 : 14,
// // //               cursor: saved ? 'pointer' : 'not-allowed'
// // //             }}
// // //             title="Edit selection"
// // //           >
// // //             Edit
// // //           </button>
// // //         </div>

// // //         <button
// // //           onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
// // //           style={{
// // //             padding: isNarrow ? '6px 10px' : 8,
// // //             borderRadius: 10,
// // //             border: '1px solid #D1D5DB',
// // //             background: '#FFFFFF',
// // //             fontSize: isNarrow ? 13 : 14,
// // //             cursor: 'pointer'
// // //           }}
// // //           title="Logout"
// // //         >
// // //           Logout
// // //         </button>
// // //       </div>

// // //       <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
// // //         <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
// // //         <select
// // //           disabled={saved}
// // //           value={routeCode}
// // //           onChange={e=>setRouteCode(e.target.value)}
// // //           required
// // //           style={{
// // //             padding: isNarrow ? 10 : 12,
// // //             borderRadius: 10,
// // //             border: '1px solid #D1D5DB',
// // //             fontSize: isNarrow ? 14 : 16
// // //           }}
// // //         >
// // //           <option value="">Select route…</option>
// // //           {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
// // //         </select>

// // //         <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
// // //         <select
// // //           disabled={saved}
// // //           value={vehicleNo}
// // //           onChange={e=>setVehicleNo(e.target.value)}
// // //           required
// // //           style={{
// // //             padding: isNarrow ? 10 : 12,
// // //             borderRadius: 10,
// // //             border: '1px solid #D1D5DB',
// // //             fontSize: isNarrow ? 14 : 16
// // //           }}
// // //         >
// // //           <option value="">Select vehicle…</option>
// // //           {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
// // //         </select>

// // //         <button
// // //           disabled={busy || saved}
// // //           style={{
// // //             padding: isNarrow ? '10px 12px' : '12px 14px',
// // //             borderRadius: 10,
// // //             border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
// // //             background: busy || saved ? '#F3F4F6' : '#0D47A1',
// // //             color: busy || saved ? '#6B7280' : '#fff',
// // //             fontWeight: 800,
// // //             cursor: busy || saved ? 'not-allowed' : 'pointer',
// // //             minHeight: 44,
// // //             fontSize: "clamp(14px, 3.5vw, 16px)"
// // //           }}
// // //           title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
// // //         >
// // //           {busy ? "Saving…" : saved ? "Saved" : "Save"}
// // //         </button>

// // //         {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
// // //       </form>
// // //     </div>
// // //   );
// // // }

// // /* ---------------------------- ADMIN PAGES ---------------------------- */

// // function AdminLogin({ navigate }) {
// //   const [username, setU] = useState("");
// //   const [password, setP] = useState("");
// //   const [err, setErr] = useState("");
// //   const [busy, setBusy] = useState(false);
// //   const isNarrow = useIsNarrow(380);

// //   useEffect(() => {
// //     if (getAdminToken()) navigate("/admin");
// //   }, [navigate]);

// //   const onSubmit = async (e) => {
// //     e.preventDefault();
// //     setErr("");
// //     setBusy(true);
// //     try {
// //       const r = await axios.post(`${BACKEND}/api/admin/login`, {
// //         username,
// //         password,
// //       });
// //       if (r.data?.success && r.data?.token) {
// //         setAdminToken(r.data.token);
// //         navigate("/admin");
// //       } else {
// //         setErr("Login failed");
// //       }
// //     } catch (e1) {
// //       setErr(e1?.response?.data?.message || "Login failed");
// //     } finally {
// //       setBusy(false);
// //     }
// //   };

// //   return (
// //     <div
// //       style={{
// //         padding: isNarrow ? 12 : 20,
// //         margin: isNarrow ? "16px auto" : "32px auto",
// //         width: "min(100vw - 24px, 560px)",
// //       }}
// //     >
// //       <div
// //         style={{
// //           display: "flex",
// //           alignItems: "center",
// //           gap: isNarrow ? 10 : 12,
// //           marginBottom: isNarrow ? 10 : 14,
// //           flexWrap: "wrap",
// //         }}
// //       >
// //         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
// //           <img
// //             src={iitmLogo}
// //             alt="IITM"
// //             style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }}
// //           />
// //           <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Admin Login</h2>
// //         </div>
// //         <button
// //           type="button"
// //           onClick={() => navigate("/")}
// //           style={{
// //             marginLeft: "auto",
// //             padding: isNarrow ? "6px 10px" : "8px 12px",
// //             borderRadius: 10,
// //             border: "1px solid #D1D5DB",
// //             background: "#FFFFFF",
// //             fontWeight: 700,
// //             fontSize: isNarrow ? 13 : 14,
// //             cursor: "pointer",
// //           }}
// //           title="Go to Home"
// //         >
// //           Home
// //         </button>
// //       </div>
// //       <div
// //         style={{
// //           background: "#fff",
// //           border: "1px solid #E5E7EB",
// //           borderRadius: 12,
// //           padding: isNarrow ? 12 : 16,
// //         }}
// //       >
// //         <form
// //           onSubmit={onSubmit}
// //           style={{ display: "grid", gap: isNarrow ? 8 : 10 }}
// //         >
// //           <input
// //             value={username}
// //             onChange={(e) => setU(e.target.value)}
// //             placeholder="admin username"
// //             required
// //             style={{
// //               padding: isNarrow ? 10 : 12,
// //               borderRadius: 10,
// //               border: "1px solid #D1D5DB",
// //               fontWeight: 600,
// //               fontSize: isNarrow ? 14 : 16,
// //             }}
// //           />
// //           <input
// //             value={password}
// //             onChange={(e) => setP(e.target.value)}
// //             placeholder="admin password"
// //             type="password"
// //             required
// //             style={{
// //               padding: isNarrow ? 10 : 12,
// //               borderRadius: 10,
// //               border: "1px solid #D1D5DB",
// //               fontWeight: 600,
// //               fontSize: isNarrow ? 14 : 16,
// //             }}
// //           />
// //           <button
// //             disabled={busy}
// //             style={{
// //               padding: isNarrow ? "10px 12px" : "12px 14px",
// //               borderRadius: 10,
// //               border: "1px solid #0D47A1",
// //               background: "#0D47A1",
// //               color: "#fff",
// //               fontWeight: 800,
// //               cursor: "pointer",
// //               width: "100%",
// //               minHeight: 40,
// //               fontSize: "clamp(14px, 3.5vw, 16px)",
// //             }}
// //           >
// //             {busy ? "Signing in..." : "Login"}
// //           </button>
// //           {err && (
// //             <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>
// //           )}
// //         </form>
// //       </div>
// //     </div>
// //   );
// // }

// // function AdminDashboard({ navigate }) {
// //   const [tab, setTab] = useState("drivers"); // 'drivers' | 'routes' | 'live'
// //   const [drivers, setDrivers] = useState([]);
// //   const [assignments, setAssignments] = useState({}); // vehicleNo -> row
// //   const [localAssign, setLocalAssign] = useState({}); // vehicleNo -> { routeCode, driverId, autopilotEnabled }
// //   const [editing, setEditing] = useState({}); // vehicleNo -> true when row is in edit mode
// //   const [newName, setNewName] = useState("");
// //   const [newPhone, setNewPhone] = useState("");
// //   const [err, setErr] = useState("");
// //   const [busySave, setBusySave] = useState(null); // vehicleno while saving
// //   const [liveBuses, setLiveBuses] = useState([]);
// //   const adminToken = getAdminToken();
// //   const isNarrow = useIsNarrow(480);

// //   useEffect(() => {
// //     if (!adminToken) navigate("/admin/login");
// //   }, [adminToken, navigate]);

// //   const authHeader = useMemo(
// //     () => ({
// //       headers: { Authorization: `Bearer ${adminToken}` },
// //     }),
// //     [adminToken]
// //   );

// //   async function loadDrivers() {
// //     try {
// //       const r = await axios.get(`${BACKEND}/api/admin/drivers`, authHeader);
// //       setDrivers(r.data?.drivers || []);
// //     } catch (e) {
// //       console.warn("loadDrivers error", e?.message || e);
// //     }
// //   }

// //   async function loadAssignments() {
// //     try {
// //       const r = await axios.get(`${BACKEND}/api/admin/assignments`, authHeader);
// //       const rows = r.data?.rows || [];
// //       const map = {};
// //       for (const row of rows) {
// //         map[row.vehicleNo] = row;
// //       }
// //       setAssignments(map);
// //       // whenever we reload from backend, treat all as "saved" (not editing)
// //       setEditing({});
// //       setLocalAssign({});
// //     } catch (e) {
// //       console.warn("loadAssignments error", e?.message || e);
// //     }
// //   }

// //   useEffect(() => {
// //     if (!adminToken) return;
// //     loadDrivers();
// //     loadAssignments();
// //   }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

// //   // live tracking (simple polling)
// //   useEffect(() => {
// //     if (tab !== "live") return;
// //     let cancelled = false;

// //     async function loadLive() {
// //       try {
// //         const r = await axios.get(`${BACKEND}/api/vehicle-data`, {
// //           timeout: 8000,
// //         });
// //         if (!cancelled) {
// //           const arr = Array.isArray(r.data) ? r.data : [];
// //           setLiveBuses(arr);
// //         }
// //       } catch (e) {
// //         if (!cancelled) {
// //           console.warn("admin live map error", e?.message || e);
// //         }
// //       }
// //     }
// //     loadLive();
// //     const id = setInterval(loadLive, 5000);
// //     return () => {
// //       cancelled = true;
// //       clearInterval(id);
// //     };
// //   }, [tab]);

// //   const handleAddDriver = async (e) => {
// //     e.preventDefault();
// //     setErr("");
// //     try {
// //       const r = await axios.post(
// //         `${BACKEND}/api/admin/drivers`,
// //         { name: newName, phoneNumber: newPhone },
// //         authHeader
// //       );
// //       if (r.data?.success && r.data?.driver) {
// //         setDrivers((prev) => [...prev, r.data.driver]);
// //         setNewName("");
// //         setNewPhone("");
// //       } else {
// //         setErr("Failed to create driver");
// //       }
// //     } catch (e1) {
// //       setErr(e1?.response?.data?.message || "Failed to create driver");
// //     }
// //   };

// //   const handleRemoveDriver = async (id) => {
// //     if (!window.confirm("Remove this driver?")) return;
// //     try {
// //       await axios.delete(`${BACKEND}/api/admin/drivers/${id}`, authHeader);
// //       setDrivers((prev) => prev.filter((d) => d._id !== id));
// //       // Note: assignments that still reference this driver will continue to exist until reassigned
// //     } catch (e1) {
// //       alert(e1?.response?.data?.message || "Failed to remove driver");
// //     }
// //   };

// //   function getBaseAssignForVehicle(veh) {
// //     const row = assignments[veh];
// //     if (!row) {
// //       return {
// //         vehicleNo: veh,
// //         routeCode: "",
// //         driverId: "",
// //         autopilotEnabled: false,
// //       };
// //     }
// //     return {
// //       vehicleNo: veh,
// //       routeCode: row.routeCode || "",
// //       driverId: row.driver?._id || "",
// //       autopilotEnabled: !!row.autopilotEnabled,
// //     };
// //   }

// //   function updateLocalAssign(veh, patch) {
// //     setLocalAssign((prev) => ({
// //       ...prev,
// //       [veh]: { ...getBaseAssignForVehicle(veh), ...(prev[veh] || {}), ...patch },
// //     }));
// //   }

// //   const handleSaveAssignment = async (veh) => {
// //   const base = getBaseAssignForVehicle(veh);
// //   const row = localAssign[veh] || base;
// //   const hasAssignment = !!assignments[veh]; // vehicle currently has an active route?

// //   // RESET error first
// //   setErr("");

// //   // ---- CASE 1: Admin cleared both fields and there *was* an assignment -> CLEAR MODE ----
// //   if (!row.routeCode && !row.driverId && hasAssignment) {
// //     setBusySave(veh);
// //     try {
// //       await axios.post(
// //         `${BACKEND}/api/admin/vehicle-assign`,
// //         {
// //           vehicleNo: row.vehicleNo,
// //           clear: true,              // 👈 tells backend: deactivate assignment
// //         },
// //         authHeader
// //       );
// //       await loadAssignments();      // refresh table
// //       setEditing((prev) => ({ ...prev, [veh]: false }));
// //     } catch (e1) {
// //       setErr(e1?.response?.data?.message || "Failed to clear assignment");
// //     } finally {
// //       setBusySave(null);
// //     }
// //     return;
// //   }

// //   // ---- CASE 2: No assignment and still empty -> nothing to save ----
// //   if (!row.routeCode || !row.driverId) {
// //     setErr("Select both Route & Driver, or leave the row as-is.");
// //     return;
// //   }

// //   // ---- CASE 3: Normal ASSIGN / UPDATE ----
// //   setBusySave(veh);
// //   try {
// //     await axios.post(
// //       `${BACKEND}/api/admin/vehicle-assign`,
// //       {
// //         vehicleNo: row.vehicleNo,
// //         routeCode: row.routeCode,
// //         driverId: row.driverId,
// //         autopilotEnabled: !!row.autopilotEnabled,
// //       },
// //       authHeader
// //     );
// //     await loadAssignments();
// //     setEditing((prev) => ({ ...prev, [veh]: false }));
// //   } catch (e1) {
// //     setErr(e1?.response?.data?.message || "Failed to save assignment");
// //   } finally {
// //     setBusySave(null);
// //   }
// // };


// //   const onLogout = () => {
// //     setAdminToken("");
// //     navigate("/admin/login");
// //   };

// //   // simple driver lookup for live map
// //   const driverByVehicle = useMemo(() => {
// //     const out = {};
// //     Object.values(assignments).forEach((row) => {
// //       if (row.vehicleNo && row.driver) {
// //         out[row.vehicleNo] = row.driver;
// //       }
// //     });
// //     return out;
// //   }, [assignments]);

// //   return (
// //     <div className="admin-layout">
// //       {/* left sidebar */}
// //       <div
// //         className="sidebar"
// //         style={{
// //           width: isNarrow ? 220 : 260, // responsive width override
// //         }}
// //       >
// //         {/* top section: logo, back button, nav */}
// //         <div>
// //           <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
// //             <img
// //               src={iitmLogo}
// //               alt="IITM"
// //               style={{ width: 32, height: 32, borderRadius: 8 }}
// //             />
// //             <div>
// //               <div style={{ fontWeight: 800, fontSize: 16 }}>Admin</div>
// //               <div style={{ fontSize: 12, opacity: 0.8 }}>Moving Mountains</div>
// //             </div>
// //           </div>
// //           <button
// //             onClick={() => navigate("/")}
// //             style={{
// //               marginTop: 8,
// //               textAlign: "left",
// //               padding: "6px 10px",
// //               borderRadius: 10,
// //               border: "1px solid #4b5563",
// //               background: "transparent",
// //               color: "#e5e7eb",
// //               cursor: "pointer",
// //               fontSize: 13,
// //             }}
// //           >
// //             ← Back to map
// //           </button>
// //           <div
// //             style={{
// //               marginTop: 16,
// //               display: "flex",
// //               flexDirection: "column",
// //               gap: 6,
// //             }}
// //           >
// //             <button
// //               onClick={() => setTab("drivers")}
// //               style={{
// //                 textAlign: "left",
// //                 padding: "8px 10px",
// //                 borderRadius: 10,
// //                 border: "none",
// //                 background: tab === "drivers" ? "#f9fafb" : "transparent",
// //                 color: tab === "drivers" ? "#111827" : "#e5e7eb",
// //                 fontWeight: 600,
// //                 cursor: "pointer",
// //               }}
// //             >
// //               + Add Driver
// //             </button>
// //             <button
// //               onClick={() => setTab("routes")}
// //               style={{
// //                 textAlign: "left",
// //                 padding: "8px 10px",
// //                 borderRadius: 10,
// //                 border: "none",
// //                 background: tab === "routes" ? "#f9fafb" : "transparent",
// //                 color: tab === "routes" ? "#111827" : "#e5e7eb",
// //                 fontWeight: 600,
// //                 cursor: "pointer",
// //               }}
// //             >
// //               Route Allocation
// //             </button>
// //             <button
// //               onClick={() => setTab("live")}
// //               style={{
// //                 textAlign: "left",
// //                 padding: "8px 10px",
// //                 borderRadius: 10,
// //                 border: "none",
// //                 background: tab === "live" ? "#f9fafb" : "transparent",
// //                 color: tab === "live" ? "#111827" : "#e5e7eb",
// //                 fontWeight: 600,
// //                 cursor: "pointer",
// //               }}
// //             >
// //               Live Tracking
// //             </button>
// //           </div>
// //         </div>

// //         {/* bottom logout – pinned to sidebar bottom */}
// //         <button
// //           onClick={onLogout}
// //           style={{
// //             marginTop: 12,
// //             padding: "8px 10px",
// //             borderRadius: 10,
// //             border: "1px solid #6b7280",
// //             background: "transparent",
// //             color: "#e5e7eb",
// //             cursor: "pointer",
// //             fontSize: 13,
// //           }}
// //         >
// //           Logout
// //         </button>
// //       </div>

// //       {/* main content */}
// //       <div className="content">
// //         {tab === "drivers" && (
// //           <div style={{ maxWidth: 800, margin: "0 auto" }}>
// //             <h2 style={{ marginTop: 0 }}>Drivers</h2>
// //             <form
// //               onSubmit={handleAddDriver}
// //               style={{
// //                 display: "grid",
// //                 gridTemplateColumns: "2fr 2fr auto",
// //                 gap: 8,
// //                 alignItems: "center",
// //                 marginBottom: 16,
// //               }}
// //             >
// //               <input
// //                 value={newName}
// //                 onChange={(e) => setNewName(e.target.value)}
// //                 placeholder="Driver name"
// //                 required
// //                 style={{
// //                   padding: 10,
// //                   borderRadius: 10,
// //                   border: "1px solid #D1D5DB",
// //                   fontSize: 14,
// //                 }}
// //               />
// //               <input
// //                 value={newPhone}
// //                 onChange={(e) => setNewPhone(e.target.value)}
// //                 placeholder="Phone number (unique)"
// //                 required
// //                 style={{
// //                   padding: 10,
// //                   borderRadius: 10,
// //                   border: "1px solid #D1D5DB",
// //                   fontSize: 14,
// //                 }}
// //               />
// //               <button
// //                 type="submit"
// //                 style={{
// //                   padding: "10px 16px",
// //                   borderRadius: 10,
// //                   border: "1px solid #0D47A1",
// //                   background: "#0D47A1",
// //                   color: "#fff",
// //                   fontWeight: 700,
// //                   cursor: "pointer",
// //                   fontSize: 14,
// //                 }}
// //               >
// //                 Add
// //               </button>
// //             </form>
// //             {err && (
// //               <div
// //                 style={{
// //                   color: "#b00020",
// //                   fontWeight: 600,
// //                   marginBottom: 8,
// //                 }}
// //               >
// //                 {err}
// //               </div>
// //             )}
// //             <div
// //               style={{
// //                 display: "grid",
// //                 gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
// //                 gap: 12,
// //               }}
// //             >
// //               {drivers.map((d) => (
// //                 <div
// //                   key={d._id}
// //                   style={{
// //                     background: "#fff",
// //                     borderRadius: 12,
// //                     border: "1px solid #e5e7eb",
// //                     padding: 12,
// //                     display: "flex",
// //                     flexDirection: "column",
// //                     gap: 4,
// //                   }}
// //                 >
// //                   <div style={{ fontWeight: 700, fontSize: 15 }}>{d.name}</div>
// //                   <div style={{ fontSize: 13, opacity: 0.8 }}>
// //                     Phone: {d.phoneNumber || "—"}
// //                   </div>
// //                   <div style={{ fontSize: 12, opacity: 0.6 }}>
// //                     username: <code>{d.username}</code>
// //                   </div>
// //                   <button
// //                     onClick={() => handleRemoveDriver(d._id)}
// //                     style={{
// //                       marginTop: 8,
// //                       alignSelf: "flex-start",
// //                       padding: "6px 10px",
// //                       borderRadius: 8,
// //                       border: "1px solid #f97373",
// //                       background: "#fef2f2",
// //                       color: "#b91c1c",
// //                       fontSize: 12,
// //                       fontWeight: 600,
// //                       cursor: "pointer",
// //                     }}
// //                   >
// //                     Remove
// //                   </button>
// //                 </div>
// //               ))}
// //               {drivers.length === 0 && (
// //                 <div style={{ fontSize: 14, opacity: 0.7 }}>
// //                   No drivers yet. Add your first driver above.
// //                 </div>
// //               )}
// //             </div>
// //           </div>
// //         )}

// //         {tab === "routes" && (
// //           <div style={{ maxWidth: 1100, margin: "0 auto" }}>
// //             <h2 style={{ marginTop: 0 }}>Route Allocation</h2>
// //             {err && (
// //               <div
// //                 style={{
// //                   color: "#b00020",
// //                   fontWeight: 600,
// //                   marginBottom: 8,
// //                 }}
// //               >
// //                 {err}
// //               </div>
// //             )}
// //             <div style={{ overflowX: "auto" }}>
// //               <table
// //                 style={{
// //                   width: "100%",
// //                   borderCollapse: "collapse",
// //                   background: "#fff",
// //                   borderRadius: 12,
// //                   overflow: "hidden",
// //                 }}
// //               >
// //                 <thead>
// //                   <tr
// //                     style={{
// //                       background: "#f9fafb",
// //                       borderBottom: "1px solid #e5e7eb",
// //                     }}
// //                   >
// //                     <th style={{ padding: 8, textAlign: "left" }}>#</th>
// //                     <th style={{ padding: 8, textAlign: "left" }}>Vehicle</th>
// //                     <th style={{ padding: 8, textAlign: "left" }}>Route</th>
// //                     <th style={{ padding: 8, textAlign: "left" }}>Driver</th>
// //                     <th style={{ padding: 8, textAlign: "left" }}>Auto-pilot</th>
// //                     <th style={{ padding: 8, textAlign: "left" }} />
// //                   </tr>
// //                 </thead>
// //                 <tbody>
// //                   {VEHICLES.map((veh, idx) => {
// //                     const base = getBaseAssignForVehicle(veh);
// //                     const local = localAssign[veh] || base;
// //                     const hasAssignment =
// //                       !!base.routeCode && !!base.driverId; // existing saved row?
// //                     const isEditing = !!editing[veh];
// //                     const locked = hasAssignment && !isEditing;
// //                     const saving = busySave === veh;

// //                     return (
// //                       <tr
// //                         key={veh}
// //                         style={{ borderBottom: "1px solid #f3f4f6" }}
// //                       >
// //                         <td style={{ padding: 8 }}>{idx + 1}</td>
// //                         <td style={{ padding: 8, fontWeight: 600 }}>{veh}</td>
// //                         <td style={{ padding: 8 }}>
// //                           <select
// //                             value={local.routeCode}
// //                             onChange={(e) =>
// //                               updateLocalAssign(veh, {
// //                                 routeCode: e.target.value,
// //                               })
// //                             }
// //                             disabled={locked || saving}
// //                             style={{
// //                               padding: 8,
// //                               borderRadius: 8,
// //                               border: "1px solid #d1d5db",
// //                               fontSize: 13,
// //                               minWidth: 180,
// //                               backgroundColor: locked ? "#f9fafb" : "white",
// //                             }}
// //                           >
// //                             <option value="">Select route…</option>
// //                             {ADMIN_ROUTES.map((r) => (
// //                               <option key={r.code} value={r.code}>
// //                                 {r.label}
// //                               </option>
// //                             ))}
// //                           </select>
// //                         </td>
// //                         <td style={{ padding: 8 }}>
// //                           <select
// //                             value={local.driverId}
// //                             onChange={(e) =>
// //                               updateLocalAssign(veh, {
// //                                 driverId: e.target.value,
// //                               })
// //                             }
// //                             disabled={locked || saving}
// //                             style={{
// //                               padding: 8,
// //                               borderRadius: 8,
// //                               border: "1px solid #d1d5db",
// //                               fontSize: 13,
// //                               minWidth: 180,
// //                               backgroundColor: locked ? "#f9fafb" : "white",
// //                             }}
// //                           >
// //                             <option value="">Select driver…</option>
// //                             {drivers.map((d) => (
// //                               <option key={d._id} value={d._id}>
// //                                 {d.name} ({d.phoneNumber || d.username})
// //                               </option>
// //                             ))}
// //                           </select>
// //                         </td>
// //                         <td style={{ padding: 8 }}>
// //                           <label
// //                             style={{
// //                               display: "inline-flex",
// //                               alignItems: "center",
// //                               gap: 6,
// //                               fontSize: 13,
// //                               opacity: locked ? 0.6 : 1,
// //                             }}
// //                           >
// //                             <input
// //                               type="checkbox"
// //                               checked={!!local.autopilotEnabled}
// //                               onChange={(e) =>
// //                                 updateLocalAssign(veh, {
// //                                   autopilotEnabled: e.target.checked,
// //                                 })
// //                               }
// //                               disabled={locked || saving}
// //                             />
// //                             On
// //                           </label>
// //                         </td>
// //                         <td style={{ padding: 8 }}>
// //                           <button
// //                             onClick={() => {
// //                               if (locked) {
// //                                 // switch to edit mode
// //                                 updateLocalAssign(veh, {}); // ensure local copy
// //                                 setEditing((prev) => ({
// //                                   ...prev,
// //                                   [veh]: true,
// //                                 }));
// //                               } else {
// //                                 // save current data
// //                                 handleSaveAssignment(veh);
// //                               }
// //                             }}
// //                             disabled={saving}
// //                             style={{
// //                               padding: "6px 12px",
// //                               borderRadius: 8,
// //                               border: "1px solid #0D47A1",
// //                               background: saving
// //                                 ? "#e5e7eb"
// //                                 : locked
// //                                 ? "#0D47A1"
// //                                 : "#0D47A1",
// //                               color: saving ? "#4b5563" : "#fff",
// //                               fontSize: 13,
// //                               fontWeight: 700,
// //                               cursor: saving ? "not-allowed" : "pointer",
// //                             }}
// //                           >
// //                             {saving
// //                               ? "Saving…"
// //                               : locked
// //                               ? "Edit"
// //                               : "Save"}
// //                           </button>
// //                         </td>
// //                       </tr>
// //                     );
// //                   })}
// //                 </tbody>
// //               </table>
// //             </div>
// //           </div>
// //         )}

// //         {tab === "live" && (
// //           <div style={{ height: "calc(100vh - 40px)" }}>
// //             <h2 style={{ marginTop: 0 }}>Live Tracking</h2>
// //             <div style={{ height: "calc(100% - 36px)" }}>
// //               <MapContainer
// //                 center={center}
// //                 zoom={DEFAULT_ZOOM}
// //                 maxZoom={22}
// //                 style={{ width: "100%", height: "100%" }}
// //               >
// //                 <TileLayer
// //                   attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
// //                   url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
// //                   maxNativeZoom={19}
// //                   maxZoom={22}
// //                 />
// //                 {liveBuses.map((bus) => {
// //                   // const label = vehicleLabel(bus.vehicleno);
// //                   const rc = assignments[bus.vehicleno]?.routeCode;   // admin assignments map is row objects
// //                   const label = vehicleLabel(bus.vehicleno, rc);
// //                   const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
// //                   const drv = driverByVehicle[bus.vehicleno];
// //                   return (
// //                     <Marker
// //                       key={bus.vehicleno}
// //                       position={{
// //                         lat: bus.latitude,
// //                         lng: bus.longitude,
// //                       }}
// //                       // icon={createVehicleDivIcon(
// //                       //   label,
// //                       //   Number(bus.heading) || 0
// //                       // )}
// //                       icon={createVehicleDivIcon(label, Number(bus.heading) || 0, palette)}
// //                     >
// //                       <Popup>
// //                         <div className="info-window">
// //                           <h4>
// //                             {bus.vehicleno} ({label})
// //                           </h4>
// //                           <p>
// //                             <strong>Speed:</strong>{" "}
// //                             {Number.isFinite(bus.speed)
// //                               ? bus.speed.toFixed(1)
// //                               : "N/A"}{" "}
// //                             km/h
// //                           </p>
// //                           {drv && (
// //                             <>
// //                               <p>
// //                                 <strong>Driver:</strong> {drv.name}</p>
// //                               <p>
// //                                 <strong>Phone:</strong>{" "}
// //                                 {drv.phoneNumber || "—"}
// //                               </p>
// //                             </>
// //                           )}
// //                           {!drv && (
// //                             <p style={{ fontSize: 13, opacity: 0.7 }}>
// //                               No driver assigned
// //                             </p>
// //                           )}
// //                         </div>
// //                       </Popup>
// //                     </Marker>
// //                   );
// //                 })}
// //               </MapContainer>
// //             </div>
// //           </div>
// //         )}
// //       </div>
// //     </div>
// //   );
// // }

// // function RecenterMap({ center, zoom }) {
// //   const map = useMap();

// //   useEffect(() => {
// //     if (!center) return;
// //     map.setView([center.lat, center.lng], zoom ?? map.getZoom(), { animate: true });
// //   }, [center, zoom, map]);

// //   return null;
// // }



// // /* ---------------------------- MAP PAGE ---------------------------- */
// // function MapPage() {
// //   const [buses, setBuses] = useState([]);
// //   const [userLocation, setUserLocation] = useState(null);
// //   const [socketStatus, setSocketStatus] = useState('disconnected');
// //   const [roads, setRoads] = useState(null);
// //   const [filterType, setFilterType] = useState('all');
// //   const [mapCenter, setMapCenter] = useState(center);
// //   const [journeyPlan, setJourneyPlan] = useState(null);
// //   const [locError, setLocError] = useState("");
// //   const [geoState, setGeoState] = useState("unknown"); // "granted" | "prompt" | "denied" | "unknown"
// //   const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(true);
// //   const headerRef = useRef(null);
// //   const [headerHeight, setHeaderHeight] = useState(0);

// //   // maintenance banner state
// //   const [maintenance, setMaintenance] = useState(false);
// //   const [maintenanceReason, setMaintenanceReason] = useState('');
// //   const lastUpdateRef = useRef(0);
// //     // ---- Backend ETA (JWT) state ----
// //   const [etaToken, setEtaToken] = useState(null);    // JWT for /api/eta/*
// //   const [stopEtas, setStopEtas] = useState({});      // { [stopId]: { data, error } }

// //   const etaTokenRef = useRef(null);
// //   const etaTokenFetchingRef = useRef(false);

// //   const flatRoadLinesRef = useRef([]);
// //   const lastSmoothedRef = useRef(new Map());

// //   // ---- Schedule Modal state ----
// //   const [showSchedule, setShowSchedule] = useState(false);
// //   const [activeTab, setActiveTab] = useState(0);

// //   // --- responsive helpers ---
// //   const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 480);
// //   const clamp = (min, pref, max) => `clamp(${min}, ${pref}, ${max})`;
// //   const pad = isNarrow ? 12 : 16;
// //   const gap = isNarrow ? 8  : 12;
// //   const chipFS = isNarrow ? 12 : 13;

// //   useEffect(() => {
// //     const onResize = () => setIsNarrow(window.innerWidth <= 480);
// //     window.addEventListener('resize', onResize);
// //     return () => window.removeEventListener('resize', onResize);
// //   }, []);

// //     // ---- Helper: fetch / refresh JWT for ETA API ----
// //   const fetchEtaToken = useCallback(async () => {
// //     if (!BACKEND || !API_KEY) return null;

// //     // Avoid spamming if many 401s come at once
// //     if (etaTokenFetchingRef.current) {
// //       return etaTokenRef.current;
// //     }

// //     etaTokenFetchingRef.current = true;
// //     try {
// //       const res = await axios.post(
// //         `${BACKEND}/api/auth/token`,
// //         {},
// //         {
// //           timeout: 5000,
// //           headers: {
// //             Authorization: `Bearer ${API_KEY}`, // apiKeyAuth middleware
// //           },
// //         }
// //       );
// //       const t = res.data?.token;
// //       if (t) {
// //         etaTokenRef.current = t;
// //         setEtaToken(t);
// //         return t;
// //       }
// //       return null;
// //     } catch (e) {
// //       console.warn('Failed to fetch ETA JWT token:', e?.message || e);
// //       return null;
// //     } finally {
// //       etaTokenFetchingRef.current = false;
// //     }
// //   }, []);

// //     // ---- Get initial JWT token for ETA API ----
// //   useEffect(() => {
// //     fetchEtaToken();
// //   }, [fetchEtaToken]);

// //     // ---- Poll backend ETA for every stop every 5 seconds ----
// //   useEffect(() => {
// //     if (!BACKEND) return;

// //     let cancelled = false;

// //     async function fetchAllStopEtas() {
// //       // If we don't have a token yet, try to get one
// //       if (!etaTokenRef.current) {
// //         await fetchEtaToken();
// //         if (!etaTokenRef.current || cancelled) return;
// //       }

// //       try {
// //         const results = await Promise.all(
// //           busStops.map(async (s) => {
// //             try {
// //               const res = await axios.get(
// //                 `${BACKEND}/api/eta/stop/${s.id}`,
// //                 {
// //                   timeout: 8000,
// //                   headers: {
// //                     Authorization: `Bearer ${etaTokenRef.current}`,
// //                   },
// //                 }
// //               );
// //               return { stopId: s.id, data: res.data, error: null };
// //             } catch (err) {
// //               // If token expired, refresh in background
// //               if (err?.response?.status === 401) {
// //                 console.warn('ETA JWT expired for stop', s.id, '— refreshing token');
// //                 fetchEtaToken().catch(() => {});
// //               } else {
// //                 console.warn(
// //                   'ETA error for stop',
// //                   s.id,
// //                   err?.response?.status || '',
// //                   err?.message || err
// //                 );
// //               }
// //               return { stopId: s.id, data: null, error: true };
// //             }
// //           })
// //         );

// //         if (cancelled) return;

// //         setStopEtas((prev) => {
// //           const next = { ...prev };
// //           for (const r of results) {
// //             next[r.stopId] = { data: r.data, error: r.error };
// //           }
// //           return next;
// //         });
// //       } catch (e) {
// //         if (!cancelled) {
// //           console.warn('ETA batch fetch failed:', e?.message || e);
// //         }
// //       }
// //     }

// //     // first call + interval
// //     fetchAllStopEtas();
// //     const id = setInterval(fetchAllStopEtas, 5000);

// //     return () => {
// //       cancelled = true;
// //       clearInterval(id);
// //     };
// //   }, [fetchEtaToken]);


// //   // ---- stable socket + pause flag ----
// //   const socketRef = useRef(null);
// //   const ignoreUpdatesRef = useRef(false);

// //   // ---- assignments (vehicle -> routeCode) ----
// //   const [assignments, setAssignments] = useState({});
// //   async function refreshAssignments() {
// //     try {
// //       const r = await axios.get(`${BACKEND}/api/vehicle-route/active`);
// //       const map = {};
// //       for (const row of r.data?.rows || []) map[row.vehicleNo] = row.routeCode;
// //       setAssignments(map);
// //     } catch {}
// //   }
// //   useEffect(() => { refreshAssignments(); const t = setInterval(refreshAssignments, 60000); return () => clearInterval(t); }, []);

// //   // Load roads
// //   useEffect(() => {
// //     let cancelled = false;
// //     (async () => {
// //       try {
// //         const r = await fetch(ROADS_URL);
// //         if (!cancelled && r.ok) setRoads(await r.json());
// //       } catch (e) {
// //         if (!cancelled) console.warn('Failed to load roads:', e?.message || e);
// //       }
// //     })();
// //     return () => { cancelled = true; };
// //   }, []);

// //   // Flatten roads to LineStrings
// //   useEffect(() => {
// //     if (!roads) return;
// //     const lines = [];
// //     turf.flattenEach(roads, (feat) => {
// //       if (feat.geometry?.type === 'LineString') lines.push(feat);
// //     });
// //     flatRoadLinesRef.current = lines;
// //   }, [roads]);

// //   // // Geolocation
// //   // useEffect(() => {
// //   //   if ('geolocation' in navigator) {
// //   //     navigator.geolocation.getCurrentPosition(
// //   //       (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
// //   //       (err) => console.warn('Geolocation error:', err?.message),
// //   //       { enableHighAccuracy: true, timeout: 5000 }
// //   //     );
// //   //   }
// //   // }, []);

// //   useEffect(() => {
// //   let perm;
// //   (async () => {
// //     try {
// //       if (!navigator.permissions?.query) return;
// //       perm = await navigator.permissions.query({ name: "geolocation" });
// //       setGeoState(perm.state);
// //       perm.onchange = () => setGeoState(perm.state);
// //     } catch {
// //       // ignore
// //     }
// //   })();
// //   return () => {
// //     if (perm) perm.onchange = null;
// //   };
// // }, []);


// // //   const requestUserLocation = useCallback(() => {
// // //   setLocError("");

// // //   if (!("geolocation" in navigator)) {
// // //     setLocError("Geolocation not supported on this device/browser.");
// // //     return;
// // //   }

// // //   navigator.geolocation.getCurrentPosition(
// // //     (pos) => {
// // //       const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
// // //       setUserLocation(loc);
// // //       setMapCenter(loc); // ✅ recenter map here
// // //       setLocError("");
// // //     },
// // //     (err) => {
// // //       console.warn("Geolocation error:", err?.message || err);
// // //       // If user didn't allow, browser will show prompt only if not blocked.
// // //       setLocError("Please allow location to center the map near you.");
// // //     },
// // //     { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
// // //   );
// // // }, []);

// // const requestUserLocation = useCallback(() => {
// //   setLocError("");

// //   if (!("geolocation" in navigator)) {
// //     setLocError("Geolocation not supported on this device/browser.");
// //     return;
// //   }

// //   navigator.geolocation.getCurrentPosition(
// //     (pos) => {
// //       const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
// //       setUserLocation(loc);
// //       setMapCenter(loc);
// //       setLocError("");
// //     },
// //     (err) => {
// //       // err.code === 1 => PERMISSION_DENIED
// //       if (err?.code === 1) {
// //         // If browser permission is truly "denied", it won't show popup again.
// //         setLocError(
// //           "Location is blocked in browser settings. Click the 🔒 icon near the URL → Site settings → Location → Allow (or Ask), then refresh."
// //         );
// //       } else {
// //         setLocError(err?.message || "Unable to fetch location.");
// //       }
// //     },
// //     { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
// //   );
// // }, []);


// // useEffect(() => {
// //   // ✅ On every page open (and after browser refresh), request location and recenter
// //   requestUserLocation();
// // }, [requestUserLocation]);


// //   // Initial fetch
// //   useEffect(() => {
// //     if (!BACKEND) {
// //       setMaintenance(true);
// //       setMaintenanceReason('Server URL not configured');
// //       return;
// //     }
// //     axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 })
// //       .then((res) => {
// //         const arr = Array.isArray(res.data) ? res.data : [];
// //         const byVeh = new Map();
// //         for (const d of arr) {
// //           const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
// //           byVeh.set(d.vehicleno, {
// //             vehicleno: d.vehicleno,
// //             latitude: d.latitude,
// //             longitude: d.longitude,
// //             speed: d.speed,
// //             heading
// //           });
// //         }
// //         const list = [...byVeh.values()];
// //         setBuses(processVehicles(list));
// //         lastUpdateRef.current = Date.now();
// //       })
// //       .catch((err) => {
// //         console.error('Initial fetch error:', err?.message || err);
// //         setMaintenance(true);
// //         setMaintenanceReason('Unable to fetch live data');
// //       });
// //   }, []);

// //   // ---- WebSocket packet handler ----
// //   const onPacket = (data) => {
// //     if (ignoreUpdatesRef.current) return;
// //     const incoming = {
// //       vehicleno: data.vehicleno,
// //       latitude: data.latitude,
// //       longitude: data.longitude,
// //       speed: data.speed,
// //       heading: Number.isFinite(data?.heading) ? toHeading(data.heading) : toHeading(data?.direction)
// //     };

// //     setBuses((prev) => {
// //       const nextByVeh = new Map(prev.map((b) => [b.vehicleno, b]));
// //       const processed = processVehicles([incoming], nextByVeh);
// //       const merged = new Map(prev.map((b) => [b.vehicleno, b]));
// //       for (const p of processed) merged.set(p.vehicleno, p);
// //       return [...merged.values()];
// //     });

// //     lastUpdateRef.current = Date.now();
// //   };

// //   // Live updates (Socket.IO)
// //   useEffect(() => {
// //     if (!BACKEND) return;

// //     setSocketStatus('connecting');
// //     const socket = io(BACKEND, {
// //       transports: ['websocket', 'polling'],
// //       reconnection: true,
// //       reconnectionAttempts: Infinity,
// //       reconnectionDelay: 1000,
// //       timeout: 10000,
// //       withCredentials: false
// //     });
// //     socketRef.current = socket;

// //     socket.on('connect', () => setSocketStatus('connected'));
// //     socket.on('disconnect', () => setSocketStatus('disconnected'));
// //     socket.on('connect_error', (e) => {
// //       console.warn('Socket connect_error:', e?.message || e);
// //       setSocketStatus('disconnected');
// //     });

// //     socket.on('new-bus-data', onPacket);

// //     return () => {
// //       socket.off('new-bus-data', onPacket);
// //       socket.disconnect();
// //       socketRef.current = null;
// //     };
// //   }, [roads]); // re-connect only if roads change

// //   // Staleness / maintenance detector
// //   useEffect(() => {
// //     const t = setInterval(() => {
// //       const sinceMs = Date.now() - lastUpdateRef.current;
// //       const stale = sinceMs > STALE_MS;
// //       const downAndEmpty = socketStatus === 'disconnected' && buses.length === 0;

// //       if (stale || downAndEmpty) {
// //         const mins = Math.floor(sinceMs / 60000);
// //         const secs = Math.floor((sinceMs % 60000) / 1000);
// //         setMaintenance(true);
// //         setMaintenanceReason(
// //           stale ? `No new data for ${mins}m ${secs}s` : 'Live connection unavailable'
// //         );
// //       } else {
// //         setMaintenance(false);
// //         setMaintenanceReason('');
// //       }
// //     }, 5000);
// //     return () => clearInterval(t);
// //   }, [socketStatus, buses.length]);

// //   // ---- Processing pipeline: smooth then snap ----
// //   function snapToRoad(coord, flatLines) {
// //     if (!flatLines || flatLines.length === 0) return null;
// //     const pt = turf.point([coord.lng, coord.lat]);

// //     let best = null;
// //     let bestDist = Infinity;

// //     for (const line of flatLines) {
// //       const np = turf.nearestPointOnLine(line, pt, { units: 'meters' });
// //       const dist = np?.properties?.dist;
// //       if (Number.isFinite(dist) && dist < bestDist) {
// //         bestDist = dist;
// //         const [lng, lat] = np.geometry.coordinates;
// //         best = { lat, lng, distM: dist };
// //       }
// //     }
// //     return best;
// //   }

// //   function processVehicles(list, prevMapOpt) {
// //     const flatLines = flatRoadLinesRef.current;
// //     const last = lastSmoothedRef.current;
// //     const result = [];

// //     for (const d of list) {
// //       const id = d.vehicleno;
// //       const raw = { lat: d.latitude, lng: d.longitude };

// //       const prev = last.get(id);
// //       const smoothedCoord = smoothCoord(prev?.coord, raw, SMOOTH_ALPHA);
// //       const smoothedHeading = smoothHeading(prev?.heading, d.heading, HEADING_ALPHA);

// //       const snapped = snapToRoad(smoothedCoord, flatLines);

// //       const use = snapped?.distM != null && snapped.distM <= SNAP_MAX_DIST_M
// //         ? { lat: snapped.lat, lng: snapped.lng }
// //         : smoothedCoord;

// //       last.set(id, { coord: use, heading: smoothedHeading });

// //       result.push({
// //         vehicleno: d.vehicleno,
// //         latitude: use.lat,
// //         longitude: use.lng,
// //         speed: d.speed,
// //         heading: smoothedHeading
// //       });
// //     }

// //     if (prevMapOpt) {
// //       for (const [id, b] of prevMapOpt) {
// //         if (list.find(x => x.vehicleno === id)) continue;
// //         const prev = last.get(id);
// //         if (!prev) {
// //           result.push(b);
// //           continue;
// //         }
// //         result.push({
// //           vehicleno: id,
// //           latitude: prev.coord.lat,
// //           longitude: prev.coord.lng,
// //           speed: b.speed,
// //           heading: prev.heading
// //         });
// //       }
// //     }

// //     return result;
// //   }

// //     // ---------------- Route geometry + ETA helpers ----------------
// //   const stopById = useMemo(() => Object.fromEntries(busStops.map(s => [s.id, s])), []);
// //   function routePolyline(ids) {
// //     return ids.map(id => ({ lat: stopById[id].lat, lng: stopById[id].lng }));
// //   }
// //   function cumDist(poly) {
// //     const out = [0];
// //     for (let i = 1; i < poly.length; i++) out.push(out[i - 1] + mBetween(poly[i - 1], poly[i]));
// //     return out;
// //   }
// //   const routeGeom = useMemo(() => {
// //     const g = {};
// //     for (const r of ROUTES) {
// //       const poly = routePolyline(r.ids);
// //       g[r.code] = { poly, cum: cumDist(poly), ids: r.ids };
// //     }
// //     return g;
// //   }, [stopById]);

// //   function projectOnRoute(pt, poly) {
// //     if (!poly || poly.length < 2) return null;
// //     let best = null;
// //     for (let i = 0; i < poly.length - 1; i++) {
// //       const seg = turf.lineString([[poly[i].lng, poly[i].lat], [poly[i + 1].lng, poly[i + 1].lat]]);
// //       const p = turf.nearestPointOnLine(seg, turf.point([pt.lng, pt.lat]), { units: "meters" });
// //       const d = p.properties?.dist ?? Infinity;
// //       if (!best || d < best.dist) {
// //         const [lng, lat] = p.geometry.coordinates;
// //         best = { idx: i, point: { lat, lng }, dist: d };
// //       }
// //     }
// //     return best;
// //   }

// //   function distanceAlong(poly, cum, proj, targetIndex) {
// //     const dHead = mBetween(proj.point, poly[proj.idx + 1]);
// //     const dTail = cum[targetIndex] - cum[proj.idx + 1];
// //     return dHead + Math.max(0, dTail);
// //   }

// //   // // ---- ETA constants: match backend ----
// //   // const AVG_SPEED_KMPH = 20;
// //   // const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;
// //   // const WAIT_RADIUS_M = 25;         // within 25m of first stop
// //   // const WAIT_SPEED_KMPH_MAX = 1.0;  // consider "waiting" if <= 1 km/h

// //   // // Constant-speed ETA (20 km/h), ignoring live speed
// //   // function etaFromMeters(distanceM) {
// //   //   const seconds = distanceM / AVG_SPEED_MPS;
// //   //   const minutes = Math.round(seconds / 60);
// //   //   return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
// //   // }

// //   // function nextStopIndex(routeIds, projIdx) {
// //   //   return Math.min(routeIds.length - 1, projIdx + 1);
// //   // }

// //   // // For a given bus, find nearest upcoming stop on its route + ETA
// //   // function nearestStopEtaForBusOnItsRoute(bus) {
// //   //   const routeCode = assignments[bus.vehicleno];
// //   //   if (!routeCode) return null;
// //   //   const geom = routeGeom[routeCode];
// //   //   if (!geom || geom.poly.length < 2) return null;

// //   //   const pt = { lat: bus.latitude, lng: bus.longitude };
// //   //   const proj = projectOnRoute(pt, geom.poly);
// //   //   if (!proj) return null;

// //   //   const firstStopId = geom.ids[0];
// //   //   const firstStop = stopById[firstStopId];
// //   //   let waitingAtFirstStop = false;
// //   //   if (firstStop) {
// //   //     const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
// //   //     const speed = Number(bus.speed);
// //   //     if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
// //   //       waitingAtFirstStop = true;
// //   //     }
// //   //   }

// //   //   const nextIdx = nextStopIndex(geom.ids, proj.idx);
// //   //   const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

// //   //   let etaMin = undefined;
// //   //   if (!waitingAtFirstStop) {
// //   //     etaMin = etaFromMeters(distanceM);
// //   //   }

// //   //   return {
// //   //     stopId: geom.ids[nextIdx],
// //   //     distanceM,
// //   //     etaMin,
// //   //     routeCode,
// //   //     waitingAtFirstStop,
// //   //   };
// //   // }

// //     // ---- ETA constants: match backend ----
// //   const AVG_SPEED_KMPH = 20;
// //   const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;

// //   // holding detection at ANY stop
// //   const HOLD_STOP_RADIUS_M = 30;
// //   const HOLD_SPEED_KMPH_MAX = 1.5;

// //   // Constant-speed ETA (20 km/h), ignoring live speed
// //   function etaFromMeters(distanceM) {
// //     const seconds = distanceM / AVG_SPEED_MPS;
// //     const minutes = Math.round(seconds / 60);
// //     return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
// //   }

// //   function nextStopIndex(routeIds, projIdx) {
// //     return Math.min(routeIds.length - 1, projIdx + 1);
// //   }

// //   function detectHoldingStopOnRoute(bus, geom) {
// //     const pt = { lat: bus.latitude, lng: bus.longitude };
// //     const speed = Number(bus.speed);

// //     let bestIdx = -1;
// //     let bestDist = Infinity;

// //     for (let i = 0; i < geom.ids.length; i++) {
// //       const s = stopById[geom.ids[i]];
// //       if (!s) continue;

// //       const dist = mBetween(pt, { lat: s.lat, lng: s.lng });
// //       if (dist < bestDist) {
// //         bestDist = dist;
// //         bestIdx = i;
// //       }
// //     }

// //     if (bestIdx < 0) {
// //       return {
// //         isHolding: false,
// //         stopId: null,
// //         stopIndex: -1,
// //         distToStop: Infinity
// //       };
// //     }

// //     const slowOrUnknown =
// //       !Number.isFinite(speed) || speed <= HOLD_SPEED_KMPH_MAX;

// //     const isHolding =
// //       bestDist <= HOLD_STOP_RADIUS_M && slowOrUnknown;

// //     return {
// //       isHolding,
// //       stopId: geom.ids[bestIdx],
// //       stopIndex: bestIdx,
// //       distToStop: bestDist
// //     };
// //   }

// //   // For a given bus, find nearest stop ETA on its route
// //   function nearestStopEtaForBusOnItsRoute(bus) {
// //     const routeCode = assignments[bus.vehicleno];
// //     if (!routeCode) return null;

// //     const geom = routeGeom[routeCode];
// //     if (!geom || geom.poly.length < 2) return null;

// //     const pt = { lat: bus.latitude, lng: bus.longitude };
// //     const proj = projectOnRoute(pt, geom.poly);
// //     if (!proj) return null;

// //     const holdInfo = detectHoldingStopOnRoute(bus, geom);

// //     // If bus is standing at a stop, show that stop as current stop with ETA 0
// //     if (holdInfo.isHolding) {
// //       return {
// //         stopId: holdInfo.stopId,
// //         distanceM: 0,
// //         etaMin: 0,
// //         routeCode,
// //         waitingAtStop: true
// //       };
// //     }

// //     const nextIdx = nextStopIndex(geom.ids, proj.idx);
// //     const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

// //     return {
// //       stopId: geom.ids[nextIdx],
// //       distanceM,
// //       etaMin: etaFromMeters(distanceM),
// //       routeCode,
// //       waitingAtStop: false
// //     };
// //   }

// //   // For a given stop, find the best incoming vehicle + ETA
// //   // function nextArrivalAtStop(stopId) {
// //   //   const candidates = [];

// //   //   for (const bus of buses) {
// //   //     if (startsWithDigit(bus.vehicleno)) continue;
// //   //     const rc = assignments[bus.vehicleno];
// //   //     if (!rc) continue;
// //   //     const geom = routeGeom[rc];
// //   //     if (!geom) continue;

// //   //     const indexInRoute = geom.ids.indexOf(stopId);
// //   //     if (indexInRoute < 0) continue;

// //   //     const pt = { lat: bus.latitude, lng: bus.longitude };
// //   //     const proj = projectOnRoute(pt, geom.poly);
// //   //     if (!proj) continue;

// //   //     const targetIndex = Math.max(indexInRoute, proj.idx + 1);
// //   //     if (targetIndex >= geom.ids.length) continue;

// //   //     const distanceM = distanceAlong(geom.poly, geom.cum, proj, targetIndex);

// //   //     // If bus is already past this stop, skip
// //   //     if (distanceM < 0) continue;

// //   //     const firstStopId = geom.ids[0];
// //   //     const firstStop = stopById[firstStopId];
// //   //     let waitingAtFirstStop = false;
// //   //     if (firstStop) {
// //   //       const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
// //   //       const speed = Number(bus.speed);
// //   //       if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
// //   //         waitingAtFirstStop = true;
// //   //       }
// //   //     }

// //   //     let etaMin = undefined;
// //   //     if (!waitingAtFirstStop) {
// //   //       etaMin = etaFromMeters(distanceM);
// //   //     }

// //   //     candidates.push({
// //   //       vehicle: bus.vehicleno,
// //   //       routeCode: rc,
// //   //       etaMin,
// //   //       waitingAtFirstStop,
// //   //       distanceM,
// //   //     });
// //   //   }

// //   //   if (!candidates.length) return null;

// //   //   // Prefer moving vehicles (etaMin != null), then fallback to closest waiting one
// //   //   const moving = candidates.filter(c => c.etaMin != null);
// //   //   if (moving.length) {
// //   //     return moving.reduce((a, b) => (a.etaMin <= b.etaMin ? a : b));
// //   //   }

// //   //   // All are waiting at first stop: pick closest along-route
// //   //   return candidates.reduce((a, b) => (a.distanceM <= b.distanceM ? a : b));
// //   // }


// //   // ---- Derived lists ----
// //   const visibleBuses = buses.filter(b => !startsWithDigit(b.vehicleno));
// //   const filteredBuses = visibleBuses.filter(b => matchesFilter(b.vehicleno, filterType, assignments));

// //   // -------------- SCHEDULE UI helpers --------------
// //   const TABS = [
// //     { key: 'hostel-main', title: 'Hostel → Main Gate', data: HOSTEL_TO_MAIN, notes: HOSTEL_NOTES, fmt: 'ampm' },
// //     { key: 'main-hostel', title: 'Main Gate → Hostel', data: MAIN_TO_HOSTEL, notes: MAIN_NOTES, fmt: 'ampm' },
// //     { key: 'vg-main',     title: 'Velachery Gate → Main Gate', data: VELACHERY_TO_MAIN, notes: VELACHERY_NOTES, fmt: 'ampm' },
// //     { key: 'vg-hostel',   title: 'Velachery Gate → Hostel', data: VELACHERY_TO_HOSTEL, notes: VELACHERY_NOTES, fmt: 'ampm' },
// //     { key: 'rp-ed',       title: 'Research Park → ED', data: RP_TO_ED_TIMES, notes: VELACHERY_NOTES, fmt: '24' },
// //     { key: 'ed-rp',       title: 'ED → Research Park', data: ED_TO_RP_TIMES, notes: VELACHERY_NOTES, fmt: '24' }
// //   ];

// //   function TimeChip({ t }) {
// //     return (
// //       <div
// //         style={{
// //           padding: '10px 12px',
// //           borderRadius: 12,
// //           border: '1px solid #e2e8f0',
// //           background: '#fff',
// //           textAlign: 'center',
// //           fontWeight: 700,
// //           fontSize: clamp('12px','2.8vw','14px')
// //         }}
// //       >
// //         {t}
// //       </div>
// //     );
// //   }

// //   function CartTable({ rows, title }) {
// //     const th = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', fontWeight: 700 };
// //     const td = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #f2f2f2' };

// //     return (
// //       <div style={{ marginTop: 8 }}>
// //         <div style={{ fontWeight: 700, margin: '8px 0' }}>{title}</div>
// //         <div style={{ overflowX: 'auto' }}>
// //           <table style={{ width: '100%', borderCollapse: 'collapse' }}>
// //             <thead>
// //               <tr>
// //                 <th style={th}>Cart</th>
// //                 <th style={th}>RP → ED</th>
// //                 <th style={th}>ED → RP</th>
// //               </tr>
// //             </thead>
// //             <tbody>
// //               {rows.map(([Cart, a, b], i) => (
// //                 <tr key={`${Cart}-${i}`}>
// //                   <td style={td}>{Cart}</td>
// //                   <td style={td}>{a}</td>
// //                   <td style={td}>{b}</td>
// //                 </tr>
// //               ))}
// //             </tbody>
// //           </table>
// //         </div>
// //       </div>
// //     );
// //   }

// //   function ScheduleBody({ tab }) {
// //     if (tab.fmt === 'table-Cart') {
// //       const rows1 = Cart1.map(([a, b]) => ['Cart1', a, b]);
// //       const rows3 = Cart3.map(([a, b]) => ['Cart3', a, b]);
// //       return (
// //         <>
// //           <CartTable rows={rows1} title="Cart1" />
// //           <CartTable rows={rows3} title="Cart3" />
// //         </>
// //       );
// //     }
// //     if (tab.fmt === 'table-overall') {
// //       const all = [
// //         ...Cart1.map(([a, b]) => ['Cart1', a, b]),
// //         ...Cart3.map(([a, b]) => ['Cart3', a, b])
// //       ];
// //       return <CartTable rows={all} title="Overall" />;
// //     }

// //     const times = tab.data;
// //     return (
// //       <>
// //         <div
// //           style={{
// //             display: 'grid',
// //             gridTemplateColumns: isNarrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(140px, 1fr))',
// //             gap: 8
// //           }}
// //         >
// //           {times.map((t) => <TimeChip key={t} t={t} />)}
// //         </div>

// //         {tab.notes && tab.notes.length > 0 && (
// //           <div style={{ marginTop: 12 }}>
// //             {tab.notes.map((n) => (
// //               <div
// //                 key={n.time}
// //                 style={{
// //                   fontSize: 13,
// //                   background: '#fff8e1',
// //                   border: '1px solid #f0d48a',
// //                   padding: '6px 10px',
// //                   borderRadius: 10,
// //                   marginTop: 6
// //                 }}
// //               >
// //                 <strong>{n.time}</strong> – {n.note}
// //               </div>
// //             ))}
// //           </div>
// //         )}
// //       </>
// //     );
// //   }

// //   function ScheduleModal() {
// //     const tab = TABS[activeTab];
// //     const tabListRef = useRef(null);
// //     const tabScrollRef = useRef(0);

// //     useLayoutEffect(() => {
// //       const el = tabListRef.current;
// //       if (el && el.scrollLeft !== tabScrollRef.current) {
// //         el.scrollLeft = tabScrollRef.current;
// //       }
// //     });

// //     return (
// //       <div
// //         onClick={() => setShowSchedule(false)}
// //         style={{
// //           position: 'fixed',
// //           inset: 0,
// //           background: 'rgba(0,0,0,0.45)',
// //           zIndex: 99999,
// //           display: 'flex',
// //           alignItems: 'center',
// //           justifyContent: 'center',
// //           padding: 16
// //         }}
// //       >
// //         <div
// //           onClick={(e) => e.stopPropagation()}
// //           style={{
// //             position: 'relative',
// //             width: isNarrow ? '96vw' : 'min(1100px, 96vw)',
// //             maxHeight: '90dvh',
// //             background: '#ffffff',
// //             borderRadius: 16,
// //             boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
// //             display: 'flex',
// //             flexDirection: 'column',
// //             overflow: 'hidden'
// //           }}
// //         >
// //           {/* Header */}
// //           <div
// //             style={{
// //               padding: isNarrow ? '8px 10px' : '12px 16px',
// //               borderBottom: '1px solid #eee',
// //               display: 'flex',
// //               alignItems: 'center',
// //               gap: 12
// //             }}
// //           >
// //             <img src={iitmLogo} alt="IIT Madras" style={{ width: isNarrow ? 26 : 32, height: isNarrow ? 26 : 32 }} />
// //             <div style={{ fontWeight: 800, fontSize: clamp('14px','2.8vw','18px') }}>
// //               IIT Madras – Campus Bus &amp; Cart Schedule
// //             </div>
// //             <button
// //               onClick={() => setShowSchedule(false)}
// //               style={{
// //                 marginLeft: 'auto',
// //                 border: 'none',
// //                 background: '#f4f4f5',
// //                 borderRadius: 999,
// //                 width: 34,
// //                 height: 34,
// //                 cursor: 'pointer',
// //                 fontSize: 16
// //               }}
// //               title="Close"
// //             >
// //               ✕
// //             </button>
// //           </div>

// //           {/* Tab bar */}
// //           <div
// //             ref={tabListRef}
// //             onScroll={(e) => { tabScrollRef.current = e.currentTarget.scrollLeft; }}
// //             style={{
// //               display: 'block',
// //               padding: '8px 12px',
// //               borderBottom: '1px solid #eee',
// //               overflowX: 'auto',
// //               overflowY: 'hidden',
// //               whiteSpace: 'nowrap',
// //               WebkitOverflowScrolling: 'touch',
// //               scrollbarWidth: 'thin',
// //               overscrollBehaviorX: 'contain',
// //               touchAction: 'pan-x'
// //             }}
// //           >
// //             <div style={{ display: 'inline-flex', gap: 8, minWidth: '100%', marginBottom: isNarrow ? '45px' : '18px'}}>
// //               {TABS.map((t, i) => (
// //                 <button
// //                   key={t.key}
// //                   onClick={() => setActiveTab(i)}
// //                   style={{
// //                     whiteSpace: 'nowrap',
// //                     padding: isNarrow ? '8px 10px' : '8px 12px',
// //                     borderRadius: 10,
// //                     border: i === activeTab ? '1px solid #0D47A1' : '1px solid #ddd',
// //                     background: i === activeTab ? '#eaf2ff' : '#ffffff',
// //                     fontWeight: 600,
// //                     cursor: 'pointer',
// //                     flex: '0 0 auto'
// //                   }}
// //                 >
// //                   {t.title}
// //                 </button>
// //               ))}
// //             </div>
// //           </div>

// //           {/* Body */}
// //           <div style={{ padding: isNarrow ? 8 : 12, overflow: 'auto' }}>
// //             <div style={{ fontWeight: 700, marginBottom: 8 }}>{tab.title}</div>
// //             <ScheduleBody tab={tab} />
// //           </div>
// //         </div>
// //       </div>
// //     );
// //   }

// //   // Pause live updates when modal open; refresh once on close, then resume
// //   useEffect(() => {
// //     if (showSchedule) { ignoreUpdatesRef.current = true; return; }
// //     (async () => {
// //       try {
// //         if (!BACKEND) return;
// //         const res = await axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 });
// //         const arr = Array.isArray(res.data) ? res.data : [];
// //         const byVeh = new Map();
// //         for (const d of arr) {
// //           const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
// //           byVeh.set(d.vehicleno, {
// //             vehicleno: d.vehicleno,
// //             latitude: d.latitude,
// //             longitude: d.longitude,
// //             speed: d.speed,
// //             heading
// //           });
// //         }
// //         const list = [...byVeh.values()];
// //         setBuses(processVehicles(list));
// //         lastUpdateRef.current = Date.now();
// //       } catch (e) {
// //         console.warn('Refresh after modal close failed:', e?.message || e);
// //       } finally {
// //         ignoreUpdatesRef.current = false;
// //       }
// //     })();
// //   }, [showSchedule]);

// //   // Responsive stop icons (smaller on phones)
// //   const stopIconInstance = useMemo(
// //     () => createCustomIcon(stopIconImg, isNarrow ? [40, 40] : [50, 50]),
// //     [isNarrow]
// //   );

// //   useLayoutEffect(() => {
// //   const el = headerRef.current;
// //   if (!el) return;

// //   const updateHeaderHeight = () => {
// //     setHeaderHeight(el.getBoundingClientRect().height || 0);
// //   };

// //   updateHeaderHeight();

// //   let resizeObserver;

// //   if (typeof ResizeObserver !== "undefined") {
// //     resizeObserver = new ResizeObserver(updateHeaderHeight);
// //     resizeObserver.observe(el);
// //   }

// //   window.addEventListener("resize", updateHeaderHeight);

// //   return () => {
// //     if (resizeObserver) resizeObserver.disconnect();
// //     window.removeEventListener("resize", updateHeaderHeight);
// //   };
// // }, []);
// //   // --------------------------- RENDER ---------------------------
// //   return (
// //     <div className="map-container">
// //       {/* Header */}
// // {/* Header */}
// // <div
// // ref={headerRef}
// //   style={{
// //     position: 'fixed',
// //     top: 'max(8px, env(safe-area-inset-top))',
// //     left: '50%',
// //     transform: 'translateX(-50%)',
// //     zIndex: 10000,
// //     background: '#ffffff',
// //     border: '1px solid #e6e6e7',
// //     borderRadius: 16,
// //     padding: isHeaderCollapsed ? (isNarrow ? 8 : 10) : pad,
// //     boxShadow: '0 18px 40px rgba(0,0,0,0.25)',
// //     width: isHeaderCollapsed
// //       ? (isNarrow ? 'calc(100vw - 16px)' : 'min(420px, calc(100vw - 32px))')
// //       : (isNarrow ? 'calc(100vw - 16px)' : 'min(560px, calc(100vw - 32px))'),
// //     backdropFilter: 'blur(2px)',
// //     transition: 'width 180ms ease, padding 180ms ease'
// //   }}
// // >
// //   {/* Title row */}
// //   <div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? 8 : 12 }}>
// //     <img
// //       src={iitmLogo}
// //       alt="IIT Madras"
// //       style={{
// //         width: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
// //         height: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
// //         flex: '0 0 auto'
// //       }}
// //     />

// //     <h2
// //       style={{
// //         margin: 0,
// //         fontSize: isHeaderCollapsed
// //           ? clamp('15px', '3vw', '20px')
// //           : clamp('18px', '3.2vw', '28px'),
// //         lineHeight: 1.1,
// //         letterSpacing: 0.3,
// //         fontWeight: 800,
// //         whiteSpace: 'nowrap',
// //         overflow: 'hidden',
// //         textOverflow: 'ellipsis',
// //         flex: 1
// //       }}
// //     >
// //       Moving Mountains
// //     </h2>

// //     {isHeaderCollapsed && (
// //       <div
// //         style={{
// //           fontSize: isNarrow ? 12 : 13,
// //           fontWeight: 800,
// //           color: '#111827',
// //           whiteSpace: 'nowrap'
// //         }}
// //         title="Visible vehicles / total vehicles"
// //       >
// //         {filteredBuses.length}/{buses.length}
// //       </div>
// //     )}

// //     {!isHeaderCollapsed && (
// //       <a
// //         href="/admin"
// //         style={{
// //           fontWeight: 700,
// //           whiteSpace: 'nowrap'
// //         }}
// //       >
// //         Admin ↗
// //       </a>
// //     )}

// //     <button
// //       type="button"
// //       onClick={() => setIsHeaderCollapsed((v) => !v)}
// //       aria-label={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
// //       title={isHeaderCollapsed ? 'Expand' : 'Collapse'}
// //       style={{
// //         border: '1px solid #D1D5DB',
// //         background: '#ffffff',
// //         color: '#111827',
// //         borderRadius: 10,
// //         padding: isNarrow ? '6px 8px' : '7px 10px',
// //         fontWeight: 800,
// //         fontSize: isNarrow ? 13 : 14,
// //         lineHeight: 1,
// //         cursor: 'pointer',
// //         minWidth: isNarrow ? 34 : 38,
// //         minHeight: isNarrow ? 32 : 36
// //       }}
// //     >
// //       {isHeaderCollapsed ? '▾' : '▴'}
// //     </button>
// //   </div>

// //   {!isHeaderCollapsed && (
// //     <>
// //       {/* Status row */}
// //       <div
// //         style={{
// //           display: 'flex',
// //           alignItems: 'center',
// //           justifyContent: 'space-between',
// //           marginTop: 10,
// //           gap: 12,
// //           flexWrap: 'wrap'
// //         }}
// //       >
// //         <span
// //           style={{
// //             display: 'inline-block',
// //             padding: '4px 10px',
// //             borderRadius: 999,
// //             fontSize: chipFS,
// //             fontWeight: 700,
// //             background:
// //               socketStatus === 'connected'
// //                 ? '#E9F9EE'
// //                 : socketStatus === 'connecting'
// //                 ? '#FFF7E1'
// //                 : '#FFECEC',
// //             color:
// //               socketStatus === 'connected'
// //                 ? '#0A7A2A'
// //                 : socketStatus === 'connecting'
// //                 ? '#7A5D00'
// //                 : '#B00020',
// //             border:
// //               socketStatus === 'connected'
// //                 ? '1px solid #C6EBD1'
// //                 : socketStatus === 'connecting'
// //                 ? '1px solid #F4E3B0'
// //                 : '1px solid #FFD0D0',
// //             textTransform: 'lowercase'
// //           }}
// //           title={`Socket: ${socketStatus}`}
// //         >
// //           {socketStatus}
// //         </span>

// //         <div
// //           style={{
// //             fontSize: clamp('14px','2.6vw','18px'),
// //             fontWeight: 700,
// //             letterSpacing: 0.5,
// //             color: '#111827'
// //           }}
// //         >
// //           {filteredBuses.length} / {buses.length}
// //         </div>
// //       </div>

// //       {/* Controls row */}
// //       <div
// //         style={{
// //           display: 'flex',
// //           alignItems: 'center',
// //           gap: gap,
// //           marginTop: gap,
// //           flexWrap: 'wrap'
// //         }}
// //       >
// //         <div style={{ position: 'relative', flex: '1 1 200px' }}>
// //           <select
// //             value={filterType}
// //             onChange={(e) => setFilterType(e.target.value)}
// //             title="Filter vehicles"
// //             style={{
// //               width: '100%',
// //               padding: isNarrow ? '10px 34px 10px 12px' : '10px 40px 10px 14px',
// //               borderRadius: 10,
// //               border: '1px solid #D1D5DB',
// //               background: '#FFFFFF',
// //               fontWeight: 600,
// //               cursor: 'pointer',
// //               appearance: 'none',
// //               WebkitAppearance: 'none',
// //               MozAppearance: 'none'
// //             }}
// //           >
// //             <option value="all">All Vehicles</option>
// //             <option value="cart">Cart</option>
// //             <option value="bus">Bus</option>
// //             <optgroup label="Filter by Route">
// //               {ROUTES.map(r => (
// //                 <option key={r.code} value={`route:${r.code}`}>{r.label}</option>
// //               ))}
// //             </optgroup>
// //           </select>

// //           <div
// //             style={{
// //               position: 'absolute',
// //               right: isNarrow ? 8 : 12,
// //               top: '50%',
// //               transform: 'translateY(-50%)',
// //               width: 0,
// //               height: 0,
// //               borderLeft: '6px solid transparent',
// //               borderRight: '6px solid transparent',
// //               borderTop: '7px solid #111',
// //               pointerEvents: 'none'
// //             }}
// //           />
// //         </div>

// //         <button
// //           onClick={() => {
// //             requestUserLocation();
// //             setTimeout(() => {
// //               window.location.reload();
// //             }, 150);
// //           }}
// //           title="Recenter + Reload"
// //           style={{
// //             padding: '10px 14px',
// //             borderRadius: 10,
// //             border: '1px solid #D1D5DB',
// //             background: '#FFFFFF',
// //             fontWeight: 700,
// //             cursor: 'pointer',
// //             minHeight: isNarrow ? 36 : 40,
// //             fontSize: clamp('13px','2.8vw','14px')
// //           }}
// //         >
// //           Refresh
// //         </button>

// //         <button
// //           onClick={() => setShowSchedule(true)}
// //           title="Show schedule"
// //           style={{
// //             padding: '10px 16px',
// //             borderRadius: 10,
// //             border: '1px solid #0D47A1',
// //             background: '#0D47A1',
// //             color: '#FFFFFF',
// //             fontWeight: 800,
// //             cursor: 'pointer',
// //             boxShadow: '0 6px 16px rgba(13,71,161,0.25)',
// //             minHeight: isNarrow ? 36 : 40,
// //             fontSize: clamp('13px','2.8vw','14px')
// //           }}
// //         >
// //           Schedule
// //         </button>
// //       </div>

// //       {maintenance && (
// //         <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#fff8e1', border: '1px solid #f0d48a', fontWeight: 600 }}>
// //           Under maintenance — {maintenanceReason}
// //         </div>
// //       )}

// //       {locError && (
// //         <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#FFECEC', border: '1px solid #FFD0D0', fontWeight: 600 }}>
// //           {locError}

// //           <button
// //             type="button"
// //             onClick={requestUserLocation}
// //             style={{ marginLeft: 10, padding: '6px 10px', borderRadius: 10, border: '1px solid #D1D5DB', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
// //             title={geoState === "denied" ? "Enable in site settings first, then try again" : "Try again"}
// //           >
// //             {geoState === "denied" ? "Open site settings" : "Allow location"}
// //           </button>
// //         </div>
// //       )}
// //     </>
// //   )}
// // </div>

// // {/* {SHOW_MINT_SUMMIT_PROMO && (
// //   <a
// //     href={MINT_SUMMIT_REGISTRATION_URL}
// //     target="_blank"
// //     rel="noreferrer"
// //     className="mint-summit-floating-banner"
// //     style={{
// //       top: isHeaderCollapsed ? 76 : 310,
// //       width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
// //     }}
// //     title="Register for MInT Mobility Summit 2026"
// //   >
// //     <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
// //     <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
// //     <span className="mint-summit-cta">Register ↗</span>
// //   </a>
// // )} */}

// // {SHOW_MINT_SUMMIT_PROMO && (
// //   <a
// //     href={MINT_SUMMIT_REGISTRATION_URL}
// //     target="_blank"
// //     rel="noreferrer"
// //     className="mint-summit-floating-banner"
// //     style={{
// //       top: headerHeight
// //         ? `calc(max(8px, env(safe-area-inset-top)) + ${headerHeight}px + ${isNarrow ? 8 : 10}px)`
// //         : isHeaderCollapsed
// //         ? 76
// //         : 260,
// //       width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
// //     }}
// //     title="Register for MInT Mobility Summit 2026"
// //   >
// //     <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
// //     <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
// //     <span className="mint-summit-cta">Register ↗</span>
// //   </a>
// // )}

// //       <JourneyPlanner
// //         backend={BACKEND}
// //         userLocation={userLocation}
// //         requestUserLocation={requestUserLocation}
// //         onPlanChange={setJourneyPlan}
// //         onLocationUpdate={(location) => {
// //           setUserLocation(location);
// //         }}
// //       />

// //       {/* Schedule Modal */}
// //       {showSchedule && <ScheduleModal />}

// //       {/* Map uses dynamic viewport on mobile (100dvh) */}
// //       {/* <MapContainer center={center} zoom={DEFAULT_ZOOM} maxZoom={22} style={{ width: '100vw', height: '100dvh' }}> */}
// //       <MapContainer
// //         center={mapCenter}
// //         zoom={DEFAULT_ZOOM}
// //         minZoom={15}
// //         maxZoom={22}
// //         maxBounds={CAMPUS_MAP_BOUNDS}
// //         maxBoundsViscosity={1.0}
// //         worldCopyJump={false}
// //         style={{ width: '100vw', height: '100dvh' }}
// //       >
// //       <RecenterMap center={mapCenter} zoom={DEFAULT_ZOOM} />
// //       <JourneyMapLayer plan={journeyPlan} />

// //         <TileLayer
// //           attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
// //           url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
// //           maxNativeZoom={19}
// //           maxZoom={22}
// //           noWrap={true}
// //         />

// //         {userLocation && (
// //           <Marker position={{ lat: userLocation.lat, lng: userLocation.lng }} icon={userIconInstance}>
// //             <Popup>
// //               <div className="info-window">
// //                 <h4>You are here</h4>
// //               </div>
// //             </Popup>
// //           </Marker>
// //         )}
// //         {/* here i have updated the backend eta */}
// //         {/* Bus Stops with backend ETA (polled every 5s) */}
// //         {busStops.map((stop) => {
// //           const etaState = stopEtas[stop.id];           // { data, error } or undefined
// //           const payload = etaState?.data;               // backend JSON
// //           const next = payload?.next || null;           // { vehicle, goingTowards, etaMinutes } | null

// //           return (
// //             <Marker
// //               key={stop.id}
// //               position={{ lat: stop.lat, lng: stop.lng }}
// //               icon={stopIconInstance}
// //             >
// //               <Popup>
// //                 <div className="info-window">
// //                   <h4>{stop.name}</h4>

// //                   {/* 1) Still loading / first poll */}
// //                   {!etaState && <p>Loading ETA…</p>}

// //                   {/* 2) Request for this stop failed */}
// //                   {etaState && !payload && <p>ETA not available.</p>}

// //                   {/* 3) Backend says: no approaching assignment */}
// //                   {payload && payload.success && !next && (
// //                     <p>No assigned vehicle approaching.</p>
// //                   )}

// //                   {/* 4) We have a next arrival from backend */}
// //                   {payload && payload.success && next && (
// //                     <>
// //                       {/* You said you may not need vehicle number/type, 
// //                           so we only show direction + ETA. */}
// //                       <p>
// //                           Next Vehicle Details:
// //                         </p>
// //                       {next.vehicle && (
// //                         <p>
// //                           <strong>Vehicle Type:</strong> {next.vehicle}
// //                         </p>
// //                       )}
// //                       {next.goingTowards && (
// //                         <p>
// //                           <strong>Direction:</strong> {next.goingTowards}
// //                         </p>
// //                       )}
// //                       {/* <p>
// //                         <strong>ETA:</strong>{" "}
// //                         {next.etaMinutes != null
// //                           ? `${next.etaMinutes} min`
// //                           : "— (has not departed yet)"}
// //                       </p> */}
// //                       <p>
// //   <strong>ETA:</strong>{" "}
// //   {next.etaStatus === "AT_STOP"
// //     ? "0 min (at stop)"
// //     : next.etaMinutes != null
// //     ? `${next.etaMinutes} min`
// //     : "— (has not departed yet)"}
// // </p>
// //                     </>
// //                   )}
// //                 </div>
// //               </Popup>
// //             </Marker>
// //           );
// //         })}



// //         {/* Vehicles */}
// //         {filteredBuses.map((bus) => {
// //           // const label = vehicleLabel(bus.vehicleno);
// //           const rc = assignments[bus.vehicleno];
// //           const label = vehicleLabel(bus.vehicleno, rc);
// //           const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
// //           const info = nearestStopEtaForBusOnItsRoute(bus);
// //           // const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
// //           // const eta = info ? `${info.etaMin} min` : 'Unknown';
// //           const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
// //           const eta =
// //             info && info.etaMin != null
// //               ? `${info.etaMin} min`
// //               : info
// //               ? "—"
// //               : "Unknown";

// //           return (
// //             <Marker
// //               key={bus.vehicleno}
// //               position={{ lat: bus.latitude, lng: bus.longitude }}
// //               // icon={createVehicleDivIcon(label, bus.heading)}
// //               icon={createVehicleDivIcon(label, bus.heading, palette)}

// //             >
// //               <Popup>
// //                 <div className="info-window">
// //                   <h4>{bus.vehicleno} ({label})</h4>
// //                   {/* <p><strong>Legend:</strong> B = Bus, C = Cart</p> */}
// //                   <p><strong>Legend:</strong> B = Bus, C = Cart, CR = Cart (RP↔ED)</p>
// //                   <p><strong>Speed:</strong> {Number.isFinite(bus.speed) ? bus.speed.toFixed(1) : 'N/A'} km/h</p>
// //                   {assignments[bus.vehicleno] && (
// //                     <p><strong>Route:</strong> {ROUTES.find(r=>r.code===assignments[bus.vehicleno])?.label || assignments[bus.vehicleno]}</p>
// //                   )}
// //                   {nearestName && <p><strong>Next Stop:</strong> {nearestName}</p>}
// //                   {/* <p><strong>ETA:</strong> {eta}</p> */}
// //                   {/* <p>
// //                     <strong>ETA:</strong>{" "}
// //                     {eta}
// //                     {info && info.waitingAtFirstStop ? " (has not departed yet)" : ""}
// //                   </p> */}
// //                   <p>
// //   <strong>ETA:</strong>{" "}
// //   {eta}
// //   {info && info.waitingAtStop ? " (waiting at stop)" : ""}
// // </p>
// //                   {Number.isFinite(bus.heading) && <p><strong>Heading:</strong> {Math.round(bus.heading)}°</p>}
// //                 </div>
// //               </Popup>
// //             </Marker>
// //           );
// //         })}
// //       </MapContainer>

// //       {/* Footer: Feedback link */}
// //       <div
// //         style={{
// //           position: 'fixed',
// //           left: 0,
// //           right: 0,
// //           bottom: -5,
// //           display: 'flex',
// //           justifyContent: 'center',
// //           zIndex: 10000,
// //           pointerEvents: 'none',
// //           paddingBottom: 'max(6px, env(safe-area-inset-bottom))'
// //         }}
// //       >
// //         <div
// //           style={{
// //             width: '100vw',
// //             pointerEvents: 'auto',
// //             display: 'flex',
// //             alignItems: 'center',
// //             justifyContent: 'space-between',
// //             gap: 12,
// //             padding: isNarrow ? '6px 10px' : '6px 16px',
// //             borderRadius: 14,
// //             background:
// //               'linear-gradient(135deg, rgba(17,24,39,0.85), rgba(31,41,55,0.85))',
// //             color: '#F9FAFB',
// //             border: '1px solid rgba(255,255,255,0.14)',
// //             boxShadow:
// //               '0 12px 30px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
// //             backdropFilter: 'saturate(160%) blur(10px)',
// //             WebkitBackdropFilter: 'saturate(160%) blur(10px)',
// //             fontFamily:
// //               '"SF Pro Text", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"',
// //             fontSize: clamp('12px','2.8vw','14px'),
// //             lineHeight: 1.4,
// //             fontWeight: 500,
// //             letterSpacing: '0.2px',
// //           }}
// //         >
// //           <div
// //             style={{
// //               display: 'flex',
// //               alignItems: 'center',
// //               gap: 10,
// //               flex: 1,
// //               minWidth: 0,
// //             }}
// //           >
// //             <div
// //               style={{
// //                 width: 8,
// //                 height: 8,
// //                 borderRadius: 9999,
// //                 background:
// //                   'radial-gradient(circle at 30% 30%, #34D399, #059669)',
// //                 boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
// //                 flex: '0 0 auto',
// //               }}
// //             />
// //             <span
// //               style={{
// //                 opacity: 0.95,
// //                 whiteSpace: 'nowrap',
// //                 overflow: 'hidden',
// //                 textOverflow: 'ellipsis',
// //               }}
// //             >
// //                Amit Kumar | CE20B009 | MInT Lab
// //             </span>
// //           </div>

// //           {/* {FEEDBACK_URL ? (
// //             <a
// //               href={FEEDBACK_URL}
// //               target="_blank"
// //               rel="noreferrer"
// //               style={{
// //                 display: 'inline-flex',
// //                 alignItems: 'center',
// //                 justifyContent: 'center',
// //                 padding: '8px 12px',
// //                 borderRadius: 10,
// //                 border: '1px solid rgba(255,255,255,0.18)',
// //                 background:
// //                   'linear-gradient(180deg, rgba(128, 126, 126, 0.14), rgba(221, 212, 212, 0.06))',
// //                 color: '#F9FAFB',
// //                 textDecoration: 'none',
// //                 fontWeight: 600,
// //                 letterSpacing: '0.25px',
// //                 transform: 'translateZ(0)',
// //                 transition: 'transform 120ms ease, opacity 120ms ease',
// //                 cursor: 'pointer',
// //                 boxShadow:
// //                   'inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 14px rgba(0,0,0,0.25)',
// //               }}
// //               onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
// //               onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
// //             >
// //               Open feedback form ↗
// //             </a>
// //           ) : ( */}

// //           {FOOTER_ACTION_URL ? (
// //   <a
// //     href={FOOTER_ACTION_URL}
// //     target="_blank"
// //     rel="noreferrer"
// //     className={
// //       SHOW_MINT_SUMMIT_PROMO
// //         ? 'footer-action-button mint-summit-glitter-button'
// //         : 'footer-action-button'
// //     }
// //     title={FOOTER_ACTION_LABEL}
// //   >
// //     {FOOTER_ACTION_LABEL}
// //   </a>
// // ) : (
// //             <span
// //               style={{
// //                 padding: '8px 12px',
// //                 borderRadius: 10,
// //                 border: '1px dashed rgba(255,255,255,0.25)',
// //                 color: 'rgba(249,250,251,0.8)',
// //                 fontWeight: 500,
// //               }}
// //             >
// //               {/* &lt;form-link&gt; */}
// //               &lt;form-link&gt;
// //             </span>
// //           )}
// //         </div>
// //       </div>

// //     </div>
// //   );
// // }

// // function MaintenancePoster() {
// //   return (
// //     <div className="maintenance-page">
// //       <div className="maintenance-card">
// //         <div className="maintenance-logo-wrap">
// //           <img src={iitmLogo} alt="IIT Madras" className="maintenance-logo" />
// //         </div>

// //         <div className="maintenance-badge">Server migration in progress</div>

// //         <h1>We’ll be back very soon</h1>

// //         <p className="maintenance-main-text">
// //           Hey, I'm Amit Kumar. Thank you for using Moving Mountains. We value your time and effort.
// //           Our server is currently being upgraded to provide a smoother, faster,
// //           and more reliable experience.
// //         </p>

// //         <p className="maintenance-sub-text">
// //           The website will be available again shortly once the migration is
// //           completed. We appreciate your patience and support.
// //         </p>

// //         <div className="maintenance-loader">
// //           <span></span>
// //           <span></span>
// //           <span></span>
// //         </div>

// //         <div className="maintenance-footer">
// //           Thanks for using our service.
// //           <br />
// //           — Team Moving Mountains
// //         </div>
// //       </div>
// //     </div>
// //   );
// // }

// // /* ---------------------------- APP (router) ---------------------------- */

// // export default function App() {
// //   const { path, navigate } = usePath();
// //   const token = getToken();
// //   const adminToken = getAdminToken();

// //   // Track page views on main map for Prometheus
// //   useEffect(() => {
// //     if (!BACKEND) return;
// //     if (path !== "/") return;
// //     axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
// //   }, [path]);

// //   // Forward /driver -> /driverRoute when logged in
// //   useEffect(() => {
// //     if (path === "/driver" && token) {
// //       navigate("/driverRoute");
// //     }
// //   }, [path, token, navigate]);

// //   // Admin redirects
// //   useEffect(() => {
// //     if (path === "/admin/login" && adminToken) {
// //       navigate("/admin");
// //     } else if (path === "/admin" && !adminToken) {
// //       navigate("/admin/login");
// //     }
// //   }, [path, adminToken, navigate]);

// //   if (MAINTENANCE_MODE) {
// //     return <MaintenancePoster />;
// //   }

// //   if (path === "/driver") {
// //     if (token) return null;
// //     return <DriverLogin navigate={navigate} />;
// //   }
// //   if (path === "/driverRoute") return <DriverRoute navigate={navigate} />;

// //   if (path === "/admin/login") {
// //     if (adminToken) return null;
// //     return <AdminLogin navigate={navigate} />;
// //   }
// //   if (path === "/admin") {
// //     return <AdminDashboard navigate={navigate} />;
// //   }

// //   return <MapPage />;
// // }

// // // export default function App() {
// // //   const { path, navigate } = usePath();
// // //   const token = getToken();

// // //   // Track page views on main map for Prometheus
// // //   useEffect(() => {
// // //     if (!BACKEND) return;
// // //     if (path !== "/") return;
// // //     axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
// // //   }, [path]);

// // //   // Forward /driver -> /driverRoute when logged in (no hooks called conditionally)
// // //   useEffect(() => {
// // //     if (path === '/driver' && token) {
// // //       navigate('/driverRoute');
// // //     }
// // //   }, [path, token, navigate]);

// // //   if (path === '/driver') {
// // //     if (token) return null;
// // //     return <DriverLogin navigate={navigate} />;
// // //   }
// // //   if (path === '/driverRoute') return <DriverRoute navigate={navigate} />;
// // //   return <MapPage />;
// // // }

// // // // traveliitm/frontend/src/App.js
// // // // import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
// // // import React, {
// // //   useEffect,
// // //   useState,
// // //   useRef,
// // //   useLayoutEffect,
// // //   useMemo,
// // //   useCallback
// // // } from 'react';

// // // import axios from 'axios';
// // // import { io } from 'socket.io-client';
// // // import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
// // // import L from 'leaflet';
// // // import 'leaflet/dist/leaflet.css';
// // // import './App.css';
// // // import * as turf from '@turf/turf';

// // // // ---- Assets in src/assets ----
// // // import userIcon from './assets/user-icon.png';
// // // import stopIconImg from './assets/bus-stop.png';
// // // import iitmLogo from './assets/iitm-logo.png';

// // // // ---- Backend base URL ----
// // // const BACKEND = process.env.REACT_APP_BACKEND_URL;
// // // const API_KEY = process.env.REACT_APP_API_KEY;

// // // // ---- Temporary maintenance mode ----
// // // // Set this to false after server migration is completed.
// // // const MAINTENANCE_MODE = false;

// // // // ---- Static roads URL (public/roads.geojson) ----
// // // const ROADS_URL = `${process.env.PUBLIC_URL || ''}/roads.geojson`;

// // // // ---- Map zoom param ----
// // // const DEFAULT_ZOOM = 18;

// // // // ---- Smoothing / Snap params ----
// // // const SMOOTH_ALPHA = 0.3;
// // // const HEADING_ALPHA = 0.35;
// // // const SNAP_MAX_DIST_M = 20;

// // // // Staleness threshold for under-maintenance banner
// // // const STALE_MS = 150_000; // ~2.5 minutes

// // // // Initial center (fallback)
// // // const center = { lat: 12.9915, lng: 80.2336 };

// // // // ---------------- STOPS WITH IDS (for route math) ----------------
// // // const busStops = [
// // //   { name: 'Velachery Gate IIT Campus Stop', lat: 12.988763557585395, lng: 80.22367960515129, id: 1 },
// // //   { name: 'NAC2 Stop Towards GC', lat: 12.990236, lng: 80.227548, id: 2 },
// // //   { name: 'Nac2 Towards Velachery Stop', lat: 12.9898636, lng: 80.2271109, id: 3 },
// // //   { name: 'CRC Towards GC Stop', lat: 12.99089, lng: 80.230274, id: 4 },
// // //   { name: 'CRC Towards Velachery Stop', lat: 12.99079680, lng: 80.23016660, id: 5 },  
// // //   { name: 'HSB Stop Towards Velachery Stop', lat: 12.99076780, lng: 80.23168950, id: 6},
// // //   { name: 'HSB Stop Towards GC', lat: 12.991037, lng: 80.232065, id: 7},
// // //   { name: 'KV Stop Towards Main Gate', lat: 12.991868573807471, lng: 80.23367454352295, id: 8 },
// // //   { name: 'KV Stop Towards GC', lat: 12.991910, lng: 80.233785, id: 9 },
// // //   { name: 'Postoffice Stop Towards Main Gate', lat: 12.99388680, lng: 80.23428760, id: 10 },
// // //   { name: 'Postoffice Stop Towards GC', lat: 12.99393340, lng: 80.23459350, id: 11 },
// // //   { name: 'E1 Stop Towards Main Gate', lat: 12.9960173, lng: 80.2359173, id: 12 },
// // //   { name: 'E1 Stop Towards GC', lat: 12.996117, lng: 80.236184, id: 13},
// // //   { name: 'Vana Vani Stop Towards Main Gate', lat: 12.998704, lng: 80.2391799, id: 14 },
// // //   { name: 'Vana Vani Stop Towards GC', lat: 12.999084,   lng:  80.239380, id: 15 },
// // //   { name: 'D1 Stop Towards Main Gate', lat:13.002546, lng: 80.240091, id: 16},
// // //   { name: 'D1 Stop Towards GC', lat: 13.002679,  lng: 80.240219, id: 17 },
// // //   { name: 'Main Gate', lat: 13.00612964850378, lng: 80.24191299117685, id: 18 },
// // //   { name: 'GC Stop Towards Hostel', lat: 12.991342, lng: 80.233637, id: 19 },
// // //   { name: 'Library Stop', lat: 12.9907517, lng: 80.2334554, id: 20 },
// // //   { name: 'OAT Stop Towards Hostel', lat: 12.989257,  lng: 80.233031, id: 21},
// // //   { name: 'Gymkhana Stop Towards GC', lat: 12.9866372, lng: 80.2332951, id: 22},
// // //   { name: 'Gymkhana Stop Towards Hostel', lat: 12.986615, lng: 80.233366, id: 23 },
// // //   { name: 'Narmada Stop Towards GC', lat: 12.9862759, lng: 80.2350107, id: 24 },
// // //   { name: 'Narmada Stop Towards Hostel', lat: 12.986546, lng: 80.235301, id: 25 },
// // //   { name: 'Jamuna and Ganga Stand', lat: 12.986566332825637, lng: 80.23855704439099, id: 26 },
// // //   { name: 'RP Stand', lat: 12.990088, lng: 80.241799, id: 28},
// // //   { name: 'ED Stand', lat: 12.989874633072303, lng: 80.22646042038593, id: 29},
// // // ];

// // // // Route sequences by stop id
// // // const Hostel_to_Main   = [26, 24, 22, 20, 6, 5, 3, 1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
// // // const Main_to_Hostel   = [18, 17, 15, 13, 11, 9, 6, 5, 3, 1, 2, 4, 7, 19, 21, 23, 25, 26];
// // // const Velachery_to_Main= [1, 2, 4, 7, 8, 10, 12, 14, 16, 18];
// // // const Velachery_to_Hostel=[1, 2, 4, 7, 19, 21, 23, 25, 26];
// // // const Hotel_to_Velachery=[26, 24, 22, 20, 6, 5, 3, 1];
// // // const Main_to_Velachery=[18, 17, 15, 13, 11, 9, 6, 5, 3, 1];
// // // const RP_to_ED         = [28, 22, 21, 20, 6, 5, 3, 29];
// // // const ED_to_RP         = [29, 2, 4, 7, 19, 21, 23, 28];

// // // const ROUTES = [
// // //   { code: "HOSTEL_MAIN", label: "Hostel → Main Gate", ids: Hostel_to_Main },
// // //   { code: "MAIN_HOSTEL", label: "Main Gate → Hostel", ids: Main_to_Hostel },
// // //   { code: "VG_MAIN",     label: "Velachery Gate → Main Gate", ids: Velachery_to_Main },
// // //   { code: "VG_HOSTEL",   label: "Velachery Gate → Hostel", ids: Velachery_to_Hostel },
// // //   { code: "MAIN_VG",     label: "Main Gate → Velachery Gate", ids: Main_to_Velachery },
// // //   { code: "HOSTEL_VG",   label: "Hostel → Velachery Gate", ids: Hotel_to_Velachery },
// // //   { code: "RP_ED",       label: "Research Park → ED", ids: RP_to_ED },
// // //   { code: "ED_RP",       label: "ED → Research Park", ids: ED_to_RP },
// // // ];

// // // // ---- Route aliases (frontend only) ----
// // // // Backend only knows canonical codes: HOSTEL_MAIN, MAIN_HOSTEL, RP_ED, ED_RP
// // // // But filters can still expose Velachery labels.
// // // // Aliases map to canonical backend codes.
// // // const ROUTE_ALIAS_TO_CANONICAL = {
// // //   VG_MAIN: "HOSTEL_MAIN",
// // //   HOSTEL_VG: "HOSTEL_MAIN",
// // //   MAIN_VG: "MAIN_HOSTEL",
// // //   VG_HOSTEL: "MAIN_HOSTEL",
// // // };

// // // function canonicalRoute(code) {
// // //   return ROUTE_ALIAS_TO_CANONICAL[code] || code;
// // // }
// // // // Routes the admin can assign in backend (only canonical codes)
// // // const ADMIN_ROUTES = [
// // //   { code: "HOSTEL_MAIN", label: "Hostel → Main Gate" },
// // //   { code: "MAIN_HOSTEL", label: "Main Gate → Hostel" },
// // //   { code: "RP_ED", label: "Research Park → ED" },
// // //   { code: "ED_RP", label: "ED → Research Park" },
// // // ];


// // // // ---- Vehicles for driverRoute select ----
// // // const VEHICLES = [
// // //   "TN07DH1368","TN07DH1317","TN07DH1315","EV09","TN07DH1360","TN07DH1391","EV08","TN07DD5574",
// // //   "TN07CX8698","TN07DD3376","TN07CX8200","TN07DD3360","EV-12","EV-14","EV-15","EV-10","EV-05",
// // //   "EV-07","EV-06","EV-02","EV-03","EV-11","EV-01","EV-13","TN07CX8119"
// // // ];

// // // // ---- Leaflet default icon fix ----
// // // delete L.Icon.Default.prototype._getIconUrl;
// // // L.Icon.Default.mergeOptions({
// // //   iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
// // //   iconUrl: require('leaflet/dist/images/marker-icon.png'),
// // //   shadowUrl: require('leaflet/dist/images/marker-shadow.png')
// // // });

// // // // ---- Utility helpers ----
// // // const createCustomIcon = (iconUrl, size = [40, 40]) =>
// // //   L.icon({
// // //     iconUrl,
// // //     iconSize: size,
// // //     iconAnchor: [size[0] / 2, size[1]],
// // //     popupAnchor: [0, -size[1] / 2]
// // //   });

// // // const userIconInstance = createCustomIcon(userIcon, [40, 40]);

// // // const toHeading = (v) => {
// // //   const n = Number(v);
// // //   return Number.isFinite(n) ? ((n % 360) + 360) % 360 : null;
// // // };

// // // // Exponential smoothing for latitude/longitude
// // // function smoothCoord(prev, next, alpha = SMOOTH_ALPHA) {
// // //   if (!prev) return next;
// // //   return {
// // //     lat: prev.lat + alpha * (next.lat - prev.lat),
// // //     lng: prev.lng + alpha * (next.lng - prev.lng)
// // //   };
// // // }



// // // // Circular smoothing for heading (degrees)
// // // function smoothHeading(prevDeg, nextDeg, alpha = HEADING_ALPHA) {
// // //   if (!Number.isFinite(prevDeg)) return nextDeg;
// // //   if (!Number.isFinite(nextDeg)) return prevDeg;
// // //   const p = (prevDeg * Math.PI) / 180;
// // //   const n = (nextDeg * Math.PI) / 180;
// // //   const x = (1 - alpha) * Math.cos(p) + alpha * Math.cos(n);
// // //   const y = (1 - alpha) * Math.sin(p) + alpha * Math.sin(n);
// // //   const ang = (Math.atan2(y, x) * 180) / Math.PI;
// // //   return (((ang % 360) + 360) % 360);
// // // }
// // // function mBetween(a, b) { return L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng)); }

// // // // ---- Cart classification (case-insensitive "EV") ----
// // // function isCart(vehicleno) {
// // //   const s = String(vehicleno || '').trim();
// // //   return s.toUpperCase().startsWith('EV');
// // // }

// // // // Label: B for Bus, C for Cart
// // // // function vehicleLabel(vehicleno) { return isCart(vehicleno) ? 'C' : 'B'; }
// // // function vehicleLabel(vehicleno, routeCode) {
// // //   if (!isCart(vehicleno)) return "B";

// // //   const rc = canonicalRoute(routeCode || "");
// // //   if (rc === "RP_ED" || rc === "ED_RP") return "CR"; // 👈 cart on RP↔ED
// // //   return "C";
// // // }


// // // // ---- Block IDs starting with a digit ----
// // // function startsWithDigit(vehicleno) { return /^[0-9]/.test(String(vehicleno || '')); }

// // // // ---- Direction pointer “on the circle” DivIcon ----
// // // // function createVehicleDivIcon(label, headingDeg) {
// // // //   const circle = 20;
// // // //   const border = 3;
// // // //   const triH = 18;
// // // //   const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
// // // //   const totalH = triH + circle;

// // // //   const html = `
// // // //     <div style="position:relative;width:${circle}px;height:${totalH}px">
// // // //       <div style="
// // // //         position:absolute; left:0; right:0; bottom:0;
// // // //         width:${circle}px; height:${circle}px;
// // // //         border:${border}px solid #0D47A1; background:#ffffff; border-radius:50%;
// // // //         display:flex; align-items:center; justify-content:center;
// // // //         box-shadow:0 2px 8px rgba(0,0,0,0.25);
// // // //         font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
// // // //         letter-spacing:0.6px; user-select:none;">
// // // //         <span style="font-size:16px; color:#0D47A1">${label}</span>
// // // //       </div>
// // // //       <div style="
// // // //         position:absolute; left:50%; top:0;
// // // //         transform: translateX(-250%) rotate(${rot}deg);
// // // //         transform-origin: 50% ${triH * 0.7}px;
// // // //         width:0; height:0;
// // // //         border-left:${triH / 4}px solid transparent;
// // // //         border-right:${triH / 4}px solid transparent;
// // // //         border-bottom:${triH}px solid #0D47A1;
// // // //         filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
// // // //         z-index: 2; pointer-events: none;"></div>
// // // //     </div>
// // // //   `;

// // // //   return L.divIcon({
// // // //     className: 'veh-div-icon',
// // // //     html,
// // // //     iconSize: [circle, totalH],
// // // //     iconAnchor: [circle / 2, totalH - circle / 2],
// // // //     popupAnchor: [0, -(circle / 2 + triH / 2)]
// // // //   });
// // // // }

// // // function createVehicleDivIcon(label, headingDeg, palette) {
// // //   const circle = 20;
// // //   const border = 3;
// // //   const triH = 18;
// // //   const rot = Number.isFinite(headingDeg) ? headingDeg : 0;
// // //   const totalH = triH + circle;

// // //   // default bus palette
// // //   const p = palette || {
// // //     stroke: "#0D47A1",
// // //     fill: "#ffffff",
// // //     text: "#0D47A1",
// // //     pointer: "#0D47A1",
// // //   };

// // //   const html = `
// // //     <div style="position:relative;width:${circle}px;height:${totalH}px">
// // //       <div style="
// // //         position:absolute; left:0; right:0; bottom:0;
// // //         width:${circle}px; height:${circle}px;
// // //         border:${border}px solid ${p.stroke};
// // //         background:${p.fill};
// // //         border-radius:50%;
// // //         display:flex; align-items:center; justify-content:center;
// // //         box-shadow:0 2px 8px rgba(0,0,0,0.25);
// // //         font-weight:800; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
// // //         letter-spacing:0.6px; user-select:none;">
// // //         <span style="font-size:16px; color:${p.text}">${label}</span>
// // //       </div>
// // //       <div style="
// // //         position:absolute; left:50%; top:0;
// // //         transform: translateX(-250%) rotate(${rot}deg);
// // //         transform-origin: 50% ${triH * 0.7}px;
// // //         width:0; height:0;
// // //         border-left:${triH / 4}px solid transparent;
// // //         border-right:${triH / 4}px solid transparent;
// // //         border-bottom:${triH}px solid ${p.pointer};
// // //         filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
// // //         z-index: 2; pointer-events: none;"></div>
// // //     </div>
// // //   `;

// // //   return L.divIcon({
// // //     className: 'veh-div-icon',
// // //     html,
// // //     iconSize: [circle, totalH],
// // //     iconAnchor: [circle / 2, totalH - circle / 2],
// // //     popupAnchor: [0, -(circle / 2 + triH / 2)]
// // //   });
// // // }

// // // const BUS_PALETTE = {
// // //   stroke: "#0D47A1",
// // //   fill: "#ffffff",
// // //   text: "#0D47A1",
// // //   pointer: "#0D47A1",
// // // };

// // // // const CART_PALETTE = {
// // // //   stroke: "#0F766E",  // teal border
// // // //   fill: "#ECFEFF",    // light teal fill
// // // //   text: "#0F766E",
// // // //   pointer: "#0F766E",
// // // // };
// // // const CART_PALETTE = {
// // //   stroke: "#111827",   // near-black border
// // //   fill:   "#FEF08A",   // bright yellow fill
// // //   text:   "#111827",   // black text
// // //   pointer:"#111827",   // black pointer
// // // };





// // // function matchesFilter(vehicleno, filter, assignments) {
// // //   const cart = isCart(vehicleno);
// // //   if (filter === "cart") return cart;
// // //   if (filter === "bus") return !cart;

// // //   if (filter?.startsWith("route:")) {
// // //     const requested = canonicalRoute(filter.slice(6));
// // //     const assigned = canonicalRoute(assignments[vehicleno]);
// // //     return assigned === requested;
// // //   }
// // //   return true;
// // // }

// // // // ---- Filter helper ----
// // // // function matchesFilter(vehicleno, filter, assignments) {
// // // //   const cart = isCart(vehicleno);
// // // //   if (filter === 'cart') return cart;
// // // //   if (filter === 'bus')  return !cart;
// // // //   if (filter?.startsWith('route:')) {
// // // //     const code = filter.slice(6);
// // // //     return assignments[vehicleno] === code;
// // // //   }
// // // //   return true;
// // // // }

// // // // ---------------- SCHEDULE DATA (kept intact) ----------------
// // // const HOSTEL_TO_MAIN = [
// // //   '06:20 AM','06:40 AM','07:00 AM','07:20 AM','07:40 AM','08:00 AM','08:20 AM','08:40 AM',
// // //   '09:00 AM','09:20 AM','09:40 AM','10:00 AM','10:20 AM','10:40 AM','11:00 AM','11:20 AM',
// // //   '11:40 AM','12:00 PM','12:20 PM','12:40 PM','01:00 PM','01:20 PM','01:40 PM','02:00 PM',
// // //   '02:20 PM','02:40 PM','03:00 PM','03:20 PM','03:40 PM','04:00 PM','04:20 PM','04:40 PM',
// // //   '05:00 PM','05:20 PM','05:40 PM','06:00 PM','06:20 PM','06:40 PM','07:00 PM','07:20 PM',
// // //   '07:40 PM','08:00 PM','08:20 PM','08:40 PM','09:00 PM','09:20 PM'
// // // ];
// // // const HOSTEL_NOTES = [
// // //   { time: '09:30 PM', note: 'To Velachery Gate' },
// // //   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// // // ];
// // // const MAIN_TO_HOSTEL = [...HOSTEL_TO_MAIN];
// // // const MAIN_NOTES = [
// // //   { time: '09:40 PM', note: 'To Velachery Gate' },
// // //   { time: '10:00 PM', note: 'To Velachery Gate' },
// // //   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// // // ];
// // // const VELACHERY_TO_MAIN = [
// // //   '06:15 AM','06:35 AM','06:55 AM','07:15 AM','07:35 AM','07:55 AM','08:15 AM','08:35 AM','08:55 AM',
// // //   '09:15 AM','09:35 AM','09:55 AM','10:15 AM','10:35 AM','10:55 AM','11:15 AM','11:35 AM',
// // //   '12:15 PM','12:35 PM','12:55 PM','01:15 PM','01:35 PM','01:55 PM','02:15 PM','02:35 PM','02:55 PM',
// // //   '03:15 PM','03:35 PM','03:55 PM','04:15 PM','04:35 PM','04:55 PM','05:15 PM','05:35 PM','05:55 PM',
// // //   '06:15 PM','06:35 PM','06:55 PM','07:15 PM','07:35 PM','07:55 PM','08:15 PM','08:35 PM','08:55 PM',
// // //   '09:15 PM','09:35 PM'
// // // ];
// // // const VELACHERY_TO_HOSTEL = [
// // //   '06:25 AM','06:45 AM','07:05 AM','07:25 AM','07:45 AM','08:05 AM','08:25 AM','08:45 AM','09:05 AM',
// // //   '09:25 AM','09:45 AM','10:05 AM','10:25 AM','10:45 AM','11:05 AM','11:25 AM','11:45 AM',
// // //   '12:05 PM','12:25 PM','12:45 PM','01:05 PM','01:25 PM','01:45 PM','02:05 PM','02:25 PM','02:45 PM',
// // //   '03:05 PM','03:25 PM','03:45 PM','04:05 PM','04:25 PM','04:45 PM','05:05 PM','05:25 PM','05:45 PM',
// // //   '06:05 PM','06:25 PM','06:45 PM','07:05 PM','07:25 PM','07:45 PM','08:05 PM','08:25 PM','08:45 PM',
// // //   '09:05 PM','09:15 PM'
// // // ];
// // // const VELACHERY_NOTES = [
// // //   { time: 'bustransport@iitm.ac.in', note: 'For your feedback/complaints, please send a mail here.' }
// // // ];
// // // const RP_TO_ED_TIMES = [
// // //   '07:30 AM','08:05 AM','08:40 AM','09:00 AM','09:15 AM','09:35 AM','09:45 AM',
// // //   '10:15 AM','10:25 AM','10:45 AM','11:20 AM','11:40 AM',
// // //   '12:05 PM','12:45 PM','01:40 PM','02:20 PM','02:35 PM','03:00 PM','03:15 PM','03:45 PM',
// // //   '04:15 PM','04:50 PM','05:05 PM','05:50 PM','06:25 PM','07:05 PM'
// // // ];
// // // const ED_TO_RP_TIMES = [
// // //   '07:50 AM','08:25 AM','09:00 AM','09:15 AM','09:30 AM','10:00 AM','10:05 AM','10:30 AM',
// // //   '11:00 AM','11:15 AM','11:45 AM',
// // //   '12:00 PM','12:25 PM','01:05 PM','02:00 PM','02:40 PM','03:00 PM','03:20 PM','03:30 PM',
// // //   '04:05 PM','04:45 PM','05:10 PM','05:30 PM','06:10 PM','06:45 PM','07:20 PM'
// // // ];
// // // const Cart1 = [['07:30','07:50'],['08:05','08:25'],['08:40','09:00'],['09:15','09:30'],['09:45','10:05'],['10:25','11:00'],['11:20','11:45'],['12:05','12:25'],['13:40','14:00'],['14:20','14:40'],['15:00','15:20'],['15:45','16:05'],['16:50','17:10']];
// // // const Cart3 = [['09:00','09:15'],['09:35','10:00'],['10:15','10:30'],['10:45','11:15'],['11:40','12:00'],['12:45','13:05'],['14:35','15:00'],['15:15','15:30'],['16:15','16:45'],['17:05','17:30'],['17:50','18:10'],['18:25','18:45'],['19:05','19:20']];

// // // // // ---- Feedback link ----
// // // // const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

// // // // ---- Feedback / temporary summit promo links ----
// // // const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_URL || '';

// // // const MINT_SUMMIT_REGISTRATION_URL =
// // //   process.env.REACT_APP_MINT_SUMMIT_REGISTRATION_URL ||
// // //   'https://docs.google.com/forms/d/e/1FAIpQLScB7Y6RtzcOwXRsoi4Czrr_vUnOcZm7joeeKSNKvcNm7RRjLw/viewform';

// // // // Set this to false after the summit.
// // // const SHOW_MINT_SUMMIT_PROMO = false;

// // // const FOOTER_ACTION_URL = SHOW_MINT_SUMMIT_PROMO
// // //   ? MINT_SUMMIT_REGISTRATION_URL
// // //   : FEEDBACK_URL;

// // // const FOOTER_ACTION_LABEL = SHOW_MINT_SUMMIT_PROMO
// // //   ? 'Register for MInT Summit ✨'
// // //   : 'Open feedback form ↗';

// // // // ---------------- Tiny router helpers (for /driver, /driverRoute) ----------------
// // // function usePath() {
// // //   const [path, setPath] = useState(window.location.pathname);
// // //   useEffect(() => {
// // //     const onPop = () => setPath(window.location.pathname);
// // //     window.addEventListener('popstate', onPop);
// // //     return () => window.removeEventListener('popstate', onPop);
// // //   }, []);
// // //   const navigate = (p) => { if (p !== window.location.pathname) { window.history.pushState({}, "", p); setPath(p); } };
// // //   return { path, navigate };
// // // }
// // // function getToken() { return localStorage.getItem("driverToken") || ""; }
// // // function setToken(t) { localStorage.setItem("driverToken", t || ""); }
// // // function getUsername() { return localStorage.getItem("driverUsername") || ""; }
// // // function setUsername(u) { localStorage.setItem("driverUsername", u || ""); }

// // // // --- Admin auth helpers (separate from driver) ---
// // // function getAdminToken() {
// // //   return localStorage.getItem("adminToken") || "";
// // // }
// // // function setAdminToken(t) {
// // //   localStorage.setItem("adminToken", t || "");
// // // }


// // // // ---------------- Driver pages ----------------
// // // // --- small responsive hook for narrow phones ---
// // // function useIsNarrow(bp = 380) {
// // //   const [isNarrow, setIsNarrow] = useState(window.innerWidth <= bp);
// // //   useEffect(() => {
// // //     const onResize = () => setIsNarrow(window.innerWidth <= bp);
// // //     window.addEventListener('resize', onResize);
// // //     return () => window.removeEventListener('resize', onResize);
// // //   }, [bp]);
// // //   return isNarrow;
// // // }

// // // function DriverLogin({ navigate }) {
// // //   const [username, setU] = useState("");
// // //   const [password, setP] = useState("");
// // //   const [err, setErr] = useState("");
// // //   const [busy, setBusy] = useState(false);
// // //   const isNarrow = useIsNarrow(380);

// // //   useEffect(() => {
// // //     if (getToken()) navigate("/driverRoute");
// // //   }, [navigate]);

// // //   const onSubmit = async (e) => {
// // //     e.preventDefault();
// // //     setErr(""); setBusy(true);
// // //     try {
// // //       const r = await axios.post(`${BACKEND}/api/driver/login`, { username, password });
// // //       if (r.data?.success && r.data?.token) {
// // //         setToken(r.data.token);
// // //         setUsername(r.data.username);
// // //         navigate("/driverRoute");
// // //       } else setErr("Login failed");
// // //     } catch (e1) {
// // //       setErr(e1?.response?.data?.message || "Login failed");
// // //     } finally { setBusy(false); }
// // //   };

// // //   return (
// // //     <div
// // //       style={{
// // //         padding: isNarrow ? 12 : 20,
// // //         margin: isNarrow ? "16px auto" : "32px auto",
// // //         width: "min(100vw - 24px, 560px)",
// // //       }}
// // //     >
// // //       {/* Header with Home button */}
// // //       <div
// // //         style={{
// // //           display: "flex",
// // //           alignItems: "center",
// // //           gap: isNarrow ? 10 : 12,
// // //           marginBottom: isNarrow ? 10 : 14,
// // //           flexWrap: "wrap",
// // //         }}
// // //       >
// // //         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
// // //           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
// // //           <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Driver Login</h2>
// // //         </div>

// // //         <button
// // //           type="button"
// // //           onClick={() => navigate("/")}
// // //           style={{
// // //             marginLeft: "auto",
// // //             padding: isNarrow ? "6px 10px" : "8px 12px",
// // //             borderRadius: 10,
// // //             border: "1px solid #D1D5DB",
// // //             background: "#FFFFFF",
// // //             fontWeight: 700,
// // //             fontSize: isNarrow ? 13 : 14,
// // //             cursor: "pointer",
// // //           }}
// // //           title="Go to Home"
// // //         >
// // //           Home
// // //         </button>
// // //       </div>

// // //       {/* Card */}
// // //       <div
// // //         style={{
// // //           background: "#fff",
// // //           border: "1px solid #E5E7EB",
// // //           borderRadius: 12,
// // //           padding: isNarrow ? 12 : 16,
// // //         }}
// // //       >
// // //         <form onSubmit={onSubmit} style={{ display: "grid", gap: isNarrow ? 8 : 10 }}>
// // //           <input
// // //             value={username}
// // //             onChange={(e) => setU(e.target.value)}
// // //             placeholder="username"
// // //             required
// // //             style={{
// // //               padding: isNarrow ? 10 : 12,
// // //               borderRadius: 10,
// // //               border: "1px solid #D1D5DB",
// // //               fontWeight: 600,
// // //               fontSize: isNarrow ? 14 : 16,
// // //             }}
// // //           />
// // //           <input
// // //             value={password}
// // //             onChange={(e) => setP(e.target.value)}
// // //             placeholder="password"
// // //             type="password"
// // //             required
// // //             style={{
// // //               padding: isNarrow ? 10 : 12,
// // //               borderRadius: 10,
// // //               border: "1px solid #D1D5DB",
// // //               fontWeight: 600,
// // //               fontSize: isNarrow ? 14 : 16,
// // //             }}
// // //           />
// // //           <button
// // //             disabled={busy}
// // //             style={{
// // //               padding: isNarrow ? "10px 12px" : "12px 14px",
// // //               borderRadius: 10,
// // //               border: "1px solid #0D47A1",
// // //               background: "#0D47A1",
// // //               color: "#fff",
// // //               fontWeight: 800,
// // //               cursor: "pointer",
// // //               width: "100%",
// // //               minHeight: 40,
// // //               fontSize: "clamp(14px, 3.5vw, 16px)",
// // //             }}
// // //           >
// // //             {busy ? "Signing in..." : "Login"}
// // //           </button>
// // //           {err && <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>}
// // //         </form>
// // //       </div>
// // //     </div>
// // //   );
// // // }

// // // function DriverRoute({ navigate }) {
// // //   const [routeCode, setRouteCode] = useState("");
// // //   const [vehicleNo, setVehicleNo] = useState("");
// // //   const [autoPilot, setAutoPilot] = useState(false); // 👈 NEW: auto-pilot flag
// // //   const [saved, setSaved] = useState(false);
// // //   const [busy, setBusy] = useState(false);
// // //   const [err, setErr] = useState("");
// // //   const isNarrow = useIsNarrow(380);

// // //   useEffect(() => {
// // //     if (!getToken()) navigate("/driver");
// // //     (async () => {
// // //       try {
// // //         const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
// // //           headers: { Authorization: `Bearer ${getToken()}` },
// // //         });
// // //         const row = r.data?.row;
// // //         if (row) {
// // //           setVehicleNo(row.vehicleNo);
// // //           setRouteCode(row.routeCode);
// // //           setAutoPilot(!!row.autopilotEnabled); // 👈 load auto-pilot state from backend
// // //           setSaved(true);
// // //         }
// // //       } catch {}
// // //     })();
// // //     // eslint-disable-next-line react-hooks/exhaustive-deps
// // //   }, []);

// // //   const onSave = async (e) => {
// // //     e.preventDefault();
// // //     setErr(""); setBusy(true);
// // //     try {
// // //       await axios.post(
// // //         `${BACKEND}/api/vehicle-route/assign`,
// // //         {
// // //           vehicleNo,
// // //           routeCode,
// // //           active: true,
// // //           autopilotEnabled: autoPilot, // 👈 send auto-pilot choice to backend
// // //         },
// // //         { headers: { Authorization: `Bearer ${getToken()}` } }
// // //       );
// // //       setSaved(true);
// // //     } catch (e1) {
// // //       setErr(e1?.response?.data?.message || "Save failed");
// // //     } finally {
// // //       setBusy(false);
// // //     }
// // //   };

// // //   return (
// // //     <div
// // //       style={{
// // //         padding: isNarrow ? 16 : 20,
// // //         maxWidth: 640,
// // //         margin: isNarrow ? "16px auto" : "32px auto",
// // //         width: "min(100vw - 24px, 640px)",
// // //       }}
// // //     >
// // //       <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
// // //         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
// // //           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
// // //           <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
// // //         </div>
// // //         <div
// // //           style={{
// // //             marginLeft: 'auto',
// // //             fontWeight: 700,
// // //             opacity: 0.8,
// // //             fontSize: "clamp(13px, 3.2vw, 16px)",
// // //             maxWidth: '60%',
// // //             overflow: 'hidden',
// // //             textOverflow: 'ellipsis',
// // //             whiteSpace: 'nowrap',
// // //           }}
// // //           title={`@${getUsername()}`}
// // //         >
// // //           @{getUsername()}
// // //         </div>
// // //       </div>

// // //       {/* Top-right actions */}
// // //       <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
// // //         <div style={{ display: 'flex', gap: 8 }}>
// // //           <button
// // //             onClick={() => navigate('/')}
// // //             style={{
// // //               padding: isNarrow ? '6px 10px' : '8px 12px',
// // //               borderRadius: 10,
// // //               border: '1px solid #D1D5DB',
// // //               background: '#FFFFFF',
// // //               fontWeight: 700,
// // //               fontSize: isNarrow ? 13 : 14,
// // //               cursor: 'pointer'
// // //             }}
// // //             title="Go to Home"
// // //           >
// // //             Home
// // //           </button>
// // //           <button
// // //             type="button"
// // //             onClick={() => setSaved(false)}
// // //             disabled={!saved}
// // //             style={{
// // //               padding: isNarrow ? '6px 10px' : '8px 12px',
// // //               borderRadius: 10,
// // //               border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
// // //               background: saved ? '#fff' : '#F9FAFB',
// // //               color: saved ? '#111' : '#9CA3AF',
// // //               fontWeight: 800,
// // //               fontSize: isNarrow ? 13 : 14,
// // //               cursor: saved ? 'pointer' : 'not-allowed'
// // //             }}
// // //             title="Edit selection"
// // //           >
// // //             Edit
// // //           </button>
// // //         </div>

// // //         <button
// // //           onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
// // //           style={{
// // //             padding: isNarrow ? '6px 10px' : 8,
// // //             borderRadius: 10,
// // //             border: '1px solid #D1D5DB',
// // //             background: '#FFFFFF',
// // //             fontSize: isNarrow ? 13 : 14,
// // //             cursor: 'pointer'
// // //           }}
// // //           title="Logout"
// // //         >
// // //           Logout
// // //         </button>
// // //       </div>

// // //       <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
// // //         <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
// // //         <select
// // //           disabled={saved}
// // //           value={routeCode}
// // //           onChange={e=>setRouteCode(e.target.value)}
// // //           required
// // //           style={{
// // //             padding: isNarrow ? 10 : 12,
// // //             borderRadius: 10,
// // //             border: '1px solid #D1D5DB',
// // //             fontSize: isNarrow ? 14 : 16
// // //           }}
// // //         >
// // //           <option value="">Select route…</option>
// // //           {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
// // //         </select>

// // //         <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
// // //         <select
// // //           disabled={saved}
// // //           value={vehicleNo}
// // //           onChange={e=>setVehicleNo(e.target.value)}
// // //           required
// // //           style={{
// // //             padding: isNarrow ? 10 : 12,
// // //             borderRadius: 10,
// // //             border: '1px solid #D1D5DB',
// // //             fontSize: isNarrow ? 14 : 16
// // //           }}
// // //         >
// // //           <option value="">Select vehicle…</option>
// // //           {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
// // //         </select>

// // //         {/* 👇 NEW: Auto-pilot toggle */}
// // //         <div
// // //           style={{
// // //             marginTop: isNarrow ? 6 : 8,
// // //             padding: isNarrow ? 8 : 10,
// // //             borderRadius: 10,
// // //             border: '1px solid #E5E7EB',
// // //             background: '#F9FAFB',
// // //             display: 'flex',
// // //             alignItems: 'flex-start',
// // //             gap: 8,
// // //           }}
// // //         >
// // //           <input
// // //             id="autoPilot"
// // //             type="checkbox"
// // //             checked={autoPilot}
// // //             disabled={saved}
// // //             onChange={(e) => setAutoPilot(e.target.checked)}
// // //             style={{ marginTop: 3, cursor: saved ? 'default' : 'pointer' }}
// // //           />
// // //           <label
// // //             htmlFor="autoPilot"
// // //             style={{ cursor: saved ? 'default' : 'pointer' }}
// // //           >
// // //             <div style={{ fontWeight: 700, fontSize: isNarrow ? 14 : 15 }}>
// // //               Auto-pilot (auto reverse at destination)
// // //             </div>
// // //             <div
// // //               style={{
// // //                 fontSize: isNarrow ? 12 : 13,
// // //                 color: '#6B7280',
// // //                 marginTop: 2,
// // //               }}
// // //             >
// // //               When enabled, once you reach the last stop of this route, your
// // //               assignment will automatically switch to the return leg.
// // //             </div>
// // //           </label>
// // //         </div>

// // //         <button
// // //           disabled={busy || saved}
// // //           style={{
// // //             padding: isNarrow ? '10px 12px' : '12px 14px',
// // //             borderRadius: 10,
// // //             border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
// // //             background: busy || saved ? '#F3F4F6' : '#0D47A1',
// // //             color: busy || saved ? '#6B7280' : '#fff',
// // //             fontWeight: 800,
// // //             cursor: busy || saved ? 'not-allowed' : 'pointer',
// // //             minHeight: 44,
// // //             fontSize: "clamp(14px, 3.5vw, 16px)"
// // //           }}
// // //           title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
// // //         >
// // //           {busy ? "Saving…" : saved ? "Saved" : "Save"}
// // //         </button>

// // //         {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
// // //       </form>
// // //     </div>
// // //   );
// // // }


// // // // function DriverRoute({ navigate }) {
// // // //   const [routeCode, setRouteCode] = useState("");
// // // //   const [vehicleNo, setVehicleNo] = useState("");
// // // //   const [saved, setSaved] = useState(false);
// // // //   const [busy, setBusy] = useState(false);
// // // //   const [err, setErr] = useState("");
// // // //   const isNarrow = useIsNarrow(380);

// // // //   useEffect(() => {
// // // //     if (!getToken()) navigate("/driver");
// // // //     (async () => {
// // // //       try {
// // // //         const r = await axios.get(`${BACKEND}/api/vehicle-route/mine`, {
// // // //           headers: { Authorization: `Bearer ${getToken()}` },
// // // //         });
// // // //         const row = r.data?.row;
// // // //         if (row) { setVehicleNo(row.vehicleNo); setRouteCode(row.routeCode); setSaved(true); }
// // // //       } catch {}
// // // //     })();
// // // //     // eslint-disable-next-line react-hooks/exhaustive-deps
// // // //   }, []);

// // // //   const onSave = async (e) => {
// // // //     e.preventDefault();
// // // //     setErr(""); setBusy(true);
// // // //     try {
// // // //       await axios.post(
// // // //         `${BACKEND}/api/vehicle-route/assign`,
// // // //         { vehicleNo, routeCode, active: true },
// // // //         { headers: { Authorization: `Bearer ${getToken()}` } }
// // // //       );
// // // //       setSaved(true);
// // // //     } catch (e1) {
// // // //       setErr(e1?.response?.data?.message || "Save failed");
// // // //     } finally {
// // // //       setBusy(false);
// // // //     }
// // // //   };

// // // //   return (
// // // //     <div
// // // //       style={{
// // // //         padding: isNarrow ? 16 : 20,
// // // //         maxWidth: 640,
// // // //         margin: isNarrow ? "16px auto" : "32px auto",
// // // //         width: "min(100vw - 24px, 640px)",
// // // //       }}
// // // //     >
// // // //       <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isNarrow ? 10 : 14, flexWrap: 'wrap' }}>
// // // //         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
// // // //           <img src={iitmLogo} alt="IITM" style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }} />
// // // //           <h2 style={{ margin: 0, fontSize: "clamp(20px, 6vw, 28px)" }}>Select Your Route</h2>
// // // //         </div>
// // // //         <div
// // // //           style={{
// // // //             marginLeft: 'auto',
// // // //             fontWeight: 700,
// // // //             opacity: 0.8,
// // // //             fontSize: "clamp(13px, 3.2vw, 16px)",
// // // //             maxWidth: '60%',
// // // //             overflow: 'hidden',
// // // //             textOverflow: 'ellipsis',
// // // //             whiteSpace: 'nowrap',
// // // //           }}
// // // //           title={`@${getUsername()}`}
// // // //         >
// // // //           @{getUsername()}
// // // //         </div>
// // // //       </div>

// // // //       {/* Top-right actions */}
// // // //       <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
// // // //         <div style={{ display: 'flex', gap: 8 }}>
// // // //           <button
// // // //             onClick={() => navigate('/')}
// // // //             style={{
// // // //               padding: isNarrow ? '6px 10px' : '8px 12px',
// // // //               borderRadius: 10,
// // // //               border: '1px solid #D1D5DB',
// // // //               background: '#FFFFFF',
// // // //               fontWeight: 700,
// // // //               fontSize: isNarrow ? 13 : 14,
// // // //               cursor: 'pointer'
// // // //             }}
// // // //             title="Go to Home"
// // // //           >
// // // //             Home
// // // //           </button>
// // // //           <button
// // // //             type="button"
// // // //             onClick={() => setSaved(false)}
// // // //             disabled={!saved}
// // // //             style={{
// // // //               padding: isNarrow ? '6px 10px' : '8px 12px',
// // // //               borderRadius: 10,
// // // //               border: '1px solid ' + (saved ? '#555' : '#E5E7EB'),
// // // //               background: saved ? '#fff' : '#F9FAFB',
// // // //               color: saved ? '#111' : '#9CA3AF',
// // // //               fontWeight: 800,
// // // //               fontSize: isNarrow ? 13 : 14,
// // // //               cursor: saved ? 'pointer' : 'not-allowed'
// // // //             }}
// // // //             title="Edit selection"
// // // //           >
// // // //             Edit
// // // //           </button>
// // // //         </div>

// // // //         <button
// // // //           onClick={() => { setToken(""); setUsername(""); navigate("/driver"); }}
// // // //           style={{
// // // //             padding: isNarrow ? '6px 10px' : 8,
// // // //             borderRadius: 10,
// // // //             border: '1px solid #D1D5DB',
// // // //             background: '#FFFFFF',
// // // //             fontSize: isNarrow ? 13 : 14,
// // // //             cursor: 'pointer'
// // // //           }}
// // // //           title="Logout"
// // // //         >
// // // //           Logout
// // // //         </button>
// // // //       </div>

// // // //       <form onSubmit={onSave} style={{ display: 'grid', gap: isNarrow ? 10 : 12 }}>
// // // //         <label style={{ fontWeight: 700, fontSize: isNarrow ? 15 : 16 }}>Route</label>
// // // //         <select
// // // //           disabled={saved}
// // // //           value={routeCode}
// // // //           onChange={e=>setRouteCode(e.target.value)}
// // // //           required
// // // //           style={{
// // // //             padding: isNarrow ? 10 : 12,
// // // //             borderRadius: 10,
// // // //             border: '1px solid #D1D5DB',
// // // //             fontSize: isNarrow ? 14 : 16
// // // //           }}
// // // //         >
// // // //           <option value="">Select route…</option>
// // // //           {ROUTES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
// // // //         </select>

// // // //         <label style={{ fontWeight: 700, marginTop: 6, fontSize: isNarrow ? 15 : 16 }}>Vehicle</label>
// // // //         <select
// // // //           disabled={saved}
// // // //           value={vehicleNo}
// // // //           onChange={e=>setVehicleNo(e.target.value)}
// // // //           required
// // // //           style={{
// // // //             padding: isNarrow ? 10 : 12,
// // // //             borderRadius: 10,
// // // //             border: '1px solid #D1D5DB',
// // // //             fontSize: isNarrow ? 14 : 16
// // // //           }}
// // // //         >
// // // //           <option value="">Select vehicle…</option>
// // // //           {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
// // // //         </select>

// // // //         <button
// // // //           disabled={busy || saved}
// // // //           style={{
// // // //             padding: isNarrow ? '10px 12px' : '12px 14px',
// // // //             borderRadius: 10,
// // // //             border: '1px solid ' + (busy || saved ? '#D1D5DB' : '#0D47A1'),
// // // //             background: busy || saved ? '#F3F4F6' : '#0D47A1',
// // // //             color: busy || saved ? '#6B7280' : '#fff',
// // // //             fontWeight: 800,
// // // //             cursor: busy || saved ? 'not-allowed' : 'pointer',
// // // //             minHeight: 44,
// // // //             fontSize: "clamp(14px, 3.5vw, 16px)"
// // // //           }}
// // // //           title={saved ? 'Already saved. Click Edit to modify.' : 'Save selection'}
// // // //         >
// // // //           {busy ? "Saving…" : saved ? "Saved" : "Save"}
// // // //         </button>

// // // //         {err && <div style={{ color: '#b00020', fontWeight: 600 }}>{err}</div>}
// // // //       </form>
// // // //     </div>
// // // //   );
// // // // }

// // // /* ---------------------------- ADMIN PAGES ---------------------------- */

// // // function AdminLogin({ navigate }) {
// // //   const [username, setU] = useState("");
// // //   const [password, setP] = useState("");
// // //   const [err, setErr] = useState("");
// // //   const [busy, setBusy] = useState(false);
// // //   const isNarrow = useIsNarrow(380);

// // //   useEffect(() => {
// // //     if (getAdminToken()) navigate("/admin");
// // //   }, [navigate]);

// // //   const onSubmit = async (e) => {
// // //     e.preventDefault();
// // //     setErr("");
// // //     setBusy(true);
// // //     try {
// // //       const r = await axios.post(`${BACKEND}/api/admin/login`, {
// // //         username,
// // //         password,
// // //       });
// // //       if (r.data?.success && r.data?.token) {
// // //         setAdminToken(r.data.token);
// // //         navigate("/admin");
// // //       } else {
// // //         setErr("Login failed");
// // //       }
// // //     } catch (e1) {
// // //       setErr(e1?.response?.data?.message || "Login failed");
// // //     } finally {
// // //       setBusy(false);
// // //     }
// // //   };

// // //   return (
// // //     <div
// // //       style={{
// // //         padding: isNarrow ? 12 : 20,
// // //         margin: isNarrow ? "16px auto" : "32px auto",
// // //         width: "min(100vw - 24px, 560px)",
// // //       }}
// // //     >
// // //       <div
// // //         style={{
// // //           display: "flex",
// // //           alignItems: "center",
// // //           gap: isNarrow ? 10 : 12,
// // //           marginBottom: isNarrow ? 10 : 14,
// // //           flexWrap: "wrap",
// // //         }}
// // //       >
// // //         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
// // //           <img
// // //             src={iitmLogo}
// // //             alt="IITM"
// // //             style={{ width: isNarrow ? 30 : 36, height: isNarrow ? 30 : 36 }}
// // //           />
// // //           <h2 style={{ margin: 0, fontSize: isNarrow ? 18 : 20 }}>Admin Login</h2>
// // //         </div>
// // //         <button
// // //           type="button"
// // //           onClick={() => navigate("/")}
// // //           style={{
// // //             marginLeft: "auto",
// // //             padding: isNarrow ? "6px 10px" : "8px 12px",
// // //             borderRadius: 10,
// // //             border: "1px solid #D1D5DB",
// // //             background: "#FFFFFF",
// // //             fontWeight: 700,
// // //             fontSize: isNarrow ? 13 : 14,
// // //             cursor: "pointer",
// // //           }}
// // //           title="Go to Home"
// // //         >
// // //           Home
// // //         </button>
// // //       </div>
// // //       <div
// // //         style={{
// // //           background: "#fff",
// // //           border: "1px solid #E5E7EB",
// // //           borderRadius: 12,
// // //           padding: isNarrow ? 12 : 16,
// // //         }}
// // //       >
// // //         <form
// // //           onSubmit={onSubmit}
// // //           style={{ display: "grid", gap: isNarrow ? 8 : 10 }}
// // //         >
// // //           <input
// // //             value={username}
// // //             onChange={(e) => setU(e.target.value)}
// // //             placeholder="admin username"
// // //             required
// // //             style={{
// // //               padding: isNarrow ? 10 : 12,
// // //               borderRadius: 10,
// // //               border: "1px solid #D1D5DB",
// // //               fontWeight: 600,
// // //               fontSize: isNarrow ? 14 : 16,
// // //             }}
// // //           />
// // //           <input
// // //             value={password}
// // //             onChange={(e) => setP(e.target.value)}
// // //             placeholder="admin password"
// // //             type="password"
// // //             required
// // //             style={{
// // //               padding: isNarrow ? 10 : 12,
// // //               borderRadius: 10,
// // //               border: "1px solid #D1D5DB",
// // //               fontWeight: 600,
// // //               fontSize: isNarrow ? 14 : 16,
// // //             }}
// // //           />
// // //           <button
// // //             disabled={busy}
// // //             style={{
// // //               padding: isNarrow ? "10px 12px" : "12px 14px",
// // //               borderRadius: 10,
// // //               border: "1px solid #0D47A1",
// // //               background: "#0D47A1",
// // //               color: "#fff",
// // //               fontWeight: 800,
// // //               cursor: "pointer",
// // //               width: "100%",
// // //               minHeight: 40,
// // //               fontSize: "clamp(14px, 3.5vw, 16px)",
// // //             }}
// // //           >
// // //             {busy ? "Signing in..." : "Login"}
// // //           </button>
// // //           {err && (
// // //             <div style={{ color: "#b00020", fontWeight: 600 }}>{err}</div>
// // //           )}
// // //         </form>
// // //       </div>
// // //     </div>
// // //   );
// // // }

// // // function AdminDashboard({ navigate }) {
// // //   const [tab, setTab] = useState("drivers"); // 'drivers' | 'routes' | 'live'
// // //   const [drivers, setDrivers] = useState([]);
// // //   const [assignments, setAssignments] = useState({}); // vehicleNo -> row
// // //   const [localAssign, setLocalAssign] = useState({}); // vehicleNo -> { routeCode, driverId, autopilotEnabled }
// // //   const [editing, setEditing] = useState({}); // vehicleNo -> true when row is in edit mode
// // //   const [newName, setNewName] = useState("");
// // //   const [newPhone, setNewPhone] = useState("");
// // //   const [err, setErr] = useState("");
// // //   const [busySave, setBusySave] = useState(null); // vehicleno while saving
// // //   const [liveBuses, setLiveBuses] = useState([]);
// // //   const adminToken = getAdminToken();
// // //   const isNarrow = useIsNarrow(480);

// // //   useEffect(() => {
// // //     if (!adminToken) navigate("/admin/login");
// // //   }, [adminToken, navigate]);

// // //   const authHeader = useMemo(
// // //     () => ({
// // //       headers: { Authorization: `Bearer ${adminToken}` },
// // //     }),
// // //     [adminToken]
// // //   );

// // //   async function loadDrivers() {
// // //     try {
// // //       const r = await axios.get(`${BACKEND}/api/admin/drivers`, authHeader);
// // //       setDrivers(r.data?.drivers || []);
// // //     } catch (e) {
// // //       console.warn("loadDrivers error", e?.message || e);
// // //     }
// // //   }

// // //   async function loadAssignments() {
// // //     try {
// // //       const r = await axios.get(`${BACKEND}/api/admin/assignments`, authHeader);
// // //       const rows = r.data?.rows || [];
// // //       const map = {};
// // //       for (const row of rows) {
// // //         map[row.vehicleNo] = row;
// // //       }
// // //       setAssignments(map);
// // //       // whenever we reload from backend, treat all as "saved" (not editing)
// // //       setEditing({});
// // //       setLocalAssign({});
// // //     } catch (e) {
// // //       console.warn("loadAssignments error", e?.message || e);
// // //     }
// // //   }

// // //   useEffect(() => {
// // //     if (!adminToken) return;
// // //     loadDrivers();
// // //     loadAssignments();
// // //   }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

// // //   // live tracking (simple polling)
// // //   useEffect(() => {
// // //     if (tab !== "live") return;
// // //     let cancelled = false;

// // //     async function loadLive() {
// // //       try {
// // //         const r = await axios.get(`${BACKEND}/api/vehicle-data`, {
// // //           timeout: 8000,
// // //         });
// // //         if (!cancelled) {
// // //           const arr = Array.isArray(r.data) ? r.data : [];
// // //           setLiveBuses(arr);
// // //         }
// // //       } catch (e) {
// // //         if (!cancelled) {
// // //           console.warn("admin live map error", e?.message || e);
// // //         }
// // //       }
// // //     }
// // //     loadLive();
// // //     const id = setInterval(loadLive, 5000);
// // //     return () => {
// // //       cancelled = true;
// // //       clearInterval(id);
// // //     };
// // //   }, [tab]);

// // //   const handleAddDriver = async (e) => {
// // //     e.preventDefault();
// // //     setErr("");
// // //     try {
// // //       const r = await axios.post(
// // //         `${BACKEND}/api/admin/drivers`,
// // //         { name: newName, phoneNumber: newPhone },
// // //         authHeader
// // //       );
// // //       if (r.data?.success && r.data?.driver) {
// // //         setDrivers((prev) => [...prev, r.data.driver]);
// // //         setNewName("");
// // //         setNewPhone("");
// // //       } else {
// // //         setErr("Failed to create driver");
// // //       }
// // //     } catch (e1) {
// // //       setErr(e1?.response?.data?.message || "Failed to create driver");
// // //     }
// // //   };

// // //   const handleRemoveDriver = async (id) => {
// // //     if (!window.confirm("Remove this driver?")) return;
// // //     try {
// // //       await axios.delete(`${BACKEND}/api/admin/drivers/${id}`, authHeader);
// // //       setDrivers((prev) => prev.filter((d) => d._id !== id));
// // //       // Note: assignments that still reference this driver will continue to exist until reassigned
// // //     } catch (e1) {
// // //       alert(e1?.response?.data?.message || "Failed to remove driver");
// // //     }
// // //   };

// // //   function getBaseAssignForVehicle(veh) {
// // //     const row = assignments[veh];
// // //     if (!row) {
// // //       return {
// // //         vehicleNo: veh,
// // //         routeCode: "",
// // //         driverId: "",
// // //         autopilotEnabled: false,
// // //       };
// // //     }
// // //     return {
// // //       vehicleNo: veh,
// // //       routeCode: row.routeCode || "",
// // //       driverId: row.driver?._id || "",
// // //       autopilotEnabled: !!row.autopilotEnabled,
// // //     };
// // //   }

// // //   function updateLocalAssign(veh, patch) {
// // //     setLocalAssign((prev) => ({
// // //       ...prev,
// // //       [veh]: { ...getBaseAssignForVehicle(veh), ...(prev[veh] || {}), ...patch },
// // //     }));
// // //   }

// // //   const handleSaveAssignment = async (veh) => {
// // //   const base = getBaseAssignForVehicle(veh);
// // //   const row = localAssign[veh] || base;
// // //   const hasAssignment = !!assignments[veh]; // vehicle currently has an active route?

// // //   // RESET error first
// // //   setErr("");

// // //   // ---- CASE 1: Admin cleared both fields and there *was* an assignment -> CLEAR MODE ----
// // //   if (!row.routeCode && !row.driverId && hasAssignment) {
// // //     setBusySave(veh);
// // //     try {
// // //       await axios.post(
// // //         `${BACKEND}/api/admin/vehicle-assign`,
// // //         {
// // //           vehicleNo: row.vehicleNo,
// // //           clear: true,              // 👈 tells backend: deactivate assignment
// // //         },
// // //         authHeader
// // //       );
// // //       await loadAssignments();      // refresh table
// // //       setEditing((prev) => ({ ...prev, [veh]: false }));
// // //     } catch (e1) {
// // //       setErr(e1?.response?.data?.message || "Failed to clear assignment");
// // //     } finally {
// // //       setBusySave(null);
// // //     }
// // //     return;
// // //   }

// // //   // ---- CASE 2: No assignment and still empty -> nothing to save ----
// // //   if (!row.routeCode || !row.driverId) {
// // //     setErr("Select both Route & Driver, or leave the row as-is.");
// // //     return;
// // //   }

// // //   // ---- CASE 3: Normal ASSIGN / UPDATE ----
// // //   setBusySave(veh);
// // //   try {
// // //     await axios.post(
// // //       `${BACKEND}/api/admin/vehicle-assign`,
// // //       {
// // //         vehicleNo: row.vehicleNo,
// // //         routeCode: row.routeCode,
// // //         driverId: row.driverId,
// // //         autopilotEnabled: !!row.autopilotEnabled,
// // //       },
// // //       authHeader
// // //     );
// // //     await loadAssignments();
// // //     setEditing((prev) => ({ ...prev, [veh]: false }));
// // //   } catch (e1) {
// // //     setErr(e1?.response?.data?.message || "Failed to save assignment");
// // //   } finally {
// // //     setBusySave(null);
// // //   }
// // // };


// // //   const onLogout = () => {
// // //     setAdminToken("");
// // //     navigate("/admin/login");
// // //   };

// // //   // simple driver lookup for live map
// // //   const driverByVehicle = useMemo(() => {
// // //     const out = {};
// // //     Object.values(assignments).forEach((row) => {
// // //       if (row.vehicleNo && row.driver) {
// // //         out[row.vehicleNo] = row.driver;
// // //       }
// // //     });
// // //     return out;
// // //   }, [assignments]);

// // //   return (
// // //     <div className="admin-layout">
// // //       {/* left sidebar */}
// // //       <div
// // //         className="sidebar"
// // //         style={{
// // //           width: isNarrow ? 220 : 260, // responsive width override
// // //         }}
// // //       >
// // //         {/* top section: logo, back button, nav */}
// // //         <div>
// // //           <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
// // //             <img
// // //               src={iitmLogo}
// // //               alt="IITM"
// // //               style={{ width: 32, height: 32, borderRadius: 8 }}
// // //             />
// // //             <div>
// // //               <div style={{ fontWeight: 800, fontSize: 16 }}>Admin</div>
// // //               <div style={{ fontSize: 12, opacity: 0.8 }}>Moving Mountains</div>
// // //             </div>
// // //           </div>
// // //           <button
// // //             onClick={() => navigate("/")}
// // //             style={{
// // //               marginTop: 8,
// // //               textAlign: "left",
// // //               padding: "6px 10px",
// // //               borderRadius: 10,
// // //               border: "1px solid #4b5563",
// // //               background: "transparent",
// // //               color: "#e5e7eb",
// // //               cursor: "pointer",
// // //               fontSize: 13,
// // //             }}
// // //           >
// // //             ← Back to map
// // //           </button>
// // //           <div
// // //             style={{
// // //               marginTop: 16,
// // //               display: "flex",
// // //               flexDirection: "column",
// // //               gap: 6,
// // //             }}
// // //           >
// // //             <button
// // //               onClick={() => setTab("drivers")}
// // //               style={{
// // //                 textAlign: "left",
// // //                 padding: "8px 10px",
// // //                 borderRadius: 10,
// // //                 border: "none",
// // //                 background: tab === "drivers" ? "#f9fafb" : "transparent",
// // //                 color: tab === "drivers" ? "#111827" : "#e5e7eb",
// // //                 fontWeight: 600,
// // //                 cursor: "pointer",
// // //               }}
// // //             >
// // //               + Add Driver
// // //             </button>
// // //             <button
// // //               onClick={() => setTab("routes")}
// // //               style={{
// // //                 textAlign: "left",
// // //                 padding: "8px 10px",
// // //                 borderRadius: 10,
// // //                 border: "none",
// // //                 background: tab === "routes" ? "#f9fafb" : "transparent",
// // //                 color: tab === "routes" ? "#111827" : "#e5e7eb",
// // //                 fontWeight: 600,
// // //                 cursor: "pointer",
// // //               }}
// // //             >
// // //               Route Allocation
// // //             </button>
// // //             <button
// // //               onClick={() => setTab("live")}
// // //               style={{
// // //                 textAlign: "left",
// // //                 padding: "8px 10px",
// // //                 borderRadius: 10,
// // //                 border: "none",
// // //                 background: tab === "live" ? "#f9fafb" : "transparent",
// // //                 color: tab === "live" ? "#111827" : "#e5e7eb",
// // //                 fontWeight: 600,
// // //                 cursor: "pointer",
// // //               }}
// // //             >
// // //               Live Tracking
// // //             </button>
// // //           </div>
// // //         </div>

// // //         {/* bottom logout – pinned to sidebar bottom */}
// // //         <button
// // //           onClick={onLogout}
// // //           style={{
// // //             marginTop: 12,
// // //             padding: "8px 10px",
// // //             borderRadius: 10,
// // //             border: "1px solid #6b7280",
// // //             background: "transparent",
// // //             color: "#e5e7eb",
// // //             cursor: "pointer",
// // //             fontSize: 13,
// // //           }}
// // //         >
// // //           Logout
// // //         </button>
// // //       </div>

// // //       {/* main content */}
// // //       <div className="content">
// // //         {tab === "drivers" && (
// // //           <div style={{ maxWidth: 800, margin: "0 auto" }}>
// // //             <h2 style={{ marginTop: 0 }}>Drivers</h2>
// // //             <form
// // //               onSubmit={handleAddDriver}
// // //               style={{
// // //                 display: "grid",
// // //                 gridTemplateColumns: "2fr 2fr auto",
// // //                 gap: 8,
// // //                 alignItems: "center",
// // //                 marginBottom: 16,
// // //               }}
// // //             >
// // //               <input
// // //                 value={newName}
// // //                 onChange={(e) => setNewName(e.target.value)}
// // //                 placeholder="Driver name"
// // //                 required
// // //                 style={{
// // //                   padding: 10,
// // //                   borderRadius: 10,
// // //                   border: "1px solid #D1D5DB",
// // //                   fontSize: 14,
// // //                 }}
// // //               />
// // //               <input
// // //                 value={newPhone}
// // //                 onChange={(e) => setNewPhone(e.target.value)}
// // //                 placeholder="Phone number (unique)"
// // //                 required
// // //                 style={{
// // //                   padding: 10,
// // //                   borderRadius: 10,
// // //                   border: "1px solid #D1D5DB",
// // //                   fontSize: 14,
// // //                 }}
// // //               />
// // //               <button
// // //                 type="submit"
// // //                 style={{
// // //                   padding: "10px 16px",
// // //                   borderRadius: 10,
// // //                   border: "1px solid #0D47A1",
// // //                   background: "#0D47A1",
// // //                   color: "#fff",
// // //                   fontWeight: 700,
// // //                   cursor: "pointer",
// // //                   fontSize: 14,
// // //                 }}
// // //               >
// // //                 Add
// // //               </button>
// // //             </form>
// // //             {err && (
// // //               <div
// // //                 style={{
// // //                   color: "#b00020",
// // //                   fontWeight: 600,
// // //                   marginBottom: 8,
// // //                 }}
// // //               >
// // //                 {err}
// // //               </div>
// // //             )}
// // //             <div
// // //               style={{
// // //                 display: "grid",
// // //                 gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
// // //                 gap: 12,
// // //               }}
// // //             >
// // //               {drivers.map((d) => (
// // //                 <div
// // //                   key={d._id}
// // //                   style={{
// // //                     background: "#fff",
// // //                     borderRadius: 12,
// // //                     border: "1px solid #e5e7eb",
// // //                     padding: 12,
// // //                     display: "flex",
// // //                     flexDirection: "column",
// // //                     gap: 4,
// // //                   }}
// // //                 >
// // //                   <div style={{ fontWeight: 700, fontSize: 15 }}>{d.name}</div>
// // //                   <div style={{ fontSize: 13, opacity: 0.8 }}>
// // //                     Phone: {d.phoneNumber || "—"}
// // //                   </div>
// // //                   <div style={{ fontSize: 12, opacity: 0.6 }}>
// // //                     username: <code>{d.username}</code>
// // //                   </div>
// // //                   <button
// // //                     onClick={() => handleRemoveDriver(d._id)}
// // //                     style={{
// // //                       marginTop: 8,
// // //                       alignSelf: "flex-start",
// // //                       padding: "6px 10px",
// // //                       borderRadius: 8,
// // //                       border: "1px solid #f97373",
// // //                       background: "#fef2f2",
// // //                       color: "#b91c1c",
// // //                       fontSize: 12,
// // //                       fontWeight: 600,
// // //                       cursor: "pointer",
// // //                     }}
// // //                   >
// // //                     Remove
// // //                   </button>
// // //                 </div>
// // //               ))}
// // //               {drivers.length === 0 && (
// // //                 <div style={{ fontSize: 14, opacity: 0.7 }}>
// // //                   No drivers yet. Add your first driver above.
// // //                 </div>
// // //               )}
// // //             </div>
// // //           </div>
// // //         )}

// // //         {tab === "routes" && (
// // //           <div style={{ maxWidth: 1100, margin: "0 auto" }}>
// // //             <h2 style={{ marginTop: 0 }}>Route Allocation</h2>
// // //             {err && (
// // //               <div
// // //                 style={{
// // //                   color: "#b00020",
// // //                   fontWeight: 600,
// // //                   marginBottom: 8,
// // //                 }}
// // //               >
// // //                 {err}
// // //               </div>
// // //             )}
// // //             <div style={{ overflowX: "auto" }}>
// // //               <table
// // //                 style={{
// // //                   width: "100%",
// // //                   borderCollapse: "collapse",
// // //                   background: "#fff",
// // //                   borderRadius: 12,
// // //                   overflow: "hidden",
// // //                 }}
// // //               >
// // //                 <thead>
// // //                   <tr
// // //                     style={{
// // //                       background: "#f9fafb",
// // //                       borderBottom: "1px solid #e5e7eb",
// // //                     }}
// // //                   >
// // //                     <th style={{ padding: 8, textAlign: "left" }}>#</th>
// // //                     <th style={{ padding: 8, textAlign: "left" }}>Vehicle</th>
// // //                     <th style={{ padding: 8, textAlign: "left" }}>Route</th>
// // //                     <th style={{ padding: 8, textAlign: "left" }}>Driver</th>
// // //                     <th style={{ padding: 8, textAlign: "left" }}>Auto-pilot</th>
// // //                     <th style={{ padding: 8, textAlign: "left" }} />
// // //                   </tr>
// // //                 </thead>
// // //                 <tbody>
// // //                   {VEHICLES.map((veh, idx) => {
// // //                     const base = getBaseAssignForVehicle(veh);
// // //                     const local = localAssign[veh] || base;
// // //                     const hasAssignment =
// // //                       !!base.routeCode && !!base.driverId; // existing saved row?
// // //                     const isEditing = !!editing[veh];
// // //                     const locked = hasAssignment && !isEditing;
// // //                     const saving = busySave === veh;

// // //                     return (
// // //                       <tr
// // //                         key={veh}
// // //                         style={{ borderBottom: "1px solid #f3f4f6" }}
// // //                       >
// // //                         <td style={{ padding: 8 }}>{idx + 1}</td>
// // //                         <td style={{ padding: 8, fontWeight: 600 }}>{veh}</td>
// // //                         <td style={{ padding: 8 }}>
// // //                           <select
// // //                             value={local.routeCode}
// // //                             onChange={(e) =>
// // //                               updateLocalAssign(veh, {
// // //                                 routeCode: e.target.value,
// // //                               })
// // //                             }
// // //                             disabled={locked || saving}
// // //                             style={{
// // //                               padding: 8,
// // //                               borderRadius: 8,
// // //                               border: "1px solid #d1d5db",
// // //                               fontSize: 13,
// // //                               minWidth: 180,
// // //                               backgroundColor: locked ? "#f9fafb" : "white",
// // //                             }}
// // //                           >
// // //                             <option value="">Select route…</option>
// // //                             {ADMIN_ROUTES.map((r) => (
// // //                               <option key={r.code} value={r.code}>
// // //                                 {r.label}
// // //                               </option>
// // //                             ))}
// // //                           </select>
// // //                         </td>
// // //                         <td style={{ padding: 8 }}>
// // //                           <select
// // //                             value={local.driverId}
// // //                             onChange={(e) =>
// // //                               updateLocalAssign(veh, {
// // //                                 driverId: e.target.value,
// // //                               })
// // //                             }
// // //                             disabled={locked || saving}
// // //                             style={{
// // //                               padding: 8,
// // //                               borderRadius: 8,
// // //                               border: "1px solid #d1d5db",
// // //                               fontSize: 13,
// // //                               minWidth: 180,
// // //                               backgroundColor: locked ? "#f9fafb" : "white",
// // //                             }}
// // //                           >
// // //                             <option value="">Select driver…</option>
// // //                             {drivers.map((d) => (
// // //                               <option key={d._id} value={d._id}>
// // //                                 {d.name} ({d.phoneNumber || d.username})
// // //                               </option>
// // //                             ))}
// // //                           </select>
// // //                         </td>
// // //                         <td style={{ padding: 8 }}>
// // //                           <label
// // //                             style={{
// // //                               display: "inline-flex",
// // //                               alignItems: "center",
// // //                               gap: 6,
// // //                               fontSize: 13,
// // //                               opacity: locked ? 0.6 : 1,
// // //                             }}
// // //                           >
// // //                             <input
// // //                               type="checkbox"
// // //                               checked={!!local.autopilotEnabled}
// // //                               onChange={(e) =>
// // //                                 updateLocalAssign(veh, {
// // //                                   autopilotEnabled: e.target.checked,
// // //                                 })
// // //                               }
// // //                               disabled={locked || saving}
// // //                             />
// // //                             On
// // //                           </label>
// // //                         </td>
// // //                         <td style={{ padding: 8 }}>
// // //                           <button
// // //                             onClick={() => {
// // //                               if (locked) {
// // //                                 // switch to edit mode
// // //                                 updateLocalAssign(veh, {}); // ensure local copy
// // //                                 setEditing((prev) => ({
// // //                                   ...prev,
// // //                                   [veh]: true,
// // //                                 }));
// // //                               } else {
// // //                                 // save current data
// // //                                 handleSaveAssignment(veh);
// // //                               }
// // //                             }}
// // //                             disabled={saving}
// // //                             style={{
// // //                               padding: "6px 12px",
// // //                               borderRadius: 8,
// // //                               border: "1px solid #0D47A1",
// // //                               background: saving
// // //                                 ? "#e5e7eb"
// // //                                 : locked
// // //                                 ? "#0D47A1"
// // //                                 : "#0D47A1",
// // //                               color: saving ? "#4b5563" : "#fff",
// // //                               fontSize: 13,
// // //                               fontWeight: 700,
// // //                               cursor: saving ? "not-allowed" : "pointer",
// // //                             }}
// // //                           >
// // //                             {saving
// // //                               ? "Saving…"
// // //                               : locked
// // //                               ? "Edit"
// // //                               : "Save"}
// // //                           </button>
// // //                         </td>
// // //                       </tr>
// // //                     );
// // //                   })}
// // //                 </tbody>
// // //               </table>
// // //             </div>
// // //           </div>
// // //         )}

// // //         {tab === "live" && (
// // //           <div style={{ height: "calc(100vh - 40px)" }}>
// // //             <h2 style={{ marginTop: 0 }}>Live Tracking</h2>
// // //             <div style={{ height: "calc(100% - 36px)" }}>
// // //               <MapContainer
// // //                 center={center}
// // //                 zoom={DEFAULT_ZOOM}
// // //                 maxZoom={22}
// // //                 style={{ width: "100%", height: "100%" }}
// // //               >
// // //                 <TileLayer
// // //                   attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
// // //                   url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
// // //                   maxNativeZoom={19}
// // //                   maxZoom={22}
// // //                 />
// // //                 {liveBuses.map((bus) => {
// // //                   // const label = vehicleLabel(bus.vehicleno);
// // //                   const rc = assignments[bus.vehicleno]?.routeCode;   // admin assignments map is row objects
// // //                   const label = vehicleLabel(bus.vehicleno, rc);
// // //                   const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
// // //                   const drv = driverByVehicle[bus.vehicleno];
// // //                   return (
// // //                     <Marker
// // //                       key={bus.vehicleno}
// // //                       position={{
// // //                         lat: bus.latitude,
// // //                         lng: bus.longitude,
// // //                       }}
// // //                       // icon={createVehicleDivIcon(
// // //                       //   label,
// // //                       //   Number(bus.heading) || 0
// // //                       // )}
// // //                       icon={createVehicleDivIcon(label, Number(bus.heading) || 0, palette)}
// // //                     >
// // //                       <Popup>
// // //                         <div className="info-window">
// // //                           <h4>
// // //                             {bus.vehicleno} ({label})
// // //                           </h4>
// // //                           <p>
// // //                             <strong>Speed:</strong>{" "}
// // //                             {Number.isFinite(bus.speed)
// // //                               ? bus.speed.toFixed(1)
// // //                               : "N/A"}{" "}
// // //                             km/h
// // //                           </p>
// // //                           {drv && (
// // //                             <>
// // //                               <p>
// // //                                 <strong>Driver:</strong> {drv.name}</p>
// // //                               <p>
// // //                                 <strong>Phone:</strong>{" "}
// // //                                 {drv.phoneNumber || "—"}
// // //                               </p>
// // //                             </>
// // //                           )}
// // //                           {!drv && (
// // //                             <p style={{ fontSize: 13, opacity: 0.7 }}>
// // //                               No driver assigned
// // //                             </p>
// // //                           )}
// // //                         </div>
// // //                       </Popup>
// // //                     </Marker>
// // //                   );
// // //                 })}
// // //               </MapContainer>
// // //             </div>
// // //           </div>
// // //         )}
// // //       </div>
// // //     </div>
// // //   );
// // // }

// // // function RecenterMap({ center, zoom }) {
// // //   const map = useMap();

// // //   useEffect(() => {
// // //     if (!center) return;
// // //     map.setView([center.lat, center.lng], zoom ?? map.getZoom(), { animate: true });
// // //   }, [center, zoom, map]);

// // //   return null;
// // // }



// // // /* ---------------------------- MAP PAGE ---------------------------- */
// // // function MapPage() {
// // //   const [buses, setBuses] = useState([]);
// // //   const [userLocation, setUserLocation] = useState(null);
// // //   const [socketStatus, setSocketStatus] = useState('disconnected');
// // //   const [roads, setRoads] = useState(null);
// // //   const [filterType, setFilterType] = useState('all');
// // //   const [mapCenter, setMapCenter] = useState(center);
// // //   const [locError, setLocError] = useState("");
// // //   const [geoState, setGeoState] = useState("unknown"); // "granted" | "prompt" | "denied" | "unknown"
// // //   const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(true);
// // //   const headerRef = useRef(null);
// // //   const [headerHeight, setHeaderHeight] = useState(0);

// // //   // maintenance banner state
// // //   const [maintenance, setMaintenance] = useState(false);
// // //   const [maintenanceReason, setMaintenanceReason] = useState('');
// // //   const lastUpdateRef = useRef(0);
// // //     // ---- Backend ETA (JWT) state ----
// // //   const [etaToken, setEtaToken] = useState(null);    // JWT for /api/eta/*
// // //   const [stopEtas, setStopEtas] = useState({});      // { [stopId]: { data, error } }

// // //   const etaTokenRef = useRef(null);
// // //   const etaTokenFetchingRef = useRef(false);

// // //   const flatRoadLinesRef = useRef([]);
// // //   const lastSmoothedRef = useRef(new Map());

// // //   // ---- Schedule Modal state ----
// // //   const [showSchedule, setShowSchedule] = useState(false);
// // //   const [activeTab, setActiveTab] = useState(0);

// // //   // --- responsive helpers ---
// // //   const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 480);
// // //   const clamp = (min, pref, max) => `clamp(${min}, ${pref}, ${max})`;
// // //   const pad = isNarrow ? 12 : 16;
// // //   const gap = isNarrow ? 8  : 12;
// // //   const chipFS = isNarrow ? 12 : 13;

// // //   useEffect(() => {
// // //     const onResize = () => setIsNarrow(window.innerWidth <= 480);
// // //     window.addEventListener('resize', onResize);
// // //     return () => window.removeEventListener('resize', onResize);
// // //   }, []);

// // //     // ---- Helper: fetch / refresh JWT for ETA API ----
// // //   const fetchEtaToken = useCallback(async () => {
// // //     if (!BACKEND || !API_KEY) return null;

// // //     // Avoid spamming if many 401s come at once
// // //     if (etaTokenFetchingRef.current) {
// // //       return etaTokenRef.current;
// // //     }

// // //     etaTokenFetchingRef.current = true;
// // //     try {
// // //       const res = await axios.post(
// // //         `${BACKEND}/api/auth/token`,
// // //         {},
// // //         {
// // //           timeout: 5000,
// // //           headers: {
// // //             Authorization: `Bearer ${API_KEY}`, // apiKeyAuth middleware
// // //           },
// // //         }
// // //       );
// // //       const t = res.data?.token;
// // //       if (t) {
// // //         etaTokenRef.current = t;
// // //         setEtaToken(t);
// // //         return t;
// // //       }
// // //       return null;
// // //     } catch (e) {
// // //       console.warn('Failed to fetch ETA JWT token:', e?.message || e);
// // //       return null;
// // //     } finally {
// // //       etaTokenFetchingRef.current = false;
// // //     }
// // //   }, []);

// // //     // ---- Get initial JWT token for ETA API ----
// // //   useEffect(() => {
// // //     fetchEtaToken();
// // //   }, [fetchEtaToken]);

// // //     // ---- Poll backend ETA for every stop every 5 seconds ----
// // //   useEffect(() => {
// // //     if (!BACKEND) return;

// // //     let cancelled = false;

// // //     async function fetchAllStopEtas() {
// // //       // If we don't have a token yet, try to get one
// // //       if (!etaTokenRef.current) {
// // //         await fetchEtaToken();
// // //         if (!etaTokenRef.current || cancelled) return;
// // //       }

// // //       try {
// // //         const results = await Promise.all(
// // //           busStops.map(async (s) => {
// // //             try {
// // //               const res = await axios.get(
// // //                 `${BACKEND}/api/eta/stop/${s.id}`,
// // //                 {
// // //                   timeout: 8000,
// // //                   headers: {
// // //                     Authorization: `Bearer ${etaTokenRef.current}`,
// // //                   },
// // //                 }
// // //               );
// // //               return { stopId: s.id, data: res.data, error: null };
// // //             } catch (err) {
// // //               // If token expired, refresh in background
// // //               if (err?.response?.status === 401) {
// // //                 console.warn('ETA JWT expired for stop', s.id, '— refreshing token');
// // //                 fetchEtaToken().catch(() => {});
// // //               } else {
// // //                 console.warn(
// // //                   'ETA error for stop',
// // //                   s.id,
// // //                   err?.response?.status || '',
// // //                   err?.message || err
// // //                 );
// // //               }
// // //               return { stopId: s.id, data: null, error: true };
// // //             }
// // //           })
// // //         );

// // //         if (cancelled) return;

// // //         setStopEtas((prev) => {
// // //           const next = { ...prev };
// // //           for (const r of results) {
// // //             next[r.stopId] = { data: r.data, error: r.error };
// // //           }
// // //           return next;
// // //         });
// // //       } catch (e) {
// // //         if (!cancelled) {
// // //           console.warn('ETA batch fetch failed:', e?.message || e);
// // //         }
// // //       }
// // //     }

// // //     // first call + interval
// // //     fetchAllStopEtas();
// // //     const id = setInterval(fetchAllStopEtas, 5000);

// // //     return () => {
// // //       cancelled = true;
// // //       clearInterval(id);
// // //     };
// // //   }, [fetchEtaToken]);


// // //   // ---- stable socket + pause flag ----
// // //   const socketRef = useRef(null);
// // //   const ignoreUpdatesRef = useRef(false);

// // //   // ---- assignments (vehicle -> routeCode) ----
// // //   const [assignments, setAssignments] = useState({});
// // //   async function refreshAssignments() {
// // //     try {
// // //       const r = await axios.get(`${BACKEND}/api/vehicle-route/active`);
// // //       const map = {};
// // //       for (const row of r.data?.rows || []) map[row.vehicleNo] = row.routeCode;
// // //       setAssignments(map);
// // //     } catch {}
// // //   }
// // //   useEffect(() => { refreshAssignments(); const t = setInterval(refreshAssignments, 60000); return () => clearInterval(t); }, []);

// // //   // Load roads
// // //   useEffect(() => {
// // //     let cancelled = false;
// // //     (async () => {
// // //       try {
// // //         const r = await fetch(ROADS_URL);
// // //         if (!cancelled && r.ok) setRoads(await r.json());
// // //       } catch (e) {
// // //         if (!cancelled) console.warn('Failed to load roads:', e?.message || e);
// // //       }
// // //     })();
// // //     return () => { cancelled = true; };
// // //   }, []);

// // //   // Flatten roads to LineStrings
// // //   useEffect(() => {
// // //     if (!roads) return;
// // //     const lines = [];
// // //     turf.flattenEach(roads, (feat) => {
// // //       if (feat.geometry?.type === 'LineString') lines.push(feat);
// // //     });
// // //     flatRoadLinesRef.current = lines;
// // //   }, [roads]);

// // //   // // Geolocation
// // //   // useEffect(() => {
// // //   //   if ('geolocation' in navigator) {
// // //   //     navigator.geolocation.getCurrentPosition(
// // //   //       (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
// // //   //       (err) => console.warn('Geolocation error:', err?.message),
// // //   //       { enableHighAccuracy: true, timeout: 5000 }
// // //   //     );
// // //   //   }
// // //   // }, []);

// // //   useEffect(() => {
// // //   let perm;
// // //   (async () => {
// // //     try {
// // //       if (!navigator.permissions?.query) return;
// // //       perm = await navigator.permissions.query({ name: "geolocation" });
// // //       setGeoState(perm.state);
// // //       perm.onchange = () => setGeoState(perm.state);
// // //     } catch {
// // //       // ignore
// // //     }
// // //   })();
// // //   return () => {
// // //     if (perm) perm.onchange = null;
// // //   };
// // // }, []);


// // // //   const requestUserLocation = useCallback(() => {
// // // //   setLocError("");

// // // //   if (!("geolocation" in navigator)) {
// // // //     setLocError("Geolocation not supported on this device/browser.");
// // // //     return;
// // // //   }

// // // //   navigator.geolocation.getCurrentPosition(
// // // //     (pos) => {
// // // //       const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
// // // //       setUserLocation(loc);
// // // //       setMapCenter(loc); // ✅ recenter map here
// // // //       setLocError("");
// // // //     },
// // // //     (err) => {
// // // //       console.warn("Geolocation error:", err?.message || err);
// // // //       // If user didn't allow, browser will show prompt only if not blocked.
// // // //       setLocError("Please allow location to center the map near you.");
// // // //     },
// // // //     { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
// // // //   );
// // // // }, []);

// // // const requestUserLocation = useCallback(() => {
// // //   setLocError("");

// // //   if (!("geolocation" in navigator)) {
// // //     setLocError("Geolocation not supported on this device/browser.");
// // //     return;
// // //   }

// // //   navigator.geolocation.getCurrentPosition(
// // //     (pos) => {
// // //       const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
// // //       setUserLocation(loc);
// // //       setMapCenter(loc);
// // //       setLocError("");
// // //     },
// // //     (err) => {
// // //       // err.code === 1 => PERMISSION_DENIED
// // //       if (err?.code === 1) {
// // //         // If browser permission is truly "denied", it won't show popup again.
// // //         setLocError(
// // //           "Location is blocked in browser settings. Click the 🔒 icon near the URL → Site settings → Location → Allow (or Ask), then refresh."
// // //         );
// // //       } else {
// // //         setLocError(err?.message || "Unable to fetch location.");
// // //       }
// // //     },
// // //     { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
// // //   );
// // // }, []);


// // // useEffect(() => {
// // //   // ✅ On every page open (and after browser refresh), request location and recenter
// // //   requestUserLocation();
// // // }, [requestUserLocation]);


// // //   // Initial fetch
// // //   useEffect(() => {
// // //     if (!BACKEND) {
// // //       setMaintenance(true);
// // //       setMaintenanceReason('Server URL not configured');
// // //       return;
// // //     }
// // //     axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 })
// // //       .then((res) => {
// // //         const arr = Array.isArray(res.data) ? res.data : [];
// // //         const byVeh = new Map();
// // //         for (const d of arr) {
// // //           const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
// // //           byVeh.set(d.vehicleno, {
// // //             vehicleno: d.vehicleno,
// // //             latitude: d.latitude,
// // //             longitude: d.longitude,
// // //             speed: d.speed,
// // //             heading
// // //           });
// // //         }
// // //         const list = [...byVeh.values()];
// // //         setBuses(processVehicles(list));
// // //         lastUpdateRef.current = Date.now();
// // //       })
// // //       .catch((err) => {
// // //         console.error('Initial fetch error:', err?.message || err);
// // //         setMaintenance(true);
// // //         setMaintenanceReason('Unable to fetch live data');
// // //       });
// // //   }, []);

// // //   // ---- WebSocket packet handler ----
// // //   const onPacket = (data) => {
// // //     if (ignoreUpdatesRef.current) return;
// // //     const incoming = {
// // //       vehicleno: data.vehicleno,
// // //       latitude: data.latitude,
// // //       longitude: data.longitude,
// // //       speed: data.speed,
// // //       heading: Number.isFinite(data?.heading) ? toHeading(data.heading) : toHeading(data?.direction)
// // //     };

// // //     setBuses((prev) => {
// // //       const nextByVeh = new Map(prev.map((b) => [b.vehicleno, b]));
// // //       const processed = processVehicles([incoming], nextByVeh);
// // //       const merged = new Map(prev.map((b) => [b.vehicleno, b]));
// // //       for (const p of processed) merged.set(p.vehicleno, p);
// // //       return [...merged.values()];
// // //     });

// // //     lastUpdateRef.current = Date.now();
// // //   };

// // //   // Live updates (Socket.IO)
// // //   useEffect(() => {
// // //     if (!BACKEND) return;

// // //     setSocketStatus('connecting');
// // //     const socket = io(BACKEND, {
// // //       transports: ['websocket', 'polling'],
// // //       reconnection: true,
// // //       reconnectionAttempts: Infinity,
// // //       reconnectionDelay: 1000,
// // //       timeout: 10000,
// // //       withCredentials: false
// // //     });
// // //     socketRef.current = socket;

// // //     socket.on('connect', () => setSocketStatus('connected'));
// // //     socket.on('disconnect', () => setSocketStatus('disconnected'));
// // //     socket.on('connect_error', (e) => {
// // //       console.warn('Socket connect_error:', e?.message || e);
// // //       setSocketStatus('disconnected');
// // //     });

// // //     socket.on('new-bus-data', onPacket);

// // //     return () => {
// // //       socket.off('new-bus-data', onPacket);
// // //       socket.disconnect();
// // //       socketRef.current = null;
// // //     };
// // //   }, [roads]); // re-connect only if roads change

// // //   // Staleness / maintenance detector
// // //   useEffect(() => {
// // //     const t = setInterval(() => {
// // //       const sinceMs = Date.now() - lastUpdateRef.current;
// // //       const stale = sinceMs > STALE_MS;
// // //       const downAndEmpty = socketStatus === 'disconnected' && buses.length === 0;

// // //       if (stale || downAndEmpty) {
// // //         const mins = Math.floor(sinceMs / 60000);
// // //         const secs = Math.floor((sinceMs % 60000) / 1000);
// // //         setMaintenance(true);
// // //         setMaintenanceReason(
// // //           stale ? `No new data for ${mins}m ${secs}s` : 'Live connection unavailable'
// // //         );
// // //       } else {
// // //         setMaintenance(false);
// // //         setMaintenanceReason('');
// // //       }
// // //     }, 5000);
// // //     return () => clearInterval(t);
// // //   }, [socketStatus, buses.length]);

// // //   // ---- Processing pipeline: smooth then snap ----
// // //   function snapToRoad(coord, flatLines) {
// // //     if (!flatLines || flatLines.length === 0) return null;
// // //     const pt = turf.point([coord.lng, coord.lat]);

// // //     let best = null;
// // //     let bestDist = Infinity;

// // //     for (const line of flatLines) {
// // //       const np = turf.nearestPointOnLine(line, pt, { units: 'meters' });
// // //       const dist = np?.properties?.dist;
// // //       if (Number.isFinite(dist) && dist < bestDist) {
// // //         bestDist = dist;
// // //         const [lng, lat] = np.geometry.coordinates;
// // //         best = { lat, lng, distM: dist };
// // //       }
// // //     }
// // //     return best;
// // //   }

// // //   function processVehicles(list, prevMapOpt) {
// // //     const flatLines = flatRoadLinesRef.current;
// // //     const last = lastSmoothedRef.current;
// // //     const result = [];

// // //     for (const d of list) {
// // //       const id = d.vehicleno;
// // //       const raw = { lat: d.latitude, lng: d.longitude };

// // //       const prev = last.get(id);
// // //       const smoothedCoord = smoothCoord(prev?.coord, raw, SMOOTH_ALPHA);
// // //       const smoothedHeading = smoothHeading(prev?.heading, d.heading, HEADING_ALPHA);

// // //       const snapped = snapToRoad(smoothedCoord, flatLines);

// // //       const use = snapped?.distM != null && snapped.distM <= SNAP_MAX_DIST_M
// // //         ? { lat: snapped.lat, lng: snapped.lng }
// // //         : smoothedCoord;

// // //       last.set(id, { coord: use, heading: smoothedHeading });

// // //       result.push({
// // //         vehicleno: d.vehicleno,
// // //         latitude: use.lat,
// // //         longitude: use.lng,
// // //         speed: d.speed,
// // //         heading: smoothedHeading
// // //       });
// // //     }

// // //     if (prevMapOpt) {
// // //       for (const [id, b] of prevMapOpt) {
// // //         if (list.find(x => x.vehicleno === id)) continue;
// // //         const prev = last.get(id);
// // //         if (!prev) {
// // //           result.push(b);
// // //           continue;
// // //         }
// // //         result.push({
// // //           vehicleno: id,
// // //           latitude: prev.coord.lat,
// // //           longitude: prev.coord.lng,
// // //           speed: b.speed,
// // //           heading: prev.heading
// // //         });
// // //       }
// // //     }

// // //     return result;
// // //   }

// // //     // ---------------- Route geometry + ETA helpers ----------------
// // //   const stopById = useMemo(() => Object.fromEntries(busStops.map(s => [s.id, s])), []);
// // //   function routePolyline(ids) {
// // //     return ids.map(id => ({ lat: stopById[id].lat, lng: stopById[id].lng }));
// // //   }
// // //   function cumDist(poly) {
// // //     const out = [0];
// // //     for (let i = 1; i < poly.length; i++) out.push(out[i - 1] + mBetween(poly[i - 1], poly[i]));
// // //     return out;
// // //   }
// // //   const routeGeom = useMemo(() => {
// // //     const g = {};
// // //     for (const r of ROUTES) {
// // //       const poly = routePolyline(r.ids);
// // //       g[r.code] = { poly, cum: cumDist(poly), ids: r.ids };
// // //     }
// // //     return g;
// // //   }, [stopById]);

// // //   function projectOnRoute(pt, poly) {
// // //     if (!poly || poly.length < 2) return null;
// // //     let best = null;
// // //     for (let i = 0; i < poly.length - 1; i++) {
// // //       const seg = turf.lineString([[poly[i].lng, poly[i].lat], [poly[i + 1].lng, poly[i + 1].lat]]);
// // //       const p = turf.nearestPointOnLine(seg, turf.point([pt.lng, pt.lat]), { units: "meters" });
// // //       const d = p.properties?.dist ?? Infinity;
// // //       if (!best || d < best.dist) {
// // //         const [lng, lat] = p.geometry.coordinates;
// // //         best = { idx: i, point: { lat, lng }, dist: d };
// // //       }
// // //     }
// // //     return best;
// // //   }

// // //   function distanceAlong(poly, cum, proj, targetIndex) {
// // //     const dHead = mBetween(proj.point, poly[proj.idx + 1]);
// // //     const dTail = cum[targetIndex] - cum[proj.idx + 1];
// // //     return dHead + Math.max(0, dTail);
// // //   }

// // //   // // ---- ETA constants: match backend ----
// // //   // const AVG_SPEED_KMPH = 20;
// // //   // const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;
// // //   // const WAIT_RADIUS_M = 25;         // within 25m of first stop
// // //   // const WAIT_SPEED_KMPH_MAX = 1.0;  // consider "waiting" if <= 1 km/h

// // //   // // Constant-speed ETA (20 km/h), ignoring live speed
// // //   // function etaFromMeters(distanceM) {
// // //   //   const seconds = distanceM / AVG_SPEED_MPS;
// // //   //   const minutes = Math.round(seconds / 60);
// // //   //   return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
// // //   // }

// // //   // function nextStopIndex(routeIds, projIdx) {
// // //   //   return Math.min(routeIds.length - 1, projIdx + 1);
// // //   // }

// // //   // // For a given bus, find nearest upcoming stop on its route + ETA
// // //   // function nearestStopEtaForBusOnItsRoute(bus) {
// // //   //   const routeCode = assignments[bus.vehicleno];
// // //   //   if (!routeCode) return null;
// // //   //   const geom = routeGeom[routeCode];
// // //   //   if (!geom || geom.poly.length < 2) return null;

// // //   //   const pt = { lat: bus.latitude, lng: bus.longitude };
// // //   //   const proj = projectOnRoute(pt, geom.poly);
// // //   //   if (!proj) return null;

// // //   //   const firstStopId = geom.ids[0];
// // //   //   const firstStop = stopById[firstStopId];
// // //   //   let waitingAtFirstStop = false;
// // //   //   if (firstStop) {
// // //   //     const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
// // //   //     const speed = Number(bus.speed);
// // //   //     if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
// // //   //       waitingAtFirstStop = true;
// // //   //     }
// // //   //   }

// // //   //   const nextIdx = nextStopIndex(geom.ids, proj.idx);
// // //   //   const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

// // //   //   let etaMin = undefined;
// // //   //   if (!waitingAtFirstStop) {
// // //   //     etaMin = etaFromMeters(distanceM);
// // //   //   }

// // //   //   return {
// // //   //     stopId: geom.ids[nextIdx],
// // //   //     distanceM,
// // //   //     etaMin,
// // //   //     routeCode,
// // //   //     waitingAtFirstStop,
// // //   //   };
// // //   // }

// // //     // ---- ETA constants: match backend ----
// // //   const AVG_SPEED_KMPH = 20;
// // //   const AVG_SPEED_MPS = (AVG_SPEED_KMPH * 1000) / 3600;

// // //   // holding detection at ANY stop
// // //   const HOLD_STOP_RADIUS_M = 30;
// // //   const HOLD_SPEED_KMPH_MAX = 1.5;

// // //   // Constant-speed ETA (20 km/h), ignoring live speed
// // //   function etaFromMeters(distanceM) {
// // //     const seconds = distanceM / AVG_SPEED_MPS;
// // //     const minutes = Math.round(seconds / 60);
// // //     return minutes < 0 || !Number.isFinite(minutes) ? 0 : minutes;
// // //   }

// // //   function nextStopIndex(routeIds, projIdx) {
// // //     return Math.min(routeIds.length - 1, projIdx + 1);
// // //   }

// // //   function detectHoldingStopOnRoute(bus, geom) {
// // //     const pt = { lat: bus.latitude, lng: bus.longitude };
// // //     const speed = Number(bus.speed);

// // //     let bestIdx = -1;
// // //     let bestDist = Infinity;

// // //     for (let i = 0; i < geom.ids.length; i++) {
// // //       const s = stopById[geom.ids[i]];
// // //       if (!s) continue;

// // //       const dist = mBetween(pt, { lat: s.lat, lng: s.lng });
// // //       if (dist < bestDist) {
// // //         bestDist = dist;
// // //         bestIdx = i;
// // //       }
// // //     }

// // //     if (bestIdx < 0) {
// // //       return {
// // //         isHolding: false,
// // //         stopId: null,
// // //         stopIndex: -1,
// // //         distToStop: Infinity
// // //       };
// // //     }

// // //     const slowOrUnknown =
// // //       !Number.isFinite(speed) || speed <= HOLD_SPEED_KMPH_MAX;

// // //     const isHolding =
// // //       bestDist <= HOLD_STOP_RADIUS_M && slowOrUnknown;

// // //     return {
// // //       isHolding,
// // //       stopId: geom.ids[bestIdx],
// // //       stopIndex: bestIdx,
// // //       distToStop: bestDist
// // //     };
// // //   }

// // //   // For a given bus, find nearest stop ETA on its route
// // //   function nearestStopEtaForBusOnItsRoute(bus) {
// // //     const routeCode = assignments[bus.vehicleno];
// // //     if (!routeCode) return null;

// // //     const geom = routeGeom[routeCode];
// // //     if (!geom || geom.poly.length < 2) return null;

// // //     const pt = { lat: bus.latitude, lng: bus.longitude };
// // //     const proj = projectOnRoute(pt, geom.poly);
// // //     if (!proj) return null;

// // //     const holdInfo = detectHoldingStopOnRoute(bus, geom);

// // //     // If bus is standing at a stop, show that stop as current stop with ETA 0
// // //     if (holdInfo.isHolding) {
// // //       return {
// // //         stopId: holdInfo.stopId,
// // //         distanceM: 0,
// // //         etaMin: 0,
// // //         routeCode,
// // //         waitingAtStop: true
// // //       };
// // //     }

// // //     const nextIdx = nextStopIndex(geom.ids, proj.idx);
// // //     const distanceM = distanceAlong(geom.poly, geom.cum, proj, nextIdx);

// // //     return {
// // //       stopId: geom.ids[nextIdx],
// // //       distanceM,
// // //       etaMin: etaFromMeters(distanceM),
// // //       routeCode,
// // //       waitingAtStop: false
// // //     };
// // //   }

// // //   // For a given stop, find the best incoming vehicle + ETA
// // //   // function nextArrivalAtStop(stopId) {
// // //   //   const candidates = [];

// // //   //   for (const bus of buses) {
// // //   //     if (startsWithDigit(bus.vehicleno)) continue;
// // //   //     const rc = assignments[bus.vehicleno];
// // //   //     if (!rc) continue;
// // //   //     const geom = routeGeom[rc];
// // //   //     if (!geom) continue;

// // //   //     const indexInRoute = geom.ids.indexOf(stopId);
// // //   //     if (indexInRoute < 0) continue;

// // //   //     const pt = { lat: bus.latitude, lng: bus.longitude };
// // //   //     const proj = projectOnRoute(pt, geom.poly);
// // //   //     if (!proj) continue;

// // //   //     const targetIndex = Math.max(indexInRoute, proj.idx + 1);
// // //   //     if (targetIndex >= geom.ids.length) continue;

// // //   //     const distanceM = distanceAlong(geom.poly, geom.cum, proj, targetIndex);

// // //   //     // If bus is already past this stop, skip
// // //   //     if (distanceM < 0) continue;

// // //   //     const firstStopId = geom.ids[0];
// // //   //     const firstStop = stopById[firstStopId];
// // //   //     let waitingAtFirstStop = false;
// // //   //     if (firstStop) {
// // //   //       const distToFirst = mBetween(pt, { lat: firstStop.lat, lng: firstStop.lng });
// // //   //       const speed = Number(bus.speed);
// // //   //       if (distToFirst <= WAIT_RADIUS_M && (!Number.isFinite(speed) || speed <= WAIT_SPEED_KMPH_MAX)) {
// // //   //         waitingAtFirstStop = true;
// // //   //       }
// // //   //     }

// // //   //     let etaMin = undefined;
// // //   //     if (!waitingAtFirstStop) {
// // //   //       etaMin = etaFromMeters(distanceM);
// // //   //     }

// // //   //     candidates.push({
// // //   //       vehicle: bus.vehicleno,
// // //   //       routeCode: rc,
// // //   //       etaMin,
// // //   //       waitingAtFirstStop,
// // //   //       distanceM,
// // //   //     });
// // //   //   }

// // //   //   if (!candidates.length) return null;

// // //   //   // Prefer moving vehicles (etaMin != null), then fallback to closest waiting one
// // //   //   const moving = candidates.filter(c => c.etaMin != null);
// // //   //   if (moving.length) {
// // //   //     return moving.reduce((a, b) => (a.etaMin <= b.etaMin ? a : b));
// // //   //   }

// // //   //   // All are waiting at first stop: pick closest along-route
// // //   //   return candidates.reduce((a, b) => (a.distanceM <= b.distanceM ? a : b));
// // //   // }


// // //   // ---- Derived lists ----
// // //   const visibleBuses = buses.filter(b => !startsWithDigit(b.vehicleno));
// // //   const filteredBuses = visibleBuses.filter(b => matchesFilter(b.vehicleno, filterType, assignments));

// // //   // -------------- SCHEDULE UI helpers --------------
// // //   const TABS = [
// // //     { key: 'hostel-main', title: 'Hostel → Main Gate', data: HOSTEL_TO_MAIN, notes: HOSTEL_NOTES, fmt: 'ampm' },
// // //     { key: 'main-hostel', title: 'Main Gate → Hostel', data: MAIN_TO_HOSTEL, notes: MAIN_NOTES, fmt: 'ampm' },
// // //     { key: 'vg-main',     title: 'Velachery Gate → Main Gate', data: VELACHERY_TO_MAIN, notes: VELACHERY_NOTES, fmt: 'ampm' },
// // //     { key: 'vg-hostel',   title: 'Velachery Gate → Hostel', data: VELACHERY_TO_HOSTEL, notes: VELACHERY_NOTES, fmt: 'ampm' },
// // //     { key: 'rp-ed',       title: 'Research Park → ED', data: RP_TO_ED_TIMES, notes: VELACHERY_NOTES, fmt: '24' },
// // //     { key: 'ed-rp',       title: 'ED → Research Park', data: ED_TO_RP_TIMES, notes: VELACHERY_NOTES, fmt: '24' }
// // //   ];

// // //   function TimeChip({ t }) {
// // //     return (
// // //       <div
// // //         style={{
// // //           padding: '10px 12px',
// // //           borderRadius: 12,
// // //           border: '1px solid #e2e8f0',
// // //           background: '#fff',
// // //           textAlign: 'center',
// // //           fontWeight: 700,
// // //           fontSize: clamp('12px','2.8vw','14px')
// // //         }}
// // //       >
// // //         {t}
// // //       </div>
// // //     );
// // //   }

// // //   function CartTable({ rows, title }) {
// // //     const th = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', fontWeight: 700 };
// // //     const td = { textAlign: 'left', padding: '8px', borderBottom: '1px solid #f2f2f2' };

// // //     return (
// // //       <div style={{ marginTop: 8 }}>
// // //         <div style={{ fontWeight: 700, margin: '8px 0' }}>{title}</div>
// // //         <div style={{ overflowX: 'auto' }}>
// // //           <table style={{ width: '100%', borderCollapse: 'collapse' }}>
// // //             <thead>
// // //               <tr>
// // //                 <th style={th}>Cart</th>
// // //                 <th style={th}>RP → ED</th>
// // //                 <th style={th}>ED → RP</th>
// // //               </tr>
// // //             </thead>
// // //             <tbody>
// // //               {rows.map(([Cart, a, b], i) => (
// // //                 <tr key={`${Cart}-${i}`}>
// // //                   <td style={td}>{Cart}</td>
// // //                   <td style={td}>{a}</td>
// // //                   <td style={td}>{b}</td>
// // //                 </tr>
// // //               ))}
// // //             </tbody>
// // //           </table>
// // //         </div>
// // //       </div>
// // //     );
// // //   }

// // //   function ScheduleBody({ tab }) {
// // //     if (tab.fmt === 'table-Cart') {
// // //       const rows1 = Cart1.map(([a, b]) => ['Cart1', a, b]);
// // //       const rows3 = Cart3.map(([a, b]) => ['Cart3', a, b]);
// // //       return (
// // //         <>
// // //           <CartTable rows={rows1} title="Cart1" />
// // //           <CartTable rows={rows3} title="Cart3" />
// // //         </>
// // //       );
// // //     }
// // //     if (tab.fmt === 'table-overall') {
// // //       const all = [
// // //         ...Cart1.map(([a, b]) => ['Cart1', a, b]),
// // //         ...Cart3.map(([a, b]) => ['Cart3', a, b])
// // //       ];
// // //       return <CartTable rows={all} title="Overall" />;
// // //     }

// // //     const times = tab.data;
// // //     return (
// // //       <>
// // //         <div
// // //           style={{
// // //             display: 'grid',
// // //             gridTemplateColumns: isNarrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(140px, 1fr))',
// // //             gap: 8
// // //           }}
// // //         >
// // //           {times.map((t) => <TimeChip key={t} t={t} />)}
// // //         </div>

// // //         {tab.notes && tab.notes.length > 0 && (
// // //           <div style={{ marginTop: 12 }}>
// // //             {tab.notes.map((n) => (
// // //               <div
// // //                 key={n.time}
// // //                 style={{
// // //                   fontSize: 13,
// // //                   background: '#fff8e1',
// // //                   border: '1px solid #f0d48a',
// // //                   padding: '6px 10px',
// // //                   borderRadius: 10,
// // //                   marginTop: 6
// // //                 }}
// // //               >
// // //                 <strong>{n.time}</strong> – {n.note}
// // //               </div>
// // //             ))}
// // //           </div>
// // //         )}
// // //       </>
// // //     );
// // //   }

// // //   function ScheduleModal() {
// // //     const tab = TABS[activeTab];
// // //     const tabListRef = useRef(null);
// // //     const tabScrollRef = useRef(0);

// // //     useLayoutEffect(() => {
// // //       const el = tabListRef.current;
// // //       if (el && el.scrollLeft !== tabScrollRef.current) {
// // //         el.scrollLeft = tabScrollRef.current;
// // //       }
// // //     });

// // //     return (
// // //       <div
// // //         onClick={() => setShowSchedule(false)}
// // //         style={{
// // //           position: 'fixed',
// // //           inset: 0,
// // //           background: 'rgba(0,0,0,0.45)',
// // //           zIndex: 99999,
// // //           display: 'flex',
// // //           alignItems: 'center',
// // //           justifyContent: 'center',
// // //           padding: 16
// // //         }}
// // //       >
// // //         <div
// // //           onClick={(e) => e.stopPropagation()}
// // //           style={{
// // //             position: 'relative',
// // //             width: isNarrow ? '96vw' : 'min(1100px, 96vw)',
// // //             maxHeight: '90dvh',
// // //             background: '#ffffff',
// // //             borderRadius: 16,
// // //             boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
// // //             display: 'flex',
// // //             flexDirection: 'column',
// // //             overflow: 'hidden'
// // //           }}
// // //         >
// // //           {/* Header */}
// // //           <div
// // //             style={{
// // //               padding: isNarrow ? '8px 10px' : '12px 16px',
// // //               borderBottom: '1px solid #eee',
// // //               display: 'flex',
// // //               alignItems: 'center',
// // //               gap: 12
// // //             }}
// // //           >
// // //             <img src={iitmLogo} alt="IIT Madras" style={{ width: isNarrow ? 26 : 32, height: isNarrow ? 26 : 32 }} />
// // //             <div style={{ fontWeight: 800, fontSize: clamp('14px','2.8vw','18px') }}>
// // //               IIT Madras – Campus Bus &amp; Cart Schedule
// // //             </div>
// // //             <button
// // //               onClick={() => setShowSchedule(false)}
// // //               style={{
// // //                 marginLeft: 'auto',
// // //                 border: 'none',
// // //                 background: '#f4f4f5',
// // //                 borderRadius: 999,
// // //                 width: 34,
// // //                 height: 34,
// // //                 cursor: 'pointer',
// // //                 fontSize: 16
// // //               }}
// // //               title="Close"
// // //             >
// // //               ✕
// // //             </button>
// // //           </div>

// // //           {/* Tab bar */}
// // //           <div
// // //             ref={tabListRef}
// // //             onScroll={(e) => { tabScrollRef.current = e.currentTarget.scrollLeft; }}
// // //             style={{
// // //               display: 'block',
// // //               padding: '8px 12px',
// // //               borderBottom: '1px solid #eee',
// // //               overflowX: 'auto',
// // //               overflowY: 'hidden',
// // //               whiteSpace: 'nowrap',
// // //               WebkitOverflowScrolling: 'touch',
// // //               scrollbarWidth: 'thin',
// // //               overscrollBehaviorX: 'contain',
// // //               touchAction: 'pan-x'
// // //             }}
// // //           >
// // //             <div style={{ display: 'inline-flex', gap: 8, minWidth: '100%', marginBottom: isNarrow ? '45px' : '18px'}}>
// // //               {TABS.map((t, i) => (
// // //                 <button
// // //                   key={t.key}
// // //                   onClick={() => setActiveTab(i)}
// // //                   style={{
// // //                     whiteSpace: 'nowrap',
// // //                     padding: isNarrow ? '8px 10px' : '8px 12px',
// // //                     borderRadius: 10,
// // //                     border: i === activeTab ? '1px solid #0D47A1' : '1px solid #ddd',
// // //                     background: i === activeTab ? '#eaf2ff' : '#ffffff',
// // //                     fontWeight: 600,
// // //                     cursor: 'pointer',
// // //                     flex: '0 0 auto'
// // //                   }}
// // //                 >
// // //                   {t.title}
// // //                 </button>
// // //               ))}
// // //             </div>
// // //           </div>

// // //           {/* Body */}
// // //           <div style={{ padding: isNarrow ? 8 : 12, overflow: 'auto' }}>
// // //             <div style={{ fontWeight: 700, marginBottom: 8 }}>{tab.title}</div>
// // //             <ScheduleBody tab={tab} />
// // //           </div>
// // //         </div>
// // //       </div>
// // //     );
// // //   }

// // //   // Pause live updates when modal open; refresh once on close, then resume
// // //   useEffect(() => {
// // //     if (showSchedule) { ignoreUpdatesRef.current = true; return; }
// // //     (async () => {
// // //       try {
// // //         if (!BACKEND) return;
// // //         const res = await axios.get(`${BACKEND}/api/vehicle-data`, { timeout: 10000 });
// // //         const arr = Array.isArray(res.data) ? res.data : [];
// // //         const byVeh = new Map();
// // //         for (const d of arr) {
// // //           const heading = Number.isFinite(d?.heading) ? toHeading(d.heading) : toHeading(d?.direction);
// // //           byVeh.set(d.vehicleno, {
// // //             vehicleno: d.vehicleno,
// // //             latitude: d.latitude,
// // //             longitude: d.longitude,
// // //             speed: d.speed,
// // //             heading
// // //           });
// // //         }
// // //         const list = [...byVeh.values()];
// // //         setBuses(processVehicles(list));
// // //         lastUpdateRef.current = Date.now();
// // //       } catch (e) {
// // //         console.warn('Refresh after modal close failed:', e?.message || e);
// // //       } finally {
// // //         ignoreUpdatesRef.current = false;
// // //       }
// // //     })();
// // //   }, [showSchedule]);

// // //   // Responsive stop icons (smaller on phones)
// // //   const stopIconInstance = useMemo(
// // //     () => createCustomIcon(stopIconImg, isNarrow ? [40, 40] : [50, 50]),
// // //     [isNarrow]
// // //   );

// // //   useLayoutEffect(() => {
// // //   const el = headerRef.current;
// // //   if (!el) return;

// // //   const updateHeaderHeight = () => {
// // //     setHeaderHeight(el.getBoundingClientRect().height || 0);
// // //   };

// // //   updateHeaderHeight();

// // //   let resizeObserver;

// // //   if (typeof ResizeObserver !== "undefined") {
// // //     resizeObserver = new ResizeObserver(updateHeaderHeight);
// // //     resizeObserver.observe(el);
// // //   }

// // //   window.addEventListener("resize", updateHeaderHeight);

// // //   return () => {
// // //     if (resizeObserver) resizeObserver.disconnect();
// // //     window.removeEventListener("resize", updateHeaderHeight);
// // //   };
// // // }, []);
// // //   // --------------------------- RENDER ---------------------------
// // //   return (
// // //     <div className="map-container">
// // //       {/* Header */}
// // // {/* Header */}
// // // <div
// // // ref={headerRef}
// // //   style={{
// // //     position: 'fixed',
// // //     top: 'max(8px, env(safe-area-inset-top))',
// // //     left: '50%',
// // //     transform: 'translateX(-50%)',
// // //     zIndex: 10000,
// // //     background: '#ffffff',
// // //     border: '1px solid #e6e6e7',
// // //     borderRadius: 16,
// // //     padding: isHeaderCollapsed ? (isNarrow ? 8 : 10) : pad,
// // //     boxShadow: '0 18px 40px rgba(0,0,0,0.25)',
// // //     width: isHeaderCollapsed
// // //       ? (isNarrow ? 'calc(100vw - 16px)' : 'min(420px, calc(100vw - 32px))')
// // //       : (isNarrow ? 'calc(100vw - 16px)' : 'min(560px, calc(100vw - 32px))'),
// // //     backdropFilter: 'blur(2px)',
// // //     transition: 'width 180ms ease, padding 180ms ease'
// // //   }}
// // // >
// // //   {/* Title row */}
// // //   <div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? 8 : 12 }}>
// // //     <img
// // //       src={iitmLogo}
// // //       alt="IIT Madras"
// // //       style={{
// // //         width: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
// // //         height: isHeaderCollapsed ? 26 : (isNarrow ? 28 : 36),
// // //         flex: '0 0 auto'
// // //       }}
// // //     />

// // //     <h2
// // //       style={{
// // //         margin: 0,
// // //         fontSize: isHeaderCollapsed
// // //           ? clamp('15px', '3vw', '20px')
// // //           : clamp('18px', '3.2vw', '28px'),
// // //         lineHeight: 1.1,
// // //         letterSpacing: 0.3,
// // //         fontWeight: 800,
// // //         whiteSpace: 'nowrap',
// // //         overflow: 'hidden',
// // //         textOverflow: 'ellipsis',
// // //         flex: 1
// // //       }}
// // //     >
// // //       Moving Mountains
// // //     </h2>

// // //     {isHeaderCollapsed && (
// // //       <div
// // //         style={{
// // //           fontSize: isNarrow ? 12 : 13,
// // //           fontWeight: 800,
// // //           color: '#111827',
// // //           whiteSpace: 'nowrap'
// // //         }}
// // //         title="Visible vehicles / total vehicles"
// // //       >
// // //         {filteredBuses.length}/{buses.length}
// // //       </div>
// // //     )}

// // //     {!isHeaderCollapsed && (
// // //       <a
// // //         href="/admin"
// // //         style={{
// // //           fontWeight: 700,
// // //           whiteSpace: 'nowrap'
// // //         }}
// // //       >
// // //         Admin ↗
// // //       </a>
// // //     )}

// // //     <button
// // //       type="button"
// // //       onClick={() => setIsHeaderCollapsed((v) => !v)}
// // //       aria-label={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
// // //       title={isHeaderCollapsed ? 'Expand' : 'Collapse'}
// // //       style={{
// // //         border: '1px solid #D1D5DB',
// // //         background: '#ffffff',
// // //         color: '#111827',
// // //         borderRadius: 10,
// // //         padding: isNarrow ? '6px 8px' : '7px 10px',
// // //         fontWeight: 800,
// // //         fontSize: isNarrow ? 13 : 14,
// // //         lineHeight: 1,
// // //         cursor: 'pointer',
// // //         minWidth: isNarrow ? 34 : 38,
// // //         minHeight: isNarrow ? 32 : 36
// // //       }}
// // //     >
// // //       {isHeaderCollapsed ? '▾' : '▴'}
// // //     </button>
// // //   </div>

// // //   {!isHeaderCollapsed && (
// // //     <>
// // //       {/* Status row */}
// // //       <div
// // //         style={{
// // //           display: 'flex',
// // //           alignItems: 'center',
// // //           justifyContent: 'space-between',
// // //           marginTop: 10,
// // //           gap: 12,
// // //           flexWrap: 'wrap'
// // //         }}
// // //       >
// // //         <span
// // //           style={{
// // //             display: 'inline-block',
// // //             padding: '4px 10px',
// // //             borderRadius: 999,
// // //             fontSize: chipFS,
// // //             fontWeight: 700,
// // //             background:
// // //               socketStatus === 'connected'
// // //                 ? '#E9F9EE'
// // //                 : socketStatus === 'connecting'
// // //                 ? '#FFF7E1'
// // //                 : '#FFECEC',
// // //             color:
// // //               socketStatus === 'connected'
// // //                 ? '#0A7A2A'
// // //                 : socketStatus === 'connecting'
// // //                 ? '#7A5D00'
// // //                 : '#B00020',
// // //             border:
// // //               socketStatus === 'connected'
// // //                 ? '1px solid #C6EBD1'
// // //                 : socketStatus === 'connecting'
// // //                 ? '1px solid #F4E3B0'
// // //                 : '1px solid #FFD0D0',
// // //             textTransform: 'lowercase'
// // //           }}
// // //           title={`Socket: ${socketStatus}`}
// // //         >
// // //           {socketStatus}
// // //         </span>

// // //         <div
// // //           style={{
// // //             fontSize: clamp('14px','2.6vw','18px'),
// // //             fontWeight: 700,
// // //             letterSpacing: 0.5,
// // //             color: '#111827'
// // //           }}
// // //         >
// // //           {filteredBuses.length} / {buses.length}
// // //         </div>
// // //       </div>

// // //       {/* Controls row */}
// // //       <div
// // //         style={{
// // //           display: 'flex',
// // //           alignItems: 'center',
// // //           gap: gap,
// // //           marginTop: gap,
// // //           flexWrap: 'wrap'
// // //         }}
// // //       >
// // //         <div style={{ position: 'relative', flex: '1 1 200px' }}>
// // //           <select
// // //             value={filterType}
// // //             onChange={(e) => setFilterType(e.target.value)}
// // //             title="Filter vehicles"
// // //             style={{
// // //               width: '100%',
// // //               padding: isNarrow ? '10px 34px 10px 12px' : '10px 40px 10px 14px',
// // //               borderRadius: 10,
// // //               border: '1px solid #D1D5DB',
// // //               background: '#FFFFFF',
// // //               fontWeight: 600,
// // //               cursor: 'pointer',
// // //               appearance: 'none',
// // //               WebkitAppearance: 'none',
// // //               MozAppearance: 'none'
// // //             }}
// // //           >
// // //             <option value="all">All Vehicles</option>
// // //             <option value="cart">Cart</option>
// // //             <option value="bus">Bus</option>
// // //             <optgroup label="Filter by Route">
// // //               {ROUTES.map(r => (
// // //                 <option key={r.code} value={`route:${r.code}`}>{r.label}</option>
// // //               ))}
// // //             </optgroup>
// // //           </select>

// // //           <div
// // //             style={{
// // //               position: 'absolute',
// // //               right: isNarrow ? 8 : 12,
// // //               top: '50%',
// // //               transform: 'translateY(-50%)',
// // //               width: 0,
// // //               height: 0,
// // //               borderLeft: '6px solid transparent',
// // //               borderRight: '6px solid transparent',
// // //               borderTop: '7px solid #111',
// // //               pointerEvents: 'none'
// // //             }}
// // //           />
// // //         </div>

// // //         <button
// // //           onClick={() => {
// // //             requestUserLocation();
// // //             setTimeout(() => {
// // //               window.location.reload();
// // //             }, 150);
// // //           }}
// // //           title="Recenter + Reload"
// // //           style={{
// // //             padding: '10px 14px',
// // //             borderRadius: 10,
// // //             border: '1px solid #D1D5DB',
// // //             background: '#FFFFFF',
// // //             fontWeight: 700,
// // //             cursor: 'pointer',
// // //             minHeight: isNarrow ? 36 : 40,
// // //             fontSize: clamp('13px','2.8vw','14px')
// // //           }}
// // //         >
// // //           Refresh
// // //         </button>

// // //         <button
// // //           onClick={() => setShowSchedule(true)}
// // //           title="Show schedule"
// // //           style={{
// // //             padding: '10px 16px',
// // //             borderRadius: 10,
// // //             border: '1px solid #0D47A1',
// // //             background: '#0D47A1',
// // //             color: '#FFFFFF',
// // //             fontWeight: 800,
// // //             cursor: 'pointer',
// // //             boxShadow: '0 6px 16px rgba(13,71,161,0.25)',
// // //             minHeight: isNarrow ? 36 : 40,
// // //             fontSize: clamp('13px','2.8vw','14px')
// // //           }}
// // //         >
// // //           Schedule
// // //         </button>
// // //       </div>

// // //       {maintenance && (
// // //         <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#fff8e1', border: '1px solid #f0d48a', fontWeight: 600 }}>
// // //           Under maintenance — {maintenanceReason}
// // //         </div>
// // //       )}

// // //       {locError && (
// // //         <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: '#FFECEC', border: '1px solid #FFD0D0', fontWeight: 600 }}>
// // //           {locError}

// // //           <button
// // //             type="button"
// // //             onClick={requestUserLocation}
// // //             style={{ marginLeft: 10, padding: '6px 10px', borderRadius: 10, border: '1px solid #D1D5DB', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
// // //             title={geoState === "denied" ? "Enable in site settings first, then try again" : "Try again"}
// // //           >
// // //             {geoState === "denied" ? "Open site settings" : "Allow location"}
// // //           </button>
// // //         </div>
// // //       )}
// // //     </>
// // //   )}
// // // </div>

// // // {/* {SHOW_MINT_SUMMIT_PROMO && (
// // //   <a
// // //     href={MINT_SUMMIT_REGISTRATION_URL}
// // //     target="_blank"
// // //     rel="noreferrer"
// // //     className="mint-summit-floating-banner"
// // //     style={{
// // //       top: isHeaderCollapsed ? 76 : 310,
// // //       width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
// // //     }}
// // //     title="Register for MInT Mobility Summit 2026"
// // //   >
// // //     <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
// // //     <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
// // //     <span className="mint-summit-cta">Register ↗</span>
// // //   </a>
// // // )} */}

// // // {SHOW_MINT_SUMMIT_PROMO && (
// // //   <a
// // //     href={MINT_SUMMIT_REGISTRATION_URL}
// // //     target="_blank"
// // //     rel="noreferrer"
// // //     className="mint-summit-floating-banner"
// // //     style={{
// // //       top: headerHeight
// // //         ? `calc(max(8px, env(safe-area-inset-top)) + ${headerHeight}px + ${isNarrow ? 8 : 10}px)`
// // //         : isHeaderCollapsed
// // //         ? 76
// // //         : 260,
// // //       width: isNarrow ? 'calc(100vw - 20px)' : 'min(520px, calc(100vw - 32px))',
// // //     }}
// // //     title="Register for MInT Mobility Summit 2026"
// // //   >
// // //     <span className="mint-summit-badge">MInT Summit 2026 ✨</span>
// // //     <span className="mint-summit-text">6–8 July · Mobility Digital Twins | NAC2: 611</span>
// // //     <span className="mint-summit-cta">Register ↗</span>
// // //   </a>
// // // )}

// // //       {/* Schedule Modal */}
// // //       {showSchedule && <ScheduleModal />}

// // //       {/* Map uses dynamic viewport on mobile (100dvh) */}
// // //       {/* <MapContainer center={center} zoom={DEFAULT_ZOOM} maxZoom={22} style={{ width: '100vw', height: '100dvh' }}> */}
// // //       <MapContainer center={mapCenter} zoom={DEFAULT_ZOOM} maxZoom={22} style={{ width: '100vw', height: '100dvh' }}>
// // //       <RecenterMap center={mapCenter} zoom={DEFAULT_ZOOM} />

// // //         <TileLayer
// // //           attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
// // //           url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
// // //           maxNativeZoom={19}
// // //           maxZoom={22}
// // //         />

// // //         {userLocation && (
// // //           <Marker position={{ lat: userLocation.lat, lng: userLocation.lng }} icon={userIconInstance}>
// // //             <Popup>
// // //               <div className="info-window">
// // //                 <h4>You are here</h4>
// // //               </div>
// // //             </Popup>
// // //           </Marker>
// // //         )}
// // //         {/* here i have updated the backend eta */}
// // //         {/* Bus Stops with backend ETA (polled every 5s) */}
// // //         {busStops.map((stop) => {
// // //           const etaState = stopEtas[stop.id];           // { data, error } or undefined
// // //           const payload = etaState?.data;               // backend JSON
// // //           const next = payload?.next || null;           // { vehicle, goingTowards, etaMinutes } | null

// // //           return (
// // //             <Marker
// // //               key={stop.id}
// // //               position={{ lat: stop.lat, lng: stop.lng }}
// // //               icon={stopIconInstance}
// // //             >
// // //               <Popup>
// // //                 <div className="info-window">
// // //                   <h4>{stop.name}</h4>

// // //                   {/* 1) Still loading / first poll */}
// // //                   {!etaState && <p>Loading ETA…</p>}

// // //                   {/* 2) Request for this stop failed */}
// // //                   {etaState && !payload && <p>ETA not available.</p>}

// // //                   {/* 3) Backend says: no approaching assignment */}
// // //                   {payload && payload.success && !next && (
// // //                     <p>No assigned vehicle approaching.</p>
// // //                   )}

// // //                   {/* 4) We have a next arrival from backend */}
// // //                   {payload && payload.success && next && (
// // //                     <>
// // //                       {/* You said you may not need vehicle number/type, 
// // //                           so we only show direction + ETA. */}
// // //                       <p>
// // //                           Next Vehicle Details:
// // //                         </p>
// // //                       {next.vehicle && (
// // //                         <p>
// // //                           <strong>Vehicle Type:</strong> {next.vehicle}
// // //                         </p>
// // //                       )}
// // //                       {next.goingTowards && (
// // //                         <p>
// // //                           <strong>Direction:</strong> {next.goingTowards}
// // //                         </p>
// // //                       )}
// // //                       {/* <p>
// // //                         <strong>ETA:</strong>{" "}
// // //                         {next.etaMinutes != null
// // //                           ? `${next.etaMinutes} min`
// // //                           : "— (has not departed yet)"}
// // //                       </p> */}
// // //                       <p>
// // //   <strong>ETA:</strong>{" "}
// // //   {next.etaStatus === "AT_STOP"
// // //     ? "0 min (at stop)"
// // //     : next.etaMinutes != null
// // //     ? `${next.etaMinutes} min`
// // //     : "— (has not departed yet)"}
// // // </p>
// // //                     </>
// // //                   )}
// // //                 </div>
// // //               </Popup>
// // //             </Marker>
// // //           );
// // //         })}



// // //         {/* Vehicles */}
// // //         {filteredBuses.map((bus) => {
// // //           // const label = vehicleLabel(bus.vehicleno);
// // //           const rc = assignments[bus.vehicleno];
// // //           const label = vehicleLabel(bus.vehicleno, rc);
// // //           const palette = isCart(bus.vehicleno) ? CART_PALETTE : BUS_PALETTE;
// // //           const info = nearestStopEtaForBusOnItsRoute(bus);
// // //           // const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
// // //           // const eta = info ? `${info.etaMin} min` : 'Unknown';
// // //           const nearestName = info ? (busStops.find(s => s.id === info.stopId)?.name) : null;
// // //           const eta =
// // //             info && info.etaMin != null
// // //               ? `${info.etaMin} min`
// // //               : info
// // //               ? "—"
// // //               : "Unknown";

// // //           return (
// // //             <Marker
// // //               key={bus.vehicleno}
// // //               position={{ lat: bus.latitude, lng: bus.longitude }}
// // //               // icon={createVehicleDivIcon(label, bus.heading)}
// // //               icon={createVehicleDivIcon(label, bus.heading, palette)}

// // //             >
// // //               <Popup>
// // //                 <div className="info-window">
// // //                   <h4>{bus.vehicleno} ({label})</h4>
// // //                   {/* <p><strong>Legend:</strong> B = Bus, C = Cart</p> */}
// // //                   <p><strong>Legend:</strong> B = Bus, C = Cart, CR = Cart (RP↔ED)</p>
// // //                   <p><strong>Speed:</strong> {Number.isFinite(bus.speed) ? bus.speed.toFixed(1) : 'N/A'} km/h</p>
// // //                   {assignments[bus.vehicleno] && (
// // //                     <p><strong>Route:</strong> {ROUTES.find(r=>r.code===assignments[bus.vehicleno])?.label || assignments[bus.vehicleno]}</p>
// // //                   )}
// // //                   {nearestName && <p><strong>Next Stop:</strong> {nearestName}</p>}
// // //                   {/* <p><strong>ETA:</strong> {eta}</p> */}
// // //                   {/* <p>
// // //                     <strong>ETA:</strong>{" "}
// // //                     {eta}
// // //                     {info && info.waitingAtFirstStop ? " (has not departed yet)" : ""}
// // //                   </p> */}
// // //                   <p>
// // //   <strong>ETA:</strong>{" "}
// // //   {eta}
// // //   {info && info.waitingAtStop ? " (waiting at stop)" : ""}
// // // </p>
// // //                   {Number.isFinite(bus.heading) && <p><strong>Heading:</strong> {Math.round(bus.heading)}°</p>}
// // //                 </div>
// // //               </Popup>
// // //             </Marker>
// // //           );
// // //         })}
// // //       </MapContainer>

// // //       {/* Footer: Feedback link */}
// // //       <div
// // //         style={{
// // //           position: 'fixed',
// // //           left: 0,
// // //           right: 0,
// // //           bottom: -5,
// // //           display: 'flex',
// // //           justifyContent: 'center',
// // //           zIndex: 10000,
// // //           pointerEvents: 'none',
// // //           paddingBottom: 'max(6px, env(safe-area-inset-bottom))'
// // //         }}
// // //       >
// // //         <div
// // //           style={{
// // //             width: '100vw',
// // //             pointerEvents: 'auto',
// // //             display: 'flex',
// // //             alignItems: 'center',
// // //             justifyContent: 'space-between',
// // //             gap: 12,
// // //             padding: isNarrow ? '6px 10px' : '6px 16px',
// // //             borderRadius: 14,
// // //             background:
// // //               'linear-gradient(135deg, rgba(17,24,39,0.85), rgba(31,41,55,0.85))',
// // //             color: '#F9FAFB',
// // //             border: '1px solid rgba(255,255,255,0.14)',
// // //             boxShadow:
// // //               '0 12px 30px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
// // //             backdropFilter: 'saturate(160%) blur(10px)',
// // //             WebkitBackdropFilter: 'saturate(160%) blur(10px)',
// // //             fontFamily:
// // //               '"SF Pro Text", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"',
// // //             fontSize: clamp('12px','2.8vw','14px'),
// // //             lineHeight: 1.4,
// // //             fontWeight: 500,
// // //             letterSpacing: '0.2px',
// // //           }}
// // //         >
// // //           <div
// // //             style={{
// // //               display: 'flex',
// // //               alignItems: 'center',
// // //               gap: 10,
// // //               flex: 1,
// // //               minWidth: 0,
// // //             }}
// // //           >
// // //             <div
// // //               style={{
// // //                 width: 8,
// // //                 height: 8,
// // //                 borderRadius: 9999,
// // //                 background:
// // //                   'radial-gradient(circle at 30% 30%, #34D399, #059669)',
// // //                 boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
// // //                 flex: '0 0 auto',
// // //               }}
// // //             />
// // //             <span
// // //               style={{
// // //                 opacity: 0.95,
// // //                 whiteSpace: 'nowrap',
// // //                 overflow: 'hidden',
// // //                 textOverflow: 'ellipsis',
// // //               }}
// // //             >
// // //                Amit Kumar | CE20B009 | MInT Lab
// // //             </span>
// // //           </div>

// // //           {/* {FEEDBACK_URL ? (
// // //             <a
// // //               href={FEEDBACK_URL}
// // //               target="_blank"
// // //               rel="noreferrer"
// // //               style={{
// // //                 display: 'inline-flex',
// // //                 alignItems: 'center',
// // //                 justifyContent: 'center',
// // //                 padding: '8px 12px',
// // //                 borderRadius: 10,
// // //                 border: '1px solid rgba(255,255,255,0.18)',
// // //                 background:
// // //                   'linear-gradient(180deg, rgba(128, 126, 126, 0.14), rgba(221, 212, 212, 0.06))',
// // //                 color: '#F9FAFB',
// // //                 textDecoration: 'none',
// // //                 fontWeight: 600,
// // //                 letterSpacing: '0.25px',
// // //                 transform: 'translateZ(0)',
// // //                 transition: 'transform 120ms ease, opacity 120ms ease',
// // //                 cursor: 'pointer',
// // //                 boxShadow:
// // //                   'inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 14px rgba(0,0,0,0.25)',
// // //               }}
// // //               onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
// // //               onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
// // //             >
// // //               Open feedback form ↗
// // //             </a>
// // //           ) : ( */}

// // //           {FOOTER_ACTION_URL ? (
// // //   <a
// // //     href={FOOTER_ACTION_URL}
// // //     target="_blank"
// // //     rel="noreferrer"
// // //     className={
// // //       SHOW_MINT_SUMMIT_PROMO
// // //         ? 'footer-action-button mint-summit-glitter-button'
// // //         : 'footer-action-button'
// // //     }
// // //     title={FOOTER_ACTION_LABEL}
// // //   >
// // //     {FOOTER_ACTION_LABEL}
// // //   </a>
// // // ) : (
// // //             <span
// // //               style={{
// // //                 padding: '8px 12px',
// // //                 borderRadius: 10,
// // //                 border: '1px dashed rgba(255,255,255,0.25)',
// // //                 color: 'rgba(249,250,251,0.8)',
// // //                 fontWeight: 500,
// // //               }}
// // //             >
// // //               {/* &lt;form-link&gt; */}
// // //               &lt;form-link&gt;
// // //             </span>
// // //           )}
// // //         </div>
// // //       </div>

// // //     </div>
// // //   );
// // // }

// // // function MaintenancePoster() {
// // //   return (
// // //     <div className="maintenance-page">
// // //       <div className="maintenance-card">
// // //         <div className="maintenance-logo-wrap">
// // //           <img src={iitmLogo} alt="IIT Madras" className="maintenance-logo" />
// // //         </div>

// // //         <div className="maintenance-badge">Server migration in progress</div>

// // //         <h1>We’ll be back very soon</h1>

// // //         <p className="maintenance-main-text">
// // //           Hey, I'm Amit Kumar. Thank you for using Moving Mountains. We value your time and effort.
// // //           Our server is currently being upgraded to provide a smoother, faster,
// // //           and more reliable experience.
// // //         </p>

// // //         <p className="maintenance-sub-text">
// // //           The website will be available again shortly once the migration is
// // //           completed. We appreciate your patience and support.
// // //         </p>

// // //         <div className="maintenance-loader">
// // //           <span></span>
// // //           <span></span>
// // //           <span></span>
// // //         </div>

// // //         <div className="maintenance-footer">
// // //           Thanks for using our service.
// // //           <br />
// // //           — Team Moving Mountains
// // //         </div>
// // //       </div>
// // //     </div>
// // //   );
// // // }

// // // /* ---------------------------- APP (router) ---------------------------- */

// // // export default function App() {
// // //   const { path, navigate } = usePath();
// // //   const token = getToken();
// // //   const adminToken = getAdminToken();

// // //   // Track page views on main map for Prometheus
// // //   useEffect(() => {
// // //     if (!BACKEND) return;
// // //     if (path !== "/") return;
// // //     axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
// // //   }, [path]);

// // //   // Forward /driver -> /driverRoute when logged in
// // //   useEffect(() => {
// // //     if (path === "/driver" && token) {
// // //       navigate("/driverRoute");
// // //     }
// // //   }, [path, token, navigate]);

// // //   // Admin redirects
// // //   useEffect(() => {
// // //     if (path === "/admin/login" && adminToken) {
// // //       navigate("/admin");
// // //     } else if (path === "/admin" && !adminToken) {
// // //       navigate("/admin/login");
// // //     }
// // //   }, [path, adminToken, navigate]);

// // //   if (MAINTENANCE_MODE) {
// // //     return <MaintenancePoster />;
// // //   }

// // //   if (path === "/driver") {
// // //     if (token) return null;
// // //     return <DriverLogin navigate={navigate} />;
// // //   }
// // //   if (path === "/driverRoute") return <DriverRoute navigate={navigate} />;

// // //   if (path === "/admin/login") {
// // //     if (adminToken) return null;
// // //     return <AdminLogin navigate={navigate} />;
// // //   }
// // //   if (path === "/admin") {
// // //     return <AdminDashboard navigate={navigate} />;
// // //   }

// // //   return <MapPage />;
// // // }

// // // // export default function App() {
// // // //   const { path, navigate } = usePath();
// // // //   const token = getToken();

// // // //   // Track page views on main map for Prometheus
// // // //   useEffect(() => {
// // // //     if (!BACKEND) return;
// // // //     if (path !== "/") return;
// // // //     axios.post(`${BACKEND}/api/track/page-view`).catch(() => {});
// // // //   }, [path]);

// // // //   // Forward /driver -> /driverRoute when logged in (no hooks called conditionally)
// // // //   useEffect(() => {
// // // //     if (path === '/driver' && token) {
// // // //       navigate('/driverRoute');
// // // //     }
// // // //   }, [path, token, navigate]);

// // // //   if (path === '/driver') {
// // // //     if (token) return null;
// // // //     return <DriverLogin navigate={navigate} />;
// // // //   }
// // // //   if (path === '/driverRoute') return <DriverRoute navigate={navigate} />;
// // // //   return <MapPage />;
// // // // }